/**
 * Main Application JavaScript
 * Migrated to Bootstrap 5 + Vite
 */

// Import plugins and global libraries
import { Litepicker } from './plugins.js';

/**
 * Bootstrap 5 + CKEditor 4 Focus Trap Fix
 *
 * Bootstrap 5 modals use focus trap to prevent focus from leaving the modal.
 * CKEditor dialogs (link, image, etc.) conflict with this, causing errors.
 *
 * Solution: Disable focus trap for modals containing CKEditor instances
 * and handle focus management manually to allow CKEditor dialogs to work.
 */
document.addEventListener('DOMContentLoaded', function() {
    // Override Bootstrap Modal focus trap when CKEditor is present
    const originalModalShow = bootstrap.Modal.prototype.show;
    bootstrap.Modal.prototype.show = function() {
        const modalElement = this._element;

        // Check if modal will contain CKEditor
        const hasCKEditor = modalElement.querySelector('textarea[id*="email"], textarea[id*="Bookmark"], textarea[id*="writtenRemark"]');

        if (hasCKEditor) {
            // Disable focus trap for modals with CKEditor
            if (!modalElement.hasAttribute('data-bs-focus')) {
                modalElement.setAttribute('data-bs-focus', 'false');
            }
        }

        return originalModalShow.call(this);
    };

    // Additional safety: prevent focus trap from interfering with CKEditor dialogs
    document.addEventListener('focusin', function(e) {
        // Allow focus in CKEditor dialogs
        if (e.target.closest('.cke_dialog')) {
            e.stopImmediatePropagation();
        }
    }, true);
});

/**
 * Ensure legacy Bootstrap 3 data-* attributes continue to work with Bootstrap 5.
 * Copies attributes such as data-toggle => data-bs-toggle while keeping originals intact.
 */
function migrateLegacyBootstrapAttributes(root) {
    var context = root && root.nodeType ? root : document;
    var mappings = [
        { oldAttr: 'data-toggle', newAttr: 'data-bs-toggle' },
        { oldAttr: 'data-target', newAttr: 'data-bs-target' },
        { oldAttr: 'data-dismiss', newAttr: 'data-bs-dismiss' },
        { oldAttr: 'data-spy', newAttr: 'data-bs-spy' }
    ];

    mappings.forEach(function (mapping) {
        var selector = '[' + mapping.oldAttr + ']';

        // Handle the root element itself if it carries the legacy attribute
        if (context.nodeType === 1 && context.hasAttribute(mapping.oldAttr) && !context.hasAttribute(mapping.newAttr)) {
            context.setAttribute(mapping.newAttr, context.getAttribute(mapping.oldAttr));
        }

        var nodes = (context.querySelectorAll ? context.querySelectorAll(selector) : []);
        Array.prototype.forEach.call(nodes, function (node) {
            if (!node.hasAttribute(mapping.newAttr)) {
                node.setAttribute(mapping.newAttr, node.getAttribute(mapping.oldAttr));
            }
        });
    });
}

/**
 * Observe DOM mutations so dynamically inserted fragments also get upgraded.
 */
function startBootstrapAttributeObserver() {
    if (!window.MutationObserver || !document.body) {
        return;
    }

    var observer = new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
            Array.prototype.forEach.call(mutation.addedNodes || [], function (node) {
                if (node.nodeType === 1) {
                    migrateLegacyBootstrapAttributes(node);
                    refreshBootstrapInteractions(node);
                }
            });
            Array.prototype.forEach.call(mutation.removedNodes || [], function (node) {
                if (node.nodeType === 1) {
                    disposeBootstrapInteractions(node);
                }
            });
        });
        removeOrphanTooltips();
    });

    observer.observe(document.body, { childList: true, subtree: true });
}

/**
 * Collect an element plus its matching descendants for a given selector,
 * including the root itself when it matches (querySelectorAll only finds descendants).
 */
function collectMatching(root, selector) {
    var elements = root.querySelectorAll ? Array.prototype.slice.call(root.querySelectorAll(selector)) : [];
    if (root.matches?.(selector)) {
        elements.unshift(root);
    }
    return elements;
}

/**
 * Re-initialise tooltips and popovers on demand using the Bootstrap 5 API.
 */
function refreshBootstrapInteractions(root) {
    if (!window.bootstrap) {
        return;
    }

    var context = root && root.querySelectorAll ? root : document;

    collectMatching(context, '[data-bs-toggle="tooltip"]').forEach(function (el) {
        initBootstrapInteraction(el, window.bootstrap.Tooltip);
    });

    collectMatching(context, '[data-bs-toggle="popover"]').forEach(function (el) {
        initBootstrapInteraction(el, window.bootstrap.Popover);
    });
}

function initBootstrapInteraction(el, Plugin) {
    var alreadyExisted = !!Plugin.getInstance(el);
    var instance = Plugin.getOrCreateInstance(el);

    if (alreadyExisted) {
        return;
    }

    window.requestAnimationFrame(function () {
        if (document.activeElement !== el) {
            instance.hide();
        }
    });
}

/**
 * Dispose tooltip/popover instances on elements removed from the DOM so their
 * popup markup (appended to document.body, separate from the trigger element)
 * doesn't linger after the trigger itself is gone (e.g. after an AJAX re-render).
 */
function disposeBootstrapInteractions(root) {
    if (!window.bootstrap || !root.querySelectorAll) {
        return;
    }

    collectMatching(root, '[data-bs-toggle="tooltip"]').forEach(function (el) {
        var tooltip = window.bootstrap.Tooltip.getInstance(el);
        if (tooltip) {
            tooltip.dispose();
        }
    });

    collectMatching(root, '[data-bs-toggle="popover"]').forEach(function (el) {
        var popover = window.bootstrap.Popover.getInstance(el);
        if (popover) {
            popover.dispose();
        }
    });
}

/**
 * Remove tooltip/popover popups whose trigger is no longer in the document (e.g. a list item
 * re-rendered while its tooltip was shown); otherwise the popup stays in the top left corner.
 * Bootstrap links the trigger to the popup with aria-describedby while the popup is shown.
 */
function removeOrphanTooltips() {
    document.querySelectorAll('.tooltip[id], .popover[id]').forEach(function (tip) {
        if (!document.querySelector('[aria-describedby~="' + tip.id + '"]')) {
            tip.remove();
        }
    });
}

// Tooltip on a button that triggers an action is hidden on click, so it does not linger
// when the action re-renders or removes the button (e.g. "Premakni med pretekla članstva").
document.addEventListener('click', function (e) {
    var trigger = e.target.closest && e.target.closest('[data-bs-toggle="tooltip"]');
    if (trigger && window.bootstrap) {
        var tooltip = window.bootstrap.Tooltip.getInstance(trigger);
        if (tooltip) {
            tooltip.hide();
        }
    }
}, true);

var JsonData = {};
JsonData.education = null;
JsonData.language = null;
JsonData.company = null;

$(document).ready(function () {

    migrateLegacyBootstrapAttributes();
    startBootstrapAttributeObserver();
    refreshBootstrapInteractions();

    $('.panel-group .collapse').each(function () {
        var header = $(this).prev('.card-header, .panel-heading');
        if ($(this).hasClass('show') || $(this).hasClass('in')) {
            header.addClass('is-open');
        }
    }).on('show.bs.collapse', function () {
        $(this).prev('.card-header, .panel-heading').addClass('is-open');
    }).on('hide.bs.collapse', function () {
        $(this).prev('.card-header, .panel-heading').removeClass('is-open');
    });


    /*** 2-factor authentication ***/
    $('.securityCode2fa').each(function () {
        var container = $(this);
        var inputs = container.find('input');

        inputs.each(function (index) {
            var thisInput = $(this);

            $(thisInput).on('keyup', function (e) {
                var thisField = $(this);
                if (e.keyCode == 8 || e.keyCode == 46) {
                    e.preventDefault();
                    $(thisField).val('');
                    $(thisField).prev().focus();
                    return;
                }

                var maxLen = $(thisField).attr('maxlength') || 1;
                if ($(thisField).val().length >= maxLen) {
                    $(thisField).next().focus();
                }
            });

            if (index === 0) {
                $(thisInput).on('paste', function (e) {
                    e.preventDefault();
                    var clipboardData = (e.originalEvent || e).clipboardData;
                    var pastedData = clipboardData.getData('text').replace(/\D/g, '');
                    if (!pastedData) {
                        return;
                    }

                    inputs.each(function (i) {
                        $(this).val(pastedData.charAt(i) || '');
                    });

                    inputs.eq(Math.min(pastedData.length, inputs.length - 1)).focus();
                });
            }
        });

        inputs.first().trigger('focus');
    });


    // recalculate footer position on toggle element click
    $('body').on('click', '[data-toggle="collapse"], [data-bs-toggle="collapse"]', function (e) {
        setTimeout(stickyFooter, 300);
    });


    /*** Globals ***/
    refreshBootstrapInteractions();

    // Dostopnost (ID 15): vprašanja DA/NE so radijska gumba (fragment a11y :: yesno). Izbira prikaže pripadajoči
    // razdelek (data-toggle-pane) in skrije sosednje v istem .tab-content; ob nalaganju se prikaže razdelek izbrane vrednosti.
    function showYesNoPane(radio) {
        var target = radio.getAttribute('data-toggle-pane');
        if (!target) {
            return;
        }
        var $pane = $(target);
        $pane.siblings('.tab-pane').removeClass('show active in');
        $pane.addClass('show active');
        setTimeout(stickyFooter, 200);
    }

    $('body').on('change', '.yesNo input[type="radio"]', function () {
        if (this.checked) {
            showYesNoPane(this);
        }
    });

    $('.yesNo input[type="radio"]:checked').each(function () {
        showYesNoPane(this);
    });

    // required fields
    $('body').find('.form-group.required').each(function () {
        var thisFormGroup = $(this);

        if ($(thisFormGroup).find('label.control-label span').html() != '*') {
            $(thisFormGroup).find('label.control-label').append('<span>*</span>');
        }
    });

    // remove form error class
    $("body").on('cssClassChanged', '.form-group', function () {
        var thisFormGroup = $(this)[0];

        if (!$(thisFormGroup).is('.has-error')) {
            $(thisFormGroup).find('.help-block.has-error').remove();
        }
    });

    $('body').on('change', '.form-group.has-error :input', function () {
        var thisFormGroup = $(this).closest('.form-group');
        $(thisFormGroup).find('.help-block.has-error').remove();
        $(thisFormGroup).removeClass('has-error');
        $(thisFormGroup).find('.has-error').removeClass('has-error');
        // dostopnost (ID 19): popravljeno polje ni več označeno kot napačno
        $(thisFormGroup).find('[aria-invalid="true"]').removeAttr('aria-invalid');
    });


    // close popover on click outside
    $('html').on('mouseup', function (e) {
        if (!$(e.target).closest('.popover').length) {
            $('.popover').each(function () {
                $(this.previousSibling).popover('hide');
            });
        }
    });

    $('#logoutBtn').on('click', function(e) {
        e.preventDefault();
        $.ajax({
            url: '/logout',
            type: 'POST'
        }).always(function() {
            window.location.href = '/login';
        });
    });

});


function stickyFooter() {
    // Modern sticky footer using CSS flexbox (see _globals.scss)
    // This function ensures proper layout after dynamic content changes

    // Ensure body has footerInitBottom class if not already present
    if (!document.body.classList.contains('footerInitBottom')) {
        return; // Only apply to pages that opt-in with footerInitBottom class
    }

    // Force a reflow to ensure flexbox calculations are updated
    // Useful when content changes dynamically (e.g., DataTables, TinyMCE)
    const footer = document.querySelector('footer');
    if (footer) {
        // Trigger reflow by accessing offsetHeight
        void footer.offsetHeight;
    }
}

window.stickyFooter = stickyFooter;

/**
 * Dostopnost (WCAG 2.1.1, 4.1.2 – register ID 33): potrditveno polje v oznaki-gumbu.
 * Stanje nosi pravo, vizualno skrito polje (.visually-hidden), ki ga oznaka preklaplja
 * nativno – z miško, s tipkovnico (preslednica) in z bralnikom zaslona. Ta funkcija
 * samo uskladi videz oznake (ikona, barva) s stanjem polja; kliče se ob dogodku 'change'.
 */
function syncToggleLabel(input, activeClass = 'btn-success', inactiveClass = 'btn-secondary') {
    var $label = $(input).closest('label');
    var checked = input.checked;
    $label.find('i.fa-square, i.fa-check-square').toggleClass('fa-check-square', checked).toggleClass('fa-square', !checked);
    $label.toggleClass(activeClass, checked);
    if (inactiveClass) {
        $label.toggleClass(inactiveClass, !checked);
    }
    return checked;
}

window.syncToggleLabel = syncToggleLabel;

// Gumb-povezava (<a class="btn">), onemogočena le z razredom .disabled, mora stanje sporočiti
// tudi podpornim tehnologijam (aria-disabled) in je ne sme biti mogoče sprožiti s tipkovnico.
function setButtonLinkDisabled($el, disabled) {
    $el.toggleClass('disabled', disabled).attr('aria-disabled', disabled ? 'true' : null);
}

window.setButtonLinkDisabled = setButtonLinkDisabled;

$(document).on('click', 'a.btn[aria-disabled="true"]', function (e) {
    e.preventDefault();
    e.stopImmediatePropagation();
});

// Napoved statusa bralniku zaslona brez premika fokusa (WCAG 4.1.3). Ključ je ime data-msg-* na #a11yStatus.
function a11yAnnounce(key) {
    var region = document.getElementById('a11yStatus');
    if (!region) {
        return;
    }
    var msg = region.getAttribute('data-msg-' + key) || key;
    region.textContent = '';
    setTimeout(function () {
        region.textContent = msg;
    }, 100);
}

window.a11yAnnounce = a11yAnnounce;

// Napoved poljubnega besedila (izprazni in po kratkem zamiku zapiše – ponovljeno besedilo se znova napove)
function a11yAnnounceText(text) {
    var region = document.getElementById('a11yStatus');
    if (!region) {
        return;
    }
    region.textContent = '';
    setTimeout(function () {
        region.textContent = text;
    }, 100);
}

// ID 16: ikonski gumbi v seznamih vnosov (Uredi/Izbriši ...) dobijo razločljivo ime z nazivom vnosa,
// npr. "Uredi zapis: Univerza v Ljubljani".
function a11yLabelItemActions(root) {
    $(root || document).find('.resultItem').each(function () {
        var title = $(this).find('.textContainer p').filter(function () {
            return String($(this).text() ?? '').trim() !== '';
        }).first().text().replace(/\s+/g, ' ').trim();
        if (title.length > 80) {
            title = title.substring(0, 80) + '…';
        }
        $(this).find('.a11y-action-label').each(function () {
            var base = $(this).attr('data-base') || String($(this).text() ?? '').trim();
            $(this).attr('data-base', base).text(title ? base + ': ' + title : base);
        });
    });
}

window.a11yLabelItemActions = a11yLabelItemActions;

// Sprožilci collapse (npr. "Pojasnilo") – aria-expanded sledi dejanskemu stanju panela. Bootstrap ga posodobi
// ob preklopu, strani pa panele odpirajo tudi neposredno z .addClass('show'), zato opazujemo spremembe razreda.
function syncCollapseTrigger(target) {
    if (!target.id) {
        return;
    }
    var expanded = target.classList.contains('show') ? 'true' : 'false';
    document.querySelectorAll('[data-bs-toggle="collapse"][data-bs-target="#' + target.id + '"]').forEach(function (trigger) {
        // aria-expanded je dovoljen le na gumbih/povezavah (ne npr. na <label>)
        if (/^(BUTTON|A)$/.test(trigger.tagName) || trigger.getAttribute('role') === 'button') {
            trigger.setAttribute('aria-expanded', expanded);
        } else {
            trigger.removeAttribute('aria-expanded'); // Bootstrap ga ob kliku doda tudi na <label>
        }
    });
}

$(function () {
    var collapseObserver = new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
            syncCollapseTrigger(m.target);
        });
    });
    $('[data-bs-toggle="collapse"][data-bs-target]').each(function () {
        var target = document.querySelector($(this).attr('data-bs-target'));
        if (target) {
            syncCollapseTrigger(target);
            collapseObserver.observe(target, {attributes: true, attributeFilter: ['class']});
        }
    });
    a11yLabelItemActions(document);

    // ID 33: začetni videz vseh potrditvenih polj v oznakah-gumbih uskladimo z dejanskim stanjem polja
    // (tudi na straneh samo za branje), da slika in bralnik zaslona vedno sporočata isto.
    $('label.btn > input[type="checkbox"].visually-hidden, label.checkboxLabel > input[type="checkbox"].visually-hidden').each(function () {
        var $label = $(this).closest('label');
        if ($label.hasClass('checkboxLabel')) {
            syncToggleLabel(this, 'checked', null);
        } else if ($label.hasClass('rejectButton')) {
            syncToggleLabel(this, 'btn-warning');
        } else {
            syncToggleLabel(this);
        }
    });
});

function initDatePicker(elementId, options = {}) {
    const element = document.getElementById(elementId);

    if (!element) {
        console.log(`Element with id "${elementId}" not found`);
        return null;
    }

    const existingPicker = $(element).data('litepicker');
    if (existingPicker) {
        existingPicker.setOptions(options);
        return existingPicker;
    }

    const defaultOptions = {
        element: element,
        format: 'D. M. YYYY',
        // jezik koledarja (imena mesecev in dni) sledi jeziku strani (WCAG 3.1.2)
        lang: (document.documentElement.getAttribute('lang') || 'sl').substring(0, 2) === 'en' ? 'en-GB' : 'sl',
        allowRepick: true,
        autoApply: true,
        autoRefresh: true,
        dropdowns: {
            minYear: 1900,
            maxYear: 2100,
            months: true,
            years: true
        },
        setup: (picker) => {
            let isManualInput = false;

            const parseManualInput = () => {
                if (!isManualInput) return;

                const value = element.value.trim();

                if (value === '') {
                    picker.clearSelection();
                    setTimeout(() => {
                        picker.hide();
                    }, 10);
                    isManualInput = false;
                    return;
                }

                const match = value.match(/^(\d{1,2})\.?\s*(\d{1,2})\.?\s*(\d{4})$/);

                if (match) {
                    const day = parseInt(match[1], 10);
                    const month = parseInt(match[2], 10);
                    const year = parseInt(match[3], 10);

                    try {
                        picker.setDate(new Date(year, month - 1, day));
                    } catch (e) {
                        console.log('Invalid date');
                    }
                }

                setTimeout(() => {
                    picker.hide();
                }, 10);

                isManualInput = false;
            };

            element.addEventListener('input', () => {
                isManualInput = true;
            });

            element.addEventListener('blur', () => {
                parseManualInput();
            });

            element.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.keyCode === 13) {
                    e.preventDefault();
                    isManualInput = true;
                    parseManualInput();
                    element.blur();
                }
            });

            picker.on('show', () => {
                isManualInput = false;
            });
        }
    };

    const pickerOptions = { ...defaultOptions, ...options };

    const picker = new Litepicker(pickerOptions);
    $(element).data('litepicker', picker);
    a11yDatePicker(element, picker);

    return picker;
}

// Dostopnost (ID 18): datum je mogoče vnesti ročno (razčlenitev zgoraj), koledar je le pomoč.
// Polje dobi vidno navodilo o obliki (aria-describedby), izbirnika meseca/leta v koledarju imata ime, Esc zapre koledar.
function a11yDatePicker(element, picker) {
    var hint = a11yMsg('date-format');
    if (hint && element.id && !document.getElementById(element.id + '-format')) {
        var $hint = $('<div class="form-text a11y-date-format"></div>').attr('id', element.id + '-format').text(hint);
        var $parent = $(element).parent();
        if ($parent.is('.d-flex, .d-inline-flex')) {
            // polje v vrstici z gumbi (admin) – navodilo pod celotno vrstico kontrolnikov, poravnano s poljem
            var col = ($parent.attr('class') || '').match(/\bcol-(sm|md)-(\d+)\b/);
            if (col) {
                $hint.addClass('col-' + col[1] + '-' + col[2] + ' offset-' + col[1] + '-' + (12 - parseInt(col[2], 10)));
            }
            $parent.after($hint);
        } else {
            $(element).after($hint);
        }
        a11yAddDescribedBy(element, element.id + '-format');
    }
    element.setAttribute('autocomplete', 'off');
    element.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && picker.ui && picker.ui.offsetParent !== null) {
            picker.hide();
            e.stopPropagation(); // ne zapri obdajajočega pogovornega okna
        }
    });
    picker.on('render', function (ui) {
        $(ui).find('select.month-item-name').attr('aria-label', a11yMsg('date-month'));
        $(ui).find('select.month-item-year').attr('aria-label', a11yMsg('date-year'));
        if (!ui.__a11yEsc) {
            ui.__a11yEsc = true;
            // Esc tudi iz kontrolnikov koledarja: zapri in vrni fokus v polje
            ui.addEventListener('keydown', function (e) {
                if (e.key === 'Escape') {
                    e.stopPropagation();
                    picker.hide();
                    element.focus();
                }
            });
        }
    });
}

window.a11yDatePicker = a11yDatePicker;

function initDateTimePicker(elementId, options = {}) {
    const element = document.getElementById(elementId);
    if (!element) {
        console.log(`Element with id "${elementId}" not found`);
        return null;
    }

    const minuteStep     = options.minuteStep     ?? 1;
    const defaultHours   = options.defaultHours   ?? 0;
    const defaultMinutes = options.defaultMinutes ?? 0;

    let defaultDate = options.defaultDate ?? null;
    if (defaultDate) {
        const d = new Date(defaultDate);
        d.setHours(defaultHours, defaultMinutes, 0, 0);
        defaultDate = d;
    }

    const fp = flatpickr(element, {
        locale: 'sl',
        dateFormat: 'j. n. Y H:i',
        enableTime: true,
        time_24hr: options.time24hr ?? true,
        minuteIncrement: minuteStep,
        allowInput: true,
        disableMobile: true,
        defaultDate,
        minDate: options.minDate ?? null,
        maxDate: options.maxDate ?? null,

        parseDate(dateStr) {
            const match = dateStr.trim().match(
                /^(\d{1,2})\.?\s*(\d{1,2})\.?\s*(\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/
            );
            if (!match) return null;
            return new Date(
                parseInt(match[3]),
                parseInt(match[2]) - 1,
                parseInt(match[1]),
                parseInt(match[4] ?? defaultHours),
                parseInt(match[5] ?? defaultMinutes),
                0
            );
        },

        onChange(selectedDates) {
            if (options.onChange) options.onChange(selectedDates[0] ?? null);
        },

        onClose(selectedDates) {
            if (options.onClose) options.onClose(selectedDates[0] ?? null);
        }
    });

    const pad = n => String(n).padStart(2, '0');

    fp.getDateTime    = () => fp.selectedDates[0] ?? null;

    fp.getDateTimeISO = () => {
        const d = fp.getDateTime();
        if (!d) return null;
        return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}` +
            `T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };

    fp.setDateTime = (date) => fp.setDate(date, true);

    return fp;
}

function initDateRangePickers(fromId, toId) {
    const fromPicker = initDatePicker(fromId);
    const toPicker = initDatePicker(toId);

    if (!fromPicker || !toPicker) {
        console.log('Could not initialize date range pickers');
        return null;
    }

    fromPicker.on('selected', (date) => {
        toPicker.setOptions({ minDate: date.dateInstance });
    });

    toPicker.on('selected', (date) => {
        fromPicker.setOptions({ maxDate: date.dateInstance });
    });

    fromPicker.on('clear', () => {
        toPicker.setOptions({ minDate: null });
    });

    toPicker.on('clear', () => {
        fromPicker.setOptions({ maxDate: null });
    });

    const fromElement = document.getElementById(fromId);
    const toElement = document.getElementById(toId);

    fromElement.addEventListener('blur', () => {
        setTimeout(() => {
            const fromDate = fromPicker.getDate();
            if (fromDate) {
                toPicker.setOptions({ minDate: fromDate });
            } else {
                toPicker.setOptions({ minDate: null });
            }
        }, 50);
    });

    toElement.addEventListener('blur', () => {
        setTimeout(() => {
            const toDate = toPicker.getDate();
            if (toDate) {
                fromPicker.setOptions({ maxDate: toDate });
            } else {
                fromPicker.setOptions({ maxDate: null });
            }
        }, 50);
    });

    return { fromPicker, toPicker };
}


// Registracija
async function registration() {
    // Wait for pwstrength plugin to load before initializing
    await window.pwstrengthReady;

    i18next.init({
        lng: 'si',
        resources: {
            si: {
                translation: {
                    "wordLength": "Geslo je prekratko",
                    "wordNotEmail": "V geslu ne sme biti e-poštnega naslova",
                    "wordSimilarToUsername": "Geslo ne sme vsebovati uporabniškega imena",
                    "wordTwoCharacterClasses": "Use different character classes",
                    "wordRepetitions": "Too many repetitions",
                    "wordSequences": "Your password contains sequences",
                    "errorList": "Napake:",
                    "veryWeak": "Zelo šibko",
                    "weak": "Šibko",
                    "normal": "Povprečno",
                    "medium": "Srednje varno",
                    "strong": "Varno",
                    "veryStrong": "Zelo varno"
                }
            }
        }
    }, function () {
        // Initialized and ready to go
        var options = {};
        options.ui = {
            container: "#pwd-container",
            showVerdictsInsideProgressBar: true,
            viewports: {
                progress: ".pwstrength_viewport_progress"
            }
        };
        options.common = {
            debug: false,
            showErrors: true,
            usernameField: "#email"
        };
        $(':password').pwstrength(options);

    });


    $('.textUpperCase').keyup(function () {
        $(this).val($(this).val().toUpperCase());
    });


    // Dostopnost (ID 11): potrditev privolitve ni več pogojena z drsenjem do konca besedila
    // (bralnik zaslona, tipkovnica in povečava tega ne omogočajo). Polje je vedno omogočeno.
    $('body').on('change', '.legalAgreementBtn input[type="checkbox"]', function () {
        syncToggleLabel(this);
    });
    $('.legalAgreementBtn input[type="checkbox"]:checked').trigger('change');

    setTimeout(stickyFooter, 200);
}

// Vnos osnovnih podatkov
function formA1() {

    $("#stalni_naslov_hitriVnos").geocomplete({
        country: "SI",
        type: ['(regions)']
    }).on("geocode:result", function (event, result) {

        $("#stalni_naslov_stevilka").val("").val(getGeocompleteData(result, 'street_number'));
        $("#stalni_naslov_ulica").val("").val(getGeocompleteData(result, 'route'));
        $("#stalni_naslov_posta_st").val("").val(getGeocompleteData(result, 'postal_code'));
        $("#stalni_naslov_posta").val("").val(getGeocompleteData(result, 'postal_town'));
        $("#stalni_naslov_drzava").val("").val(getGeocompleteData(result, 'country'));

        $("#stalni_naslov_stevilka").trigger("change");
        $("#stalni_naslov_ulica").trigger("change");
        $("#stalni_naslov_posta_st").trigger("change");
        $("#stalni_naslov_posta").trigger("change");
        $("#stalni_naslov_drzava").trigger("change");

    });

    $("#obvescanje_hitriVnos").geocomplete({
        country: "SI",
        type: ['(regions)']
    }).on("geocode:result", function (event, result) {

        $("#obvescanje_stevilka").val("").val(getGeocompleteData(result, 'street_number'));
        $("#obvescanje_ulica").val("").val(getGeocompleteData(result, 'route'));
        $("#obvescanje_posta_st").val("").val(getGeocompleteData(result, 'postal_code'));
        $("#obvescanje_posta").val("").val(getGeocompleteData(result, 'postal_town'));
        $("#obvescanje_drzava").val("").val(getGeocompleteData(result, 'country'));

        $("#obvescanje_stevilka").trigger("change");
        $("#obvescanje_ulica").trigger("change");
        $("#obvescanje_posta_st").trigger("change");
        $("#obvescanje_posta").trigger("change");
        $("#obvescanje_drzava").trigger("change");
    });

    $("#emso").ForceNumericOnly();

    $('#emso').on('blur', function (e) {
        var emsoField = $(this);

        if (EMSOvalidator.validate($('input#emso'), $('input#emso').val())) {
            // set gender
            if (parseInt($('input#emso').val().substring(9, 12)) <= 499) {
                $('#spol').val('MALE');
            } else {
                $('#spol').val('FEMALE');
            }

            // set date of birth
            var dayOfBirth = $('input#emso').val().substring(0, 2);
            var monthOfBirth = $('input#emso').val().substring(2, 4);
            var yearOfBirth = $('input#emso').val().substring(4, 7);

            if (yearOfBirth < 800) {
                yearOfBirth = '2' + yearOfBirth;
            } else {
                yearOfBirth = '1' + yearOfBirth;
            }

            $('#danRojstva').val(dayOfBirth).trigger('change');
            $('#mesecRojstva').val(monthOfBirth).trigger('change');
            $('#letoRojstva').val(yearOfBirth).trigger('change');


            $(emsoField).closest('.mb-3, .form-group').removeClass('has-error');
            $(emsoField).next('.help-block.has-error').remove();

        } else {
            $(emsoField).next('.help-block.has-error').remove();
            $(emsoField).closest('.mb-3, .form-group').addClass('has-error');
            $(emsoField).after($('<span class="help-block has-error"></span>').text(emsoError));

            $('#danRojstva').val("").trigger('change');
            $('#mesecRojstva').val("").trigger('change');
            $('#letoRojstva').val("").trigger('change');
        }
    });

    // if differentContactAddress is init checked
    if ($('.differentContactAddress').is(':checked')) {
        $('#obvescanje_panel').addClass('show');
    }

    $("#danRojstva").ForceNumericOnly();
    $("#mesecRojstva").ForceNumericOnly();
    $("#letoRojstva").ForceNumericOnly();

    // Handle recruitment path suggestions
    $('.recruitment-path-option').on('click', function(e) {
        e.preventDefault();
        var pathValue = $(this).attr('data-value');
        $('#nabornaPot').val(pathValue).trigger('change').trigger('focus'); // dostopnost: fokus po izbiri v meniju
    });

    stickyFooter();
}

function formA2() {

    $('body').on('change', '.statement .btn input[type="checkbox"]', function () {
        var checked = syncToggleLabel(this);
        $(this).closest('.form-group.statement').toggleClass('selected', checked);
    });

    // if statement is init checked
    $('.statement').each(function () {
        var thisStatement = $(this);
        if ($(thisStatement).find('input[type="checkbox"][id^="statement"]').is(':checked')) {
            $(thisStatement).find('.btn input[type="checkbox"]').trigger('change');
        }
    });

    // open panel if addidtional info textarea is filled
    $('textarea').each(function () {
        var thisTextarea = $(this);
        if (String($(thisTextarea).val() ?? '').trim()) {
            $(thisTextarea).closest('.collapse').addClass('show');
        }

    });

    $("button[name='action'][value='next']").on('click', function (e) {
        e.preventDefault();

        var form = $('form')[0];
        var postUrl = $(form).attr('action');

        var action = "checkAccreditationBreak";

        if ($('#isAccreditated').val() == "") {
            if (!$('#osnovniPodatki #hiddenNext').length)
                $('#osnovniPodatki').append('<input id="hiddenNext" type="hidden" name="action" value="next" />');

            $('#osnovniPodatki').submit();
            return;
        }

        var formData = $('#osnovniPodatki').serializeArray();
        formData.push({name: 'action', value: action});

        $.ajax({
            url: postUrl,
            data: formData,
            type: 'POST'
        })
            .fail(function (response) {
                alert("Pri pošiljanju podatkov je prišlo do napake.");
            })
            .done(function (response) {
                if (response == "breaksAccreditation") {
                    Swal.fire({
                        title: formA2warning,
                        text: formA2warningText,
                        icon: "warning",
                        showCancelButton: true,
                        confirmButtonColor: "#DD6B55",
                        confirmButtonText: formA2confirm,
                        cancelButtonText: formA2cancel,
                    }).then((result) => {
                        if (result.isConfirmed) {
                            if (!$('#osnovniPodatki #hiddenNext').length)
                                $('#osnovniPodatki').append('<input id="hiddenNext" type="hidden" name="action" value="next" />');

                            $('#osnovniPodatki').submit();
                        }
                    });
                } else {
                    if (!$('form #hiddenNext').length)
                        $('#osnovniPodatki').append('<input id="hiddenNext" type="hidden" name="action" value="next" />');

                    $('#osnovniPodatki').submit();
                        }
            });

    });
}

var userRoleUrlPath = "";
var nominationId = "";

function nominationView() {

    userRoleUrlPath = "user";
    nominationId = $('#nominationId').val();

    $('body').on('change', '.statement .btn input[type="checkbox"]', function () {
        var checked = syncToggleLabel(this);
        $(success).prop('disabled', !checked);
    });

    $(".select2-select").each(function () {
        if ($(this).data("max")) {
            $(this).select2({
                max_selected_options: $(this).data("max"),
                disable_search: true,
                width: "100%",
                allowClear: true,
                placeholder: $(this).attr("placeholder") || ""
            });
        } else {
            $(this).select2({
                disable_search: false,
                width: "100%",
                search_contains: true,
                placeholder_text_single: $(this).attr("placeholder"),
                no_results_text: formBnoResultText,
                allowClear: true,
                placeholder: $(this).attr("placeholder") || ""
            });
        }

    });


    // edit item
    $('body').on('click', '.resultItem .editItem', function (e) {

        var itemId = $(this).closest('.resultItem').find('input:hidden').val();
        var itemName = $(this).closest('.resultItem').find('input:hidden').attr('name');
        var dynamicSection = $(this).closest('.dynamicSection');

        $(dynamicSection).find('.addItem').trigger('click');
        $(dynamicSection).find('.actionTitle').text($(dynamicSection).find('.actionTitle').data('edit'));

        // populate edit data from json
        var dataSection = eval("JsonData." + $(dynamicSection).data('section'));
        var thisItemData = _.find(dataSection, function (item) {
            return item[itemName] == itemId;
        });


        $(dynamicSection).find('.dynamicEditItem :input').each(function () {
            var thisInput = $(this);
            var fieldType = $(thisInput).getType();

            switch (fieldType) {
                case 'hidden':
                    $(thisInput).val(thisItemData[$(this).attr('name')]);
                    break;
                case 'text':
                    $(thisInput).val($('<div>').html(thisItemData[$(this).attr('name')]).text());
                    break;
                case 'select':
                case 'select-one':
                    var idFieldName = $(this).data('idField');
                    var thisFieldValue = idFieldName ? thisItemData[idFieldName] : getOptionValueByText($(thisInput), thisItemData[$(this).attr('name')]);
                    $(thisInput).val(thisFieldValue).trigger('change').trigger("chosen:updated");
                    break
            }
        });

        setTimeout(stickyFooter, 200);
    });

    // add item
    $('body').on('click', '.addItem', function (e) {
        var dynamicEditItem = $(this).closest('.dynamicEditItem');

        $(dynamicEditItem).find('.actionTitle').text((dynamicEditItem).find('.actionTitle').data('new'));

        $(dynamicEditItem).find('.mb-3.has-error, .form-group.has-error').removeClass('has-error');
        $(dynamicEditItem).find('.mb-3 .help-block.has-error, .form-group .help-block.has-error').remove();

        $(dynamicEditItem).find('.addItemActionContainer').addClass('d-none');
        $(dynamicEditItem).find('.saveItemActionContainer').removeClass('d-none');
        $(dynamicEditItem).find('.addOrEditItem').removeClass('d-none');

        if ($(dynamicEditItem).find('input:hidden').val() == "") {
            $(dynamicEditItem).find(':input').val('');
        }

        setTimeout(stickyFooter, 200);
    });

    //decline
    $('body').on('click', '.declineItem', function (e) {
        e.preventDefault();
        declineNomination()
        setTimeout(stickyFooter, 200);
    });

    //finish updating data item
    $('body').on('click', '.finishUpdatingDataItem', function (e) {
        e.preventDefault();
        finishUpdatingData("finish_updating_data")
        setTimeout(stickyFooter, 200);
    });

    //finish updating data item and take a test
    $('body').on('click', '.takeATest', function (e) {
        e.preventDefault();
        finishUpdatingData("takeATest")
        setTimeout(stickyFooter, 200);
    });


    // cancel
    $('body').on('click', '.cancelItem', function (e) {
        var dynamicEditItem = $(this).closest('.dynamicEditItem');


        Swal.fire({
            title: formBcancelInsert,
            text: formBchangeNotSaved,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#DD6B55",
            confirmButtonText: formBconfirm,
            cancelButtonText: formBcancel,
        }).then((result) => {
            if (result.isConfirmed) {

            closeDynamicItem($(dynamicEditItem));


            }
        });


    });


    // add attachment
    $('body').on('change', 'input[type="file"]', function () {
        var thisInput = $(this);
        var thisDroppableArea = $(thisInput).closest('.droppableArea');
        $(thisDroppableArea).removeClass('droppable');

        if (this.files.length == 1) {

            var correctFileType = false;
            if (this.files[0].type == 'application/pdf' || this.files[0].type == 'application/x-pdf') {
                correctFileType = true;
            }


            if (!correctFileType) {
                $(thisDroppableArea).find('p').html("<em class='text-danger' style='font-size: 18px;'><i class='fas fa-exclamation-triangle'></i>" + formBwrongFormat + "</em>");
                setTimeout(function () {
                    $(thisInput).val('');
                    $(thisDroppableArea).find('p').html("<em>" + formBattachmentHelp + "<em>");
                }, 3000);
                $(thisDroppableArea).find('button').hide();
                return;
            }

            if (this.files[0].size <= (20 * 1024 * 1024) && correctFileType) {
                $(thisDroppableArea).find('p').html("<em>" + formBchosenFile + ":</em> <b>" + this.files[0].name + "</b>.");
                $(thisDroppableArea).find('button').fadeIn();
            } else {
                $(thisDroppableArea).find('p').html("<em class='text-danger' style='font-size: 18px;'><i class='fas fa-exclamation-triangle'></i>" + formBsizeError + "</em>");
                setTimeout(function () {
                    $(thisInput).val('');

                    $(thisDroppableArea).find('p').html("<em>" + formBattachmentHelp + "<em>");
                }, 3000);
                $(thisDroppableArea).find('button').hide();
            }
        } else {
            $(thisDroppableArea).find('p').html("<em>" + formBattachmentHelp + "<em>");
            $(thisDroppableArea).find('button').hide();
        }

    });

    $(".droppableArea").on("dragover", function (event) {
        event.preventDefault();
        event.stopPropagation();
        $(this).addClass('droppable');
    });

    $(".droppableArea").on("dragleave", function (event) {
        event.preventDefault();
        event.stopPropagation();
        $(this).removeClass('droppable');
    });


    $('body').on('click', '.upload', function (e) {
        e.preventDefault();
        var thisButton = $(this);
        var thisSection = $(thisButton).parents('.uploadSectionItem');
        var thisDataSection = $(thisSection).data('section');
        var thisInput = $(thisButton).parents('.input-group').find('input[type="file"]');
        var postUrl = $('form').attr('action');
        var action = $(thisButton).data("section");


        var formData = new FormData();
        formData.append('action', 'upload' + action);
        formData.append('file', $(thisInput)[0].files[0]);

        $(thisButton).hide();
        $(thisInput).parents('.droppableArea').find('p').prepend(' <i class="fas fa-circle-notch fa-spin"></i> ');
        //$(thisButton).closest('.input-group').append(' <div class="loader" style="margin-top: 11px;margin-left: 10px;position: absolute;"><i class="fas fa-circle-notch fa-spin"></i></div> ');

        $.ajax({
            url: postUrl,
            data: formData,
            type: 'POST',
            contentType: false,
            processData: false
        })
            .fail(function (response) {
                alert(formBerrorFile);
                $(thisButton).show();
                $('div.loader').remove();
            })
            .done(function (response) {

                $(thisInput).val('').trigger('change');
                $(thisButton).show();
                $('div.loader').remove();

                if (typeof response.error != 'undefined') {
                    alert(response.error);
                    return;
                }

                $(thisSection).find('.attachmentResults').loadTemplate($('#attachmentTemplate'), response);
                switch (thisDataSection) {
                    case 'statement1':
                        if ($('.statement1 .attachmentResults .resultItem').length >= 1) {
                            $(thisButton).closest('.row.mb-3').hide();
                        }
                        break;
                    case 'statement2':
                        if ($('.statement2 .attachmentResults .resultItem').length >= 1) {
                            $(thisButton).closest('.row.mb-3').hide();
                        }
                        break;
                    case 'statement3':
                        if ($('.statement3 .attachmentResults .resultItem').length >= 1) {
                            $(thisButton).closest('.row.mb-3').hide();
                        }
                        break;
                }
                checkIfAllThreeStatementsAreUploaded();
            });
    });

    // remove attachment

    $('body').on('click', '.uploadSectionItem.statement1 .deleteItem', function (e) {
        e.preventDefault();
        deleteAttachmentForNomination($(this), 'deletestatement1');
    });

    $('body').on('click', '.uploadSectionItem.statement2 .deleteItem', function (e) {
        e.preventDefault();
        deleteAttachmentForNomination($(this), 'deletestatement2');
    });

    $('body').on('click', '.uploadSectionItem.statement3 .deleteItem', function (e) {
        e.preventDefault();
        deleteAttachmentForNomination($(this), 'deletestatement3');
    });

    loadAttachmentsForNomination("/" + userRoleUrlPath + "/nomination/" + nominationId);

}

var userRoleUrlPath = "";
var nominationId = "";

function nominationUploadStatements() {

    userRoleUrlPath = "user";
    nominationId = $('#nominationId').val();

    $('body').on('change', '.statement .btn input[type="checkbox"]', function () {
        var checked = syncToggleLabel(this);
        $(success).prop('disabled', !checked);
    });

    $(".select2-select").each(function () {
        if ($(this).data("max")) {
            $(this).select2({
                max_selected_options: $(this).data("max"),
                disable_search: true,
                width: "100%",
                allowClear: true,
                placeholder: $(this).attr("placeholder") || ""
            });
        } else {
            $(this).select2({
                disable_search: false,
                width: "100%",
                search_contains: true,
                placeholder_text_single: $(this).attr("placeholder"),
                no_results_text: "Ni zadetkov",
                allowClear: true,
                placeholder: $(this).attr("placeholder") || ""
            });
        }

    });


    // edit item
    $('body').on('click', '.resultItem .editItem', function (e) {

        var itemId = $(this).closest('.resultItem').find('input:hidden').val();
        var itemName = $(this).closest('.resultItem').find('input:hidden').attr('name');
        var dynamicSection = $(this).closest('.dynamicSection');

        $(dynamicSection).find('.addItem').trigger('click');
        $(dynamicSection).find('.actionTitle').text($(dynamicSection).find('.actionTitle').data('edit'));

        // populate edit data from json
        var dataSection = eval("JsonData." + $(dynamicSection).data('section'));
        var thisItemData = _.find(dataSection, function (item) {
            return item[itemName] == itemId;
        });


        $(dynamicSection).find('.dynamicEditItem :input').each(function () {
            var thisInput = $(this);
            var fieldType = $(thisInput).getType();

            switch (fieldType) {
                case 'hidden':
                    $(thisInput).val(thisItemData[$(this).attr('name')]);
                    break;
                case 'text':
                    $(thisInput).val($('<div>').html(thisItemData[$(this).attr('name')]).text());
                    break;
                case 'select':
                case 'select-one':
                    var idFieldName = $(this).data('idField');
                    var thisFieldValue = idFieldName ? thisItemData[idFieldName] : getOptionValueByText($(thisInput), thisItemData[$(this).attr('name')]);
                    $(thisInput).val(thisFieldValue).trigger('change').trigger("chosen:updated");
                    break
            }
        });

        setTimeout(stickyFooter, 200);
    });

    // add item
    $('body').on('click', '.addItem', function (e) {
        var dynamicEditItem = $(this).closest('.dynamicEditItem');

        $(dynamicEditItem).find('.actionTitle').text((dynamicEditItem).find('.actionTitle').data('new'));

        $(dynamicEditItem).find('.mb-3.has-error, .form-group.has-error').removeClass('has-error');
        $(dynamicEditItem).find('.mb-3 .help-block.has-error, .form-group .help-block.has-error').remove();

        $(dynamicEditItem).find('.addItemActionContainer').addClass('d-none');
        $(dynamicEditItem).find('.saveItemActionContainer').removeClass('d-none');
        $(dynamicEditItem).find('.addOrEditItem').removeClass('d-none');

        if ($(dynamicEditItem).find('input:hidden').val() == "") {
            $(dynamicEditItem).find(':input').val('');
        }

        setTimeout(stickyFooter, 200);
    });

    //decline
    $('body').on('click', '.declineItem', function (e) {
        e.preventDefault();
        declineNomination()
    });


    // cancel
    $('body').on('click', '.cancelItem', function (e) {
        var dynamicEditItem = $(this).closest('.dynamicEditItem');


        Swal.fire({
            title: formBcancelInsert,
            text: formBchangeNotSaved,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#DD6B55",
            confirmButtonText: formBconfirm,
            cancelButtonText: formBcancel,
        }).then((result) => {
            if (result.isConfirmed) {

            closeDynamicItem($(dynamicEditItem));


            }
        });


    });


    // add attachment
    $('body').on('change', 'input[type="file"]', function () {
        var thisInput = $(this);
        var thisDroppableArea = $(thisInput).closest('.droppableArea');
        $(thisDroppableArea).removeClass('droppable');

        if (this.files.length == 1) {

            var correctFileType = false;
            if (this.files[0].type == 'application/pdf' || this.files[0].type == 'application/x-pdf') {
                correctFileType = true;
            }

            if (!correctFileType) {
                $(thisDroppableArea).find('p').html("<em class='text-danger' style='font-size: 18px;'><i class='fas fa-exclamation-triangle'></i>" + formBwrongFormat + "</em>");
                setTimeout(function () {
                    $(thisInput).val('');
                    $(thisDroppableArea).find('p').html("<em>" + formBattachmentHelp + "<em>");
                }, 3000);
                $(thisDroppableArea).find('button').hide();
                return;
            }

            if (this.files[0].size <= (20 * 1024 * 1024) && correctFileType) {
                $(thisDroppableArea).find('p').html("<em>" + formBchosenFile + ":</em> <b>" + this.files[0].name + "</b>.");
                $(thisDroppableArea).find('button').fadeIn();
            } else {
                $(thisDroppableArea).find('p').html("<em class='text-danger' style='font-size: 18px;'><i class='fas fa-exclamation-triangle'></i>" + formBsizeError + "</em>");
                setTimeout(function () {
                    $(thisInput).val('');
                    $(thisDroppableArea).find('p').html("<em>" + formBattachmentHelp + "<em>");
                }, 3000);
                $(thisDroppableArea).find('button').hide();
            }
        } else {
            $(thisDroppableArea).find('p').html("<em>" + formBattachmentHelp + "<em>");
            $(thisDroppableArea).find('button').hide();
        }

    });

    $(".droppableArea").on("dragover", function (event) {
        event.preventDefault();
        event.stopPropagation();
        $(this).addClass('droppable');
    });

    $(".droppableArea").on("dragleave", function (event) {
        event.preventDefault();
        event.stopPropagation();
        $(this).removeClass('droppable');
    });


    $('body').on('click', '.upload', function (e) {
        e.preventDefault();
        var thisButton = $(this);
        var thisSection = $(thisButton).parents('.uploadSectionItem');
        var thisDataSection = $(thisSection).data('section');
        var thisInput = $(thisButton).parents('.input-group').find('input[type="file"]');
        var postUrl = $('form').attr('action');
        var action = $(thisButton).data("section");


        var formData = new FormData();
        formData.append('action', 'upload' + action);
        formData.append('file', $(thisInput)[0].files[0]);

        $(thisButton).hide();
        $(thisInput).parents('.droppableArea').find('p').prepend(' <i class="fas fa-circle-notch fa-spin"></i> ');
        //$(thisButton).closest('.input-group').append(' <div class="loader" style="margin-top: 11px;margin-left: 10px;position: absolute;"><i class="fas fa-circle-notch fa-spin"></i></div> ');

        $.ajax({
            url: postUrl,
            data: formData,
            type: 'POST',
            contentType: false,
            processData: false
        })
            .fail(function (response) {
                alert(formBerrorFile);
                $(thisButton).show();
                $('div.loader').remove();
            })
            .done(function (response) {

                $(thisInput).val('').trigger('change');
                $(thisButton).show();
                $('div.loader').remove();

                if (typeof response.error != 'undefined') {
                    alert(response.error);
                    return;
                }

                $(thisSection).find('.attachmentResults').loadTemplate($('#attachmentTemplate'), response);
                switch (thisDataSection) {
                    case 'statement1':
                        if ($('.statement1 .attachmentResults .resultItem').length >= 1) {
                            $(thisButton).closest('.row.mb-3').hide();
                        }
                        break;
                    case 'statement2':
                        if ($('.statement2 .attachmentResults .resultItem').length >= 1) {
                            $(thisButton).closest('.row.mb-3').hide();
                        }
                        break;
                    case 'statement3':
                        if ($('.statement3 .attachmentResults .resultItem').length >= 1) {
                            $(thisButton).closest('.row.mb-3').hide();
                        }
                        break;
                }
                checkIfAllThreeStatementsAreUploaded();
            });
    });

    // remove attachment

    $('body').on('click', '.uploadSectionItem.statement1 .deleteItem', function (e) {
        e.preventDefault();
        deleteAttachmentForNomination($(this), 'deletestatement1');
    });

    $('body').on('click', '.uploadSectionItem.statement2 .deleteItem', function (e) {
        e.preventDefault();
        deleteAttachmentForNomination($(this), 'deletestatement2');
    });

    $('body').on('click', '.uploadSectionItem.statement3 .deleteItem', function (e) {
        e.preventDefault();
        deleteAttachmentForNomination($(this), 'deletestatement3');
    });

    loadAttachmentsForNomination("/" + userRoleUrlPath + "/nomination/" + nominationId);

}


var userRoleUrlPath = "";
var conditionRecheckId = "";

function uploadConditionRecheck() {

    userRoleUrlPath = "user";
    conditionRecheckId = $('#conditionRecheckId').val();


    // edit item
    $('body').on('click', '.resultItem .editItem', function (e) {

        var itemId = $(this).closest('.resultItem').find('input:hidden').val();
        var itemName = $(this).closest('.resultItem').find('input:hidden').attr('name');
        var dynamicSection = $(this).closest('.dynamicSection');

        $(dynamicSection).find('.addItem').trigger('click');
        $(dynamicSection).find('.actionTitle').text($(dynamicSection).find('.actionTitle').data('edit'));

        // populate edit data from json
        var dataSection = eval("JsonData." + $(dynamicSection).data('section'));
        var thisItemData = _.find(dataSection, function (item) {
            return item[itemName] == itemId;
        });


        $(dynamicSection).find('.dynamicEditItem :input').each(function () {
            var thisInput = $(this);
            var fieldType = $(thisInput).getType();

            switch (fieldType) {
                case 'hidden':
                    $(thisInput).val(thisItemData[$(this).attr('name')]);
                    break;
                case 'text':
                    $(thisInput).val($('<div>').html(thisItemData[$(this).attr('name')]).text());
                    break;
                case 'select':
                case 'select-one':
                    var idFieldName = $(this).data('idField');
                    var thisFieldValue = idFieldName ? thisItemData[idFieldName] : getOptionValueByText($(thisInput), thisItemData[$(this).attr('name')]);
                    $(thisInput).val(thisFieldValue).trigger('change').trigger("chosen:updated");
                    break
            }
        });

        setTimeout(stickyFooter, 200);
    });

    // add item
    $('body').on('click', '.addItem', function (e) {
        var dynamicEditItem = $(this).closest('.dynamicEditItem');

        $(dynamicEditItem).find('.actionTitle').text((dynamicEditItem).find('.actionTitle').data('new'));

        $(dynamicEditItem).find('.mb-3.has-error, .form-group.has-error').removeClass('has-error');
        $(dynamicEditItem).find('.mb-3 .help-block.has-error, .form-group .help-block.has-error').remove();

        $(dynamicEditItem).find('.addItemActionContainer').addClass('d-none');
        $(dynamicEditItem).find('.saveItemActionContainer').removeClass('d-none');
        $(dynamicEditItem).find('.addOrEditItem').removeClass('d-none');

        if ($(dynamicEditItem).find('input:hidden').val() == "") {
            $(dynamicEditItem).find(':input').val('');
        }

        setTimeout(stickyFooter, 200);
    });

    //decline
    $('body').on('click', '.declineItem', function (e) {
        e.preventDefault();
        declineNomination()
    });


    // cancel
    $('body').on('click', '.cancelItem', function (e) {
        var dynamicEditItem = $(this).closest('.dynamicEditItem');


        Swal.fire({
            title: formBcancelInsert,
            text: formBchangeNotSaved,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#DD6B55",
            confirmButtonText: formBconfirm,
            cancelButtonText: formBcancel,
        }).then((result) => {
            if (result.isConfirmed) {
                closeDynamicItem($(dynamicEditItem));
            }
        });
    });


    // add attachment
    $('body').on('change', 'input[type="file"]', function () {
        var thisInput = $(this);
        var thisDroppableArea = $(thisInput).closest('.droppableArea');
        $(thisDroppableArea).removeClass('droppable');

        if (this.files.length == 1) {

            var correctFileType = false;
            if (this.files[0].type == 'application/pdf' || this.files[0].type == 'application/x-pdf') {
                correctFileType = true;
            }

            if (!correctFileType) {
                $(thisDroppableArea).find('p').html("<em class='text-danger' style='font-size: 18px;'><i class='fas fa-exclamation-triangle'></i>" + formBwrongFormat + "</em>");
                setTimeout(function () {
                    $(thisInput).val('');
                    $(thisDroppableArea).find('p').html("<em>" + formBattachmentHelp + "<em>");
                }, 3000);
                $(thisDroppableArea).find('button').hide();
                return;
            }

            if (this.files[0].size <= (20 * 1024 * 1024) && correctFileType) {
                $(thisDroppableArea).find('p').html("<em>" + formBchosenFile + ":</em> <b>" + this.files[0].name + "</b>.");
                $(thisDroppableArea).find('button').fadeIn();
            } else {
                $(thisDroppableArea).find('p').html("<em class='text-danger' style='font-size: 18px;'><i class='fas fa-exclamation-triangle'></i>" + formBsizeError + "</em>");
                setTimeout(function () {
                    $(thisInput).val('');
                    $(thisDroppableArea).find('p').html("<em>" + formBattachmentHelp + "<em>");
                }, 3000);
                $(thisDroppableArea).find('button').hide();
            }
        } else {
            $(thisDroppableArea).find('p').html("<em>" + formBattachmentHelp + "<em>");
            $(thisDroppableArea).find('button').hide();
        }

    });

    $(".droppableArea").on("dragover", function (event) {
        event.preventDefault();
        event.stopPropagation();
        $(this).addClass('droppable');
    });

    $(".droppableArea").on("dragleave", function (event) {
        event.preventDefault();
        event.stopPropagation();
        $(this).removeClass('droppable');
    });


    $('body').on('click', '.upload', function (e) {
        e.preventDefault();
        var thisButton = $(this);
        var thisSection = $(thisButton).parents('.uploadSectionItem');
        var thisDataSection = $(thisSection).data('section');
        var thisInput = $(thisButton).parents('.input-group').find('input[type="file"]');
        var postUrl = $('form').attr('action');
        var action = $(thisButton).data("section");


        var formData = new FormData();
        formData.append('action', 'upload' + action);
        formData.append('file', $(thisInput)[0].files[0]);

        $(thisButton).hide();
        $(thisInput).parents('.droppableArea').find('p').prepend(' <i class="fas fa-circle-notch fa-spin"></i> ');
        //$(thisButton).closest('.input-group').append(' <div class="loader" style="margin-top: 11px;margin-left: 10px;position: absolute;"><i class="fas fa-circle-notch fa-spin"></i></div> ');

        $.ajax({
            url: postUrl,
            data: formData,
            type: 'POST',
            contentType: false,
            processData: false
        })
            .fail(function (response) {
                alert(formBerrorFile);
                $(thisButton).show();
                $('div.loader').remove();
            })
            .done(function (response) {

                $(thisInput).val('').trigger('change');
                $(thisButton).show();
                $('div.loader').remove();

                if (typeof response.error != 'undefined') {
                    alert(response.error);
                    return;
                }

                $(thisSection).find('.attachmentResults').loadTemplate($('#attachmentTemplate'), response);
                $(thisButton).closest('.row.mb-3').hide();
                $("#success").removeAttr('disabled');
            });
    });

    // remove attachment

    $('body').on('click', '.uploadSectionItem.statement .deleteItem', function (e) {
        e.preventDefault();
        deleteAttachmentForConditionRecheck($(this), 'deletestatement');
    });

    loadAttachmentsForConditionRecheck();
}

function checkIfAllThreeStatementsAreUploaded() {
    var needsToShowModal = true;
    var statement1HasAttachment = $('.uploadSectionItem[data-section="statement1"] .attachmentResults .resultItem').length > 0;
    var poslovodstvo = $('#poslovodstvo').val();
    if (!statement1HasAttachment && poslovodstvo === "false") {
        needsToShowModal = false;
    }
    var statement2HasAttachment = $('.uploadSectionItem[data-section="statement2"] .attachmentResults .resultItem').length > 0;
    if (!statement2HasAttachment) {
        needsToShowModal = false;
    }
    var statement3HasAttachment = $('.uploadSectionItem[data-section="statement3"] .attachmentResults .resultItem').length > 0;
    if (!statement3HasAttachment) {
        needsToShowModal = false;
    }

    var  nominationStatus = $('#status').text().trim();
    if (nominationStatus !== nominationInvited && nominationStatus !== nominationRejected) {
        needsToShowModal = false;
    }

    if (needsToShowModal && userRoleUrlPath === "user") {
        nominationId = $('#nominationId').val();
        var company = $('#company').text();


        Swal.fire({
                title: nominationUploadWarningModalTitle,
                text: nominationUploadWarningModalText.replace("DRUŽBA", company).replace("DRUŽBA", company),
                icon: "info",
                showCancelButton: true,
                confirmButtonColor: "#A5DC86",
                confirmButtonText: nominationUploadWarningModalConfirm,
                cancelButtonText: formBcancel,
            }).then((result) => {
                if (result.isConfirmed) {
                var dynamicFormFields = {};
                dynamicFormFields["action"] = "confirmVop";

                $.ajax({
                    method: "POST",
                    url: "/" + userRoleUrlPath + "/nomination/" + nominationId + "/uploadStatements",
                    data: dynamicFormFields
                })
                    .fail(function (error) {
                        console.log("error: ", error);
                    })
                    .done(function () {
                        setTimeout(function () {
                            window.location.href = "/user/dashboard";
                        }, 200)
                    })
                }
            }
        );
    }
}

function loadAttachmentsForNomination(url) {

    var poslovodstvo = $("#poslovodstvo").val();

    $.ajax({
        url: url + "/statement1"
    }).done(function (response) {
        if (response == "" || response == null || response.length == 0) {
            $('.uploadSectionItem.statement1 .attachmentResults').html("<em>" + statement1Error + "<br/><br/></em>");
        } else {
            $('.uploadSectionItem.statement1 .attachmentResults').loadTemplate($('#attachmentTemplate'), response);

            if ($('.statement1 .attachmentResults .resultItem').length >= 1) {
                $('.statement1').find('.row.mb-3').hide();
            } else {
                var $statement1Row = $('.statement1').find('.row.mb-3');
                $statement1Row.show();
                // Reset file input and button state
                $statement1Row.find('input[type="file"]').val('');
                $statement1Row.find('.droppableArea p').html("<em>" + formBattachmentHelp + "</em>");
                $statement1Row.find('.droppableArea button').hide();
            }
        }
        checkIfAllThreeStatementsAreUploaded();
        setTimeout(function () {
            stickyFooter()
        }, 200);
    })
        .fail(function () {
            $('.uploadSectionItem.statement1 .attachmentResults').html("<em>" + statement1UploadError + "</em>");
        });

    $.ajax({
        url: url + "/statement2"
    }).done(function (response) {
        if (response == "" || response == null || response.length == 0) {
            if (poslovodstvo === "true") {
                $('.uploadSectionItem.statement2 .attachmentResults').html("<em> " + statement1Error + "<br/><br/></em>");
            }
            if (poslovodstvo === "false") {
                $('.uploadSectionItem.statement2 .attachmentResults').html("<em> " + statement2Error + "<br/><br/></em>");
            }

        } else {
            $('.uploadSectionItem.statement2 .attachmentResults').loadTemplate($('#attachmentTemplate'), response);

            if ($('.statement2 .attachmentResults .resultItem').length >= 1) {
                $('.statement2').find('.row.mb-3').hide();
            } else {
                var $statement2Row = $('.statement2').find('.row.mb-3');
                $statement2Row.show();
                // Reset file input and button state
                $statement2Row.find('input[type="file"]').val('');
                $statement2Row.find('.droppableArea p').html("<em>" + formBattachmentHelp + "</em>");
                $statement2Row.find('.droppableArea button').hide();
            }
        }
        checkIfAllThreeStatementsAreUploaded();
        setTimeout(function () {
            stickyFooter()
        }, 200);
    })
        .fail(function () {
            if (poslovodstvo === "true") {
                $('.uploadSectionItem.statement2 .attachmentResults').html("<em>" + statement1UploadError + "<br/><br/></em>");
            }
            if (poslovodstvo === "false") {
                $('.uploadSectionItem.statement2 .attachmentResults').html("<em>" + statement2UploadError + "<br/><br/></em>");
            }

        });

    $.ajax({
        url: url + "/statement3"
    }).done(function (response) {
        if (response == "" || response == null || response.length == 0) {
            if (poslovodstvo === "true") {
                $('.uploadSectionItem.statement3 .attachmentResults').html("<em>" + statement2Error + "<br/><br/></em>");
            }
            if (poslovodstvo === "false") {
                $('.uploadSectionItem.statement3 .attachmentResults').html("<em>" + statement3Error + "<br/><br/></em>");
            }
        } else {
            $('.uploadSectionItem.statement3 .attachmentResults').loadTemplate($('#attachmentTemplate'), response);

            if ($('.statement3 .attachmentResults .resultItem').length >= 1) {
                $('.statement3').find('.row.mb-3').hide();
            } else {
                var $statement3Row = $('.statement3').find('.row.mb-3');
                $statement3Row.show();
                // Reset file input and button state
                $statement3Row.find('input[type="file"]').val('');
                $statement3Row.find('.droppableArea p').html("<em>" + formBattachmentHelp + "</em>");
                $statement3Row.find('.droppableArea button').hide();
            }
        }
        checkIfAllThreeStatementsAreUploaded();
        setTimeout(function () {
            stickyFooter()
        }, 200);
    })
        .fail(function () {
            if (poslovodstvo == "true") {
                $('.uploadSectionItem.statement2 .attachmentResults').html("<em>" + statement2UploadError + "</em>");
            }
            if (poslovodstvo === "false") {
                $('.uploadSectionItem.statement2 .attachmentResults').html("<em>" + statement3UploadError + "</em>");
                }
        });

}

function loadAttachmentsForConditionRecheck() {

    $.ajax({
        url: "/user/uploadConditionRecheck/" + conditionRecheckId + "/loadStatement"
    }).done(function (response) {
        if (response == "" || response == null || response.length == 0) {
            $('.uploadSectionItem.statement .attachmentResults').html("<em>" + statementError + "<br/><br/></em>");
        } else {
            $('.uploadSectionItem.statement .attachmentResults').loadTemplate($('#attachmentTemplate'), response);

            if ($('.statement .attachmentResults .resultItem').length >= 1) {
                $('.statement').find('.row.mb-3').hide();
            } else {
                var $statementRow = $('.statement').find('.row.mb-3');
                $statementRow.show();
                // Reset file input and button state
                $statementRow.find('input[type="file"]').val('');
                $statementRow.find('.droppableArea p').html("<em>" + formBattachmentHelp + "</em>");
                $statementRow.find('.droppableArea button').hide();
            }
            $("#success").removeAttr('disabled');
        }
        setTimeout(function () {
            stickyFooter()
        }, 200);
    })
        .fail(function () {
            $('.uploadSectionItem.statement .attachmentResults').html("<em>" + statementUploadError + "</em>");
        });
}

function declineNomination(action) {


    Swal.fire({
            title: nominationReject,
            text: nominationConfirmRejection,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#DD6B55",
            confirmButtonText: formBconfirm,
            cancelButtonText: formBcancel,
        }).then((result) => {
            if (result.isConfirmed) {
                $.ajax({
                    method: "POST",
                    url: "/" + userRoleUrlPath + "/nomination/" + nominationId + "/decline",
                    data: ""
                })
                    .fail(function (error) {
                        console.log("error: ", error);
                    })
                    .done(function () {
                        setTimeout(function () {
                            window.location.href = "/user/dashboard";
                        }, 200)
                    })
            }
        });
}

function finishUpdatingData(action) {
    Swal.fire({
            title: action === "takeATest" ? nominationFinishDataAndTakeATest : nominationFinishData,
            text: action === "takeATest" ? nominationConfirmFinishDataAndTakeATest :nominationConfirmFinishData,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#DD6B55",
            confirmButtonText: formBconfirm,
            cancelButtonText: formBcancel,
        }).then((result) => {
            if (result.isConfirmed) {
                var dynamicFormFields = {};
                dynamicFormFields["action"] = action;

                $.ajax({
                    method: "POST",
                    url: "/" + userRoleUrlPath + "/nomination/" + nominationId,
                    data: dynamicFormFields
                })
                    .fail(function (error) {
                        console.log("error: ", error);
                    })
                    .done(function (response) {
                        // if test must be taken but null is returned, test is expired
                        if (action === "takeATest" && response === "") {
                            // showing modal
                            Swal.fire({
                                title: eassesmentTimeOutTitle,
                                text: eassesmentTimeOutText,
                                icon: "warning",
                                showCancelButton: false,
                                confirmButtonColor: "#DD6B55",
                                confirmButtonText: eassesmentTimeOutConfirm,
                            }).then(() => {
                                setTimeout(function () {
                                    window.location.reload();
                                }, 200)
                            })
                        }

                        // if test must be taken, we redirect user to the test
                        else {
                            if (action === "takeATest") {
                                console.log(response);
                                window.location.href = "/user/eassessment/" + response;
                            } else {
                                setTimeout(function () {
                                    window.location.reload();
                                }, 200)
                            }
                        }
                    })
            }
        });
}


function deleteAttachmentForNomination(thisDeleteButton, action) {
    var postUrl = $('form').attr('action');
    var thisResultItem = $(thisDeleteButton).closest('.resultItem');
    var thisSection = $(thisDeleteButton).closest('.uploadSectionItem');
    var itemId = $(thisResultItem).find('input:hidden').val();

    Swal.fire({
        title: formBdeleteQ,
        text: formBdeleteText,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#DD6B55",
        confirmButtonText: formBdelete2,
        cancelButtonText: formBcancel,
    }).then((result) => {
            if (result.isConfirmed) {

        var dynamicFormFields = {};
        dynamicFormFields["action"] = action;
        dynamicFormFields["id"] = itemId;

        $.ajax({
            method: "POST",
            url: postUrl,
            data: dynamicFormFields
        })
            .fail(function (response) {
                console.log(response);
                alert(formBdeleteError);
            })
            .done(function () {
                $(thisResultItem).fadeOut('fast', function () {
                    $(thisResultItem).parent().remove();

                    setTimeout(function () {
                        if ($('.statement1 .attachmentResults .resultItem').length >= 1) {
                            $('.statement1').find('.row.mb-3').hide();
                        } else {
                            var $statement1Row = $('.statement1').find('.row.mb-3');
                            $statement1Row.show();
                            // Reset file input and button state
                            $statement1Row.find('input[type="file"]').val('');
                            $statement1Row.find('.droppableArea p').html("<em>" + formBattachmentHelp + "</em>");
                            $statement1Row.find('.droppableArea button').hide();
                        }

                        if ($('.statement2 .attachmentResults .resultItem').length >= 1) {
                            $('.statement2').find('.row.mb-3').hide();
                        } else {
                            var $statement2Row = $('.statement2').find('.row.mb-3');
                            $statement2Row.show();
                            // Reset file input and button state
                            $statement2Row.find('input[type="file"]').val('');
                            $statement2Row.find('.droppableArea p').html("<em>" + formBattachmentHelp + "</em>");
                            $statement2Row.find('.droppableArea button').hide();
                        }

                        if ($('.statement3 .attachmentResults .resultItem').length >= 1) {
                            $('.statement3').find('.row.mb-3').hide();
                        } else {
                            var $statement3Row = $('.statement3').find('.row.mb-3');
                            $statement3Row.show();
                            // Reset file input and button state
                            $statement3Row.find('input[type="file"]').val('');
                            $statement3Row.find('.droppableArea p').html("<em>" + formBattachmentHelp + "</em>");
                            $statement3Row.find('.droppableArea button').hide();
                        }
                        // window.location.reload();

                    }, 200);


                });
            });


            }
    });
}

function deleteAttachmentForConditionRecheck(thisDeleteButton, action) {
    var postUrl = $('form').attr('action');
    var thisResultItem = $(thisDeleteButton).closest('.resultItem');
    var thisSection = $(thisDeleteButton).closest('.uploadSectionItem');
    var itemId = $(thisResultItem).find('input:hidden').val();

    Swal.fire({
        title: formBdeleteQ,
        text: formBdeleteText,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#DD6B55",
        confirmButtonText: formBdelete2,
        cancelButtonText: formBcancel,
    }).then((result) => {
        if (result.isConfirmed) {

            var dynamicFormFields = {};
            dynamicFormFields["action"] = action;
            dynamicFormFields["id"] = itemId;

            $.ajax({
                method: "POST",
                url: postUrl,
                data: dynamicFormFields
            })
                .fail(function (response) {
                    console.log(response);
                    alert(formBdeleteError);
                })
                .done(function () {
                    $(thisResultItem).fadeOut('fast', function () {
                        $(thisResultItem).parent().remove();

                        $("#success").attr('disabled', true);
                        setTimeout(function () {
                            if ($('.statement .attachmentResults .resultItem').length >= 1) {
                                $('.statement').find('.row.mb-3').hide();
                            } else {
                                var $statementRow = $('.statement').find('.row.mb-3');
                                $statementRow.show();
                                // Reset file input and button state
                                $statementRow.find('input[type="file"]').val('');
                                $statementRow.find('.droppableArea p').html("<em>" + formBattachmentHelp + "</em>");
                                $statementRow.find('.droppableArea button').hide();
                            }
                        }, 200);
                    });
                });
        }
    });
}

var nominationId = "";

function NominationForm1() {

    nominationId = $('#nominationId').val();

    initDateRangePickers('dateFrom', 'dateTo');

    $('body').on('change', '.statement .btn input[type="checkbox"]', function () {
        var checked = syncToggleLabel(this);
        $(this).closest('.form-group.statement').toggleClass('selected', checked);
    });

    // if statement is init checked
    $('.statement').each(function () {
        var thisStatement = $(this);
        if ($(thisStatement).find('input[type="checkbox"][id^="statement"]').is(':checked')) {
            $(thisStatement).find('.btn input[type="checkbox"]').trigger('change');
        }
    });

    // open panel if addidtional info textarea is filled
    $('textarea').each(function () {
        var thisTextarea = $(this);
        if (String($(thisTextarea).val() ?? '').trim()) {
            $(thisTextarea).closest('.collapse').addClass('show');
        }

    });

    ////////////////////////
    // dynamic add/edit form
    ////////////////////////

    // load membership
    loadMembership();


    // save membership
    $('body').on('click', '.membership .btn.saveItem', function (e) {
        e.preventDefault();
        saveDynamicItem('membership', 'dynamicResultTemplateMembership', 'saveMembership');
    });

    // delete education
    $('body').on('click', '.membership .resultItem .deleteItem', function (e) {
        deleteDynamicItem($(this), 'deleteMembership');
    });

    // edit item
    $('body').on('click', '.resultItem .editItem', function (e) {

        var itemId = $(this).closest('.resultItem').find('input:hidden').val();
        var itemName = $(this).closest('.resultItem').find('input:hidden').attr('name');
        var dynamicSection = $(this).closest('.dynamicSection');

        $(dynamicSection).find('.addItem').trigger('click');
        $(dynamicSection).find('.actionTitle').text($(dynamicSection).find('.actionTitle').data('edit'));

        // populate edit data from json
        var dataSection = eval("JsonData." + $(dynamicSection).data('section'));
        var thisItemData = _.find(dataSection, function (item) {
            return item[itemName] == itemId;
        });


        $(dynamicSection).find('.dynamicEditItem :input').each(function () {
            var thisInput = $(this);
            var fieldType = $(thisInput).getType();

            switch (fieldType) {
                case 'hidden':
                    $(thisInput).val(thisItemData[$(this).attr('name')]);
                    break;
                case 'text':
                    $(thisInput).val($('<div>').html(thisItemData[$(this).attr('name')]).text());
                    break;
                case 'select':
                case 'select-one':
                    var idFieldName = $(this).data('idField');
                    var thisFieldValue = idFieldName ? thisItemData[idFieldName] : getOptionValueByText($(thisInput), thisItemData[$(this).attr('name')]);
                    $(thisInput).val(thisFieldValue).trigger('change').trigger("chosen:updated");
                    break
            }
        });

        setTimeout(stickyFooter, 200);
    });

    // add item
    $('body').on('click', '.addItem', function (e) {
        var dynamicEditItem = $(this).closest('.dynamicEditItem');

        $(dynamicEditItem).find('.actionTitle').text((dynamicEditItem).find('.actionTitle').data('new'));

        $(dynamicEditItem).find('.mb-3.has-error, .form-group.has-error').removeClass('has-error');
        $(dynamicEditItem).find('.mb-3 .help-block.has-error, .form-group .help-block.has-error').remove();

        $(dynamicEditItem).find('.addItemActionContainer').addClass('d-none');
        $(dynamicEditItem).find('.saveItemActionContainer').removeClass('d-none');
        $(dynamicEditItem).find('.addOrEditItem').removeClass('d-none');

        if ($(dynamicEditItem).find('input:hidden').val() == "") {
            $(dynamicEditItem).find(':input').val('');
        }

        setTimeout(stickyFooter, 200);
    });


    // cancel
    $('body').on('click', '.cancelItem', function (e) {
        var dynamicEditItem = $(this).closest('.dynamicEditItem');


        Swal.fire({
            title: formBcancelInsert,
            text: formBchangeNotSaved,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#DD6B55",
            confirmButtonText: formBconfirm,
            cancelButtonText: formBcancel,
        }).then((result) => {
            if (result.isConfirmed) {

            closeDynamicItem($(dynamicEditItem));


            }
        });


    });


}

function ConditionRecheck() {

    $('body').on('change', '.statement .btn input[type="checkbox"]', function () {
        var checked = syncToggleLabel(this);
        $(this).closest('.form-group.statement').toggleClass('selected', checked);
    });

    // if statement is init checked
    $('.statement').each(function () {
        var thisStatement = $(this);
        if ($(thisStatement).find('input[type="checkbox"][id^="statement"]').is(':checked')) {
            $(thisStatement).find('.btn input[type="checkbox"]').trigger('change');
        }
    });

    // open panel if addidtional info textarea is filled
    $('textarea').each(function () {
        var thisTextarea = $(this);
        if (String($(thisTextarea).val() ?? '').trim()) {
            $(thisTextarea).closest('.collapse').addClass('show');
        }

    });
}

function loadMembership() {
    $.ajax({
        url: "/user/nomination/" + nominationId + "/form1/membership",
        dataType: "json",
    }).done(function (response) {

        JsonData.membership = response;

        if (JsonData.membership == "" || JsonData.membership == null || JsonData.membership.length == 0) {
            $('.dynamicResultsContainer.membership').html("<em>" + noMembership + "</em>")
        } else {
            fillJsTemplate('membership', 'dynamicResultTemplateMembership', JsonData.membership);
        }

        setTimeout(stickyFooter, 200);
    });
}


var userRoleUrlPath = "";
var userId = "";

function formB(userRole) {

    if (userRole == "commission" || userRole == "auditor" ||userRole == "admin" || userRole == "admin/accreditation-preview" || userRole == "commission/accreditation-preview" || userRole == "management/accreditation-preview" || userRole == "management" || userRole == "auditor/accreditation-preview") {
        userRoleUrlPath = userRole;
        userId = $('#userId').val();
    } else {
        userRoleUrlPath = "user";
    }

    $(".select2-select").each(function () {
        if ($(this).data("max")) {
            $(this).select2({
                max_selected_options: $(this).data("max"),
                disable_search: true,
                width: "100%",
                allowClear: true,
                placeholder: $(this).attr("placeholder") || ""
            });
        } else {
            $(this).select2({
                disable_search: false,
                width: "100%",
                search_contains: true,
                placeholder_text_single: $(this).attr("placeholder"),
                no_results_text: "Ni zadetkov",
                allowClear: true,
                placeholder: $(this).attr("placeholder") || ""
            });
        }
    });

    initDatePicker('competenceCertificateDate');
    initDatePicker('employedFrom');



    $("#stalni_naslov_hitriVnos").geocomplete({
        country: "SI",
        type: ['(regions)']
    }).on("geocode:result", function (event, result) {

        $("#stalni_naslov_stevilka").val("").val(getGeocompleteData(result, 'street_number'));
        $("#stalni_naslov_ulica").val("").val(getGeocompleteData(result, 'route'));
        $("#stalni_naslov_posta_st").val("").val(getGeocompleteData(result, 'postal_code'));
        $("#stalni_naslov_posta").val("").val(getGeocompleteData(result, 'postal_town'));
        $("#stalni_naslov_drzava").val("").val(getGeocompleteData(result, 'country'));

        $("#stalni_naslov_stevilka").trigger("change");
        $("#stalni_naslov_ulica").trigger("change");
        $("#stalni_naslov_posta_st").trigger("change");
        $("#stalni_naslov_posta").trigger("change");
        $("#stalni_naslov_drzava").trigger("change");

    });


    ////////////////////////
    // dynamic add/edit form
    ////////////////////////

    // save competence
    $('body').on('click', '.competence .btn.saveItem', function (e) {
        e.preventDefault();
        saveDynamicItem('competence', 'dynamicResultTemplateCompetence', 'saveCompetence');
    });

    // delete competence
    $('body').on('click', '.competence .resultItem .deleteItem', function (e) {
        deleteDynamicItem($(this), 'deleteCompetence');
    });

    // save competence
    $('body').on('click', '.competenceUser .btn.saveItem', function (e) {
        e.preventDefault();
        saveDynamicItem('competenceUser', 'dynamicResultTemplateCompetenceUser', 'saveCompetence');
    });

    // delete competence
    $('body').on('click', '.competenceUser .resultItem .deleteItem', function (e) {
        deleteDynamicItem($(this), 'deleteCompetence');
    });


    // load education
    loadEducations();


    // save education
    $('body').on('click', '.education .btn.saveItem', function (e) {
        e.preventDefault();
        saveDynamicItem('education', 'dynamicResultTemplateEducation', 'saveEducation');
    });

    // delete education
    $('body').on('click', '.education .resultItem .deleteItem', function (e) {
        deleteDynamicItem($(this), 'deleteEducation');
    });


    // load language
    loadLanguages();


    // save language
    $('body').on('click', '.language .btn.saveItem', function (e) {
        e.preventDefault();
        saveDynamicItem('language', 'dynamicResultTemplateLanguage', 'saveLanguage');
    });

    // delete language
    $('body').on('click', '.language .resultItem .deleteItem', function (e) {
        deleteDynamicItem($(this), 'deleteLanguage');
    });


    // load company
    loadCompanies();


    // company save
    $('body').on('click', '.company .btn.saveItem', function (e) {
        e.preventDefault();
        saveDynamicItem('company', 'dynamicResultTemplateCompany', 'saveMemberSB');
    });

    // delete company
    $('body').on('click', '.company .resultItem .deleteItem', function (e) {
        e.preventDefault();
        deleteDynamicItem($(this), 'deleteCompany');
    });

    // load company
    loadPreviousCompanies();


    // company save
    $('body').on('click', '.previousCompany .btn.saveItem', function (e) {
        e.preventDefault();
        saveDynamicItem('previousCompany', 'dynamicResultTemplatePreviousCompany', 'savePreviousMemberSB');
    });

    // delete company
    $('body').on('click', '.previousCompany .resultItem .deleteItem', function (e) {
        e.preventDefault();
        deleteDynamicItem($(this), 'deletePreviousCompany');
    });

    loadManagementExperience();

    // company save
    $('body').on('click', '.managementExperience .btn.saveItem', function (e) {
        e.preventDefault();
        saveDynamicItem('managementExperience', 'dynamicResultTemplateManagementExperience', 'saveManagementExperience');
    });

    // delete company
    $('body').on('click', '.managementExperience .resultItem .deleteItem', function (e) {
        e.preventDefault();
        deleteDynamicItem($(this), 'deleteManagementExperience');
    });

    // pretekle zaposlitve
    loadPreviousEmployments();

    $('body').on('click', '.previousEmployment .btn.saveItem', function (e) {
        e.preventDefault();
        saveDynamicItem('previousEmployment', 'dynamicResultTemplatePreviousEmployment', 'savePreviousEmployment');
    });

    $('body').on('click', '.previousEmployment .resultItem .deleteItem', function (e) {
        e.preventDefault();
        deleteDynamicItem($(this), 'deletePreviousEmployment');
    });


    // edit item
    $('body').on('click', '.resultItem .editItem', function (e) {

        var itemId = $(this).closest('.resultItem').find('input:hidden').val();
        var itemName = $(this).closest('.resultItem').find('input:hidden').attr('name');
        var dynamicSection = $(this).closest('.dynamicSection');

        $(dynamicSection).find('.addItem').trigger('click');
        $(dynamicSection).find('.actionTitle').text($(dynamicSection).find('.actionTitle').data('edit'));

        // populate edit data from json
        var dataSection = JsonData[$(dynamicSection).data('section')]
        var thisItemData = _.find(dataSection, function (item) {
            return item[itemName] == itemId;
        });


        $(dynamicSection).find('.dynamicEditItem :input').each(function () {
            var thisInput = $(this);
            var fieldType = $(thisInput).getType();

            switch (fieldType) {
                case 'hidden':
                    $(thisInput).val(thisItemData[$(this).attr('name')]);
                    break;
                case 'text':
                    $(thisInput).val($('<div>').html(thisItemData[$(this).attr('name')]).text());
                    if ($(thisInput).data('litepicker')) {
                        $(thisInput).data('litepicker').setDate(thisItemData[$(this).attr('name')]);
                    }
                    break;
                case 'textarea':
                    $(thisInput).val($('<div>').html(thisItemData[$(this).attr('name')]).text());
                    break;
                case 'select':
                case 'select-one':
                    var idFieldName = $(this).data('idField');
                    var thisFieldValue = idFieldName ? thisItemData[idFieldName] : getOptionValueByText($(thisInput), thisItemData[$(this).attr('name')]);
                    $(thisInput).val(thisFieldValue).trigger('change').trigger("chosen:updated");
                    break
            }
        });

        setTimeout(stickyFooter, 200);
    });

    // add item
    $('body').on('click', '.addItem', function (e) {
        var dynamicEditItem = $(this).closest('.dynamicEditItem');

        $(dynamicEditItem).find('.actionTitle').text((dynamicEditItem).find('.actionTitle').data('new'));

        $(dynamicEditItem).find('.mb-3.has-error, .form-group.has-error').removeClass('has-error');
        $(dynamicEditItem).find('.mb-3 .help-block.has-error, .form-group .help-block.has-error').remove();

        $(dynamicEditItem).find('.addItemActionContainer').addClass('d-none');
        $(dynamicEditItem).find('.saveItemActionContainer').removeClass('d-none');
        $(dynamicEditItem).find('.addOrEditItem').removeClass('d-none');

        if ($(dynamicEditItem).find('input:hidden').val() == "") {
            $(dynamicEditItem).find(':input').val('');
        }

        setTimeout(stickyFooter, 200);
    });


    // cancel
    $('body').on('click', '.cancelItem', function (e) {
        var dynamicEditItem = $(this).closest('.dynamicEditItem');


        Swal.fire({
            title: formBcancelInsert,
            text: formBchangeNotSaved,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#DD6B55",
            confirmButtonText: formBconfirm,
            cancelButtonText: formBcancel,
        }).then((result) => {
            if (result.isConfirmed) {

            closeDynamicItem($(dynamicEditItem));


            }
        });


    });


    // add attachment
    $('body').on('change', 'input[type="file"]', function () {
        var thisInput = $(this);
        var thisDroppableArea = $(thisInput).closest('.droppableArea');
        $(thisDroppableArea).removeClass('droppable');

        if (this.files.length == 1) {

            var correctFileType = false;
            if (this.files[0].type == 'application/pdf' || this.files[0].type == 'application/x-pdf' || this.files[0].type == 'image/jpg' || this.files[0].type == 'image/jpeg') {
                correctFileType = true;
            }

            if (!correctFileType) {
                $(thisDroppableArea).find('p').html("<em class='text-danger' style='font-size: 18px;'><i class='fas fa-exclamation-triangle'></i>" + formBattachmentWrongFormat + "</em>");
                setTimeout(function () {
                    $(thisInput).val('');
                    $(thisDroppableArea).find('p').html("<em>" + formBattachmentHelp + "<em>");
                }, 3000);
                $(thisDroppableArea).find('button').hide();
                return;
            }

            if (this.files[0].size <= (20 * 1024 * 1024) && correctFileType) {
                $(thisDroppableArea).find('p').html("<em>" + formBchosenFile + ":</em> <b>" + this.files[0].name + "</b>.");
                $(thisDroppableArea).find('button').fadeIn();
            } else {
                $(thisDroppableArea).find('p').html("<em class='text-danger' style='font-size: 18px;'><i class='fas fa-exclamation-triangle'></i>" + formBsizeError + "</em>");
                setTimeout(function () {
                    $(thisInput).val('');
                    $(thisDroppableArea).find('p').html("<em>" + formBattachmentHelp + "<em>");
                }, 3000);
                $(thisDroppableArea).find('button').hide();
            }
        } else {
            $(thisDroppableArea).find('p').html("<em>" + formBattachmentHelp + "<em>");
            $(thisDroppableArea).find('button').hide();
        }

    });

    $(".droppableArea").on("dragover", function (event) {
        event.preventDefault();
        event.stopPropagation();
        $(this).addClass('droppable');
    });

    $(".droppableArea").on("dragleave", function (event) {
        event.preventDefault();
        event.stopPropagation();
        $(this).removeClass('droppable');
    });


    $('body').on('click', '.upload', function (e) {
        e.preventDefault();
        var thisButton = $(this);
        var thisSection = $(thisButton).parents('.uploadSectionItem');
        var thisDataSection = $(thisSection).data('section');
        var thisInput = $(thisButton).parents('.input-group').find('input[type="file"]');
        var postUrl = $('#osnovniPodatki').attr('action');
        var action = $(thisButton).data("section");


        var formData = new FormData();
        formData.append('file', $(thisInput)[0].files[0]);

        $(thisButton).hide();
        $(thisInput).parents('.droppableArea').find('p').prepend(' <i class="fas fa-circle-notch fa-spin"></i> ');
        //$(thisButton).closest('.input-group').append(' <div class="loader" style="margin-top: 11px;margin-left: 10px;position: absolute;"><i class="fas fa-circle-notch fa-spin"></i></div> ');

        $.ajax({
            url: postUrl + '/upload' + action,
            data: formData,
            type: 'POST',
            contentType: false,
            processData: false
        })
            .fail(function (response) {
                alert("Pri prenosu datoteke je prišlo do napake.");
                $(thisButton).show();
                $('div.loader').remove();
            })
            .done(function (response) {

                $(thisInput).val('').trigger('change');
                $(thisButton).show();
                $('div.loader').remove();

                if (typeof response.error != 'undefined') {
                    alert(response.error);
                    return;
                }

                $(thisSection).find('.attachmentResults').loadTemplate($('#attachmentTemplate'), response);
                switch (thisDataSection) {
                    case 'cvFile':
                        if ($('.cvFile .attachmentResults .resultItem').length >= 1) {
                            $(thisButton).parents('.row.mb-3').hide();
                        }
                        break;
                    case 'competenceCertificate':
                        if ($('.competenceCertificate .attachmentResults .resultItem').length >= 1) {
                            $('.competenceCertificate').find('.row.mb-3').hide();
                        }
                        break;
                    case 'attachments':
                        $(thisButton).hide();
                        loadAttachments();
                        break
                }
            });
    });

    // remove attachment

    $('body').on('click', '.uploadSectionItem.cvFile .deleteItem', function (e) {
        e.preventDefault();
        deleteAttachment($(this), 'deleteCvAttachment');
    });

    $('body').on('click', '.uploadSectionItem.competenceCertificate .deleteItem', function (e) {
        e.preventDefault();
        deleteAttachment($(this), 'deleteCompetenceCertificateAttachment');
    });

    $('body').on('click', '.uploadSectionItem.attachments .deleteItem', function (e) {
        e.preventDefault();
        deleteAttachment($(this), 'deleteOtherAttachments');
    });

    $('body').on('click', '.uploadSectionItem.attachments .makeLeadingItem', function (e) {
        e.preventDefault();
        makeLeadingAttachment($(this), 'setLeadingDocument');
    });

    loadAttachments();
    loadCompetences();
    loadCompetencesAdmin();

    // disable sub-options for fieldOfWork
    $('#fieldOfWork option').each(function () {
        var label = $(this).text();
        if (/\d/.test(label)) {
            $(this).attr('disabled', 'disabled');
        }

        $("#fieldOfWork").trigger("chosen:updated");
    });

    // select parent option if disabled sub-option is previously selected (legacy)
    $('#fieldOfWork option:selected:disabled').each(function (val) {
        var label = $(this).text().split(' ')[0];
        var parentLabel = label.replace(/[0-9]/g, '');

        $('#fieldOfWork option').filter(function () {
            return this.textContent.startsWith(parentLabel + ' ');
        }).prop('selected', true);

        $(this).removeAttr('selected');
        $("#fieldOfWork").trigger("chosen:updated");
    });

    $("button[name='action'][value='next']").on('click', function (e) {
        e.preventDefault();

        var form = $('form')[0];
        var postUrl = $(form).attr('action');

        var action = "checkAccreditationBreak";

        if ($('#isAccreditated').val() == "") {
            if (!$('#osnovniPodatki #hiddenNext').length)
                $('#osnovniPodatki').append('<input id="hiddenNext" type="hidden" name="action" value="next" />');

            $('#osnovniPodatki').submit();
            return;
        }

        var formData = $('#osnovniPodatki').serializeArray();
        formData.push({name: 'action', value: action});

        $.ajax({
            url: postUrl,
            data: formData,
            type: 'POST'
        })
            .fail(function (response) {
                alert("Pri pošiljanju podatkov je prišlo do napake.");
            })
            .done(function (response) {
                if (response == "breaksAccreditation") {
                    Swal.fire({
                        title: formA2warning,
                        text: formA2warningText,
                        icon: "warning",
                        showCancelButton: true,
                        confirmButtonColor: "#DD6B55",
                        confirmButtonText: formA2confirm,
                        cancelButtonText: formA2cancel,
                    }).then((result) => {
                        if (result.isConfirmed) {
                            if (!$('#osnovniPodatki #hiddenNext').length)
                                $('#osnovniPodatki').append('<input id="hiddenNext" type="hidden" name="action" value="next" />');

                            $('#osnovniPodatki').submit();
                        }
                    });
                } else {
                    if (!$('#osnovniPodatki #hiddenNext').length)
                        $('#osnovniPodatki').append('<input id="hiddenNext" type="hidden" name="action" value="next" />');

                    $('#osnovniPodatki').submit();
                        }
            });

    });

    // članstvo v NS: izbira NE ni dovoljena, dokler so vneseni podatki o družbah – vrnemo DA in obvestimo
    $('body').on('change', '.yesNo input[name="memberSb"][value="false"]', function () {
        if (this.checked && $('.dynamicResultsContainer.company .resultItem').length > 0) {
            var $yes = $('.yesNo input[name="memberSb"][value="true"]');
            $yes.prop('checked', true).trigger('change');
            Swal.fire({
                title: formBRemoveMembership,
                icon: "warning",
                showCancelButton: false,
                confirmButtonColor: "#DD6B55",
                confirmButtonText: "OK",
                returnFocus: false
            }).then(function () {
                $yes.trigger('focus');
            });
        }
    });

    if (userRoleUrlPath === 'user') {
        // trenutna zaposlitev: premik med predhodne zaposlitve
        $('body').on('click', '.moveCurrentEmploymentToPrevious', function (e) {
            e.preventDefault();
            moveCurrentEmploymentToPrevious();
        });

        // trenutna zaposlitev: izbira NE, ko so podatki vneseni – vprašamo, ali jih prestavi med predhodne ali izbriše
        $('body').on('change', '.yesNo input[name="currentlyEmployed"][value="false"]', function () {
            if (!this.checked || !currentEmploymentHasData()) {
                return;
            }
            var $yes = $('.yesNo input[name="currentlyEmployed"][value="true"]');
            var $no = $(this);
            $yes.prop('checked', true).trigger('change');
            Swal.fire({
                title: formBnotEmployedTitle,
                text: formBnotEmployedText,
                icon: "question",
                showDenyButton: true,
                showCancelButton: true,
                confirmButtonText: formBnotEmployedMove,
                denyButtonText: formBnotEmployedDelete,
                cancelButtonText: formBcancel,
                returnFocus: false
            }).then(function (result) {
                if (result.isConfirmed) {
                    moveCurrentEmploymentToPrevious();
                } else if (result.isDenied) {
                    clearCurrentEmployment();
                    $no.prop('checked', true).trigger('change').trigger('focus');
                } else {
                    $yes.trigger('focus');
                }
            });
        });
    }

}

var CURRENT_EMPLOYMENT_FIELDS = ['#employerName', '#employedFrom', '#jobTitle', '#jobDescription'];

function currentEmploymentHasData() {
    return CURRENT_EMPLOYMENT_FIELDS.some(function (selector) {
        return String($(selector).val() ?? '').trim() !== '';
    });
}

function clearCurrentEmployment() {
    $(CURRENT_EMPLOYMENT_FIELDS.join(', ')).val('');
    $('#stalni_naslov_hitriVnos, #stalni_naslov_ulica, #stalni_naslov_stevilka, #stalni_naslov_posta_st, #stalni_naslov_posta, #stalni_naslov_drzava').val('');
}

function moveCurrentEmploymentToPrevious() {
    var data = {
        previousEmployerName: $('#employerName').val(),
        previousEmployedFrom: $('#employedFrom').val(),
        previousJobTitle: $('#jobTitle').val(),
        previousJobDescription: $('#jobDescription').val()
    };
    var missing = $.map(data, function (value) {
        return String(value ?? '').trim() === '' ? true : null;
    }).length > 0;
    if (missing) {
        Swal.fire({
            title: formBmoveEmploymentTitle,
            text: formBmoveEmploymentMissing,
            icon: "warning",
            confirmButtonText: "OK"
        });
        return;
    }

    var today = new Date();
    var todayText = today.getDate() + '. ' + (today.getMonth() + 1) + '. ' + today.getFullYear();

    Swal.fire({
        title: formBmoveEmploymentTitle,
        icon: "warning",
        input: "text",
        inputLabel: formBmoveEmploymentEndDate.replace('{0}', todayText),
        inputValue: todayText,
        showCancelButton: true,
        confirmButtonColor: "#198754",
        cancelButtonColor: "#dc3545",
        confirmButtonText: formBmoveEmploymentConfirm,
        cancelButtonText: formBcancel,
        showLoaderOnConfirm: true,
        preConfirm: function (endDate) {
            data.previousEmployedTo = endDate;
            return $.ajax({
                method: "POST",
                url: "/" + userRoleUrlPath + "/form-b/currentEmployment/moveToPrevious",
                data: data,
                dataType: "json"
            }).then(function (response) {
                return response;
            }, function (xhr) {
                var messages = [];
                try {
                    $.each(JSON.parse(xhr.responseText), function (i, errorItem) {
                        if (messages.indexOf(errorItem.message) < 0) {
                            messages.push(errorItem.message);
                        }
                    });
                } catch (ex) {
                    messages.push(formBmoveEmploymentError);
                }
                Swal.showValidationMessage(messages.join(' '));
                return false;
            });
        }
    }).then(function (result) {
        if (!result.isConfirmed) {
            return;
        }
        var $no = $('.yesNo input[name="currentlyEmployed"][value="false"]');
        clearCurrentEmployment();
        $no.prop('checked', true).trigger('change');
        loadPreviousEmployments();
        a11yAnnounceText(formBmoveEmploymentDone);
        $no.trigger('focus');
    });
}

function loadCompetences() {
    $.ajax({
        url: "/" + userRoleUrlPath + "/form-b/competences/" + userId,
        dataType: "json",
    }).done(function (response) {

        JsonData.competenceUser = response;

        if (JsonData.competenceUser == "" || JsonData.competenceUser == null || JsonData.competenceUser.length == 0) {
            $('.dynamicResultsContainer.competenceUser').html("<em>" + competenceFormNoCompetence + "<br/><br/></em>")
        } else {
            fillJsTemplate('competenceUser', 'dynamicResultTemplateCompetenceUser', JsonData.competenceUser);
        }

        setTimeout(stickyFooter, 200);
    });
}

function loadCompetencesAdmin() {
    if (userRoleUrlPath !== "user")
    {
        $.ajax({
            url: "/" + userRoleUrlPath + "/form-b/competences-admin/" + userId,
            dataType: "json",
        }).done(function (response) {

            JsonData.competence = response;

            if (JsonData.competence == "" || JsonData.competence == null || JsonData.competence.length == 0) {
                $('.dynamicResultsContainer.competence').html("<em>" + competenceFormNoCompetence + "<br/><br/></em>")
            } else {
                fillJsTemplate('competence', 'dynamicResultTemplateCompetence', JsonData.competence);
            }

            setTimeout(stickyFooter, 200);
        });
    }
}

function loadCompetencesNew(id) {
    $.ajax({
        url: "/admin/user/form-b/competences/" + id,
        dataType: "json",
    }).done(function (response) {

        JsonData.competenceUserCurrent = response;


        if (JsonData.competenceUserCurrent == "" || JsonData.competenceUserCurrent == null || JsonData.competenceUserCurrent.length == 0) {
            $('.dynamicResultsContainer.competenceCurrent').html("<em>" + competenceFormNoCompetence + "<br/><br/></em>")
        } else {
            fillJsTemplate('competenceUserCurrent', 'dynamicResultTemplateCompetenceUserCurrent', JsonData.competenceUserCurrent);
        }

        setTimeout(stickyFooter, 200);
    });
}

function loadCompetencesAdminNew(id) {
    $.ajax({
        url: "/admin/user/form-b/competences-admin/" + id,
        dataType: "json",
    }).done(function (response) {

        JsonData.competenceCurrent = response;

        if (JsonData.competenceCurrent == "" || JsonData.competenceCurrent == null || JsonData.competenceCurrent.length == 0) {
            $('.dynamicResultsContainer.competenceCurrent').html("<em>" + competenceFormNoCompetence + "<br/><br/></em>")
        } else {
            fillJsTemplate('competenceCurrent', 'dynamicResultTemplateCompetenceCurrent', JsonData.competenceCurrent);
        }

        setTimeout(function () {
            stickyFooter();
        }, 200);
    });
}

function loadAttachments() {


    $.ajax({
        url: "/" + userRoleUrlPath + "/form-b/cvfile/" + userId
    }).done(function (response) {
        if (response == "" || response == null || response.length == 0) {

            $('.uploadSectionItem.cvFile .attachmentResults').html("<em>" + formBerrorCV + "<br/><br/></em>");
        } else {
            $('.uploadSectionItem.cvFile .attachmentResults').loadTemplate($('#attachmentTemplate'), response);

            if ($('.cvFile .attachmentResults .resultItem').length >= 1) {
                $('.cvFile').find('.row.mb-3').hide();
            } else {
                $('.cvFile').find('.row.mb-3').show();
            }

        }

        setTimeout(stickyFooter, 200);
    })
        .fail(function () {
            $('.uploadSectionItem.cvFile .attachmentResults').html("<em>" + formBloadingCVerror + "</em>");
        });

    $.ajax({
        url: "/" + userRoleUrlPath + "/form-b/competencefile/" + userId
    }).done(function (response) {
        if (response == "" || response == null || response.length == 0) {
            $('.uploadSectionItem.competenceCertificate .attachmentResults').html("<em>" + formBerrorCertificate + "<br/><br/></em>");
        } else {
            $('.uploadSectionItem.competenceCertificate .attachmentResults').loadTemplate($('#attachmentTemplate'), response);

            if ($('.competenceCertificate .attachmentResults .resultItem').length >= 1) {
                $('.competenceCertificate').find('.row.mb-3').hide();
            } else {
                $('.competenceCertificate').find('.row.mb-3').show();
            }
        }

        setTimeout(stickyFooter, 200);
    })
        .fail(function () {
            $('.uploadSectionItem.competenceCertificate .attachmentResults').html("<em>" + formBloadingCertificateError + "</em>");
        });

    if (userRoleUrlPath !== "user") {
        $.ajax({
            url: "/" + userRoleUrlPath + "/form-b/competencefile-admin/" + userId
        }).done(function (response) {
            if (response == "" || response == null || response.length == 0) {
                $('.uploadSectionItem.competenceCertificateAdmin .attachmentResults').html("<em>" + formBerrorCertificate + "<br/><br/></em>");
            } else {
                $('.uploadSectionItem.competenceCertificateAdmin .attachmentResults').loadTemplate($('#attachmentTemplate'), response);
            }

            setTimeout(stickyFooter, 200);
        })
            .fail(function () {
                $('.uploadSectionItem.competenceCertificateAdmin .attachmentResults').html("<em>" + formBloadingCertificateError + "</em>");
            });
    }

    $.ajax({
        url: "/" + userRoleUrlPath + "/form-b/attachments/" + userId
    }).done(function (response) {
        if (response == "" || response == null || response.length == 0) {
            $('.uploadSectionItem.attachments .attachmentResults').html("<em>" + formBerrorOther + "<br/><br/></em>");
        } else {
            $('.uploadSectionItem.attachments .attachmentResults').loadTemplate($('#attachmentTemplate'), response);
        }

        // mark leading attachment item
        setTimeout(function () {
            $('.uploadSectionItem.attachments .resultItem .leadingDocument[value="true"]').closest('.resultItem').addClass('leadingAttachment').addClass('bg-warning');
        }, 200);

        setTimeout(stickyFooter, 200);
    })
        .fail(function () {
            $('.uploadSectionItem.attachments .attachmentResults').html("<em>" + formBloadingOtherError + "</em>");
        });

}

function loadAttachmentsNew(id) {


    $.ajax({
        url: "/admin/user/form-b/cvfile/" + id
    }).done(function (response) {
        if (response == "" || response == null || response.length == 0) {

            $('.uploadSectionItem.cvFileCurrent .attachmentResults').html("<em>" + formBerrorCV + "<br/><br/></em>");
        } else {
            $('.uploadSectionItem.cvFileCurrent .attachmentResults').loadTemplate($('#attachmentTemplate'), response);

            if ($('.cvFileCurrent .attachmentResults .resultItem').length >= 1) {
                $('.cvFileCurrent').find('.row.mb-3').hide();
            } else {
                $('.cvFileCurrent').find('.row.mb-3').show();
            }

        }

        setTimeout(stickyFooter, 200);
    })
        .fail(function () {
            $('.uploadSectionItem.cvFileCurrent .attachmentResults').html("<em>" + formBloadingCVerror + "</em>");
        });

    $.ajax({
        url: "/admin/user/form-b/competencefile/" + id
    }).done(function (response) {
        if (response == "" || response == null || response.length == 0) {
            $('.uploadSectionItem.competenceCertificateCurrent .attachmentResults').html("<em>" + formBerrorCertificate + "<br/><br/></em>");
        } else {
            $('.uploadSectionItem.competenceCertificateCurrent .attachmentResults').loadTemplate($('#attachmentTemplate'), response);

            if ($('.competenceCertificateCurrent .attachmentResults .resultItem').length >= 1) {
                $('.competenceCertificateCurrent').find('.row.mb-3').hide();
            } else {
                $('.competenceCertificateCurrent').find('.row.mb-3').show();
            }
        }

        setTimeout(stickyFooter, 200);
    })
        .fail(function () {
            $('.uploadSectionItem.competenceCertificateCurrent .attachmentResults').html("<em>" + formBloadingCertificateError + "</em>");
        });

    $.ajax({
        url: "/admin/user/form-b/competencefile-admin/" + id
    }).done(function (response) {
        if (response == "" || response == null || response.length == 0) {
            $('.uploadSectionItem.competenceCertificateAdminCurrent .attachmentResults').html("<em>" + formBerrorCertificate + "<br/><br/></em>");
        } else {
            $('.uploadSectionItem.competenceCertificateAdminCurrent .attachmentResults').loadTemplate($('#attachmentTemplate'), response);

            if ($('.competenceCertificateAdminCurrent .attachmentResults .resultItem').length >= 1) {
                $('.competenceCertificateAdminCurrent').find('.row.mb-3').hide();
            } else {
                $('.competenceCertificateAdminCurrent').find('.row.mb-3').show();
            }
        }
        setTimeout(stickyFooter, 200);
    })
        .fail(function () {
            $('.uploadSectionItem.competenceCertificateCurrent .attachmentResults').html("<em>" + formBloadingCertificateError + "</em>");
        });

    $.ajax({
        url: "/admin/user/form-b/attachments/" + id
    }).done(function (response) {
        if (response == "" || response == null || response.length == 0) {
            $('.uploadSectionItem.attachmentsCurrent .attachmentResults').html("<em>" + formBerrorOther + "<br/><br/></em>");
        } else {
            $('.uploadSectionItem.attachmentsCurrent .attachmentResults').loadTemplate($('#attachmentTemplate'), response);
        }

        // mark leading attachment item
        setTimeout(function () {
            $('.uploadSectionItem.attachmentsCurrent .resultItem .leadingDocument[value="true"]').closest('.resultItem').addClass('leadingAttachment').addClass('bg-warning');
        }, 200);

        setTimeout(stickyFooter, 200);
    })
        .fail(function () {
            $('.uploadSectionItem.attachmentsCurrent .attachmentResults').html("<em>" + formBloadingOtherError + "</em>");
        });

}

function deleteAttachment(thisDeleteButton, action) {
    var postUrl = $('form').attr('action');
    var thisResultItem = $(thisDeleteButton).closest('.resultItem');
    var thisSection = $(thisDeleteButton).closest('.uploadSectionItem');
    var itemId = $(thisResultItem).find('input:hidden').val();

    Swal.fire({
        title: formBdeleteQ,
        text: formBdeleteText,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#DD6B55",
        confirmButtonText: formBdelete2,
        cancelButtonText: formBcancel,
    }).then((result) => {
            if (result.isConfirmed) {

        var dynamicFormFields = {};
        dynamicFormFields["action"] = action;
        dynamicFormFields["id"] = itemId;

        $.ajax({
            method: "POST",
            url: postUrl,
            data: dynamicFormFields
        })
            .fail(function (response) {
                alert(formBdeleteError);
            })
            .done(function () {
                $(thisResultItem).fadeOut('fast', function () {
                    $(thisResultItem).parent().remove();

                    setTimeout(function () {
                        if ($('.cvFile .attachmentResults .resultItem').length >= 1) {
                            $('.cvFile').find('.row.mb-3').hide();
                        } else {
                            $('.cvFile').find('.row.mb-3').show();
                        }

                        if ($('.competenceCertificate .attachmentResults .resultItem').length >= 1) {
                            $('.competenceCertificate').find('.row.mb-3').hide();
                        } else {
                            $('.competenceCertificate').find('.row.mb-3').show();
                        }
                    }, 200);
                    if (action == 'deleteOtherAttachments') {
                        loadAttachments();
                    }

                });
            });


            }
    });
}

function makeLeadingAttachment(thisStarButton, action) {
    var postUrl = $('form').attr('action');
    var thisResultItem = $(thisStarButton).closest('.resultItem');
    var thisSection = $(thisStarButton).closest('.uploadSectionItem');
    var itemId = $(thisResultItem).find('input:hidden').val();

    var dynamicFormFields = {};
    dynamicFormFields["action"] = action;
    dynamicFormFields["id"] = itemId;

    $.ajax({
        method: "POST",
        url: postUrl,
        data: dynamicFormFields
    })
        .fail(function (response) {
            alert(formBdeleteError);
        })
        .done(function () {
            loadAttachments();
        });


}

function userOverviewAdmin() {
    var emso = $('#emso').text();

    if (parseInt(emso.substring(9, 12)) <= 499) {
        $('.avatarContainer').addClass('male');
    } else {
        $('.avatarContainer').addClass('female');
    }
    $.fn.dataTableExt.oStdClasses.sPageButton = "btn btn-sm btn-secondary";
    $.fn.dataTableExt.oStdClasses.sPageButtonActive = "active";

    $('#userLogHistory').DataTable({
        searching: false,
        ordering: false,
        paging: true,
        info: false,
        destroy: true,
        "lengthChange": false,
        "pageLength": 5,
        "language": {
            "emptyTable": "Ni vnosov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        }
    });

    $('#nominationTable').DataTable({
        searching: false,
        ordering: true,
         "order": [[ 1, "desc" ]],
        paging: true,
        info: false,
        destroy: true,
        "lengthChange": false,
        "pageLength": 5,
        "language": {
            "emptyTable": "Ni vnosov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        }
    });

    $('#membershipTable').DataTable({
        searching: false,
        ordering: false,
        paging: true,
        info: false,
        destroy: true,
        "lengthChange": false,
        "pageLength": 5,
        "language": {
            "emptyTable": "Ni vnosov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        }
    });

    var t = $('#archiveNoticesTable').DataTable({
        searching: false,
        ordering: true,
        paging: true,
        info: false,
        destroy: true,
        "lengthChange": false,
        "pageLength": 5,
        "language": {
            "emptyTable": "Ni vnosov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        }
    });

    t.on('click', 'tbody tr', function () {
        if ($(this).hasClass('selected')) {
            var thisTr = $(this);
            var fileId = $(thisTr).find('.file-id').html();
            var win = window.open("/admin/file/" + fileId, '_blank');
            win.focus();
        } else {
            t.$('tr.selected').removeClass('selected');
            $(this).addClass('selected');
        }
    });

    // Apply the search
    $('body').on('change', 'tr.filtersArchiveDocuments', function () {
        var thisInput = $(this).find("#search-kandidat");
        var thisValue = $(thisInput).val();

        t.columns(0).search(thisValue).draw();

    });

    t.on('click', '.deleteItem', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        $(this).parents('tr').removeClass('selected');

        var thisTr = $(this);
        var itemId = $(thisTr).parents('tr').find('.notice-id').html();

        Swal.fire({
            title: formBdeleteQ,
            text: formBdeleteText,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#DD6B55",
            confirmButtonText: formBdelete2,
            cancelButtonText: formBcancel
        }).then((result) => {
            if (result.isConfirmed) {
                var dynamicFormFields = {};
                dynamicFormFields["action"] = 'deleteArchiveNotices';
                dynamicFormFields["id"] = itemId;

                $.ajax({
                    method: "POST",
                    data: dynamicFormFields

                }).fail(function (response) {
                    Swal.fire('Error', formBdeleteError, 'error');

                }).done(function () {
                    window.location.reload();
                });
            }
        });
    });

    $('body').on('click', '.openArchiveModal', function (e) {
        e.preventDefault();
        var modal = $('#archiveNoticesModal');
        $(modal).find('.modal-title').text("").text("Dodaj nov arhivski zapis");
        bootstrap.Modal.getOrCreateInstance(modal[0]).show();
    });

    initDatePicker('archiveNoticesDocumentDate')


// add attachment
    $('body').on('change', 'input[type="file"]', function () {
        var thisInput = $(this);
        var thisDroppableArea = $(thisInput).closest('.droppableArea');
        $(thisDroppableArea).removeClass('droppable');

        if (this.files.length == 1) {

            var correctFileType = false;
            if (this.files[0].type == 'application/pdf' || this.files[0].type == 'application/x-pdf') {
                correctFileType = true;
            }

            if (!correctFileType) {
                $(thisDroppableArea).find('p').html("<em class='text-danger' style='font-size: 18px;'><i class='fas fa-exclamation-triangle'></i>" + formBwrongFormat + "</em>");
                setTimeout(function () {
                    $(thisInput).val('');
                    $(thisDroppableArea).find('p').html("<em>" + formBattachmentHelp + "<em>");
                }, 3000);
                $(thisDroppableArea).find('button').hide();
                return;
            }

            if (this.files[0].size <= (20 * 1024 * 1024) && correctFileType) {
                $(thisDroppableArea).find('p').html("<em>" + userDashboardChosenFile + ":</em> <b>" + this.files[0].name + "</b>.");
                $(thisDroppableArea).find('button').fadeIn();
            } else {
                $(thisDroppableArea).find('p').html("<em class='text-danger' style='font-size: 18px;'><i class='fas fa-exclamation-triangle'></i>" + formBsizeError + "</em>");
                setTimeout(function () {
                    $(thisInput).val('');
                    $(thisDroppableArea).find('p').html("<em>" + formBattachmentHelp + "<em>");
                }, 3000);
                $(thisDroppableArea).find('button').hide();
            }
        } else {
            $(thisDroppableArea).find('p').html("<em>" + formBattachmentHelp + "<em>");
            $(thisDroppableArea).find('button').hide();
        }

    });

    $(".droppableArea").on("dragover", function (event) {
        event.preventDefault();
        event.stopPropagation();
        $(this).addClass('droppable');
    });

    $(".droppableArea").on("dragleave", function (event) {
        event.preventDefault();
        event.stopPropagation();
        $(this).removeClass('droppable');
    });


    $('body').on('click', '.upload', function (e) {
        e.preventDefault();

        var thisButton = $(this);
        var thisSection = $(thisButton).closest('.uploadSectionItem');
        var thisDataSection = $(thisSection).data('section');
        var thisInput = $(thisButton).closest('.input-group').find('input[type="file"]');
        var postUrl = $('form').attr('action');

        if (thisInput.attr("id") === "conditionRecheckUpload") {
            var formData = new FormData();
            formData.append('action', 'uploadConditionRecheck');
            formData.append('file', $(thisInput)[0].files[0]);
            formData.append('conditionRecheckId', $('#conditionRecheckId').val());

            $(thisButton).hide();
            $(thisInput).parents('.droppableArea').find('p').prepend(' <i class="fas fa-circle-notch fa-spin"></i> ');

            $.ajax({
                url: postUrl,
                data: formData,
                type: 'POST',
                contentType: false,
                processData: false
            })
                .fail(function (response) {
                    alert(formBerrorFile);
                    $(thisButton).show();
                    $('div.loader').remove();

                })
                .done(function (response) {
                    window.location.reload();
                });
        }
        if (thisInput.attr("id") === "archiveNotices") {
            var formData = new FormData();
            formData.append('action', 'uploadArchive');
            formData.append('file', $(thisInput)[0].files[0]);
            formData.append('date', $('#archiveNoticesDocumentDate').val());
            formData.append('type', $('#archiveNoticesDocumentType').val());

            // Validate document type
            var documentType = $('#archiveNoticesDocumentType').val();
            if (!documentType || documentType === '') {
                alert('Prosimo, izberite tip dokumenta pred nalaganjem datoteke.');
                // Optionally highlight the field
                $('#archiveNoticesDocumentType').addClass('is-invalid');
                $('#archiveNoticesDocumentType').focus();
                return false;
            }

            $(thisButton).hide();
            $(thisInput).parents('.droppableArea').find('p').prepend(' <i class="fas fa-circle-notch fa-spin"></i> ');
            //$(thisButton).closest('.input-group').append(' <div class="loader" style="margin-top: 11px;margin-left: 10px;position: absolute;"><i class="fas fa-circle-notch fa-spin"></i></div> ');

            $.ajax({
                url: postUrl,
                data: formData,
                type: 'POST',
                contentType: false,
                processData: false
            })
                .fail(function (response) {
                    alert(formBerrorFile);
                    $(thisButton).show();
                    $('div.loader').remove();

                })
                .done(function (response) {
                    window.location.reload();
                });
        }
    });


    $('#messages tbody tr').each(function () {
        var thisTr = $(this);
        var messageBody = $(thisTr).find('.messageBody').html();


        $(thisTr).find('.shortMessageBody').html(truncate(messageBody, 120));

        //messageBody = messageBody.split("<body")[1].split(">").slice(1).join(">").split("</body>")[0];
        //$('.shortMessageBody').html(messageBody);
        //$(thisTr).find('.messageBody').text(messageBody);
    });

    var documentsTable = $('#documentsTable').DataTable({
        searching: false,
        ordering: true,
        paging: true,
        info: false,
        destroy: true,
        "lengthChange": false,
        "pageLength": 5,
        "language": {
            "emptyTable": "Ni vnosov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
            }
    });

    documentsTable.on('click', 'tbody tr', function () {
        if ($(this).hasClass('selected')) {
            var thisTr = $(this);
            var fileId = $(thisTr).find('.file-id').html();
            var win = window.open("/admin/file/" + fileId, '_blank');
            win.focus();
        } else {
            documentsTable.$('tr.selected').removeClass('selected');
            $(this).addClass('selected');
        }
    });

    // Apply the search
    $('body').on('change', 'tr.filtersDocuments', function () {
        var thisInput = $(this).find("#search-name");
        var thisValue = $(thisInput).val();

        documentsTable.columns(0).search(thisValue).draw();
    });


    var messagesTable = $('#messages').DataTable({
        searching: false,
        ordering: false,
        paging: true,
        info: false,
        destroy: true,
        "lengthChange": false,
        "pageLength": 5,
        "language": {
            "emptyTable": "Ni vnosov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        }
    });

    $('#messages tbody').on('click', 'tr', function () {
        showMessageModal($(this));
    });

    $('body').on('click', '.openMotivationalLetterModal', function (e) {
        e.preventDefault();
        var content = $('#motivationalLetter').val();
        var date = $('#motivationalLetterDate').val();
        showMotivationalLetterModal(content, date);
    });


    $('#last15PublishedNews tbody').on('click', 'tr', function () {
        showMessageModal($(this), $(this).find('.newsTitle').text());
    });

    $('#last15PublishedNews').DataTable({
        autoWidth: false,
        searching: false,
        ordering: false,
        paging: true,
        info: false,
        destroy: true,
        "lengthChange": false,
        "pageLength": 5,
        "language": {
            "emptyTable": "Ni vnosov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        }
    });


    $('body').on('click', '#userSnapshot .createSnapshot', function (e) {
        e.preventDefault();

        var userId = $(this).data('user-id');
        var postUrl = '/admin/create-user-snapshot/' + userId;

        $.ajax({
            url: postUrl,
            type: 'GET'
        }).fail(function (response) {
            Swal.fire({
                title: formBcreatingError,
                icon: "error",
                showCancelButton: false,
                confirmButtonColor: "#DD6B55",
                confirmButtonText: "OK",
            }).then((result) => {
            if (result.isConfirmed) {
            }
            });
        }).done(function (response) {
            Swal.fire({
                title: formBcreatingSuccess,
                icon: "success",
                showCancelButton: false,
                confirmButtonColor: "#A5DC86",
                confirmButtonText: "OK",
            }).then((result) => {
            if (result.isConfirmed) {
            }
            });
        });
    });


    $('#adminBookmarkButton').on('click', function (e) {
        e.preventDefault();

        var modal = $('#adminBookmarkModal');
        var modalTitle = "Zaznamek o kandidatu";
        var content = $(this).data('admin-bookmark-data');

        // Remove previous TinyMCE instance if exists
        if (tinymce.get('adminBookmarkText')) {
            tinymce.get('adminBookmarkText').remove();
        }

        $(modal).find('.modal-title').text("").text(modalTitle);
        $(modal).find('.modal-body #adminBookmarkText').val(content);
        bootstrap.Modal.getOrCreateInstance(modal[0]).show();
    });

    // Remove TinyMCE when modal is closed
    $('#adminBookmarkModal').on('hidden.bs.modal', function () {
        var _this = $(this);

        if (tinymce.get('adminBookmarkText')) {
            tinymce.get('adminBookmarkText').remove();
        }

        _this.find('#modal-error').addClass('d-none');
    });

    $('#adminBookmarkModal').on('shown.bs.modal', function () {
        var _this = $(this);

        // Remove any existing TinyMCE instance
        if (tinymce.get('adminBookmarkText')) {
            tinymce.get('adminBookmarkText').remove();
        }

        // Remove errors
        _this.find('#modal-error').addClass('d-none');

        // Initialize TinyMCE
        tinymce.init({
            selector: '#adminBookmarkText',
            license_key: 'gpl',
            language: 'sl-SI',
            height: 800,
            menubar: false,
            skin: false,
            content_css: false,
            contextmenu: 'paste pastetext | cut copy | link unlink | removeformat',
            plugins: ['link', 'lists', 'code', 'fullscreen'],
            toolbar: 'bold italic underline strikethrough | forecolor backcolor | subscript superscript | bullist numlist | link unlink | removeformat | cut copy paste pastetext | undo redo | code fullscreen',
            content_style: 'body { font-family: Arial; font-size: 14px; }',
            paste_as_text: false,
            paste_remove_styles_if_webkit: true,
            paste_strip_class_attributes: 'all',
            paste_retain_style_properties: 'none',
            paste_postprocess: function(plugin, args) {
                const ALLOWED_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'A', 'P', 'BR', 'UL', 'OL', 'LI']);
                const clean = function(node) {
                    const children = Array.from(node.childNodes);

                    children.forEach(function(child) {
                        if (child.nodeType === Node.TEXT_NODE) {
                            return;
                        }
                        if (child.nodeType === Node.ELEMENT_NODE) {
                            const tag = child.tagName.toUpperCase();

                            clean(child);

                            if (ALLOWED_TAGS.has(tag)) {
                                Array.from(child.attributes).forEach(function(attr) {
                                    if (tag === 'A' && (attr.name === 'href' || attr.name === 'target')) {
                                        return;
                                    }
                                    child.removeAttribute(attr.name);
                                });
                            } else {
                                const parent = child.parentNode;
                                while (child.firstChild) {
                                    parent.insertBefore(child.firstChild, child);
                                }
                                parent.removeChild(child);
                            }
                        }
                    });
                };
                clean(args.node);
            }
        });
    });

    $('#updateAdminBookmarkText').on('click', function (e) {
        e.preventDefault();
        var _this = $(this);

        if (!tinymce.get('adminBookmarkText')) {
            console.error("TinyMCE: adminBookmarkText does not exist!");
            return;
        }

        var modal = $('#adminBookmarkModal');
        var newContent = tinymce.get('adminBookmarkText').getContent();

        var data = {};
        data["action"] = 'updateAdminBookmark';
        data["content"] = newContent;

        $.ajax({
            method: "POST",
            data: data

        }).fail(function (response) {
            $(modal).find('#modal-error').removeClass('d-none');

            if (response.status === 400) {
                var errorMessage = [];
                $(modal).find('#modal-error').text("").text("Besedilo je predolgo.");
            } else {
                $(modal).find('#modal-error').text("")
                    .text("Prišlo je do napake. Prosim poskusite kasneje, ali se obrnite na skrbnika sistema.");
            }
        }).done(function () {
            // replace adminBookmark content with new content
            $('#adminBookmarkButton').data('admin-bookmark-data', newContent);
            (function(){ var bsModal = bootstrap.Modal.getInstance(modal[0]); if (bsModal) bsModal.hide(); })();
        });
    });


    if ($('#sdh-employee').val() == "true") {
        $('#sdhEmployee').prop('checked', true);
    }


    if ($('#special-candidate').val() == "true") {
        $('#specialCandidate').prop('checked', true);
    }

    $('#sdhEmployee').on('change',  function () {
        var userId = $('#userId').val();
        var checked = $('#sdhEmployee').is(':checked');
        updateUserDashboardToggles("updateSdhEmployee", userId, checked);
    });

    $('#specialCandidate').on('change',  function () {
        var userId = $('#userId').val();
        var checked = $('#specialCandidate').is(':checked');
        updateUserDashboardToggles("updateSpecialCandidate", userId, checked);
    });

    $('body').on('click', '.openEmailInterviewRequestDataModal', function (e) {
        e.preventDefault();
        var thisBtn = $(this);
        var userId = $('#userId').val();
        showModalToSendEmail(emailModal, userId);
    });

    $('.reject-statement-btn').on('click', function(e) {
        e.preventDefault();

        var button = $(this);
        var recheckId = button.data('recheck-id');
        var statusCell = button.closest('tr').find('td').eq(5); // Find status cell in the same row

        // Confirmation dialog
        if (confirm('Ali ste prepričani, da želite zavrniti izjavo?')) {

            // Disable button and show loading
            button.prop('disabled', true);
            button.html('<span class="spinner-border spinner-border-sm me-1"></span>Zavračam...');
            var userId = $('#userId').val();

            // Make AJAX request
            $.ajax({
                url: "/admin/user/" + userId + "?recheckId=" + recheckId,
                method: 'POST',
                data: {
                    action: 'rejectConditionRecheck'
                },
                success: function(response) {
                    // Show success feedback
                    button.removeClass('btn-danger').addClass('btn-success');
                    button.html('<i class="fas fa-check me-1"></i>Zavrnjeno');
                    statusCell.text('Izjava poslana');
                    statusCell.addClass('text-success fw-bold');
                },
                error: function(xhr, status, error) {
                    console.error('Failed to delete statement:', error);

                    // Reset button
                    button.prop('disabled', false);
                    button.html('<i class="fas fa-times me-1"></i>Zavrni izjavo');

                    // Show error message
                    var errorMessage = 'Napaka pri zavračanju izjave.';
                    if (xhr.responseJSON && xhr.responseJSON.message) {
                        errorMessage = xhr.responseJSON.message;
                    }
                    alert(errorMessage);
                }
            });
        }
    });
    $('.delete-statement-btn').on('click', function(e) {
        e.preventDefault();

        var button = $(this);
        var recheckId = button.data('recheck-id');
        var statusCell = button.closest('tr').find('td').eq(5); // Find status cell in the same row

        // Confirmation dialog
        if (confirm('Ali ste prepričani, da želite izbrisati izjavo?')) {

            // Disable button and show loading
            button.prop('disabled', true);
            button.html('<span class="spinner-border spinner-border-sm me-1"></span>Brišem...');
            var userId = $('#userId').val();

            // Make AJAX request
            $.ajax({
                url: "/admin/user/" + userId + "?recheckId=" + recheckId,
                method: 'POST',
                data: {
                    action: 'deleteConditionRecheck'
                },
                success: function(response) {
                    // Show success feedback
                    button.removeClass('btn-danger').addClass('btn-success');
                    button.html('<i class="fas fa-check me-1"></i>Izbrisano');
                    statusCell.text('Izjava neizpolnjena');
                    statusCell.addClass('text-success fw-bold');
                },
                error: function(xhr, status, error) {
                    console.error('Failed to reject statement:', error);

                    // Reset button
                    button.prop('disabled', false);
                    button.html('<i class="fas fa-times me-1"></i>Izbriši izjavo');

                    // Show error message
                    var errorMessage = 'Napaka pri brisanju izjave.';
                    if (xhr.responseJSON && xhr.responseJSON.message) {
                        errorMessage = xhr.responseJSON.message;
                    }
                    alert(errorMessage);
                }
            });
        }
    });
    $(document).on('click', '.edit-text-btn', function(e) {
        e.preventDefault();
        e.stopPropagation();

        var button = $(this);
        var conditionRecheckId = button.data('condition-recheck-id');
        var currentText = button.attr('data-text') || '';

        // Set values in modal
        $('#textConditionRecheckId').val(conditionRecheckId);
        $('#textAreaField').val(currentText);

        // Show modal - Bootstrap 5 way
        var myModal = new bootstrap.Modal(document.getElementById('editTextModal'));
        myModal.show();
    });

    $(document).on('click', '#saveTextBtn', function(e) {
        e.preventDefault();

        var conditionRecheckId = $('#textConditionRecheckId').val();
        var text = $('#textAreaField').val();

        var saveBtn = $(this);
        saveBtn.prop('disabled', true);
        saveBtn.html('<i class="fas fa-spinner fa-spin"></i> Shranjujem...');

        $.ajax({
            url: '/admin/condition-verification/updateText',
            method: 'POST',
            contentType: 'application/x-www-form-urlencoded',
            data: {
                conditionRecheckId: conditionRecheckId,
                text: text
            },
            success: function(response) {
                $('.edit-text-btn[data-condition-recheck-id="' + conditionRecheckId + '"]').attr('data-text', text);
                saveBtn.html('<i class="fas fa-check"></i> Shranjeno');
                saveBtn.removeClass('btn-primary').addClass('btn-success');

                setTimeout(function() {
                    var myModal = bootstrap.Modal.getInstance(document.getElementById('editTextModal'));
                    myModal.hide();

                    // Reset button
                    saveBtn.html('<i class="fas fa-save"></i> Shrani');
                    saveBtn.removeClass('btn-success').addClass('btn-primary');
                    saveBtn.prop('disabled', false);

                }, 1000);
            },
            error: function(xhr, status, error) {
                saveBtn.html('<i class="fas fa-times"></i> Napaka');
                saveBtn.removeClass('btn-primary').addClass('btn-danger');

                setTimeout(function() {
                    saveBtn.html('<i class="fas fa-save"></i> Shrani');
                    saveBtn.removeClass('btn-danger').addClass('btn-primary');
                    saveBtn.prop('disabled', false);
                }, 2000);

                alert('Napaka pri shranjevanju besedila: ' + error);
            }
        });
    });

    $('body').on('click', '.openConditionRecheckUploadModal', function (e) {
        var button = $(this);
        var conditionRecheckId = button.data('recheck-id');
        e.preventDefault();
        var modal = $('#conditionRecheckUploadModal');
        $(modal).find('.modal-title').text("").text("Naloži podpisano izjavo");
        bootstrap.Modal.getOrCreateInstance(modal[0]).show();
        $('#conditionRecheckId').val(conditionRecheckId);
    });
}

function updateUserDashboardToggles(action, id, boolean) {
    var postUrl = '/admin/user/' + id;

    var dynamicFormFields = {};
    dynamicFormFields["action"] = action;
    dynamicFormFields["check"] = boolean;

    $.ajax({
        method: "POST",
        url: postUrl,
        data: dynamicFormFields
    })
        .fail(function (response) {
            console.log(response);
        })
        .done(function () {
        });
}

function updateUserVop(action, boolean) {
    var postUrl = '/user/profile';

    var dynamicFormFields = {};
    dynamicFormFields["action"] = action;
    dynamicFormFields["check"] = boolean;


    $.ajax({
        method: "POST",
        url: postUrl,
        data: dynamicFormFields
    })
        .fail(function (response) {
            console.log(response);
        })
        .done(function () {
        });
}

function userOverview(role) {
    var emso = $('#emso').text();

    if (parseInt(emso.substring(9, 12)) <= 499) {
        $('.avatarContainer').addClass('male');
    } else {
        $('.avatarContainer').addClass('female');
    }
    $.fn.dataTableExt.oStdClasses.sPageButton = "btn btn-sm btn-secondary";
    $.fn.dataTableExt.oStdClasses.sPageButtonActive = "active";

    $('#userLogHistory').DataTable({
        searching: false,
        ordering: false,
        paging: true,
        info: false,
        destroy: true,
        "lengthChange": false,
        "pageLength": 5,
        "language": {
            "emptyTable": "Ni vnosov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
            }
    });

    $('#nominationTable').DataTable({
        searching: false,
        ordering: true,
        "order": [[ 1, "desc" ]],
        paging: true,
        info: false,
        destroy: true,
        "lengthChange": false,
        "pageLength": 5,
        "language": {
            "emptyTable": "Ni vnosov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
            }
    });

    $('#membershipTable').DataTable({
        searching: false,
        ordering: false,
        paging: true,
        info: false,
        destroy: true,
        "lengthChange": false,
        "pageLength": 5,
        "language": {
            "emptyTable": "Ni vnosov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        }
    });

    var t = $('#archiveNoticesTable').DataTable({
        searching: false,
        ordering: true,
        paging: true,
        info: false,
        destroy: true,
        "lengthChange": false,
        "pageLength": 5,
        "language": {
            "emptyTable": "Ni vnosov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        }
    });

    var documentsTable = $('#documentsTable').DataTable({
        searching: false,
        ordering: true,
        paging: true,
        info: false,
        destroy: true,
        "lengthChange": false,
        "pageLength": 5,
        "language": {
            "emptyTable": "Ni vnosov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        }
    });

    documentsTable.on('click', 'tbody tr', function () {
        if ($(this).hasClass('selected')) {
            var thisTr = $(this);
            var fileId = $(thisTr).find('.file-id').html();
            var win = window.open("/" + role + "/file/" + fileId, '_blank');
            win.focus();
        } else {
            documentsTable.$('tr.selected').removeClass('selected');
            $(this).addClass('selected');
        }
    });

    // Apply the search
    $('body').on('change', 'tr.filtersDocuments', function () {
        var thisInput = $(this).find("#search-name");
        var thisValue = $(thisInput).val();

        documentsTable.columns(0).search(thisValue).draw();
    });


    // Apply the search
    $('tr.filters').on('change', 'input', function () {

        var thisInput = $(this).closest(':input');
        var thisValue = $(thisInput).val();
        t.columns(0).search(thisValue).draw();

    });

    t.on('click', 'tbody tr', function () {
        if ($(this).hasClass('selected')) {
            var thisTr = $(this);
            var fileId = $(thisTr).find('.file-id').html();
            var win = window.open("/" + role + "/file/" + fileId, '_blank');
            win.focus();
        } else {
            t.$('tr.selected').removeClass('selected');
            $(this).addClass('selected');
        }
    });

    // Apply the search
    $('tr.filters').on('change', 'input', function () {

        var thisInput = $(this).closest(':input');
        var thisValue = $(thisInput).val();
        t.columns(0).search(thisValue).draw();

    });


    $('#messages tbody tr').each(function () {
        var thisTr = $(this);
        var messageBody = $(thisTr).find('.messageBody').html();


        $(thisTr).find('.shortMessageBody').html(truncate(messageBody, 120));

        //messageBody = messageBody.split("<body")[1].split(">").slice(1).join(">").split("</body>")[0];
        //$('.shortMessageBody').html(messageBody);
        //$(thisTr).find('.messageBody').text(messageBody);
    });

    var messagesTable = $('#messages').DataTable({
        searching: false,
        ordering: false,
        paging: true,
        info: false,
        destroy: true,
        "lengthChange": false,
        "pageLength": 5,
        "language": {
            "emptyTable": "Ni vnosov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        }
    });

    $('#messages tbody').on('click', 'tr', function () {
        showMessageModal($(this));
    });

    $('body').on('click', '.openMotivationalLetterModal', function (e) {
        e.preventDefault();
        var content = $('#motivationalLetter').val();
        var date = $('#motivationalLetterDate').val();
        showMotivationalLetterModal(content, date);
    });


    $('#last15PublishedNews tbody').on('click', 'tr', function () {
        showMessageModal($(this), $(this).find('.newsTitle').text());
    });

    $('#last15PublishedNews').DataTable({
        autoWidth: false,
        searching: false,
        ordering: false,
        paging: true,
        info: false,
        destroy: true,
        "lengthChange": false,
        "pageLength": 5,
        columnDefs: [
            {targets: 0, width: '30%'},
            {targets: 1, width: '70%'}
        ],
        "language": {
            "emptyTable": "Ni vnosov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        }
    });


    $('#adminBookmarkButton').on('click', function (e) {
        if (role == 'admin') {
            e.preventDefault();

            var modal = $('#adminBookmarkModal');
            var modalTitle = "Zaznamek o kandidatu";
            var content = $(this).data('admin-bookmark-data');

            // Remove previous TinyMCE instance if exists
            if (tinymce.get('adminBookmarkText')) {
                tinymce.get('adminBookmarkText').remove();
            }

            $(modal).find('.modal-title').text("").text(modalTitle);
            $(modal).find('.modal-body #adminBookmarkText').val(content);
            bootstrap.Modal.getOrCreateInstance(modal[0]).show();
        }
        else {
            e.preventDefault();
            e.stopPropagation();

            showMessageModalUserNotice($('#bookmark').val(), "Zaznamek o kandidatu");

            setTimeout(function () {
                $('.modal-footer:visible .btn-primary').hide();
            }, 500);

        }
    });

    // Remove TinyMCE when modal is closed
    $('#adminBookmarkModal').on('hidden.bs.modal', function () {
        var _this = $(this);

        if (tinymce.get('adminBookmarkText')) {
            tinymce.get('adminBookmarkText').remove();
        }

        _this.find('#modal-error').addClass('d-none');
    });

    $('#adminBookmarkModal').on('shown.bs.modal', function () {
        var _this = $(this);

        // Remove any existing TinyMCE instance
        if (tinymce.get('adminBookmarkText')) {
            tinymce.get('adminBookmarkText').remove();
        }

        // Remove errors
        _this.find('#modal-error').addClass('d-none');

        // Initialize TinyMCE
        tinymce.init({
            selector: '#adminBookmarkText',
            license_key: 'gpl',
            language: 'sl-SI',
            height: 800,
            menubar: false,
            skin: false,
            content_css: false,
            contextmenu: 'paste pastetext | cut copy | link unlink | removeformat',
            plugins: ['link', 'lists', 'code', 'fullscreen'],
            toolbar: 'bold italic underline strikethrough | forecolor backcolor | subscript superscript | bullist numlist | link unlink | removeformat | cut copy paste pastetext | undo redo | code fullscreen',
            content_style: 'body { font-family: Arial; font-size: 14px; }',
            paste_as_text: false,
            paste_remove_styles_if_webkit: true,
            paste_strip_class_attributes: 'all',
            paste_retain_style_properties: 'none',
            paste_postprocess: function(plugin, args) {
                const ALLOWED_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'A', 'P', 'BR', 'UL', 'OL', 'LI']);
                const clean = function(node) {
                    const children = Array.from(node.childNodes);
                    children.forEach(function(child) {
                        if (child.nodeType === Node.TEXT_NODE) {
                            return;
                        }
                        if (child.nodeType === Node.ELEMENT_NODE) {
                            const tag = child.tagName.toUpperCase();

                            clean(child);

                            if (ALLOWED_TAGS.has(tag)) {
                                Array.from(child.attributes).forEach(function(attr) {
                                    if (tag === 'A' && (attr.name === 'href' || attr.name === 'target')) {
                                        return;
                                    }
                                    child.removeAttribute(attr.name);
                                });
                            } else {
                                const parent = child.parentNode;
                                while (child.firstChild) {
                                    parent.insertBefore(child.firstChild, child);
                                }
                                parent.removeChild(child);
                            }
                        }
                    });
                };
                clean(args.node);
            }
        });
    });

    $('#updateAdminBookmarkText').on('click', function (e) {
        e.preventDefault();
        var _this = $(this);

        if (!tinymce.get('adminBookmarkText')) {
            console.error("TinyMCE: adminBookmarkText does not exist!");
            return;
        }

        var modal = $('#adminBookmarkModal');
        var newContent = tinymce.get('adminBookmarkText').getContent();

        var data = {};
        data["action"] = 'updateAdminBookmark';
        data["content"] = newContent;

        $.ajax({
            method: "POST",
            data: data

        }).fail(function (response) {
            $(modal).find('#modal-error').removeClass('d-none');

            if (response.status === 400) {
                var errorMessage = [];
                $.each(response.responseJSON, function (i, errorItem) {
                    errorMessage.push(errorItem.defaultMessage);
                });
                $(modal).find('#modal-error').text("").text(errorMessage.join('\r\n'));
            } else {
                $(modal).find('#modal-error').text("")
                    .text("Prišlo je do napake. Prosim poskusite kasneje, ali se obrnite na skrbnika sistema.");
            }
        }).done(function () {
            // replace adminBookmark content with new content
            $('#adminBookmarkButton').data('admin-bookmark-data', newContent);
            (function(){ var bsModal = bootstrap.Modal.getInstance(modal[0]); if (bsModal) bsModal.hide(); })();
        });
    });

    if ($('#sdh-employee').val() == "true") {
        $('#sdhEmployee').prop('checked', true);
        $('#sdhEmployee').prop('disabled', true);
    }
    else {
        $('#sdhEmployee').prop('disabled', true);
    }


    if ($('#special-candidate').val() == "true") {
        $('#specialCandidate').prop('checked', true);
        $('#specialCandidate').prop('disabled', true);
    }
    else {
        $('#specialCandidate').prop('disabled', true);
    }
}

function formC() {

    $('body').on('click', '.openMotivationalLetterModal', function (e) {
        e.preventDefault();
        var thisBtn = $(this);
        var versionDate = $(thisBtn).prev('span').text();
        var content = $(thisBtn).next('textarea').val();
        showMotivationalLetterModalWithVersion(versionDate, content);
    });

    if ($('#canEditBeforeNomination').val() == "true") {
        var modal = $('#motivacijskoPismoWarning');
        var modalInstance = new bootstrap.Modal(modal[0]);
        modalInstance.show();
    }

    // Initialize TinyMCE for motivational letter
    if (typeof window.tinymce !== 'undefined' && $('#motivacijskoPismo').length) {
        tinymce.init({
            selector: '#motivacijskoPismo',
            license_key: 'gpl',
            language: 'sl-SI',
            height: 800,
            menubar: false,
            skin: false,
            content_css: false,
            contextmenu: 'paste pastetext | cut copy | link unlink | removeformat',
            plugins: ['link', 'lists', 'code', 'fullscreen'],
            toolbar: 'bold italic underline strikethrough | forecolor backcolor | subscript superscript | bullist numlist | link unlink | removeformat | cut copy paste pastetext | undo redo | code fullscreen',
            content_style: 'body { font-family: Arial; font-size: 14px; }',
            paste_as_text: false,
            paste_remove_styles_if_webkit: true,
            paste_strip_class_attributes: 'all',
            paste_retain_style_properties: 'none',
            paste_postprocess: function(plugin, args) {
                const ALLOWED_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'A', 'P', 'BR', 'UL', 'OL', 'LI']);
                const clean = function(node) {
                    const children = Array.from(node.childNodes);
                    children.forEach(function(child) {
                        if (child.nodeType === Node.TEXT_NODE) {
                            return;
                        }
                        if (child.nodeType === Node.ELEMENT_NODE) {
                            const tag = child.tagName.toUpperCase();

                            clean(child);

                            if (ALLOWED_TAGS.has(tag)) {
                                Array.from(child.attributes).forEach(function(attr) {
                                    if (tag === 'A' && (attr.name === 'href' || attr.name === 'target')) {
                                        return;
                                    }
                                    child.removeAttribute(attr.name);
                                });
                            } else {
                                const parent = child.parentNode;
                                while (child.firstChild) {
                                    parent.insertBefore(child.firstChild, child);
                                }
                                parent.removeChild(child);
                            }
                        }
                    });
                };
                clean(args.node);
            },
            setup: function(editor) {
                editor.on('init', function() {
                    stickyFooter();
                });
            }
        });
    } else if (typeof window.tinymce === 'undefined') {
        console.error('TinyMCE not found!');
    }

    $("button[name='action'][value='next']").on('click', function (e) {
        e.preventDefault();

        // Get content from TinyMCE if initialized
        var letter = '';
        if (window.tinymce && tinymce.get('motivacijskoPismo')) {
            letter = tinymce.get('motivacijskoPismo').getContent();
        } else {
            letter = $('#motivacijskoPismo').val();
        }
        if (letter.length > 0) {
            Swal.fire({
                title: formCModalUnsavedTitle,
                text: formCModalUnsavedText,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#DD6B55",
                confirmButtonText: formBconfirm,
                cancelButtonText: formBcancel
            }).then((result) => {
                if (result.isConfirmed) {
                    if (!$('form #hiddenNext').length)
                        $('form').append('<input id="hiddenNext" type="hidden" name="action" value="next" />');
                    $('form').submit();
                }
            });
        } else {
            if (!$('form #hiddenNext').length)
                $('form').append('<input id="hiddenNext" type="hidden" name="action" value="next" />');
            $('form').submit();
                }
    });

    $('body').on('click', '.addItem', function (e) {
        if ($("#insertMotivationalLetter").hasClass('d-none')) {
            $("#insertMotivationalLetter").removeClass("d-none").slideDown();
        } else {
            $("#insertMotivationalLetter").slideUp(function() {
                $(this).addClass("d-none");
            });
        }
        e.preventDefault(); // prevent default link behavior
    });

    $("button[name='action'][value='saveLetter']").on("click", function(e) {
        // Sync TinyMCE content to textarea before submit
        if (window.tinymce && tinymce.get('motivacijskoPismo')) {
            tinymce.get('motivacijskoPismo').save();
        }
        $("#insertMotivationalLetter").slideUp(function() {
            $(this).addClass("d-none");
        });
    });

    // Sync TinyMCE content before any form submission
    $('#motivacijskoPismoForm').on('submit', function() {
        if (window.tinymce && tinymce.get('motivacijskoPismo')) {
            tinymce.get('motivacijskoPismo').save();
        }
    });

    stickyFooter();
}

function userStatus() {

    $(".select2-select").each(function () {
        if ($(this).data("max")) {
            $(this).select2({
                max_selected_options: $(this).data("max"),
                disable_search: true,
                width: "100%",
                allowClear: true,
                placeholder: $(this).attr("placeholder") || ""
            });
        } else {
            $(this).select2({
                disable_search: false,
                width: "100%",
                search_contains: true,
                placeholder_text_single: $(this).attr("placeholder"),
                no_results_text: "Ni zadetkov",
                allowClear: true,
                placeholder: $(this).attr("placeholder") || ""
            });
        }

    });


    $('body').on('change', '.checkboxLabel input[type="checkbox"]', function () {
        if (syncToggleLabel(this, 'checked', null)) {
            $('#interviewDate').val('').attr('disabled', 'disabled');
        } else {
            $('#interviewDate').removeAttr('disabled');
        }
    });

    // if init checked
    if ($('.checkboxLabel').find('input[type="checkbox"]').is(':checked')) {
        var thisButton = $('.checkboxLabel');
        $(thisButton).find('i').removeClass('fa-square').addClass('fa-check-square');
        $(thisButton).addClass('checked');
    }
    stickyFooter();
}

// Stanje seznama uporabnikov (stran, razvrščanje, število zadetkov) hranimo v localStorage, ne v piškotku:
// stanje DataTables 3 je veliko (~3,5 KB za 14 stolpcev), piškotek pa se pošilja z vsako zahtevo, kar je
// skupaj z ostalimi piškotki preseglo največjo velikost glave zahteve (Request header is too large).
function usersTableStateSave(settings, data) {
    try {
        localStorage.setItem('vsiUporabnikiState', JSON.stringify(data));
    } catch (e) {
        // localStorage ni na voljo (zasebno okno) – stanje se ne shrani
    }
}

function usersTableStateLoad(settings) {
    try {
        var data = localStorage.getItem('vsiUporabnikiState');
        return data ? JSON.parse(data) : null;
    } catch (e) {
        return null;
    }
}

function newexportaction(e, dt, button, config) {
    var self = this;
    var oldStart = dt.page.info().start;
    var exportInProgress = false;
    var buttonType = '';

    function resolveButtonType(cfg) {
        if (!cfg) {
            console.error('Config is missing');
            return '';
        }

        // Check className property (e.g., 'buttons-pdf buttons-html5 btn btn-secondary')
        var className = (cfg.className || '').toString().toLowerCase();
        if (className.indexOf('buttons-excel') !== -1) {
            return 'excel';
        }
        if (className.indexOf('buttons-pdf') !== -1) {
            return 'pdf';
        }

        // Fallback: check extend property if className didn't match
        if (cfg.extend) {
            var extend = cfg.extend.toString().toLowerCase();
            if (extend.indexOf('excel') !== -1 || extend === 'excel') {
                return 'excel';
            }
            if (extend.indexOf('pdf') !== -1 || extend === 'pdf') {
                return 'pdf';
            }
        }

        console.error('✗ Could not determine button type from config:', cfg);
        return '';
    }

    function findExportAction(type) {
        if (!type || !$.fn || !$.fn.dataTable || !$.fn.dataTable.ext || !$.fn.dataTable.ext.buttons) {
            return null;
        }
        var buttons = $.fn.dataTable.ext.buttons;

        if (type === 'excel') {
            if (buttons.excelHtml5 && typeof buttons.excelHtml5.action === 'function') {
                return buttons.excelHtml5.action;
            }
            if (buttons.excelFlash && typeof buttons.excelFlash.action === 'function') {
                return buttons.excelFlash.action;
            }
        } else if (type === 'pdf') {
            if (buttons.pdfHtml5 && typeof buttons.pdfHtml5.action === 'function') {
                return buttons.pdfHtml5.action;
            }
            if (buttons.pdfFlash && typeof buttons.pdfFlash.action === 'function') {
                return buttons.pdfFlash.action;
            }
        }
        return null;
    }

    buttonType = resolveButtonType(config);

    dt.one('preXhr', function (e, s, data) {
        console.log('preXhr - fetching data for export');
        // Fetch all records matching the current filter (not just the current page)
        data.start = 0;
        data.length = Math.max(dt.page.info().recordsDisplay, dt.page.len(), 1);
        exportInProgress = true;

        dt.one('preDraw', function (e, settings) {
            console.log('preDraw - export starting');
            // Call the original action function
            var exportAction;
            if (!buttonType) {
                console.error('Export failed: Unable to detect button type', config);
            } else {
                exportAction = findExportAction(buttonType);
            }

            if (exportAction) {
                console.log('Calling export action for', buttonType);
                try {
                    // Buttons 4: akcija ob koncu pokliče cb() - brez njega vrže napako
                    var result = exportAction.call(self, e, dt, button, config, function () {
                        $(button).removeClass('processing');
                    });
                    console.log('Export action completed');

                    // Remove processing class from button immediately after export
                    $(button).removeClass('processing');

                    // Handle promise if returned
                    if (result && typeof result.then === 'function') {
                        result.catch(function(err) {
                            console.warn('Export error:', err);
                            $(button).removeClass('processing');
                        });
                    }
                } catch (err) {
                    console.error('Export error:', err);
                    $(button).removeClass('processing');
                }
            } else {
                console.error('Export failed: No export action available for', buttonType);
                console.log('Available buttons:', Object.keys($.fn.dataTable.ext.buttons || {}));
                $(button).removeClass('processing');
            }

            dt.one('preXhr', function (e, s, data) {
                // DataTables thinks the first item displayed is index 0, but we're not drawing that.
                // Set the property to what it was before exporting.
                settings.displayStart = oldStart;
                data.start = oldStart;
                exportInProgress = false;
            });

            // Reload the grid with the original page. Otherwise, API functions like table.cell(this) don't work properly.
            setTimeout(function() {
                dt.ajax.reload(function() {
                    // Ensure spinner is hidden after reload completes
                    $('.showLoadingOverlay').css('height', '0px').fadeOut();
                    exportInProgress = false;

                    // Remove processing class from button to hide spinner
                    $(button).removeClass('processing');
                }, false);
            }, 0);

            // Prevent rendering of the full data to the DOM
            return false;
        });
    });

    // Requery the server with the new one-time export settings
    dt.ajax.reload();
};

function getFavourites() {
    var favourites = Cookies.get('favourites') ? Cookies.get('favourites').split(',') : null;
    if (favourites) {
        $.each(favourites, function (index, value) {
            favourites[index] = parseInt(value);
        });
    }

    return favourites;
}

function vsiUporabnikiAdmin() {
    var showAllUsers = true;


    $.fn.dataTableExt.oStdClasses.sPageButton = "btn btn-sm btn-secondary";
    $.fn.dataTableExt.oStdClasses.sPageButtonActive = "active";

    var url = window.location.pathname;
    var pageLength = 20;

    var favourites = getFavourites();

    fillFiltersWithCookie();

    $('body').on('click', '#showAllUsers', function (e) {
        e.preventDefault();
        showAllUsers = true;
        $('#showAllUsers').removeClass('btn-secondary').addClass('btn-primary');
        $('#showOnlyFavouriteUsers').removeClass('btn-primary').addClass('btn-secondary');
        $('#showOnlySpecial').removeClass('btn-primary').addClass('btn-secondary');
        $('#showOnlySDH').removeClass('btn-primary').addClass('btn-secondary');
        setBtnFilterCookie("showAllUsers");
        if (z) {
            z.draw();
        }
    });

    $('body').on('click', '#showOnlyFavouriteUsers', function (e) {
        e.preventDefault();
        showAllUsers = false;
        $('#showOnlyFavouriteUsers').removeClass('btn-secondary').addClass('btn-primary');
        $('#showAllUsers').removeClass('btn-primary').addClass('btn-secondary');
        $('#showOnlySpecial').removeClass('btn-primary').addClass('btn-secondary');
        $('#showOnlySDH').removeClass('btn-primary').addClass('btn-secondary');
        setBtnFilterCookie("showOnlyFavouriteUsers");
        if (z) {
            z.draw();
        }
    });

    $('body').on('click', '#showOnlySpecial', function (e) {
        e.preventDefault();
        showAllUsers = true;
        $('#showOnlySpecial').removeClass('btn-secondary').addClass('btn-primary');
        $('#showAllUsers').removeClass('btn-primary').addClass('btn-secondary');
        $('#showOnlyFavouriteUsers').removeClass('btn-primary').addClass('btn-secondary');
        $('#showOnlySDH').removeClass('btn-primary').addClass('btn-secondary');
        setBtnFilterCookie("showOnlySpecial");
        if (z) {
            z.draw();
        }
    });

    $('body').on('click', '#showOnlySDH', function (e) {
        e.preventDefault();
        showAllUsers = true;
        $('#showOnlySDH').removeClass('btn-secondary').addClass('btn-primary');
        $('#showAllUsers').removeClass('btn-primary').addClass('btn-secondary');
        $('#showOnlySpecial').removeClass('btn-primary').addClass('btn-secondary');
        $('#showOnlyFavouriteUsers').removeClass('btn-primary').addClass('btn-secondary');
        setBtnFilterCookie("showOnlySDH");
        if (z) {
            z.draw();
        }
    });

    selectBtnFilterFromCookie()

    // Stanje tabele je v localStorage (glej usersTableStateSave); star piškotek odstranimo,
    // ker se je pošiljal z vsako zahtevo in povečal glavo zahteve
    Cookies.remove('vsiUporabnikiState', {path: '/'});

    var z = $('#vsiUporabniki').DataTable({
        searching: false,
        ordering: true,
        info: false,
        stateSave: true,
        stateSaveCallback: usersTableStateSave,
        stateLoadCallback: usersTableStateLoad,
        "lengthChange": true,
        "lengthMenu": [[10, 20, 50, 100], [10, 20, 50, 100]],
        "pageLength": pageLength,
        "language": {
            "emptyTable": "Ni podatkov",
            "lengthMenu": "_MENU_ zadetkov na stran",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        },
        "processing": true,
        "serverSide": true,
        "ajax": {
            "url": url + "/fillUsers",
            "data": function (reqParam) {
                reqParam.kandidat = $('#search-kandidat').val();
                // reqParam.spol = $('#search-spol').val();
                reqParam.status = $('#search-status').val();
                reqParam.accreditationType = $('#search-accreditation-type').val();
                reqParam.izobrazba = $('#search-izobrazba').val();
                reqParam.zaposlitev = $('#search-trenutna-zaposlitev').val();
                reqParam.clanstvo = $('#search-clanstvo').val();
                reqParam.kompetenca = $('#search-kompetenca').val();
                reqParam.sdh = $('#showOnlySDH').attr('class') == "btn btn-primary"
                reqParam.special = $('#showOnlySpecial').attr('class') == "btn btn-primary"

                if (!showAllUsers) {
                    reqParam.favourites = getFavourites();
                }

                $('.showLoadingOverlay').css('height', '100%').fadeIn();
            },
            "dataSrc": function (json) {
                setTimeout(stickyFooter, 500);
                $('.showLoadingOverlay').css('height', '0px').fadeOut();

                // read favourites from cookies to set the stars correctly
                favourites = getFavourites();

                return json.data;
            },
            "dataType":
                'json'
        },
        dom: '<"row"<"col-12"B>>frt<"row"<"col-sm-6"p><"col-sm-6 text-end"l>>',
        buttons: [
            {
                extend: 'pdf',
                orientation: 'landscape',
                pageSize: 'A4',
                className: 'btn btn-secondary',
                text: '<i class=\"fas fa-file-pdf\"></i> PDF',
                exportOptions: {
                    columns: ':visible'
                },
                customize: function (doc) {
                    // Remove filter row (first row in table header)
                    // The first row contains dropdowns/filters, second row contains actual column headers
                    if (doc.content[1] && doc.content[1].table && doc.content[1].table.body && doc.content[1].table.body.length > 1) {
                        doc.content[1].table.body.shift(); // Remove first row (filters)
                    }

                    doc.styles['td div'] = {
                        width: '100%',
                        display: 'block',
                        color: 'red',
                        alignment: 'left'
                    }
                },
                "titleAttr": 'PDF',
                "action": window.newexportaction
            },
            {
                "extend": 'excel',
                "className": 'btn btn-secondary',
                "text": '<i class="fas fa-file-excel"></i> Excel',
                "titleAttr": 'Excel',
                "exportOptions": {"columns": ':visible'},
                "action": window.newexportaction
            },
            {
                extend: 'colvis',
                className: 'btn btn-secondary',
                text: '<i class=\"fas fa-eye\"></i> Vidnost stolpcev'
            }
        ]
        ,

        "columns":
            [
                {
                    "data": {page: "page"},
                    "render": function (data, type, full, meta) {
                        var currentPageLength = meta.settings.pageLength;
                        var index = meta.row + data.page * currentPageLength + 1;
                        return '<td>' + index + '</td>'
                    },
                    "orderable": false
                },

                // STAR toggle
                {
                    "data": {id: "id"},
                    "render": function (data) {
                        if (favourites && favourites.indexOf(data.id) > -1) {
                            return '<td><button type="button" class="a11y-icon-btn toggleFavouriteFlag on" aria-pressed="true"><i class="fas fa-star" aria-hidden="true"></i><span class="visually-hidden">Označi kot priljubljeno</span></button></td>';
                        } else {
                            return '<td><button type="button" class="a11y-icon-btn toggleFavouriteFlag" aria-pressed="false"><i class="fas fa-star" aria-hidden="true"></i><span class="visually-hidden">Označi kot priljubljeno</span></button></td>';
                        }
                    },
                    "orderable": false
                },

                // NAME
                {
                    "data": {
                        firstName: "firstName",
                        msisdn: "msisdn",
                        lastName: "lastName",
                        archiveNotices: "archiveNotices",
                        adminBookmark: "adminBookmark",
                        dateForNextNomination: "dateForNextNomination"
                    },
                    "render": function (data) {
                        var html = '<td style="width=20px">' + data.firstName + ' ' + data.lastName + '' + '' + (data.msisdn ? '<i class="fas fa-phone" data-toggle="tooltip" title="' + data.msisdn + '" data-placement="right" style="padding-left:10px" aria-hidden="true"></i><span class="visually-hidden">, telefon ' + data.msisdn + '</span>' : '') + ' ';

                        if (data.archiveNotices == true) {
                            html = html + '<i class="fas fa-file" title="Arhivski zapisi" aria-hidden="true"></i><span class="visually-hidden">, ima arhivske zapise</span>';
                        }
                        if (data.adminBookmark != null) {
                            html = html + '<button type="button" class="btn btn-mini btnBookmark"><i class="fas fa-bookmark" aria-hidden="true"></i><span class="visually-hidden">Zaznamek o kandidatu</span></button>';
                        }
                        if (data.dateForNextNomination != null) {
                            html = html + '<i class="fas fa-exclamation-triangle" style="color: darkred" data-toggle="tooltip" title="Naslednja nominacija je mogoča dne ' + data.dateForNextNomination + '" data-placement="right" aria-hidden="true"></i><span class="visually-hidden">, naslednja nominacija je mogoča dne ' + data.dateForNextNomination + '</span>';
                        }
                        return html + '</td>';
                    }
                },
                // Naborna pot
                {
                    "width": 15,
                    "data": {nabornaPot: "nabornaPot"},
                    "render": function (data) {
                        if (data.nabornaPot) {
                            return '<td style="width=20px">' + data.nabornaPot + '</td>';
                        } else return '<td style="width=20px"></td>';
                    }
                },
                // Accreditation date
                {
                    "width": 15,
                    "data": {accreditationDate: "accreditationDate"},
                    "render": function (data) {
                        if (data.accreditationDate) {
                            return '<td style="width=20px">' + data.accreditationDate + '</td>';
                        } else return '<td style="width=20px"></td>';
                    }
                },

                // Accreditation submitted date
                {
                    "width": 15,
                    "data": {accreditationSubmittedDate: "accreditationSubmittedDate"},
                    "render": function (data) {
                        if (data.accreditationSubmittedDate) {
                            return '<td style="width=20px">' + data.accreditationSubmittedDate + '</td>';
                        } else return '<td style="width=20px"></td>';
                    }
                },

                // STATUS
                {
                    "data": {
                        status: "status",
                        userNotices: "userNotice",
                        subject: "userNotice.subject",
                        message: "userNotice.message",
                        gender: "gender"
                    },
                    "render": function (data) {
                        var html = '<td>';
                        if (data.status == 'Akreditiran' && data.gender == 'FEMALE') {
                            html = html + data.status + 'a';
                        } else {
                            html = html + data.status;
                        }

                        if (data.status == "Zavrnjena akreditacija") {
                            html = html + '<button type="button" class="a11y-icon-btn btn btn-link btn-mini btnRejectionReason"> <i class="fas fa-info-circle" aria-hidden="true"></i><span class="visually-hidden">Prikaži razlog zavrnitve</span></button>';
                        }
                        return html + '</td>';
                    }
                },

                // ACCREDITATION TYPE
                {"data": "accreditationType",
                    "orderable": false},

                //EDUCATION
                {
                    "data": {educations: "educations"},
                    "render":

                        function (data) {

                            var html = '<td> ';
                            var i;
                            for (i = 0; i < data.educations.length; i++) {
                                html = html + '<div style="margin-bottom: 3px; margin-top: 3px; width: 120px; overflow-wrap: break-word; word-wrap: break-word; hyphens: auto;">' +
                                    '<a class="label label-default" style="display: inline-block; white-space: initial; padding-top: 5px; padding-bottom: 5px; line-height: normal;" data-toggle = "tooltip"' +
                                    ' title = "' + data.educations[i].educationLevel.description + '"' +
                                    ' data-placement = "right" text = "' + data.educations[i].educationLevel.code + ' ' + data.educations[i].educationalTitle + '" >' + data.educations[i].educationLevel.code + ' ' + data.educations[i].educationalTitle + '</a></div>';
                            }
                            return html + '</td>';
                        },
                    "orderable": false
                },

                // EMPLOYER
                {
                    "data": "employer",
                    "orderable": false
                },

                //MEMBERSHIP SB
                {
                    "data": {
                        memberSBs: "memberSBs",
                        member: "member"
                    },
                    "width": 40,
                    "render":

                        function (data) {
                            var html = '<td> ';
                            if (data.member) {
                                var i;
                                for (i = 0; i < data.memberSBs.length; i++) {
                                    var company = data.memberSBs[i].company;
                                    html = html + '<div style="margin-bottom: 3px; margin-top: 3px; width: 120px; overflow-wrap: break-word; word-wrap: break-word; hyphens: auto;">' +
                                        $('<a>').text(company).html() +
                                        '&nbsp;<i title = "' + data.memberSBs[i].positionSB.description + '" class="fas fa-info-circle"></i>'
                                        + '</div>';
                                }
                            }
                            return html + '</td>';
                        }
                    ,
                    "orderable": false
                },

                // COMPETENCES
                {
                    "data": {competences: "competences", competencesUser: "competencesUser"},
                    "width": 90,
                    "render":

                        function (data) {
                            var html = '<td> ';
                            var i;
                            for (i = 0; i < data.competences.length; i++) {
                                if (data.competences[i].competenceLength === null) {
                                    html = html + data.competences[i].competence.description;
                                }
                                else if (data.competences[i].competenceDescription === null || data.competences[i].competenceDescription.length == 0 ){
                                    html = html + data.competences[i].competence.description + '&nbsp;<span tabindex="0" class="label label-default" data-toggle="tooltip" title="' + data.competences[i].competenceLength.description + '" text="' + data.competences[i].competenceLength.code + '" data-placement="right">' + data.competences[i].competenceLength.code + '</span>';
                                }
                                else {
                                    html = html + data.competences[i].competence.description + ' (' + data.competences[i].competenceDescription  + ')&nbsp;<span tabindex="0" class="label label-default" data-toggle="tooltip" title="' + data.competences[i].competenceLength.description + '" text="' + data.competences[i].competenceLength.code + '" data-placement="right">' + data.competences[i].competenceLength.code + '</span>';
                                }
                                if (i != data.competences.length - 1) {
                                    html = html + ', ';
                                }
                            }
                            // for (i = 0; i < data.competencesUser.length; i++) {
                            //     html = html + data.competencesUser[i].competence.description + '&nbsp;<span tabindex="0" class="label label-default" data-toggle="tooltip" title="' + data.competencesUser[i].competenceLevel.description + '" text="' + data.competencesUser[i].competenceLevel.code + '" data-placement="right">' + data.competencesUser[i].competenceLevel.code + '</span>';
                            //     if (i != data.competencesUser.length - 1) {
                            //         html = html + ', ';
                            //     }
                            // }
                            return html + '</td>';
                        },
                    "orderable":
                        false
                },

                //NOMINATIONS
                //
                // {
                //     "data": {nominations: "nominations"},
                //     "render":
                //
                //         function (data) {
                //             var html = '<td> ';
                //             var i;
                //             for (i = 0; i < data.nominations.length; i++) {
                //                 html = html + '<p><a data-toggle="tooltip" href="/admin/user/nomination/' + data.nominations[i].nominationId + '" data-placement="right">' + data.nominations[i].companyName + '</a> </p>';
                //             }
                //             return html + '</td> ';
                //         },
                //     "orderable":
                //         false
                // },

                // CV
                {
                    "data": {id: "id",
                        hasCv: "hasCv"},
                    "render":
                        function (data) {
                            if (data.id != null && data.hasCv) {
                                return ' <td> <a href="/admin/cv/' + data.id + '" target="_blank" class="label label-default" data-toggle="tooltip" data-placement="right"><i class="fas fa-book"></i> CV</a> </td>';
                            } else return '<td/>'
                        },
                    "orderable":
                        false
                },

// NP
            {
                "data": {nominationReports: "nominationReports"},
                "render":
                    function (data) {
                        var html = '<td style="width=15px"> ';
                        var i;
                        for (i = 0; i < data.nominationReports.length; i++) {
                            html = html + '<a  data-toggle="tooltip" href="/admin/file/' + data.nominationReports[i].cmisId + '" title="' + data.nominationReports[i].company + ' ' + data.nominationReports[i].date + '" class="label label-default" data-placement="right" target="_blank" href="#" >NP</a>&nbsp;';
                        }
                        return html + '</td> ';
                    },
                "orderable":
                    false
            }

        ],
        "initComplete":


            function () {


                setTimeout(stickyFooter, 500);
            }


    });

    z.on('preXhr.dt', function (e, settings, data) {
        console.log('preXhr', data);
        if (!data.order || !data.order.length) {
            data.order = [{ column: 0, dir: 'asc' }];
        }
    });

    // Apply the search
    $('tr.filters').on('change', 'input', function () {

        var thisInput = $(this).closest(':input');
        var thisId = $(thisInput).attr('id');
        var thisValue = $(thisInput).val();
        var filters = Cookies.get('filters');

        if (filters == undefined) {
            setFiltersCookie();
            z.draw();
            setTimeout(stickyFooter, 500);
        } else {
            filters = JSON.parse(filters);
            if (filters[thisId] != thisValue) {
                setFiltersCookie();
                z.draw();
                setTimeout(stickyFooter, 500);
            }
        }


    });

    $('tr.filters').on('change', 'select', function () {

        var thisInput = $(this).closest('select');
        var thisId = $(thisInput).attr('id');
        var thisValue = $(thisInput).val();
        var filters = Cookies.get('filters');

        if (filters == undefined) {
            setFiltersCookie();
            z.draw();
            setTimeout(stickyFooter, 500);
        } else {
            filters = JSON.parse(filters);
            if (filters[thisId] != thisValue) {
                setFiltersCookie();
                z.draw();
                setTimeout(stickyFooter, 500);
            }
        }

    });

    $(".select2-select").each(function () {
        if ($(this).data("max")) {
            $(this).select2({
                max_selected_options: $(this).data("max"),
                disable_search: true,
                width: "100%",
                allowClear: true,
                placeholder: $(this).attr("placeholder") || ""
            });
        } else {
            $(this).select2({
                disable_search: false,
                width: "100%",

                search_contains: true,
                placeholder_text_single: $(this).attr("placeholder"),
                no_results_text: "Ni zadetkov",
                allowClear: true,
                placeholder: $(this).attr("placeholder") || ""
            });
        }

    });

    $('.clearAllFilters').on('click', function (e) {
        e.preventDefault();
        Cookies.remove('filters', {path: '/'});
        Cookies.remove('users_filter', {path: '/'});
        var favourites = [];
        Cookies.set('favourites', favourites.toString(), {expires: 365, path: '/'});
        z.state.clear();
        window.location.reload();

    });

    $('#vsiUporabniki').on('click', 'tbody tr', function () {
        if ($(this).hasClass('selected')) {
            var thisRow = $(this);
            var userId = z.row(thisRow).data().id;
            window.location.href = "/admin/user/" + userId;
        } else {
            z.$('tr.selected').removeClass('selected');
            $(this).addClass('selected');
        }
    });


    // show rejection reason modal
    $('#vsiUporabniki tbody').on('click', '.btnRejectionReason', function (e) {
        e.preventDefault();
        e.stopPropagation();
        //var thisRow = $(this).closest('tr');
        var thisRow = $(this).parents('tr');

        //showMessageModal($(thisRow).find('.messageBodyContainer'), $(thisRow).find('.messageBodyContainer .messageBody').data("title"));
        showMessageModalUserNotice(z.row(thisRow).data().userNotice.message, z.row(thisRow).data().userNotice.subject);

        setTimeout(function () {
            $('.modal-footer:visible .btn-primary').hide();
        }, 500);

    });

    // show rejection reason modal
    $('#vsiUporabniki tbody').on('click', '.btnBookmark', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var thisRow = $(this).parents('tr');

        showMessageModalUserNotice(z.row(thisRow).data().adminBookmark, "Zaznamek o kandidatu");

        setTimeout(function () {
            $('.modal-footer:visible .btn-primary').hide();
        }, 500);

    });


    $('#vsiUporabniki tbody').on('click', '.toggleFavouriteFlag', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var thisRow = $(this).closest('tr');

        if ($(this).is('.on')) {
            $(this).removeClass('on');
        } else {
            $(this).addClass('on');
        }
        $(this).attr('aria-pressed', $(this).is('.on') ? 'true' : 'false');

        updateFavourites(z);

    });

    setTimeout(stickyFooter, 200);

}

function fillFiltersWithCookie() {
    var cookie = Cookies.get('filters');
    if (!cookie) return;

    var filters;
    try {
        filters = JSON.parse(cookie);
    } catch (e) {
        return; // exit if JSON is invalid
    }

    for (var key in filters) {
        if (filters.hasOwnProperty(key)) {
            var $el = $('#' + key);
            if ($el.length) {
                $el.val(filters[key]);
            }
        }
    }
}

function selectBtnFilterFromCookie() {
    var cookie = Cookies.get('users_filter');
    if (!cookie) {

        $('#showAllUsers').click();
        return;
    }

    $('#' + cookie).click();
}


function updateFavourites(table) {
    var favourites = getFavourites();
    if (favourites == null) {
        favourites = [];
    }
    $('#vsiUporabniki tbody tr').each(function (index) {
        var thisRow = $(this);
        var thisUserId = table.row(thisRow).data().id;

        // star is yellow and id is not in cookies yet
        if ($(thisRow).find('.toggleFavouriteFlag').is('.on')) {
            if (favourites.indexOf(thisUserId) <= -1) {
                favourites.push(thisUserId);
            }
        }

        //star is not yellow and id is in cookies already
        if (!$(thisRow).find('.toggleFavouriteFlag').is('.on') && favourites.indexOf(thisUserId) > -1) {
            favourites = favourites.filter(function (item) {
                return item !== thisUserId
            });
        }
    });
    Cookies.set('favourites', favourites.toString(), {expires: 365, path: '/'});
}


function setFiltersCookie() {
    var filters = {
        "search-kandidat": $('#search-kandidat').val(),
        "search-status": $('#search-status').val(),
        "search-accreditation-type": $('#search-accreditation-type').val(),
        "search-izobrazba": $('#search-izobrazba').val(),
        "search-trenutna-zaposlitev": $('#search-trenutna-zaposlitev').val(),
        "search-clanstvo": $('#search-clanstvo').val(),
        "search-kompetenca": $('#search-kompetenca').val(),
    }
    Cookies.set('filters', JSON.stringify(filters), {expires: 365, path: '/'});
}

function setBtnFilterCookie(btnId) {
    Cookies.set('users_filter', btnId, {expires: 365, path: '/'});
}


function vsiUporabniki(role) {
    var showAllUsers = true;

    $.fn.dataTableExt.oStdClasses.sPageButton = "btn btn-sm btn-secondary";
    $.fn.dataTableExt.oStdClasses.sPageButtonActive = "active";

    var url = window.location.pathname;
    var pageLength = 10;

    var favourites = getFavourites();

    fillFiltersWithCookie();

    $('body').on('click', '#showAllUsers', function (e) {
        e.preventDefault();
        showAllUsers = true;
        $('#showAllUsers').removeClass('btn-secondary').addClass('btn-primary');
        $('#showOnlyFavouriteUsers').removeClass('btn-primary').addClass('btn-secondary');
        $('#showOnlySpecial').removeClass('btn-primary').addClass('btn-secondary');
        $('#showOnlySDH').removeClass('btn-primary').addClass('btn-secondary');
        setBtnFilterCookie("showAllUsers");
        if (z) {
            z.draw();
        }
    });

    $('body').on('click', '#showOnlyFavouriteUsers', function (e) {
        e.preventDefault();
        showAllUsers = false;
        $('#showOnlyFavouriteUsers').removeClass('btn-secondary').addClass('btn-primary');
        $('#showAllUsers').removeClass('btn-primary').addClass('btn-secondary');
        $('#showOnlySpecial').removeClass('btn-primary').addClass('btn-secondary');
        $('#showOnlySDH').removeClass('btn-primary').addClass('btn-secondary');
        setBtnFilterCookie("showOnlyFavouriteUsers");
        if (z) {
            z.draw();
        }
    });

    $('body').on('click', '#showOnlySpecial', function (e) {
        e.preventDefault();
        showAllUsers = true;
        $('#showOnlySpecial').removeClass('btn-secondary').addClass('btn-primary');
        $('#showAllUsers').removeClass('btn-primary').addClass('btn-secondary');
        $('#showOnlyFavouriteUsers').removeClass('btn-primary').addClass('btn-secondary');
        $('#showOnlySDH').removeClass('btn-primary').addClass('btn-secondary');
        setBtnFilterCookie("showOnlySpecial");
        if (z) {
            z.draw();
        }
    });

    $('body').on('click', '#showOnlySDH', function (e) {
        e.preventDefault();
        showAllUsers = true;
        $('#showOnlySDH').removeClass('btn-secondary').addClass('btn-primary');
        $('#showAllUsers').removeClass('btn-primary').addClass('btn-secondary');
        $('#showOnlySpecial').removeClass('btn-primary').addClass('btn-secondary');
        $('#showOnlyFavouriteUsers').removeClass('btn-primary').addClass('btn-secondary');
        setBtnFilterCookie("showOnlySDH");
        if (z) {
            z.draw();
        }
    });

    selectBtnFilterFromCookie();

    // Stanje tabele je v localStorage (glej usersTableStateSave); star piškotek odstranimo,
    // ker se je pošiljal z vsako zahtevo in povečal glavo zahteve
    Cookies.remove('vsiUporabnikiState', {path: '/'});

    var z = $('#vsiUporabniki').DataTable({
        searching: false,
        ordering: true,
        info: false,
        stateSave: true,
        stateSaveCallback: usersTableStateSave,
        stateLoadCallback: usersTableStateLoad,
        "lengthChange": true,
        "lengthMenu": [[10, 20, 50, 100], [10, 20, 50, 100]],
        "pageLength": pageLength,
        "language": {
            "emptyTable": "Ni podatkov",
            "lengthMenu": "_MENU_ zadetkov na stran",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        },
        "processing": true,
        "serverSide": true,
        "ajax": {
            "url": url + "/fillUsers",
            "data": function (reqParam) {
                reqParam.kandidat = $('#search-kandidat').val();
                reqParam.status = $('#search-status').val();
                reqParam.accreditationType = $('#search-accreditation-type').val();
                reqParam.izobrazba = $('#search-izobrazba').val();
                reqParam.clanstvo = $('#search-clanstvo').val();
                reqParam.zaposlitev = $('#search-trenutna-zaposlitev').val();
                reqParam.kompetenca = $('#search-kompetenca').val();
                reqParam.sdh = $('#showOnlySDH').attr('class') == "btn btn-primary"
                reqParam.special = $('#showOnlySpecial').attr('class') == "btn btn-primary"

                if (!showAllUsers) {
                    reqParam.favourites = getFavourites();
                }

                $('.showLoadingOverlay').css('height', '100%').fadeIn();

            },
            "dataSrc": function (json) {
                setTimeout(stickyFooter, 500);
                $('.showLoadingOverlay').css('height', '0px').fadeOut();

                // read favourites from cookies to set the stars correctly
                favourites = getFavourites();

                return json.data;
            },
            "dataType":
                'json'
        },
        dom: '<"row"<"col-12"B>>frt<"row"<"col-sm-6"p><"col-sm-6 text-end"l>>',
        buttons: [
            {
                extend: 'pdf',
                orientation: 'landscape',
                pageSize: 'A4',
                className: 'btn btn-secondary',
                text: '<i class=\"fas fa-file-pdf\"></i> PDF',
                exportOptions: {
                    columns: ':visible'
                },
                customize: function (doc) {
                    // Remove filter row (first row in table header)
                    // The first row contains dropdowns/filters, second row contains actual column headers
                    if (doc.content[1] && doc.content[1].table && doc.content[1].table.body && doc.content[1].table.body.length > 1) {
                        doc.content[1].table.body.shift(); // Remove first row (filters)
                    }

                    doc.styles['td div'] = {
                        width: '100%',
                        display: 'block',
                        color: 'red',
                        alignment: 'left'
                    }
                },
                "titleAttr": 'PDF',
                "action": window.newexportaction
            },
            {
                "extend": 'excel',
                "className": 'btn btn-secondary',
                "text": '<i class="fas fa-file-excel"></i> Excel',
                "titleAttr": 'Excel',
                "exportOptions": {"columns": ':visible'},
                "action": window.newexportaction
            },
            {
                extend: 'colvis',
                className: 'btn btn-secondary',
                text: '<i class=\"fas fa-eye\"></i> Vidnost stolpcev'
            }
        ]
        ,
        "columns":
            [
                {
                    "data": {page: "page"},
                    "render": function (data, type, full, meta) {
                        var currentPageLength = meta.settings.pageLength;
                        var index = meta.row + data.page * currentPageLength + 1;
                        return '<td>' + index + '</td>'
                    },
                    "orderable": false
                },

                // STAR toggle
                {
                    "data": {id: "id"},
                    "render": function (data) {
                        if (favourites && favourites.indexOf(data.id) > -1) {
                            return '<td><button type="button" class="a11y-icon-btn toggleFavouriteFlag on" aria-pressed="true"><i class="fas fa-star" aria-hidden="true"></i><span class="visually-hidden">Označi kot priljubljeno</span></button></td>';
                        } else {
                            return '<td><button type="button" class="a11y-icon-btn toggleFavouriteFlag" aria-pressed="false"><i class="fas fa-star" aria-hidden="true"></i><span class="visually-hidden">Označi kot priljubljeno</span></button></td>';
                        }
                    },
                    "orderable": false
                },

                // NAME
                {
                    "data": {
                        firstName: "firstName",
                        msisdn: "msisdn",
                        lastName: "lastName",
                        archiveNotices: "archiveNotices",
                        adminBookmark: "adminBookmark",
                        dateForNextNomination: "dateForNextNomination"
                    },
                    "render": function (data) {
                        var html = '<td style="width=20px">' + data.firstName + ' ' + data.lastName + '' + '&nbsp;' + (data.msisdn ? '<i class="fas fa-phone" data-toggle="tooltip" title="' + data.msisdn + '" data-placement="right" aria-hidden="true"></i><span class="visually-hidden">, telefon ' + data.msisdn + '</span>' : '') + ' ';

                        if (data.archiveNotices == true) {
                            html = html + '<i class="fas fa-file" title="Arhivski zapisi" aria-hidden="true"></i><span class="visually-hidden">, ima arhivske zapise</span>';
                        }
                        if (data.adminBookmark != null) {
                            html = html + '<button type="button" class="btn btn-mini btnBookmark"><i class="fas fa-bookmark" aria-hidden="true"></i><span class="visually-hidden">Zaznamek o kandidatu</span></button>';
                        }
                        if (data.dateForNextNomination != null) {
                            html = html + '<i class="fas fa-exclamation-triangle" style="color: darkred" data-toggle="tooltip" title="Naslednja nominacija je mogoča dne ' + data.dateForNextNomination + '" data-placement="right" aria-hidden="true"></i><span class="visually-hidden">, naslednja nominacija je mogoča dne ' + data.dateForNextNomination + '</span>';
                        }
                        return html + '</td>';
                    }
                },

                {
                    "width": 15,
                    "data": {nabornaPot: "nabornaPot"},
                    "render": function (data) {
                        if (data.nabornaPot) {
                            return '<td style="width=20px">' + data.nabornaPot + '</td>';
                        } else return '<td style="width=20px"></td>';
                    }
                },

                // Accreditation date
                {
                    "width": 15,
                    "data": {accreditationDate: "accreditationDate"},
                    "render": function (data) {
                        if (data.accreditationDate) {
                            return '<td style="width=20px">' + data.accreditationDate + '</td>';
                        } else return '<td style="width=20px"></td>';
                    }
                },

                // Accreditation submitted date
                {
                    "width": 15,
                    "data": {accreditationSubmittedDate: "accreditationSubmittedDate"},
                    "render": function (data) {
                        if (data.accreditationSubmittedDate) {
                            return '<td style="width=20px">' + data.accreditationSubmittedDate + '</td>';
                        } else return '<td style="width=20px"></td>';
                    }
                },

                // STATUS
                {
                    "data": {
                        status: "status",
                        userNotices: "userNotice",
                        subject: "userNotice.subject",
                        message: "userNotice.message",
                        gender: "gender"
                    },
                    "render": function (data) {
                        var html = '<td>';
                        if (data.status == 'Akreditiran' && data.gender == 'FEMALE') {
                            html = html + data.status + 'a';
                        } else {
                            html = html + data.status;
                        }

                        if (data.status == "Zavrnjena akreditacija") {
                            html = html + '<button type="button" class="a11y-icon-btn btn btn-link btn-mini btnRejectionReason"> <i class="fas fa-info-circle" aria-hidden="true"></i><span class="visually-hidden">Prikaži razlog zavrnitve</span></button>';
                        }
                        return html + '</td>';
                    }
                },
                   // ACCREDITATION TYPE
                {"data": "accreditationType",
                    "orderable": false},


                //EDUCATION
                {
                    "data": {educations: "educations"},
                    "render":

                        function (data) {

                            var html = '<td> ';
                            var i;
                            for (i = 0; i < data.educations.length; i++) {
                                html = html + '<div style="margin-bottom: 3px; margin-top: 3px; width: 120px; overflow-wrap: break-word; word-wrap: break-word; hyphens: auto;">' +
                                    '<a class="label label-default" style="display: inline-block; white-space: initial; padding-top: 5px; padding-bottom: 5px; line-height: normal;" data-toggle = "tooltip"' +
                                    ' title = "' + data.educations[i].educationLevel.description + '"' +
                                    ' data-placement = "right" text = "' + data.educations[i].educationLevel.code + ' ' + data.educations[i].educationalTitle + '" >' + data.educations[i].educationLevel.code + ' ' + data.educations[i].educationalTitle + '</a></div>';
                            }
                            return html + '</td>';
                        },
                    "orderable": false
                },

                // EMPLOYER
                {"data": "employer",
                    "orderable": false},

                //MEMBERSHIP SB
                {
                    "data": {
                        memberSBs: "memberSBs",
                        member: "member"
                    },
                    "width": 40,
                    "render":

                        function (data) {
                            var html = '<td> ';
                            if (data.member) {
                                var i;
                                for (i = 0; i < data.memberSBs.length; i++) {
                                    var company = data.memberSBs[i].company;
                                    html = html + '<div style="margin-bottom: 3px; margin-top: 3px; width: 120px; overflow-wrap: break-word; word-wrap: break-word; hyphens: auto;">' +
                                        $('<a>').text(company).html() +
                                        '&nbsp;<i title = "' + data.memberSBs[i].positionSB.description + '" class="fas fa-info-circle"></i>'
                                        + '</div>';
                                }
                            }
                            return html + '</td>';
                        }
                    ,
                    "orderable": false
                },

                // COMPETENCES
                {
                    "data": {competences: "competences", competencesUser: "competencesUser"},
                    "width": 90,
                    "render":

                        function (data) {
                            var html = '<td> ';
                            var i;
                            for (i = 0; i < data.competences.length; i++) {
                                if (data.competences[i].competenceLength === null) {
                                    html = html + data.competences[i].competence.description;
                                }
                                else if (data.competences[i].competenceDescription === null || data.competences[i].competenceDescription.length == 0 ){
                                    html = html + data.competences[i].competence.description + '&nbsp;<span tabindex="0" class="label label-default" data-toggle="tooltip" title="' + data.competences[i].competenceLength.description + '" text="' + data.competences[i].competenceLength.code + '" data-placement="right">' + data.competences[i].competenceLength.code + '</span>';
                                }
                                else {
                                    html = html + data.competences[i].competence.description + ' (' + data.competences[i].competenceDescription  + ')&nbsp;<span tabindex="0" class="label label-default" data-toggle="tooltip" title="' + data.competences[i].competenceLength.description + '" text="' + data.competences[i].competenceLength.code + '" data-placement="right">' + data.competences[i].competenceLength.code + '</span>';
                                }
                                if (i != data.competences.length - 1) {
                                    html = html + ', ';
                                }
                            }
                            // for (i = 0; i < data.competencesUser.length; i++) {
                            //     html = html + data.competencesUser[i].competence.description + '&nbsp;<span tabindex="0" class="label label-default" data-toggle="tooltip" title="' + data.competencesUser[i].competenceLevel.description + '" text="' + data.competencesUser[i].competenceLevel.code + '" data-placement="right">' + data.competencesUser[i].competenceLevel.code + '</span>';
                            //     if (i != data.competencesUser.length - 1) {
                            //         html = html + ', ';
                            //     }
                            // }
                            return html + '</td>';
                        },
                    "orderable":
                        false
                },

                //NOMINATIONS

                // {
                //     "data": {nominations: "nominations"},
                //     "render":
                //
                //         function (data) {
                //             var html = '<td> ';
                //             var i;
                //             for (i = 0; i < data.nominations.length; i++) {
                //                 html = html + '<p><a data-toggle="tooltip" href="/' + role + '/user/nomination/' + data.nominations[i].nominationId + '" data-placement="right">' + data.nominations[i].companyName + '</a> </p>';
                //             }
                //             return html + '</td> ';
                //         },
                //     "orderable":
                //         false
                // },

                // CV
                {
                    "data": {id: "id",
                        hasCv: "hasCv"},
                    "render":
                        function (data) {
                            if (data.id != null && data.hasCv) {
                                return ' <td> <a href="/' + role + '/cv/' + data.id + '" target="_blank" class="label label-default" data-toggle="tooltip" data-placement="right"><i class="fas fa-book"></i> CV</a> </td>';
                            } else return '<td/>'
                        },
                    "orderable":
                        false
                },

// NP
            {
                "data": {nominationReports: "nominationReports"},
                "render":
                    function (data) {
                        var html = '<td style="width=15px"> ';
                        var i;
                        for (i = 0; i < data.nominationReports.length; i++) {
                            html = html + '<a  data-toggle="tooltip" href="/' + role + '/file/' + data.nominationReports[i].cmisId + '" title="' + data.nominationReports[i].company + ' ' + data.nominationReports[i].date + '" class="label label-default" data-placement="right" target="_blank" href="#" >NP</a>&nbsp;';
                        }
                        return html + '</td> ';
                    },
                "orderable":
                    false
            }
        ],
        "initComplete":

            function () {
                setTimeout(stickyFooter, 500);
            }
    });

    z.on('preXhr.dt', function (e, settings, data) {
        if (!data.order || !data.order.length) {
            data.order = [{ column: 0, dir: 'asc' }];
        }
    });

    // Apply the search
    $('tr.filters').on('change', 'input', function () {

        var thisInput = $(this).closest(':input');
        var thisId = $(thisInput).attr('id');
        var thisValue = $(thisInput).val();
        var filters = Cookies.get('filters');

        if (filters == undefined) {
            setFiltersCookie();
            z.draw();
            setTimeout(stickyFooter, 500);
        } else {
            filters = JSON.parse(filters);
            if (filters[thisId] != thisValue) {
                setFiltersCookie();
                z.draw();
                setTimeout(stickyFooter, 500);
            }
        }

    });

    $('tr.filters').on('change', 'select', function () {

        var thisInput = $(this).closest('select');
        var thisId = $(thisInput).attr('id');
        var thisValue = $(thisInput).val();
        var filters = Cookies.get('filters');

        if (filters == undefined) {
            setFiltersCookie();
            z.draw();
            setTimeout(stickyFooter, 500);
        } else {
            filters = JSON.parse(filters);
            if (filters[thisId] != thisValue) {
                setFiltersCookie();
                z.draw();
                setTimeout(stickyFooter, 500);
            }
        }
    });

    $(".select2-select").each(function () {
        if ($(this).data("max")) {
            $(this).select2({
                max_selected_options: $(this).data("max"),
                disable_search: true,
                width: "100%",
                allowClear: true,
                placeholder: $(this).attr("placeholder") || ""
            });
        } else {
            $(this).select2({
                disable_search: false,
                width: "100%",

                search_contains: true,
                placeholder_text_single: $(this).attr("placeholder"),
                no_results_text: "Ni zadetkov",
                allowClear: true,
                placeholder: $(this).attr("placeholder") || ""
            });
        }

    });

    $('.clearAllFilters').on('click', function (e) {
        e.preventDefault();
        Cookies.remove('filters', {path: '/'});
        Cookies.remove('users_filter', {path: '/'});
        var favourites = [];
        Cookies.set('favourites', favourites.toString(), {expires: 365, path: '/'});
        z.state.clear();
        window.location.reload();

    });

    $('#vsiUporabniki').on('click', 'tbody tr', function () {
        if ($(this).hasClass('selected')) {
            var thisRow = $(this);
            var userId = z.row(thisRow).data().id;
            window.location.href = "/" + role + "/user/" + userId;
        } else {
            z.$('tr.selected').removeClass('selected');
            $(this).addClass('selected');
        }
    });

    // show rejection reason modal
    $('#vsiUporabniki tbody').on('click', '.btnRejectionReason', function (e) {
        e.preventDefault();
        e.stopPropagation();
        //var thisRow = $(this).closest('tr');
        var thisRow = $(this).parents('tr');

        showMessageModalUserNotice(z.row(thisRow).data().userNotice.message, z.row(thisRow).data().userNotice.subject);

        setTimeout(function () {
            $('.modal-footer:visible .btn-primary').hide();
        }, 500);

    });

    $('#vsiUporabniki tbody').on('click', '.btnBookmark', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var thisRow = $(this).parents('tr');

        showMessageModalUserNotice(z.row(thisRow).data().adminBookmark, "Zaznamek o kandidatu");

        setTimeout(function () {
            $('.modal-footer:visible .btn-primary').hide();
        }, 500);

    });


    $('#vsiUporabniki tbody').on('click', '.toggleFavouriteFlag', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var thisRow = $(this).closest('tr');

        if ($(this).is('.on')) {
            $(this).removeClass('on');
        } else {
            $(this).addClass('on');
        }
        $(this).attr('aria-pressed', $(this).is('.on') ? 'true' : 'false');
        updateFavourites(z);

    });

    setTimeout(stickyFooter, 200);

}


function dashboardUser() {
    $.fn.dataTableExt.oStdClasses.sPageButton = "btn btn-sm btn-secondary";
    $.fn.dataTableExt.oStdClasses.sPageButtonActive = "active";

    $('#userLogHistory').DataTable({
        searching: false,
        ordering: false,
        paging: true,
        info: false,
        destroy: true,
        "lengthChange": false,
        "pageLength": 5,
        "language": {
            "emptyTable": "Ni vnosov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        }
    });

    $('#nominationTable').DataTable({
        searching: false,
        ordering: true,
        "order": [[ 1, "desc" ]],
        paging: true,
        info: false,
        destroy: true,
        "lengthChange": false,
        "pageLength": 5,
        "language": {
            "emptyTable": "Ni vnosov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        }
    });

    $('#membershipTable').DataTable({
        searching: false,
        ordering: false,
        paging: true,
        info: false,
        destroy: true,
        "lengthChange": false,
        "pageLength": 5,
        "language": {
            "emptyTable": "Ni vnosov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        }
    });


    var t = $('#documentsTable').DataTable({
        searching: false,
        ordering: true,
        paging: true,
        info: false,
        destroy: true,
        "lengthChange": false,
        "pageLength": 5,
        "language": {
            "emptyTable": "Ni vnosov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        }
    });

    t.on('click', 'tbody tr', function () {
        if ($(this).hasClass('selected')) {
            var thisTr = $(this);
            var fileId = $(thisTr).find('.file-id').html();
            var win = window.open("/user/file/" + fileId, '_blank');
            win.focus();
        } else {
            t.$('tr.selected').removeClass('selected');
            $(this).addClass('selected');
        }
    });

    t.on('click', '.deleteItem', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        $(this).parents('tr').removeClass('selected');

        var thisTr = $(this);
        var itemId = $(thisTr).parents('tr').find('.notice-id').html();

        Swal.fire({
            title: formBdeleteQ,
            text: formBdeleteText,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#DD6B55",
            confirmButtonText: formBdelete2,
            cancelButtonText: formBcancel,
        }).then((result) => {
            if (result.isConfirmed) {

            var dynamicFormFields = {};
            dynamicFormFields["action"] = 'deleteDocuments';
            dynamicFormFields["id"] = itemId;

            $.ajax({
                method: "POST",
                data: dynamicFormFields

            }).fail(function (response) {
                console.log(response);
                alert(formBdeleteError);

            }).done(function () {
                window.location.reload();
            });
            }
        });
    });


    $('body').on('click', '.openDocumentsModal', function (e) {
        e.preventDefault();
        var modal = $('#documentsModal');
        $(modal).find('.modal-title').text("").text(userDashboardAddDocument);
        bootstrap.Modal.getOrCreateInstance(modal[0]).show();
    });

    initDatePicker('documentDate');

    // Apply the search
    $('tr.filters').on('change', 'input', function () {

        var thisInput = $(this).closest(':input');
        var thisValue = $(thisInput).val();
        t.columns(0).search(thisValue).draw();

    });

    // add attachment
    $('body').on('change', 'input[type="file"]', function () {
        var thisInput = $(this);
        var thisDroppableArea = $(thisInput).closest('.droppableArea');
        $(thisDroppableArea).removeClass('droppable');

        if (this.files.length == 1) {

            var correctFileType = false;
            if (this.files[0].type == 'application/pdf' || this.files[0].type == 'application/x-pdf') {
                correctFileType = true;
            }

            if (!correctFileType) {
                $(thisDroppableArea).find('p').html("<em class='text-danger' style='font-size: 18px;'><i class='fas fa-exclamation-triangle'></i>" + formBwrongFormat + "</em>");
                setTimeout(function () {
                    $(thisInput).val('');
                    $(thisDroppableArea).find('p').html("<em>" + formBattachmentHelp + "<em>");
                }, 3000);
                $(thisDroppableArea).find('button').hide();
                return;
            }

            if (this.files[0].size <= (20 * 1024 * 1024) && correctFileType) {
                $(thisDroppableArea).find('p').html("<em>" + userDashboardChosenFile + ":</em> <b>" + this.files[0].name + "</b>.");
                $(thisDroppableArea).find('button').fadeIn();
            } else {
                $(thisDroppableArea).find('p').html("<em class='text-danger' style='font-size: 18px;'><i class='fas fa-exclamation-triangle'></i>" + formBsizeError + "</em>");
                setTimeout(function () {
                    $(thisInput).val('');
                    $(thisDroppableArea).find('p').html("<em>" + formBattachmentHelp + "<em>");
                }, 3000);
                $(thisDroppableArea).find('button').hide();
            }
        } else {
            $(thisDroppableArea).find('p').html("<em>" + formBattachmentHelp + "<em>");
            $(thisDroppableArea).find('button').hide();
        }

    });

    $(".droppableArea").on("dragover", function (event) {
        event.preventDefault();
        event.stopPropagation();
        $(this).addClass('droppable');
    });

    $(".droppableArea").on("dragleave", function (event) {
        event.preventDefault();
        event.stopPropagation();
        $(this).removeClass('droppable');
    });

    $('body').on('click', '.upload', function (e) {
        e.preventDefault();

        var thisButton = $(this);
        var thisSection = $(thisButton).closest('.uploadSectionItem');
        var thisDataSection = $(thisSection).data('section');
        var thisInput = $(thisButton).closest('.input-group').find('input[type="file"]');
        var postUrl = $('form').attr('action');


        var formData = new FormData();
        formData.append('action', 'uploadDocument');
        formData.append('file', $(thisInput)[0].files[0]);
        formData.append('date', $('#documentDate').val());

        $(thisButton).hide();
        $(thisInput).parents('.droppableArea').find('p').prepend(' <i class="fas fa-circle-notch fa-spin"></i> ');
        //$(thisButton).closest('.input-group').append(' <div class="loader" style="margin-top: 11px;margin-left: 10px;position: absolute;"><i class="fas fa-circle-notch fa-spin"></i></div> ');

        $.ajax({
            url: postUrl,
            data: formData,
            type: 'POST',
            contentType: false,
            processData: false
        })
            .fail(function (response) {
                console.log(response);
                alert(formBerrorFile);
                $(thisButton).show();
                $('div.loader').remove();

            })
            .done(function (response) {
                window.location.reload();
            });
    });


    $('#messages tbody tr').each(function () {
        var thisTr = $(this);
        var messageBody = $(thisTr).find('.messageBody').html();


        $(thisTr).find('.shortMessageBody').html(truncate(messageBody, 120));

        //messageBody = messageBody.split("<body")[1].split(">").slice(1).join(">").split("</body>")[0];
        //$('.shortMessageBody').html(messageBody);
        //$(thisTr).find('.messageBody').text(messageBody);
    });

    var messagesTable = $('#messages').DataTable({
        searching: false,
        ordering: false,
        paging: true,
        info: false,
        destroy: true,
        "lengthChange": false,
        "pageLength": 5,
        "language": {
            "emptyTable": "Ni vnosov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
            }
    });

    $('#messages tbody').on('click', 'tr', function () {
        showMessageModal($(this));
    });


    $('#last15PublishedNews tbody').on('click', 'tr', function () {
        showMessageModal($(this), $(this).find('.newsTitle').text());
    });

    $('#last15PublishedNews').DataTable({
        autoWidth: false,
        searching: false,
        ordering: false,
        paging: true,
        info: false,
        destroy: true,
        "lengthChange": false,
        "pageLength": 5,
        "language": {
            "emptyTable": "Ni vnosov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        }
    });


    if ($('#noticeText').val() !== '') {
        var usersModal = $('#usersModal');
        var modalBody = $('#noticeText').val();
        var modalTitle = $('#noticeTitle').val();

        $(usersModal).find('.modal-title').text(modalTitle);
        $(usersModal).find('.modal-body').html(modalBody);
        bootstrap.Modal.getOrCreateInstance(usersModal[0]).show();
        $(usersModal).find('.modal-title').text(modalTitle);
    }
}

function truncate(string, chars) {
    if (string.length > chars)
        return string.substring(0, chars) + '...';
    else
        return string;
};

function showMessageModalUserNotice(modalBody, title) {
    var usersModal = $('#usersModal');
    var modalTitle = (typeof title == 'undefined' || title == null) ? 'Sporočilo' : title;

    $(usersModal).find('.modal-title').text(modalTitle);
    $(usersModal).find('.modal-body').html(modalBody);
    bootstrap.Modal.getOrCreateInstance(usersModal[0]).show();
}

function showMessageModal(messageRow, title) {
    var usersModal = $('#usersModal');
    var modalBody = $(messageRow).find('.messageBody').html();
    var modalTitle = (typeof title == 'undefined' || title == null) ? 'Sporočilo' : title;

    $(usersModal).find('.modal-title').text(modalTitle);
    $(usersModal).find('.modal-body').html(modalBody);

    // dostopnost (N-18): po zaprtju vrni fokus na sprožilni gumb (okno se odpre programsko, ne prek data-bs-toggle)
    var trigger = document.activeElement;
    $(usersModal).one('hidden.bs.modal', function () {
        if (trigger && trigger !== document.body && document.contains(trigger)) {
            trigger.focus();
        }
    });
    bootstrap.Modal.getOrCreateInstance(usersModal[0]).show();
}

function userNewsList() {
    $('#newsList tbody').on('click', 'tr', function () {
        showMessageModal($(this), $(this).find('.newsTitle').text());
    });
}


function dashboardAdmin() {
    $.fn.dataTableExt.oStdClasses.sPageButton = "btn btn-sm btn-secondary";
    $.fn.dataTableExt.oStdClasses.sPageButtonActive = "active";


    var options = {
        useEasing: true,
        useGrouping: true,
        separator: '.',
        decimal: ',',
        prefix: '',
        suffix: ''
    };
    //var demo = new CountUp("myTargetElement", 3243, 0, 0, 2.5, options);

    // TODO: Add jquery.animatenumber plugin
    // TEMPORARILY DISABLED - Missing jquery.animatenumber plugin
    // $('.dashboardCounterCountVal').each(function () {
    //     var thisNum = parseInt($(this).text());
    //
    //     // it adds only 'number' and 'numberStep' params
    //     $(this).animateNumber({
    //         number: thisNum,
    //         easing: 'easeInQuad', // require jquery.easing
    //     }, 2222);
    // });
    //
    // $('.panel .badge .userCount').each(function () {
    //     var thisNum = parseInt($(this).text());
    //
    //     // it adds only 'number' and 'numberStep' params
    //     $(this).animateNumber({
    //         number: thisNum,
    //         easing: 'easeInQuad', // require jquery.easing
    //     }, 1111);
    // });

    $('.panel-heading a').click(function () {
        $('.panel-heading').removeClass('active');
        $(this).parents('.panel-heading').addClass('active');

        setTimeout(function () {
            if ($('.panel-collapse.in').length == 0) {
                $('.panel-heading').removeClass('active');
            }
        }, 500);

    });


    $('#nominiraniKandidati').DataTable({
        searching: false,
        ordering: false,
        paging: false,
        info: false,
        dom: 'Bfrtip',
        buttons: [
            'copyHtml5',
            'excelHtml5',
            'csvHtml5',
            'pdfHtml5'
        ]
    });

    ///////////////
    // ZAPISNIKI //
    ///////////////
    var z = $('#zapisnik').DataTable({
        searching: false,
        ordering: true,
        "order": [[0, "desc"]],
        info: false,
        "lengthChange": false,
        "pageLength": 10,
        "language": {
            "emptyTable": "Ni podatkov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        },
        "processing": true,
        "serverSide": true,
        "ajax": {
            "url": "/admin/dashboard/fillRecordFiles",
            "data": function (reqParam) {
                reqParam.search = $('#searchRecordFiles').val();
                return reqParam;
            }
        },

        "columns": [
            {
                "data": "name",
                "render": function (data, type, row, meta) {
                    return '<td class="nameCell">' +
                        '<div class="viewMode"><span class="filename">' + data + '</span><button type="button" class="a11y-icon-btn renameFile" style="padding: 0px 10px;"><i class="fas fa-pencil" aria-hidden="true"></i><span class="visually-hidden">Preimenuj datoteko</span></button></div>' +
                        '<div class="editMode d-none"><input type="text" class="form-control form-control-sm" value="' + data + '"/><button type="button" class="a11y-icon-btn saveRenamedFile" style="padding: 0px 10px;"><i class="fas fa-save" aria-hidden="true"></i><span class="visually-hidden">Shrani novo ime datoteke</span></button><button type="button" class="a11y-icon-btn cancelRenamingFile" style="padding: 0px 10px;"><i class="fas fa-times" aria-hidden="true"></i><span class="visually-hidden">Prekliči preimenovanje</span></button></div>' +
                        '</td>';
                }
            },
            {
                "data": "date",
                "render": function (data, type, row, meta) {
                    return (type === 'sort' || type === 'order') ? data : '<td data-order="' + data + '"  data-sort="' + data + '">' +
                      '<div>' + moment.unix(data/1000).format('DD. MM. YYYY') + '</div>' +
                      '</td>';
                }
            },
            {
                "data": "plain",
                "render": function (data, type, row, meta) {
                    return '<td><button type="button" class="a11y-icon-btn deleteItem" data-toggle="tooltip" title="" data-original-title="Odstrani datoteko" style="margin-left : 25%"><i class="fas fa-trash" aria-hidden="true"></i><span class="visually-hidden">Odstrani datoteko</span></button></td>';
                }
            }
        ]
    });

    z.on('click', '.deleteItem', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        $(this).parents('tr').removeClass('selected');
        deleteFiles($(this), 'deleterecordFiles', z);
    });
    z.on('click', '.renameFile', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        $(this).parents('tr').find('.viewMode').addClass('d-none');
        $(this).parents('tr').find('.editMode').removeClass('d-none');
    });
    z.on('click', '.cancelRenamingFile', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        $(this).parents('tr').find('.viewMode').removeClass('d-none');
        $(this).parents('tr').find('.editMode').addClass('d-none');
        $(this).parents('tr').find('editMode input').val($(this).parents('tr').find('.viewMode .filename').text());
    });
    z.on('click', '.editMode input', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
    });
    z.on('click', '.saveRenamedFile', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        var newName = $(this).parents('tr').find('.editMode input').val();
        var itemId = z.row($(this).parents('tr')).data().id;

        $(this).parents('.nameCell').find('.viewMode').removeClass('d-none');
        $(this).parents('.nameCell').find('.editMode').addClass('d-none');

        renameFile(itemId, newName, z, "/admin/dashboard");
    });
    $('tr.filtersRecordFiles').on('change', 'input', function () {

        var thisInput = $(this).closest(':input');
        var thisIndex = $(thisInput).index() - 1;
        var thisValue = $(thisInput).val();
        z.draw();
        setTimeout(stickyFooter, 500);
    });
    z.on('click', 'tbody tr', function () {
        if ($(this).hasClass('selected')) {
            var thisRow = $(this);
            var fileId = z.row(thisRow).data().file;
            var win = window.open("/admin/file/" + fileId, '_blank');
            win.focus();
        } else {
            z.$('tr.selected').removeClass('selected');
            $(this).addClass('selected');
        }
    });

    ///////////////////
    // PODPISNA MAPA //
    ///////////////////

    var s = $('#podpisnaMapa').DataTable({
        searching: false,
        ordering: true,
        "order": [[ 1, "desc" ]],
        info: false,
        "lengthChange": false,
        "pageLength": 20,
        "language": {
            "emptyTable": "Ni podatkov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        },
        "processing": true,
        "serverSide": true,
        "ajax": {
            "url": "/admin/dashboard/fillSignatureFolderFiles",
            "data": function (reqParam) {
                reqParam.search = $('#searchSignatureFolderFiles').val();
                return reqParam;
            }
        },

        "columns": [
            {
                "data": "name",
                "render": function (data, type, row, meta) {
                        return '<td class="nameCell">' +
                          '<div class="viewMode"><span class="filename">' + data + '</span><button type="button" class="a11y-icon-btn renameFile" style="padding: 0px 10px;"><i class="fas fa-pencil" aria-hidden="true"></i><span class="visually-hidden">Preimenuj datoteko</span></button></div>' +
                          '<div class="editMode d-none"><input type="text" class="form-control form-control-sm" value="' + data + '"/><button type="button" class="a11y-icon-btn saveRenamedFile" style="padding: 0px 10px;"><i class="fas fa-save" aria-hidden="true"></i><span class="visually-hidden">Shrani novo ime datoteke</span></button><button type="button" class="a11y-icon-btn cancelRenamingFile" style="padding: 0px 10px;"><i class="fas fa-times" aria-hidden="true"></i><span class="visually-hidden">Prekliči preimenovanje</span></button></div>' +
                          '</td>';
                }
            },
            {
                "data": "date",
                "render": function (data, type, row, meta) {
                    return (type === 'sort' || type === 'order') ? data : '<td data-order="' + data + '"  data-sort="' + data + '">' +
                        '<div>' + moment.unix(data/1000).format('DD. MM. YYYY') + '</div>' +
                        '</td>';
                }
            },
            {
                "data": {hasSignature: "hasSignature"},
                "render": function (data, type, row, meta) {
                    if (data.hasSignature) {
                        return '<td><i class="fas fa-2x fa-check-circle" style="color:#198754;margin-left : 50%" aria-hidden="true"></i><span class="visually-hidden">Podpisano</span></td>';
                    } else {
                        return '<td></td>';
                    }

                }
            },
            {
                "data": {hasSignature: "hasSignature"},
                "render": function (data, type, row, meta) {
                    if (data.hasSignature) {
                        return '<td></td>';
                    } else {
                        return '<td><button type="button" class="a11y-icon-btn signature" data-toggle="tooltip" title="" data-original-title="Podpiši z Rekono" style="padding: 10px 20px; cursor: pointer"><i class="fas fa-pen-to-square" aria-hidden="true"></i><span class="visually-hidden">Podpiši z Rekono</span></button></td>';
                    }
                }
            },
            {
                "data": {hasSignature: "hasSignature"},
                "render": function (data, type, row, meta) {
                    if (!data.hasSignature) {
                        return '<td></td>';
                    } else {
                        return '<td><button type="button" class="a11y-icon-btn moveItem" data-toggle="tooltip" title="" data-original-title="Premakni v Zapisnike" style="margin-left : 50%"><i class="fas fa-arrow-down" aria-hidden="true"></i><span class="visually-hidden">Premakni v Zapisnike</span></button></td>';
                    }
                }
            },
            {
                "data": "plain",
                "render": function (data, type, row, meta) {
                    return '<td><button type="button" class="a11y-icon-btn deleteItem" data-toggle="tooltip" title="" data-original-title="Odstrani datoteko" style="margin-left : 50%"><i class="fas fa-trash" aria-hidden="true"></i><span class="visually-hidden">Odstrani datoteko</span></button></td>';
                }
            }
        ]
    });

    $('tr.filtersSignatureFolderFiles').on('change', 'input', function () {

        var thisInput = $(this).closest(':input');
        var thisIndex = $(thisInput).index() - 1;
        var thisValue = $(thisInput).val();
        s.draw();
        setTimeout(stickyFooter, 500);
    });
    s.on('click', '.signature', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        $('.showLoadingOverlay').css('height', '100%').fadeIn();
        $(this).parents('tr').removeClass('selected');
        signFile($(this), 'signsignatureFolderFiles', s);
    });
    s.on('click', '.deleteItem', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        $(this).parents('tr').removeClass('selected');
        deleteFiles($(this), 'deletesignatureFolderFiles', s);
    });
    s.on('click', '.renameFile', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        $(this).parents('tr').find('.viewMode').addClass('d-none');
        $(this).parents('tr').find('.editMode').removeClass('d-none');
    });
    s.on('click', '.cancelRenamingFile', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        $(this).parents('tr').find('.viewMode').removeClass('d-none');
        $(this).parents('tr').find('.editMode').addClass('d-none');
        $(this).parents('tr').find('editMode input').val($(this).parents('tr').find('.viewMode .filename').text());
    });
    s.on('click', '.editMode input', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
    });
    s.on('click', '.saveRenamedFile', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        var newName = $(this).parents('tr').find('.editMode input').val();
        var itemId = s.row($(this).parents('tr')).data().id;

        $(this).parents('.nameCell').find('.viewMode').removeClass('d-none');
        $(this).parents('.nameCell').find('.editMode').addClass('d-none');

        renameFile(itemId, newName, s, "/admin/dashboard");
    });
    s.on('click', '.moveItem', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        $(this).parents('tr').removeClass('selected');

        moveFiles($(this), 'movesignatureFolderFiles', s, z);
    });
    s.on('click', 'tbody tr', function () {
        if ($(this).hasClass('selected')) {
            var thisRow = $(this);
            var fileId = s.row(thisRow).data().file;
            var win = window.open("/admin/file/" + fileId, '_blank');
            win.focus();
        } else {
            s.$('tr.selected').removeClass('selected');
            $(this).addClass('selected');
        }
    });

    /////////////////////////
    // NAROČILA POSTOPKOV //
    ////////////////////////

    var t = $('#narocilaPostopkov').DataTable({
        searching: false,
        ordering: true,
        "order": [[ 0, "desc" ]],
        info: false,
        "lengthChange": false,
        "pageLength": 10,
        "language": {
            "emptyTable": "Ni podatkov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        },
        "processing": true,
        "serverSide": true,
        "ajax": {
            "url": "/admin/dashboard/fillProcessOrder",
            "data": function (reqParam) {
                reqParam.search = $('#searchProcessOrder').val();
                return reqParam;
            }
        },

        "columns": [
            {
                "data": "name",
                "render": function (data, type, row, meta) {
                    return '<td class="nameCell">' +
                        '<div class="viewMode"><span class="filename">' + data + '</span><button type="button" class="a11y-icon-btn renameFile" style="padding: 0px 10px;"><i class="fas fa-pencil" aria-hidden="true"></i><span class="visually-hidden">Preimenuj datoteko</span></button></div>' +
                        '<div class="editMode d-none"><input type="text" class="form-control form-control-sm" value="' + data + '"/><button type="button" class="a11y-icon-btn saveRenamedFile" style="padding: 0px 10px;"><i class="fas fa-save" aria-hidden="true"></i><span class="visually-hidden">Shrani novo ime datoteke</span></button><button type="button" class="a11y-icon-btn cancelRenamingFile" style="padding: 0px 10px;"><i class="fas fa-times" aria-hidden="true"></i><span class="visually-hidden">Prekliči preimenovanje</span></button></div>' +
                        '</td>';
                }
            },
            {
                "data": "date",
                "render": function (data, type, row, meta) {
                    return (type === 'sort' || type === 'order') ? data : '<td data-order="' + data + '"  data-sort="' + data + '">' +
                        '<div>' + moment.unix(data/1000).format('DD. MM. YYYY') + '</div>' +
                        '</td>';
                }
            },
            {
                "data": "plain",
                "render": function (data, type, row, meta) {
                    return '<td><button type="button" class="a11y-icon-btn moveProcessOrderToCurrentMatter" data-toggle="tooltip" title="" data-original-title="Premakni v Aktualno" style="margin-left : 50%"><i class="fas fa-arrow-down" aria-hidden="true"></i><span class="visually-hidden">Premakni v Aktualno</span></button></td>';
                }
            },
            {
                "data": "plain",
                "render": function (data, type, row, meta) {
                    return '<td><button type="button" class="a11y-icon-btn moveProcessOrderToInvitation" data-toggle="tooltip" title="" data-original-title="Premakni v Vabilo in gradivo za sejo" style="margin-left : 50%"><i class="fas fa-arrow-down" aria-hidden="true"></i><span class="visually-hidden">Premakni v Vabilo in gradivo za sejo</span></button></td>';
                }
            },
            {
                "data": "plain",
                "render": function (data, type, row, meta) {
                    return '<td><button type="button" class="a11y-icon-btn deleteItem" data-toggle="tooltip" title="" data-original-title="Odstrani datoteko" style="margin-left : 25%"><i class="fas fa-trash" aria-hidden="true"></i><span class="visually-hidden">Odstrani datoteko</span></button></td>';
                }
            }
        ]
    });
    t.on('click', '.moveProcessOrderToCurrentMatter', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        $(this).parents('tr').removeClass('selected');

        moveFiles($(this), 'copyProcessOrderToCurrentMatter', t, w);
    });
    t.on('click', '.moveProcessOrderToInvitation', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        $(this).parents('tr').removeClass('selected');

        moveFiles($(this), 'copyProcessOrderToInvitation', t, i);
    });
    $('tr.filtersProcessOrder').on('change', 'input', function () {

        var thisInput = $(this).closest(':input');
        var thisIndex = $(thisInput).index() - 1;
        var thisValue = $(thisInput).val();
        t.draw();
        setTimeout(stickyFooter, 500);
    });
    t.on('click', '.renameFile', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();


        $(this).parents('tr').find('.viewMode').addClass('d-none');
        $(this).parents('tr').find('.editMode').removeClass('d-none');
    });
    t.on('click', '.cancelRenamingFile', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        $(this).parents('tr').find('.viewMode').removeClass('d-none');
        $(this).parents('tr').find('.editMode').addClass('d-none');
        $(this).parents('tr').find('editMode input').val($(this).parents('tr').find('.viewMode .filename').text());
    });
    t.on('click', '.editMode input', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
    });
    t.on('click', '.saveRenamedFile', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        var newName = $(this).parents('tr').find('.editMode input').val();
        var itemId = t.row($(this).parents('tr')).data().id;

        $(this).parents('.nameCell').find('.viewMode').removeClass('d-none');
        $(this).parents('.nameCell').find('.editMode').addClass('d-none');

        renameFile(itemId, newName, t, "/admin/dashboard");
    });
    t.on('click', '.deleteItem', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        $(this).parents('tr').removeClass('selected');

        deleteFiles($(this), 'deleteprocessOrder', t);
    });
    t.on('click', 'tbody tr', function () {
        if ($(this).hasClass('selected')) {
            var thisRow = $(this);
            var fileId = t.row(thisRow).data().file;
            var win = window.open("/admin/file/" + fileId, '_blank');
            win.focus();
        } else {
            t.$('tr.selected').removeClass('selected');
            $(this).addClass('selected');
        }
    });


    ///////////////////
    //   AKTUALNO    //
    ///////////////////

    var w = $('#aktualno').DataTable({
        searching: false,
        ordering: true,
        "order": [[ 0, "asc" ]],
        info: false,
        "lengthChange": false,
        "pageLength": 10,
        "language": {
            "emptyTable": "Ni podatkov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        },
        "processing": true,
        "serverSide": true,
        "ajax": {
            "url": "/admin/dashboard/fillCurrentMatter",
            "data": function (reqParam) {
                reqParam.search = $('#searchCurrentMatter').val();
                return reqParam;
            }
        },

        "columns": [
            {
                "data": "name",
                "render": function (data, type, row, meta) {
                    return '<td class="nameCell">' +
                        '<div class="viewMode"><span class="filename">' + data + '</span><button type="button" class="a11y-icon-btn renameFile" style="padding: 0px 10px;"><i class="fas fa-pencil" aria-hidden="true"></i><span class="visually-hidden">Preimenuj datoteko</span></button></div>' +
                        '<div class="editMode d-none"><input type="text" class="form-control form-control-sm" value="' + data + '"/><button type="button" class="a11y-icon-btn saveRenamedFile" style="padding: 0px 10px;"><i class="fas fa-save" aria-hidden="true"></i><span class="visually-hidden">Shrani novo ime datoteke</span></button><button type="button" class="a11y-icon-btn cancelRenamingFile" style="padding: 0px 10px;"><i class="fas fa-times" aria-hidden="true"></i><span class="visually-hidden">Prekliči preimenovanje</span></button></div>' +
                        '</td>';
                }
            },
            {
                "data": "date",
                "render": function (data, type, row, meta) {
                    return (type === 'sort' || type === 'order') ? data : '<td data-order="' + data + '"  data-sort="' + data + '">' +
                        '<div>' + moment.unix(data/1000).format('DD. MM. YYYY') + '</div>' +
                        '</td>';
                }
            },
            {
                "data": "plain",
                "render": function (data, type, row, meta) {
                    return '<td><button type="button" class="a11y-icon-btn deleteItem" data-toggle="tooltip" title="" data-original-title="Odstrani datoteko" style="margin-left : 25%"><i class="fas fa-trash" aria-hidden="true"></i><span class="visually-hidden">Odstrani datoteko</span></button></td>';
                }
            }
        ]
    });
    $('tr.filtersCurrentMatter').on('change', 'input', function () {

        var thisInput = $(this).closest(':input');
        var thisIndex = $(thisInput).index() - 1;
        var thisValue = $(thisInput).val();
        w.draw();
        setTimeout(stickyFooter, 500);
    });
    w.on('click', '.renameFile', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        $(this).parents('tr').find('.viewMode').addClass('d-none');
        $(this).parents('tr').find('.editMode').removeClass('d-none');
    });
    w.on('click', '.cancelRenamingFile', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        $(this).parents('tr').find('.viewMode').removeClass('d-none');
        $(this).parents('tr').find('.editMode').addClass('d-none');
        $(this).parents('tr').find('editMode input').val($(this).parents('tr').find('.viewMode .filename').text());
    });
    w.on('click', '.editMode input', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
    });
    w.on('click', '.saveRenamedFile', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        var newName = $(this).parents('tr').find('.editMode input').val();
        var itemId = w.row($(this).parents('tr')).data().id;

        $(this).parents('.nameCell').find('.viewMode').removeClass('d-none');
        $(this).parents('.nameCell').find('.editMode').addClass('d-none');

        renameFile(itemId, newName, w, "/admin/dashboard");
    });
    w.on('click', '.deleteItem', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        $(this).parents('tr').removeClass('selected');

        deleteFiles($(this), 'deletecurrentMatter', w);
    });
    w.on('click', 'tbody tr', function () {
        if ($(this).hasClass('selected')) {
            var thisRow = $(this);
            var fileId = w.row(thisRow).data().file;
            var win = window.open("/admin/file/" + fileId, '_blank');
            win.focus();
        } else {
            w.$('tr.selected').removeClass('selected');
            $(this).addClass('selected');
        }
    });

    ///////////////////
    //   KNJIŽNICA   //
    ///////////////////
    var q = $('#knjiznica').DataTable({
        searching: false,
        ordering: true,
        "order": [[ 0, "asc" ]],
        info: false,
        "lengthChange": false,
        "pageLength": 10,
        "language": {
            "emptyTable": "Ni podatkov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        },
        "processing": true,
        "serverSide": true,
        "ajax": {
            "url": "/admin/dashboard/fillLibrary",
            "data": function (reqParam) {
                reqParam.search = $('#searchLibrary').val();
                return reqParam;
            }
        },

        "columns": [
            {
                "data": "name",
                "render": function (data, type, row, meta) {
                    return '<td class="nameCell">' +
                        '<div class="viewMode"><span class="filename">' + data + '</span><button type="button" class="a11y-icon-btn renameFile" style="padding: 0px 10px;"><i class="fas fa-pencil" aria-hidden="true"></i><span class="visually-hidden">Preimenuj datoteko</span></button></div>' +
                        '<div class="editMode d-none"><input type="text" class="form-control form-control-sm" value="' + data + '"/><button type="button" class="a11y-icon-btn saveRenamedFile" style="padding: 0px 10px;"><i class="fas fa-save" aria-hidden="true"></i><span class="visually-hidden">Shrani novo ime datoteke</span></button><button type="button" class="a11y-icon-btn cancelRenamingFile" style="padding: 0px 10px;"><i class="fas fa-times" aria-hidden="true"></i><span class="visually-hidden">Prekliči preimenovanje</span></button></div>' +
                        '</td>';
                }
            },
            {
                "data": "date",
                "render": function (data, type, row, meta) {
                    return (type === 'sort' || type === 'order') ? data : '<td data-order="' + data + '"  data-sort="' + data + '">' +
                        '<div>' + moment.unix(data/1000).format('DD. MM. YYYY') + '</div>' +
                        '</td>';
                }
            },
            {
                "data": "plain",
                "render": function (data, type, row, meta) {
                    return '<td><button type="button" class="a11y-icon-btn moveLibraryToCurrentMatter" data-toggle="tooltip" title="" data-original-title="Premakni v Aktualno" style="margin-left : 50%"><i class="fas fa-arrow-down" aria-hidden="true"></i><span class="visually-hidden">Premakni v Aktualno</span></button></td>';
                }
            },
            {
                "data": "plain",
                "render": function (data, type, row, meta) {
                    return '<td><button type="button" class="a11y-icon-btn moveLibraryToInvitation" data-toggle="tooltip" title="" data-original-title="Premakni v Vabilo in gradivo za sejo" style="margin-left : 50%"><i class="fas fa-arrow-down" aria-hidden="true"></i><span class="visually-hidden">Premakni v Vabilo in gradivo za sejo</span></button></td>';
                }
            },
            {
                "data": "plain",
                "render": function (data, type, row, meta) {
                    return '<td><button type="button" class="a11y-icon-btn deleteItem" data-toggle="tooltip" title="" data-original-title="Odstrani datoteko" style="margin-left : 25%"><i class="fas fa-trash" aria-hidden="true"></i><span class="visually-hidden">Odstrani datoteko</span></button></td>';
                }
            }
        ]
    });
    q.on('click', '.moveLibraryToCurrentMatter', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        $(this).parents('tr').removeClass('selected');

        moveFiles($(this), 'copyLibraryToCurrentMatter', q, w);
    });
    q.on('click', '.moveLibraryToInvitation', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        $(this).parents('tr').removeClass('selected');

        moveFiles($(this), 'copyLibraryToInvitation', q, i);
    });
    $('tr.filtersCompetenceProfiles').on('change', 'input', function () {

        var thisInput = $(this).closest(':input');
        var thisIndex = $(thisInput).index() - 1;
        var thisValue = $(thisInput).val();
        q.draw();
        setTimeout(stickyFooter, 500);
    });
    q.on('click', '.renameFile', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        $(this).parents('tr').find('.viewMode').addClass('d-none');
        $(this).parents('tr').find('.editMode').removeClass('d-none');
    });
    q.on('click', '.cancelRenamingFile', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        $(this).parents('tr').find('.viewMode').removeClass('d-none');
        $(this).parents('tr').find('.editMode').addClass('d-none');
        $(this).parents('tr').find('editMode input').val($(this).parents('tr').find('.viewMode .filename').text());
    });
    q.on('click', '.editMode input', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
    });
    q.on('click', '.saveRenamedFile', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        var newName = $(this).parents('tr').find('.editMode input').val();
        var itemId = q.row($(this).parents('tr')).data().id;

        $(this).parents('.nameCell').find('.viewMode').removeClass('d-none');
        $(this).parents('.nameCell').find('.editMode').addClass('d-none');

        renameFile(itemId, newName, q, "/admin/dashboard");
    });
    $('tr.filtersLibrary').on('change', 'input', function () {

        var thisInput = $(this).closest(':input');
        var thisIndex = $(thisInput).index() - 1;
        var thisValue = $(thisInput).val();
        q.draw();
        setTimeout(stickyFooter, 500);

    });
    $('tr.filtersInvitation').on('change', 'input', function () {

        var thisInput = $(this).closest(':input');
        var thisIndex = $(thisInput).index() - 1;
        var thisValue = $(thisInput).val();
        q.draw();
        setTimeout(stickyFooter, 500);
    });
    q.on('click', 'tbody tr', function () {
        if ($(this).hasClass('selected')) {
            var thisRow = $(this);
            var fileId = q.row(thisRow).data().file;
            var win = window.open("/admin/file/" + fileId, '_blank');
            win.focus();
        } else {
            q.$('tr.selected').removeClass('selected');
            $(this).addClass('selected');
        }
    });
    q.on('click', '.deleteItem', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        $(this).parents('tr').removeClass('selected');

        deleteFiles($(this), 'deletelibrary', q);
    });

    ///////////////////
    //     VABILO    //
    ///////////////////
    var i = $('#vabilo').DataTable({
        searching: false,
        ordering: true,
        "order": [[0, "asc"]],
        info: false,
        "lengthChange": false,
        "pageLength": 20,
        "language": {
            "emptyTable": "Ni podatkov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        },
        "processing": true,
        "serverSide": true,
        "ajax": {
            "url": "/admin/dashboard/fillInvitationFiles",
            "data": function (reqParam) {
                reqParam.search = $('#searchInvitation').val();
                return reqParam;
            }
        },

        "columns": [
            {
                "data": "name",
                "render": function (data, type, row, meta) {
                    return '<td class="nameCell">' +
                        '<div class="viewMode"><span class="filename">' + data + '</span><button type="button" class="a11y-icon-btn renameFile" style="padding: 0px 10px;"><i class="fas fa-pencil" aria-hidden="true"></i><span class="visually-hidden">Preimenuj datoteko</span></button></div>' +
                        '<div class="editMode d-none"><input type="text" class="form-control form-control-sm" value="' + data + '"/><button type="button" class="a11y-icon-btn saveRenamedFile" style="padding: 0px 10px;"><i class="fas fa-save" aria-hidden="true"></i><span class="visually-hidden">Shrani novo ime datoteke</span></button><button type="button" class="a11y-icon-btn cancelRenamingFile" style="padding: 0px 10px;"><i class="fas fa-times" aria-hidden="true"></i><span class="visually-hidden">Prekliči preimenovanje</span></button></div>' +
                        '</td>';
                }
            },
            {
                "data": "date",
                "render": function (data, type, row, meta) {
                    return (type === 'sort' || type === 'order') ? data : '<td data-order="' + data + '"  data-sort="' + data + '">' +
                        '<div>' + moment.unix(data / 1000).format('DD. MM. YYYY') + '</div>' +
                        '</td>';
                }
            },
            {
                "data": "plain",
                "render": function (data, type, row, meta) {
                    return '<td><button type="button" class="a11y-icon-btn deleteItem" data-toggle="tooltip" title="" data-original-title="Odstrani datoteko" style="margin-left : 25%"><i class="fas fa-trash" aria-hidden="true"></i><span class="visually-hidden">Odstrani datoteko</span></button></td>';
                }
            }
        ]
    });
    $('tr.filtersInvitation').on('change', 'input', function () {

        var thisInput = $(this).closest(':input');
        var thisIndex = $(thisInput).index() - 1;
        var thisValue = $(thisInput).val();
        i.draw();
        setTimeout(stickyFooter, 500);
    });
    i.on('click', '.renameFile', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        $(this).parents('tr').find('.viewMode').addClass('d-none');
        $(this).parents('tr').find('.editMode').removeClass('d-none');
    });
    i.on('click', '.cancelRenamingFile', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        $(this).parents('tr').find('.viewMode').removeClass('d-none');
        $(this).parents('tr').find('.editMode').addClass('d-none');
        $(this).parents('tr').find('editMode input').val($(this).parents('tr').find('.viewMode .filename').text());
    });
    i.on('click', '.editMode input', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
    });
    i.on('click', '.saveRenamedFile', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        var newName = $(this).parents('tr').find('.editMode input').val();
        var itemId = i.row($(this).parents('tr')).data().id;

        $(this).parents('.nameCell').find('.viewMode').removeClass('d-none');
        $(this).parents('.nameCell').find('.editMode').addClass('d-none');

        renameFile(itemId, newName, i, "/admin/dashboard");
    });
    i.on('click', '.deleteItem', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        $(this).parents('tr').removeClass('selected');

        deleteFiles($(this), 'deleteinvitationFiles', i);
    });
    i.on('click', 'tbody tr', function () {
        if ($(this).hasClass('selected')) {
            var thisRow = $(this);
            var fileId = i.row(thisRow).data().file;
            var win = window.open("/admin/file/" + fileId, '_blank');
            win.focus();
        } else {
            i.$('tr.selected').removeClass('selected');
            $(this).addClass('selected');
        }
    });

    //////////////////////////
    // KOMPETENČNI PROFILI //
    /////////////////////////
    var c = $('#kompetencniProfili').DataTable({
        searching: false,
        ordering: true,
        "order": [[0, "asc"]],
        info: false,
        "lengthChange": false,
        "pageLength": 10,
        "language": {
            "emptyTable": "Ni podatkov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        },
        "processing": true,
        "serverSide": true,
        "ajax": {
            "url": "/admin/dashboard/fillCompetenceProfiles",
            "data": function (reqParam) {
                reqParam.search = $('#searchCompetenceProfiles').val();
                return reqParam;
            }
        },

        "columns": [
            {
                "data": "name",
                "render": function (data, type, row, meta) {
                    return '<td class="nameCell">' +
                        '<div class="viewMode"><span class="filename">' + data + '</span><button type="button" class="a11y-icon-btn renameFile" style="padding: 0px 10px;"><i class="fas fa-pencil" aria-hidden="true"></i><span class="visually-hidden">Preimenuj datoteko</span></button></div>' +
                        '<div class="editMode d-none"><input type="text" class="form-control form-control-sm" value="' + data + '"/><button type="button" class="a11y-icon-btn saveRenamedFile" style="padding: 0px 10px;"><i class="fas fa-save" aria-hidden="true"></i><span class="visually-hidden">Shrani novo ime datoteke</span></button><button type="button" class="a11y-icon-btn cancelRenamingFile" style="padding: 0px 10px;"><i class="fas fa-times" aria-hidden="true"></i><span class="visually-hidden">Prekliči preimenovanje</span></button></div>' +
                        '</td>';
                }
            },
            {
                "data": "date",
                "render": function (data, type, row, meta) {
                    return (type === 'sort' || type === 'order') ? data : '<td data-order="' + data + '"  data-sort="' + data + '">' +
                        '<div>' + moment.unix(data / 1000).format('DD. MM. YYYY') + '</div>' +
                        '</td>';
                }
            },
            {
                "data": "plain",
                "render": function (data, type, row, meta) {
                    return '<td><button type="button" class="a11y-icon-btn moveCompetenceProfileToInvitation" data-toggle="tooltip" title="" data-original-title="Premakni v Vabilo in gradivo za sejo" style="margin-left : 50%"><i class="fas fa-arrow-down" aria-hidden="true"></i><span class="visually-hidden">Premakni v Vabilo in gradivo za sejo</span></button></td>';
                }
            },
            {
                "data": "plain",
                "render": function (data, type, row, meta) {
                    return '<td><button type="button" class="a11y-icon-btn deleteItem" data-toggle="tooltip" title="" data-original-title="Odstrani datoteko" style="margin-left : 25%"><i class="fas fa-trash" aria-hidden="true"></i><span class="visually-hidden">Odstrani datoteko</span></button></td>';
                }
            }
        ]
    });
    c.on('click', '.moveCompetenceProfileToInvitation', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        $(this).parents('tr').removeClass('selected');

        moveFiles($(this), 'copyCompetenceProfilesToInvitation', c, i);
    });
    $('tr.filtersCompetenceProfiles').on('change', 'input', function () {

        var thisInput = $(this).closest(':input');
        var thisIndex = $(thisInput).index() - 1;
        var thisValue = $(thisInput).val();
        c.draw();
        setTimeout(stickyFooter, 500);
    });
    c.on('click', '.renameFile', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        $(this).parents('tr').find('.viewMode').addClass('d-none');
        $(this).parents('tr').find('.editMode').removeClass('d-none');
    });
    c.on('click', '.cancelRenamingFile', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        $(this).parents('tr').find('.viewMode').removeClass('d-none');
        $(this).parents('tr').find('.editMode').addClass('d-none');
        $(this).parents('tr').find('editMode input').val($(this).parents('tr').find('.viewMode .filename').text());
    });
    c.on('click', '.editMode input', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
    });
    c.on('click', '.saveRenamedFile', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        var newName = $(this).parents('tr').find('.editMode input').val();
        var itemId = c.row($(this).parents('tr')).data().id;

        $(this).parents('.nameCell').find('.viewMode').removeClass('d-none');
        $(this).parents('.nameCell').find('.editMode').addClass('d-none');

        renameFile(itemId, newName, c, "/admin/dashboard");
    });
    c.on('click', '.deleteItem', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        $(this).parents('tr').removeClass('selected');

        deleteFiles($(this), 'deletecompetenceProfilesFiles', c);
    });
    c.on('click', 'tbody tr', function () {
        if ($(this).hasClass('selected')) {
            var thisRow = $(this);
            var fileId = c.row(thisRow).data().file;
            var win = window.open("/admin/file/" + fileId, '_blank');
            win.focus();
        } else {
            c.$('tr.selected').removeClass('selected');
            $(this).addClass('selected');
        }
    });


    $('.overlayAction').on('click', function (e) {
        e.preventDefault();

        var thisOverlay = $(this);
        var thisDashboardCounter = $(thisOverlay).closest('.dashboardCounter');
        var userFilterId = $(thisDashboardCounter).data('user-filter');
        var title = $(thisDashboardCounter).find('.dashboardCounterHeader').text();

        if (userFilterId == null) {
            if (thisDashboardCounter.hasClass("poslovodstvo")) {
                window.location.href = "/admin/nominations/OV";
            }
            else {
                window.location.href = "/admin/nominations/ON";
            }

        } else {

            showUsersModal(userFilterId, title, "admin");
        }

    });


    $(".select2-select").each(function () {
        if ($(this).data("max")) {
            $(this).select2({
                max_selected_options: $(this).data("max"),
                disable_search: true,
                width: "100%",
                allowClear: true,
                placeholder: $(this).attr("placeholder") || ""
            });
        } else {
            $(this).select2({
                disable_search: false,
                width: "100%",
                search_contains: true,
                placeholder_text_single: $(this).attr("placeholder"),
                no_results_text: "Ni zadetkov",
                allowClear: true,
                placeholder: $(this).attr("placeholder") || ""
            });
        }

    });


// add item
    $('body').on('click', '.addItem', function (e) {
        var dynamicEditItem = $(this).closest('.dynamicEditItem');

        $(dynamicEditItem).find('.actionTitle').text((dynamicEditItem).find('.actionTitle').data('new'));

        // $(dynamicEditItem).find('.form-group.has-error').removeClass('has-error');
        $(dynamicEditItem).find('.form-group .help-block.has-error').remove();

        $(dynamicEditItem).find('.addItemActionContainer').addClass('d-none');
        $(dynamicEditItem).find('.saveItemActionContainer').removeClass('d-none');
        $(dynamicEditItem).find('.addOrEditItem').removeClass('d-none');

        if ($(dynamicEditItem).find('input:hidden').val() == "") {
            $(dynamicEditItem).find(':input').val('');
        }

        setTimeout(stickyFooter, 200);
    });


// cancel
    $('body').on('click', '.cancelItem', function (e) {
        var dynamicEditItem = $(this).closest('.dynamicEditItem');


        Swal.fire({
            title: formBcancelInsert,
            text: formBchangeNotSaved,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#DD6B55",
            confirmButtonText: formBconfirm,
            cancelButtonText: formBcancel,
        }).then((result) => {
            if (result.isConfirmed) {

            closeDynamicItem($(dynamicEditItem));


            }
        });


    });


// add attachment
    $('body').on('change', 'input[type="file"]', function () {
        var thisInput = $(this);
        var thisDroppableArea = $(thisInput).closest('.droppableArea');
        $(thisDroppableArea).removeClass('droppable');

        if (this.files.length == 1) {
            var correctFileType = false;
            if ((this.id == 'currentMatter' || this.id == 'invitation')
                && (this.files[0].type == 'application/pdf'
                    || this.files[0].type == 'application/x-pdf'
                    || this.files[0].type == 'application/msword'
                    || this.files[0].type == 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                    || this.files[0].type == 'application/vnd.ms-excel'
                    || this.files[0].type == 'application/msexcel'
                    || this.files[0].type == 'application/xls'
                    || this.files[0].type == 'application/vnd.oasis.opendocument.spreadsheet'
                    || this.files[0].type == 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')) {
                correctFileType = true;
            } else {
                if (this.files[0].type == 'application/pdf' || this.files[0].type == 'application/x-pdf') {
                    correctFileType = true;
                }
            }

            if (!correctFileType) {
                if (this.id == 'currentMatter' || this.id == 'invitation') {
                    $(thisDroppableArea).find('p').html("<em class='text-danger' style='font-size: 18px;'><i class='fas fa-exclamation-triangle'></i>" + formatBcurrentMatterWrongFormat + "</em>");
                } else {
                    $(thisDroppableArea).find('p').html("<em class='text-danger' style='font-size: 18px;'><i class='fas fa-exclamation-triangle'></i>" + formBwrongFormat + "</em>");
                }
                setTimeout(function () {
                    $(thisInput).val('');
                    $(thisDroppableArea).find('p').html("<em>" + formBattachmentHelp + "<em>");
                }, 3000);
                $(thisDroppableArea).find('button').hide();
                return;
            }

            if (this.files[0].size <= (20 * 1024 * 1024) && correctFileType) {
                $(thisDroppableArea).find('p').html("<em>" + formBchosenFile + ":</em> <b>" + this.files[0].name + "</b>.");
                $(thisDroppableArea).find('button').fadeIn();
            } else {
                $(thisDroppableArea).find('p').html("<em class='text-danger' style='font-size: 18px;'><i class='fas fa-exclamation-triangle'></i>" + formBsizeError + "</em>");
                setTimeout(function () {
                    $(thisInput).val('');
                    $(thisDroppableArea).find('p').html("<em>" + formBattachmentHelp + "<em>");
                }, 3000);
                $(thisDroppableArea).find('button').hide();
            }
        } else {
            $(thisDroppableArea).find('p').html("<em>" + formBattachmentHelp + "<em>");
            $(thisDroppableArea).find('button').hide();
        }

    });

    $(".droppableArea").on("dragover", function (event) {
        event.preventDefault();
        event.stopPropagation();
        $(this).addClass('droppable');
    });

    $(".droppableArea").on("dragleave", function (event) {
        event.preventDefault();
        event.stopPropagation();
        $(this).removeClass('droppable');
    });


    $('body').on('click', '.upload', function (e) {
        e.preventDefault();
        var thisButton = $(this);
        var thisSection = $(thisButton).parents('.uploadSectionItem');
        var thisDataSection = $(thisSection).data('section');
        var thisInput = $(thisButton).parents('.input-group').find('input[type="file"]');
        var postUrl = "/admin/dashboard";
        var action = $(thisButton).data("section");


        var formData = new FormData();
        formData.append('action', 'upload' + action);
        formData.append('file', $(thisInput)[0].files[0]);

        $(thisButton).hide();
        $(thisInput).parents('.droppableArea').find('p').prepend(' <i class="fas fa-circle-notch fa-spin"></i> ');
        //$(thisButton).closest('.input-group').append(' <div class="loader" style="margin-top: 11px;margin-left: 10px;position: absolute;"><i class="fas fa-circle-notch fa-spin"></i></div> ');

        $.ajax({
            url: postUrl,
            data: formData,
            type: 'POST',
            contentType: false,
            processData: false
        })
            .fail(function (response) {
                alert(formBerrorFile);
                $(thisButton).show();
                $('div.loader').remove();

                if ($(thisSection).find('table').is('#zapisnik')) {
                    $('#recordFiles').val('').trigger('change');
                }
                if ($(thisSection).find('table').is('#aktualno')) {
                    $('#currentMatter').val('').trigger('change');
                }
                if ($(thisSection).find('table').is('#narocilaPostopkov')) {
                    $('#processOrder').val('').trigger('change');
                }
                if ($(thisSection).find('table').is('#podpisnaMapa')) {
                    $('#signatureFolderFiles').val('').trigger('change');
                }
                if ($(thisSection).find('table').is('#knjiznica')) {
                    $('#libraryFiles').val('').trigger('change');
                }
                if ($(thisSection).find('table').is('#vabilo')) {
                    $('#invitation').val('').trigger('change');
                }
                if ($(thisSection).find('table').is('#kompetencniProfili')) {
                    $('#competenceProfiles').val('').trigger('change');
                }
            })
            .done(function (response) {
                var dtInstance = null;

                if ($(thisSection).find('table').is('#zapisnik')) {
                    $('#recordFiles').val('').trigger('change');
                    z.row.add($('<tr data-file-id="' + response.file.id + '" data-id="' + response.id + '"><td>' + response.name + '</td><td>' + response.date + '</td><td class="text-center"><button type="button" class="a11y-icon-btn deleteItem" data-toggle="tooltip" title="" data-placement="right" data-original-title="Odstrani datoteko"><i class="fas fa-trash" aria-hidden="true"></i><span class="visually-hidden">Odstrani datoteko</span></button></td></tr>')[0]).draw();
                }
                if ($(thisSection).find('table').is('#aktualno')) {
                    $('#currentMatter').val('').trigger('change');
                    w.row.add($('<tr data-file-id="' + response.file.id + '" data-id="' + response.id + '"><td>' + response.name + '</td><td>' + response.date + '</td><td class="text-center"><button type="button" class="a11y-icon-btn deleteItem" data-toggle="tooltip" title="" data-placement="right" data-original-title="Odstrani datoteko"><i class="fas fa-trash" aria-hidden="true"></i><span class="visually-hidden">Odstrani datoteko</span></button></td></tr>')[0]).draw();
                }
                if ($(thisSection).find('table').is('#narocilaPostopkov')) {
                    $('#processOrder').val('').trigger('change');
                    t.row.add($('<tr data-file-id="' + response.file.id + '" data-id="' + response.id + '"><td>' + response.name + '</td><td>' + response.date + '</td><td><button type="button" class="a11y-icon-btn moveProcessOrderToCurrentMatter" data-toggle="tooltip" title="" data-original-title="Premakni v Aktualno" style="margin-left : 50%"><i class="fas fa-arrow-down" aria-hidden="true"></i><span class="visually-hidden">Premakni v Aktualno</span></button></td><td><button type="button" class="a11y-icon-btn moveProcessOrderToInvitation" data-toggle="tooltip" title="" data-original-title="Premakni v Vabilo in gradivo za sejo" style="margin-left : 50%"><i class="fas fa-arrow-down" aria-hidden="true"></i><span class="visually-hidden">Premakni v Vabilo in gradivo za sejo</span></button></td><td class="text-center"><button type="button" class="a11y-icon-btn deleteItem" data-toggle="tooltip" title="" data-placement="right" data-original-title="Odstrani datoteko"><i class="fas fa-trash" aria-hidden="true"></i><span class="visually-hidden">Odstrani datoteko</span></button></td></tr>')[0]).draw();
                }
                if ($(thisSection).find('table').is('#knjiznica')) {
                    $('#library').val('').trigger('change');
                    q.row.add($('<tr data-file-id="' + response.file.id + '" data-id="' + response.id + '"><td>' + response.name + '</td><td>' + response.date + '</td><td><button type="button" class="a11y-icon-btn moveLibraryToCurrentMatter" data-toggle="tooltip" title="" data-original-title="Premakni v Aktualno" style="margin-left : 50%"><i class="fas fa-arrow-down" aria-hidden="true"></i><span class="visually-hidden">Premakni v Aktualno</span></button></td><td><button type="button" class="a11y-icon-btn moveLibraryToInvitation" data-toggle="tooltip" title="" data-original-title="Premakni v Vabilo in gradivo za sejo" style="margin-left : 50%"><i class="fas fa-arrow-down" aria-hidden="true"></i><span class="visually-hidden">Premakni v Vabilo in gradivo za sejo</span></button></td><td class="text-center"><button type="button" class="a11y-icon-btn deleteItem" data-toggle="tooltip" title="" data-placement="right" data-original-title="Odstrani datoteko"><i class="fas fa-trash" aria-hidden="true"></i><span class="visually-hidden">Odstrani datoteko</span></button></td></tr>')[0]).draw();
                }
                if ($(thisSection).find('table').is('#vabilo')) {
                    $('#invitation').val('').trigger('change');
                    i.row.add($('<tr data-file-id="' + response.file.id + '" data-id="' + response.id + '"><td>' + response.name + '</td><td>' + response.date + '</td><td class="text-center"><button type="button" class="a11y-icon-btn deleteItem" data-toggle="tooltip" title="" data-placement="right" data-original-title="Odstrani datoteko"><i class="fas fa-trash" aria-hidden="true"></i><span class="visually-hidden">Odstrani datoteko</span></button></td></tr>')[0]).draw();
                }
                if ($(thisSection).find('table').is('#kompetencniProfili')) {
                    $('#competenceProfiles').val('').trigger('change');
                    c.row.add($('<tr data-file-id="' + response.file.id + '" data-id="' + response.id + '"><td>' + response.name + '</td><td>' + response.date + '</td><td><button type="button" class="a11y-icon-btn moveCompetenceProfileToInvitation" data-toggle="tooltip" title="" data-original-title="Premakni v Vabilo in gradivo za sejo" style="margin-left : 50%"><i class="fas fa-arrow-down" aria-hidden="true"></i><span class="visually-hidden">Premakni v Vabilo in gradivo za sejo</span></button></td><td class="text-center"><button type="button" class="a11y-icon-btn deleteItem" data-toggle="tooltip" title="" data-placement="right" data-original-title="Odstrani datoteko"><i class="fas fa-trash" aria-hidden="true"></i><span class="visually-hidden">Odstrani datoteko</span></button></td></tr>')[0]).draw();
                }

                if ($(thisSection).find('table').is('#podpisnaMapa')) {
                    $('#signatureFolderFiles').val('').trigger('change');
                    s.row.add($('<tr data-file-id="' + response.file.id + '" data-id="' + response.id + '"><td>' + response.name + '</td><td>' + response.date + '</td><td></td><td><button type="button" class="a11y-icon-btn signature" data-toggle="tooltip" title="" data-original-title="Podpiši z Rekono" style="padding: 10px 20px; cursor: pointer"><i class="fas fa-pen-to-square" aria-hidden="true"></i><span class="visually-hidden">Podpiši z Rekono</span></button></td><td></td><td class="text-center"><button type="button" class="a11y-icon-btn deleteItem" data-toggle="tooltip" title="" data-placement="right" data-original-title="Odstrani datoteko"><i class="fas fa-trash" aria-hidden="true"></i><span class="visually-hidden">Odstrani datoteko</span></button></td></tr>')[0]).draw();
                } else {
                }
            });
    });

}

function renameFile(id, newName, instance, url) {
    var dynamicFormFields = {};
    dynamicFormFields["action"] = "renameFile";
    dynamicFormFields["id"] = id;
    dynamicFormFields["newName"] = newName;

    $.ajax({
        method: "POST",
        url: url,
        data: dynamicFormFields

    }).fail(function (response) {
        console.log(response);

    }).done(function (response) {
        instance.draw();
    });
}

function signFile(thisButton, action, dtInstance) {

    var thisRow = $(thisButton).parents('tr');
    var itemId = dtInstance.row(thisRow).data().id;

    var dynamicFormFields = {};
    dynamicFormFields["action"] = action;
    dynamicFormFields["id"] = itemId;

    $.ajax({
        method: "POST",
        data: dynamicFormFields

    }).fail(function (response) {
        console.log(response);
        $('.showLoadingOverlay').css('height', '100%').fadeOut();
        alert("Storitev podpisovanja trenutno ni na voljo");

    }).done(function (response) {
        var dynamicFormFields = {};
        window.location.href = response;

    });

}

function deleteFiles(thisDeleteButton, action, dtInstance) {

    var thisRow = $(thisDeleteButton).parents('tr');
    var itemId = dtInstance.row(thisRow).data().id;

    Swal.fire({
        title: formBdeleteQ,
        text: formBdeleteText,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#DD6B55",
        confirmButtonText: formBdelete2,
        cancelButtonText: formBcancel,
    }).then((result) => {
            if (result.isConfirmed) {

        var dynamicFormFields = {};
        dynamicFormFields["action"] = action;
        dynamicFormFields["id"] = itemId;

        $.ajax({
            method: "POST",
            data: dynamicFormFields

        }).fail(function (response) {
            console.log(response);
            alert(formBdeleteError);

        }).done(function () {
            $(thisRow).fadeOut('fast', function () {
                dtInstance.draw();
            });
        });
            }
    });
}

function moveFiles(thisButton, action, dtInstance, dtMovedInstance) {

    var thisRow = $(thisButton).parents('tr');
    var itemId = dtInstance.row(thisRow).data().id;

    var dynamicFormFields = {};
    dynamicFormFields["action"] = action;
    dynamicFormFields["id"] = itemId;

    $.ajax({
        method: "POST",
        data: dynamicFormFields

    }).fail(function (response) {
        console.log(response);
        alert(formBdeleteError);

    }).done(function () {
        $(thisRow).fadeOut('fast', function () {
            dtInstance.draw();
            dtMovedInstance.draw();
        });
    });
}

function dashboard(role) {

    $.fn.dataTableExt.oStdClasses.sPageButton = "btn btn-sm btn-secondary";
    $.fn.dataTableExt.oStdClasses.sPageButtonActive = "active";


    var options = {
        useEasing: true,
        useGrouping: true,
        separator: '.',
        decimal: ',',
        prefix: '',
        suffix: ''
    };
    //var demo = new CountUp("myTargetElement", 3243, 0, 0, 2.5, options);

    // Animate counter numbers using jQuery's built-in animate()
    $('.dashboardCounterCountVal').each(function () {
        var $this = $(this);
        var targetNum = parseInt($this.text());

        // Skip animation if value is NaN or invalid
        if (isNaN(targetNum) || targetNum === null || targetNum === undefined) {
            console.warn('Skipping counter animation - invalid value:', $this.text());
            $this.text('0'); // Fallback to 0
            return;
        }

        $this.prop('Counter', 0).animate({
            Counter: targetNum
        }, {
            duration: 2222,
            easing: 'swing',
            step: function (now) {
                $this.text(Math.ceil(now));
            }
        });
    });

    $('.panel .badge .userCount').each(function () {
        var $this = $(this);
        var targetNum = parseInt($this.text());

        // Skip animation if value is NaN or invalid
        if (isNaN(targetNum) || targetNum === null || targetNum === undefined) {
            console.warn('Skipping userCount animation - invalid value:', $this.text());
            $this.text('0'); // Fallback to 0
            return;
        }

        $this.prop('Counter', 0).animate({
            Counter: targetNum
        }, {
            duration: 1111,
            easing: 'swing',
            step: function (now) {
                $this.text(Math.ceil(now));
            }
        });
    });

    $('.panel-heading a').click(function () {
        $('.panel-heading').removeClass('active');
        $(this).parents('.panel-heading').addClass('active');

        setTimeout(function () {
            if ($('.panel-collapse.in').length == 0) {
                $('.panel-heading').removeClass('active');
            }
        }, 500);

    });


    $('#nominiraniKandidati').DataTable({
        searching: false,
        ordering: false,
        paging: false,
        info: false,
        dom: 'Bfrtip',
        buttons: [
            'copyHtml5',
            'excelHtml5',
            'csvHtml5',
            'pdfHtml5'
        ]
    });

    var s = $('#podpisnaMapa').DataTable({
        searching: false,
        ordering: true,
        "order": [[ 1, "desc" ]],
        info: false,
        "lengthChange": false,
        "pageLength": 20,
        "language": {
            "emptyTable": "Ni podatkov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        },
        "processing": true,
        "serverSide": true,
        "ajax": {
            "url": "/" + role + "/dashboard/fillSignatureFolderFiles",
            "data": function (reqParam) {
                reqParam.search = $('#searchSignatureFolderFiles').val();
                return reqParam;
            }
        },

        "columns": [
            {"data": "name"},
            {
                "data": "date",
                "render": function (data, type, row, meta) {
                    return (type === 'sort' || type === 'order') ? data : '<td data-order="' + data + '"  data-sort="' + data + '">' +
                        '<div>' + moment.unix(data/1000).format('DD. MM. YYYY') + '</div>' +
                        '</td>';
                }
            },
            {
                "data": {hasSignature: "hasSignature"},
                "render": function (data, type, row, meta) {
                    if (data.hasSignature) {
                        return '<td><i class="fas fa-2x fa-check-circle" style="color:#198754;margin-left : 50%" aria-hidden="true"></i><span class="visually-hidden">Podpisano</span></td>';
                    } else {
                        return '<td></td>';
                    }

                }
            },
            {
                "data": {hasSignature: "hasSignature"},
                "render": function (data, type, row, meta) {
                    if (data.hasSignature) {
                        return '<td></td>';
                    }
                    if (role != "commission") {
                        return '<td></td>';
                    } else {
                        return '<td><button type="button" class="a11y-icon-btn signature" data-toggle="tooltip" title="" data-original-title="Podpiši z Rekono" style="padding: 10px 20px; cursor: pointer"><i class="fas fa-pen-to-square" aria-hidden="true"></i><span class="visually-hidden">Podpiši z Rekono</span></button></td>';
                    }
                }
            }
]
    });

    var z = $('#zapisnik').DataTable({
        searching: false,
        ordering: true,
        "order": [[0, "desc"]],
        info: false,
        "lengthChange": false,
        "pageLength": 10,
        "language": {
            "emptyTable": "Ni podatkov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        },
        "processing": true,
        "serverSide": true,
        "ajax": {
            "url": "/" + role + "/dashboard/fillRecordFiles",
            "data": function (reqParam) {
                reqParam.search = $('#searchRecordFiles').val();
                return reqParam;
            }
        },

        "columns": [
            {"data": "name"},
            {
                "data": "date",
                "render": function (data, type, row, meta) {
                    return (type === 'sort' || type === 'order') ? data : '<td data-order="' + data + '"  data-sort="' + data + '">' +
                        '<div>' + moment.unix(data/1000).format('DD. MM. YYYY') + '</div>' +
                        '</td>';
                }
            }
        ]
    });

    var t = $('#narocilaPostopkov').DataTable({
        searching: false,
        ordering: true,
        "order": [[ 0, "desc" ]],
        info: false,
        "lengthChange": false,
        "pageLength": 10,
        "language": {
            "emptyTable": "Ni podatkov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        },
        "processing": true,
        "serverSide": true,
        "ajax": {
            "url": "/" + role + "/dashboard/fillProcessOrder",
            "data": function (reqParam) {
                reqParam.search = $('#searchProcessOrder').val();
                return reqParam;
            }
        },

        "columns": [
            {"data": "name"},
            {
                "data": "date",
                "render": function (data, type, row, meta) {
                    return (type === 'sort' || type === 'order') ? data : '<td data-order="' + data + '"  data-sort="' + data + '">' +
                        '<div>' + moment.unix(data/1000).format('DD. MM. YYYY') + '</div>' +
                        '</td>';
                }
            }
        ]
    });
    var w = $('#aktualno').DataTable({
        searching: false,
        ordering: true,
        "order": [[ 0, "asc" ]],
        info: false,
        "lengthChange": false,
        "pageLength": 10,
        "language": {
            "emptyTable": "Ni podatkov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        },
        "processing": true,
        "serverSide": true,
        "ajax": {
            "url": "/" + role + "/dashboard/fillCurrentMatter",
            "data": function (reqParam) {
                reqParam.search = $('#searchCurrentMatter').val();
                return reqParam;
            }
        },

        "columns": [
            {"data": "name"},
            {
                "data": "date",
                "render": function (data, type, row, meta) {
                    return (type === 'sort' || type === 'order') ? data : '<td data-order="' + data + '"  data-sort="' + data + '">' +
                        '<div>' + moment.unix(data/1000).format('DD. MM. YYYY') + '</div>' +
                        '</td>';
                }
            }
        ]
    });

    var q = $('#knjiznica').DataTable({
        searching: false,
        ordering: true,
        "order": [[ 0, "asc" ]],
        info: false,
        "lengthChange": false,
        "pageLength": 10,
        "language": {
            "emptyTable": "Ni podatkov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        },
        "processing": true,
        "serverSide": true,
        "ajax": {
            "url": "/" + role + "/dashboard/fillLibrary",
            "data": function (reqParam) {
                reqParam.search = $('#searchLibrary').val();
                return reqParam;
            }
        },

        "columns": [
            {"data": "name"},
            {
                "data": "date",
                "render": function (data, type, row, meta) {
                    return (type === 'sort' || type === 'order') ? data : '<td data-order="' + data + '"  data-sort="' + data + '">' +
                        '<div>' + moment.unix(data/1000).format('DD. MM. YYYY') + '</div>' +
                        '</td>';
                }
            }
        ]
    });

    // Apply the search
    $('tr.filtersProcessOrder').on('change', 'input', function () {

        var thisInput = $(this).closest(':input');
        var thisIndex = $(thisInput).index() - 1;
        var thisValue = $(thisInput).val();
        t.draw();
        setTimeout(stickyFooter, 500);

    });

    // Apply the search
    $('tr.filtersLibrary').on('change', 'input', function () {

        var thisInput = $(this).closest(':input');
        var thisIndex = $(thisInput).index() - 1;
        var thisValue = $(thisInput).val();
        q.draw();
        setTimeout(stickyFooter, 500);

    });

    $('tr.filtersRecordFiles').on('change', 'input', function () {

        var thisInput = $(this).closest(':input');
        var thisIndex = $(thisInput).index() - 1;
        var thisValue = $(thisInput).val();
        z.draw();
        setTimeout(stickyFooter, 500);

    });

    $('tr.filtersSignatureFolderFiles').on('change', 'input', function () {

        var thisInput = $(this).closest(':input');
        var thisIndex = $(thisInput).index() - 1;
        var thisValue = $(thisInput).val();
        s.draw();
        setTimeout(stickyFooter, 500);

    });
    $('tr.filtersCurrentMatter').on('change', 'input', function () {

        var thisInput = $(this).closest(':input');
        var thisIndex = $(thisInput).index() - 1;
        var thisValue = $(thisInput).val();
        w.draw();
        setTimeout(stickyFooter, 500);

    });

    z.on('click', 'tbody tr', function () {
        if ($(this).hasClass('selected')) {
            var thisRow = $(this);
            var fileId = z.row(thisRow).data().file;
            var win = window.open("/" + role + "/file/" + fileId, '_blank');
            win.focus();
        } else {
            z.$('tr.selected').removeClass('selected');
            $(this).addClass('selected');
        }
    });

    s.on('click', 'tbody tr', function () {
        if ($(this).hasClass('selected')) {
            var thisRow = $(this);
            var fileId = s.row(thisRow).data().file;
            var win = window.open("/" + role + "/file/" + fileId, '_blank');
            win.focus();
        } else {
            s.$('tr.selected').removeClass('selected');
            $(this).addClass('selected');
        }
    });

    s.on('click', '.signature', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        $('.showLoadingOverlay').css('height', '100%').fadeIn();
        $(this).parents('tr').removeClass('selected');
        signFile($(this), 'signsignatureFolderFiles', s);
    });


    t.on('click', 'tbody tr', function () {
        if ($(this).hasClass('selected')) {
            var thisRow = $(this);
            var fileId = t.row(thisRow).data().file;
            var win = window.open("/" + role + "/file/" + fileId, '_blank');
            win.focus();
        } else {
            t.$('tr.selected').removeClass('selected');
            $(this).addClass('selected');
        }
    });


    w.on('click', 'tbody tr', function () {
        if ($(this).hasClass('selected')) {
            var thisRow = $(this);
            var fileId = w.row(thisRow).data().file;
            var win = window.open("/" + role + "/file/" + fileId, '_blank');
            win.focus();
        } else {
            w.$('tr.selected').removeClass('selected');
            $(this).addClass('selected');
        }
    });

    q.on('click', 'tbody tr', function () {
        if ($(this).hasClass('selected')) {
            var thisRow = $(this);
            var fileId = q.row(thisRow).data().file;
            var win = window.open("/" + role + "/file/" + fileId, '_blank');
            win.focus();
        } else {
            q.$('tr.selected').removeClass('selected');
            $(this).addClass('selected');
        }
    });

    var i = $('#vabilo').DataTable({
        searching: false,
        ordering: true,
        "order": [[0, "asc"]],
        info: false,
        "lengthChange": false,
        "pageLength": 20,
        "language": {
            "emptyTable": "Ni podatkov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        },
        "processing": true,
        "serverSide": true,
        "ajax": {
            "url": "/" + role + "/dashboard/fillInvitationFiles",
            "data": function (reqParam) {
                reqParam.search = $('#searchInvitation').val();
                return reqParam;
            }
        },

        "columns": [
            {"data": "name"},
            {
                "data": "date",
                "render": function (data, type, row, meta) {
                    return (type === 'sort' || type === 'order') ? data : '<td data-order="' + data + '"  data-sort="' + data + '">' +
                        '<div>' + moment.unix(data/1000).format('DD. MM. YYYY') + '</div>' +
                        '</td>';
                }
            }
        ]
    });
    $('tr.filtersInvitationFiles').on('change', 'input', function () {

        var thisInput = $(this).closest(':input');
        var thisIndex = $(thisInput).index() - 1;
        var thisValue = $(thisInput).val();
        i.draw();
        setTimeout(stickyFooter, 500);
    });
    i.on('click', 'tbody tr', function () {
        if ($(this).hasClass('selected')) {
            var thisRow = $(this);
            var fileId = i.row(thisRow).data().file;
            var win = window.open("/" + role + "/file/" + fileId, '_blank');
            win.focus();
        } else {
            i.$('tr.selected').removeClass('selected');
            $(this).addClass('selected');
        }
    });


    var c = $('#kompetencniProfili').DataTable({
        searching: false,
        ordering: true,
        "order": [[0, "asc"]],
        info: false,
        "lengthChange": false,
        "pageLength": 10,
        "language": {
            "emptyTable": "Ni podatkov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        },
        "processing": true,
        "serverSide": true,
        "ajax": {
            "url": "/" + role + "/dashboard/fillCompetenceProfiles",
            "data": function (reqParam) {
                reqParam.search = $('#searchCompetenceProfiles').val();
                return reqParam;
            }
        },

        "columns": [
            {"data": "name"},
            {
                "data": "date",
                "render": function (data, type, row, meta) {
                    return (type === 'sort' || type === 'order') ? data : '<td data-order="' + data + '"  data-sort="' + data + '">' +
                        '<div>' + moment.unix(data/1000).format('DD. MM. YYYY') + '</div>' +
                        '</td>';
                }
            }
        ]
    });
    $('tr.filtersCompetenceProfiles').on('change', 'input', function () {

        var thisInput = $(this).closest(':input');
        var thisIndex = $(thisInput).index() - 1;
        var thisValue = $(thisInput).val();
        c.draw();
        setTimeout(stickyFooter, 500);
    });
    c.on('click', 'tbody tr', function () {
        if ($(this).hasClass('selected')) {
            var thisRow = $(this);
            var fileId = c.row(thisRow).data().file;
            var win = window.open("/" + role + "/file/" + fileId, '_blank');
            win.focus();
        } else {
            c.$('tr.selected').removeClass('selected');
            $(this).addClass('selected');
        }
    });


    $('.overlayAction').on('click', function (e) {
        e.preventDefault();

        var thisOverlay = $(this);
        var thisDashboardCounter = $(thisOverlay).closest('.dashboardCounter');
        var userFilterId = $(thisDashboardCounter).data('user-filter');
        var title = $(thisDashboardCounter).find('.dashboardCounterHeader').text();

        if (userFilterId == null) {
            if (thisDashboardCounter.hasClass("poslovodstvo")) {
                window.location.href = "/" + role + "/nominations/OV";
            }
            else {
                window.location.href = "/" + role + "/nominations/ON";
            }

        } else {
            showUsersModal(userFilterId, title, role);
        }

    });

    $(".select2-select").each(function () {
        if ($(this).data("max")) {
            $(this).select2({
                max_selected_options: $(this).data("max"),
                disable_search: true,
                width: "100%",
                allowClear: true,
                placeholder: $(this).attr("placeholder") || ""
            });
        } else {
            $(this).select2({
                disable_search: false,
                width: "100%",
                search_contains: true,
                placeholder_text_single: $(this).attr("placeholder"),
                no_results_text: "Ni zadetkov",
                allowClear: true,
                placeholder: $(this).attr("placeholder") || ""
            });
        }
    });

    // add item
    $('body').on('click', '.addItem', function (e) {
        var dynamicEditItem = $(this).closest('.dynamicEditItem');

        $(dynamicEditItem).find('.actionTitle').text((dynamicEditItem).find('.actionTitle').data('new'));

        // $(dynamicEditItem).find('.form-group.has-error').removeClass('has-error');
        $(dynamicEditItem).find('.form-group .help-block.has-error').remove();

        $(dynamicEditItem).find('.addItemActionContainer').addClass('d-none');
        $(dynamicEditItem).find('.saveItemActionContainer').removeClass('d-none');
        $(dynamicEditItem).find('.addOrEditItem').removeClass('d-none');

        if ($(dynamicEditItem).find('input:hidden').val() == "") {
            $(dynamicEditItem).find(':input').val('');
        }

        setTimeout(stickyFooter, 200);
    });


// cancel
    $('body').on('click', '.cancelItem', function (e) {
        var dynamicEditItem = $(this).closest('.dynamicEditItem');

        Swal.fire({
            title: formBcancelInsert,
            text: formBchangeNotSaved,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#DD6B55",
            confirmButtonText: formBconfirm,
            cancelButtonText: formBcancel,
        }).then((result) => {
            if (result.isConfirmed) {
            closeDynamicItem($(dynamicEditItem));
            }
        });
    });


// add attachment
    $('body').on('change', 'input[type="file"]', function () {
        var thisInput = $(this);
        var thisDroppableArea = $(thisInput).closest('.droppableArea');
        $(thisDroppableArea).removeClass('droppable');

        if (this.files.length == 1) {

            var correctFileType = false;
            if (this.id == 'currentMatter'
                && (this.files[0].type == 'application/pdf'
                    || this.files[0].type == 'application/x-pdf'
                    || this.files[0].type == 'application/msword'
                    || this.files[0].type == 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                    || this.files[0].type == 'application/vnd.ms-excel'
                    || this.files[0].type == 'application/msexcel'
                    || this.files[0].type == 'application/xls'
                    || this.files[0].type == 'application/vnd.oasis.opendocument.spreadsheet'
                    || this.files[0].type == 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')) {
                correctFileType = true;
            }
            else {
                if (this.files[0].type == 'application/pdf' || this.files[0].type == 'application/x-pdf')
                {
                    correctFileType = true;
                }
            }

            if (!correctFileType) {
                if (this.id == 'currentMatter') {
                    $(thisDroppableArea).find('p').html("<em class='text-danger' style='font-size: 18px;'><i class='fas fa-exclamation-triangle'></i>" + formatBcurrentMatterWrongFormat + "</em>");
                }
                else {
                    $(thisDroppableArea).find('p').html("<em class='text-danger' style='font-size: 18px;'><i class='fas fa-exclamation-triangle'></i>" + formBwrongFormat + "</em>");
                }
                setTimeout(function () {
                    $(thisInput).val('');
                    $(thisDroppableArea).find('p').html("<em>" + formBattachmentHelp + "<em>");
                }, 3000);
                $(thisDroppableArea).find('button').hide();
                return;
            }

            if (this.files[0].size <= (20 * 1024 * 1024) && correctFileType) {
                $(thisDroppableArea).find('p').html("<em>" + formBchosenFile + ":</em> <b>" + this.files[0].name + "</b>.");
                $(thisDroppableArea).find('button').fadeIn();
            } else {
                $(thisDroppableArea).find('p').html("<em class='text-danger' style='font-size: 18px;'><i class='fas fa-exclamation-triangle'></i>" + formBsizeError + "</em>");
                setTimeout(function () {
                    $(thisInput).val('');
                    $(thisDroppableArea).find('p').html("<em>" + formBattachmentHelp + "<em>");
                }, 3000);
                $(thisDroppableArea).find('button').hide();
            }
        } else {
            $(thisDroppableArea).find('p').html("<em>" + formBattachmentHelp + "<em>");
            $(thisDroppableArea).find('button').hide();
        }

    });

    $(".droppableArea").on("dragover", function (event) {
        event.preventDefault();
        event.stopPropagation();
        $(this).addClass('droppable');
    });

    $(".droppableArea").on("dragleave", function (event) {
        event.preventDefault();
        event.stopPropagation();
        $(this).removeClass('droppable');
    });


    $('body').on('click', '.upload', function (e) {
        e.preventDefault();
        var thisButton = $(this);
        var thisSection = $(thisButton).parents('.uploadSectionItem');
        var thisDataSection = $(thisSection).data('section');
        var thisInput = $(thisButton).parents('.input-group').find('input[type="file"]');
        var postUrl = "/" + role + "/dashboard";
        var action = $(thisButton).data("section");


        var formData = new FormData();
        formData.append('action', 'upload' + action);
        formData.append('file', $(thisInput)[0].files[0]);

        $(thisButton).hide();
        $(thisInput).parents('.droppableArea').find('p').prepend(' <i class="fas fa-circle-notch fa-spin"></i> ');
        //$(thisButton).closest('.input-group').append(' <div class="loader" style="margin-top: 11px;margin-left: 10px;position: absolute;"><i class="fas fa-circle-notch fa-spin"></i></div> ');


        $.ajax({
            url: postUrl,
            data: formData,
            type: 'POST',
            contentType: false,
            processData: false
        })
            .fail(function (response) {
                alert(formBerrorFile);
                $(thisButton).show();
                $('div.loader').remove();

                if ($(thisSection).find('table').is('#aktualno')) {
                    $('#currentMatter').val('').trigger('change');
                }
                if ($(thisSection).find('table').is('#knjiznica')) {
                    $('#libraryFiles').val('').trigger('change');
                }
            })
            .done(function (response) {
                var dtInstance = null;

                if ($(thisSection).find('table').is('#aktualno')) {
                    $('#currentMatter').val('').trigger('change');
                    dtInstance = w;
                }
                if ($(thisSection).find('table').is('#knjiznica')) {
                    $('#library').val('').trigger('change');
                    dtInstance = q;
                }
                dtInstance.row.add($('<tr data-file-id="' + response.file.id + '" data-id="' + response.id + '"><td>' + response.name + '</td><td>' + response.date + '</td></tr>')[0]).draw();
            });
    });
}

function showUsersModal(userFilterId, title, userRole) {
    var usersModal = $('#usersModal');
    var modalBody = '';
    var modalTitle = title;
    var primaryBtnText = 'Na seznam kandidatov';

    window.location.href = "/" + userRole + "/users/" + userFilterId;
}


function showMotivationalLetterModalWithVersion(versionDate, content) {
    var modal = $('#motivacijskoPismoModal');
    var modalBody = content.replace(/\n/g, '<br/>');
    var modalTitle = "Motivacijsko pismo (" + versionDate + ")";

    $(modal).find('.modal-title').text("").text(modalTitle);
    $(modal).find('.modal-body').html("").html(modalBody);

    var modalInstance = new bootstrap.Modal(modal[0]);
    modalInstance.show();
}

function showMotivationalLetterModal(content, date) {
    var modal = $('#motivacijskoPismoModal');
    var modalBody = content.replace(/\n/g, '<br/>');
    var modalTitle = "Motivacijsko pismo - " + date;

    $(modal).find('.modal-title').text("").text(modalTitle);
    $(modal).find('.modal-body').html("").html(modalBody);

    var modalInstance = new bootstrap.Modal(modal[0]);
    modalInstance.show();
}


function loadEducations() {
    $.ajax({
        url: "/" + userRoleUrlPath + "/form-b/educations/" + userId,
        dataType: "json",
    }).done(function (response) {

        JsonData.education = response;

        if (JsonData.education == "" || JsonData.education == null || JsonData.education.length == 0) {
            $('.dynamicResultsContainer.education').html("<em>" + formBeducationNoData + "</em>")
        } else {
            fillJsTemplate('education', 'dynamicResultTemplateEducation', JsonData.education);
        }

        setTimeout(stickyFooter, 200);
    });
}

function loadEducationsNew(id) {
    $.ajax({
        url: "/admin/user/form-b/educations/" + id,
        dataType: "json",
    }).done(function (response) {

        JsonData.education = response;

        if (JsonData.education == "" || JsonData.education == null || JsonData.education.length == 0) {
            $('.dynamicResultsContainer.education').html("<em>" + formBeducationNoData + "</em>")
        } else {
            fillJsTemplate('educationCurrent', 'dynamicResultTemplateEducationCurrent', JsonData.education);
        }

        setTimeout(stickyFooter, 200);
    });
}

function loadLanguages() {
    $.ajax({
        url: "/" + userRoleUrlPath + "/form-b/languages/" + userId,
        dataType: "json",
    }).done(function (response) {

        JsonData.language = response;

        if (JsonData.language == "" || JsonData.language == null || JsonData.language.length == 0) {
            $('.dynamicResultsContainer.language').html("<em>" + formBlangNoData + "</em>")
        } else {
            fillJsTemplate('language', 'dynamicResultTemplateLanguage', JsonData.language);
        }

        setTimeout(stickyFooter, 200);
    });

    // show "drugo" field on language field
    $('body').on('change', '#language', function () {
        if ($('#language').find('option:selected').text()  == 'drugo' || $('#language').find('option:selected').text()  == 'Other') {
            $('.otherLanguageFieldContainer').removeClass('d-none');
        } else {
            $('.otherLanguageFieldContainer').not('.hidden').addClass('d-none');
            }
    });
}

function loadLanguagesNew(id) {
    $.ajax({
        url: "/admin/user/form-b/languages/" + id,
        dataType: "json",
    }).done(function (response) {

        JsonData.language = response;

        if (JsonData.language == "" || JsonData.language == null || JsonData.language.length == 0) {
            $('.dynamicResultsContainer.language').html("<em>" + formBlangNoData + "</em>")
        } else {
            fillJsTemplate('languageCurrent', 'dynamicResultTemplateLanguageCurrent', JsonData.language);
        }

        setTimeout(stickyFooter, 200);
    });

    // show "drugo" field on language field
    $('body').on('change', '#languageCurrent', function () {
        if ($(this).val() == 'drugo') {
            $('.otherLanguageFieldContainer').removeClass('d-none');
        } else {
            $('.otherLanguageFieldContainer').not('.hidden').addClass('d-none');
        }
    });
}


function loadCompanies() {
    $.ajax({
        url: "/" + userRoleUrlPath + "/form-b/membersbs/" + userId,
        dataType: "json",
    }).done(function (response) {

        JsonData.company = response;

        if (JsonData.company == "" || JsonData.company == null || JsonData.company.length == 0) {
            $('.dynamicResultsContainer.company').html("<em>" + formBcompNoData + "</em>")
        } else {
            var i;
            for (i = 0; i < JsonData.company.length; i++) {
                if (JsonData.company[i].previous == true) {
                    JsonData.company[i].previous = membershipSBIsPrevious
										JsonData.company[i].companyType = 'previous'
                } else {
										JsonData.company[i].companyType = 'current'
								}
            }
            fillJsTemplate('company', 'dynamicResultTemplateCompany', JsonData.company);

            $('.actions .previousItem').on('click', function () {
                var id = $(this).attr('id');
                $.ajax({
                    url: "/" + userRoleUrlPath + "/form-b/membersbs/moveToPrevious?id=" + id,
                    method: "POST",
                }).done(function () {
                    var dynamicEditItem = $(".dynamicSection.company").find('.dynamicEditItem');
                    var currentlyEditedItemId = $(dynamicEditItem).find('input:hidden').val();
                    if (currentlyEditedItemId != null && currentlyEditedItemId === id) {
                        closeDynamicItem($(dynamicEditItem));
                    }

                    loadCompanies();
                    loadPreviousCompanies();
                });
            });
        }

        setTimeout(stickyFooter, 200);
        checkHowManyCompanies();
    });

    if (userRoleUrlPath == 'user') {
        initDateRangePickers('positionStartDate', 'positionEndDate');
    }
}


function loadPreviousCompanies() {
    $.ajax({
        url: "/" + userRoleUrlPath + "/form-b/previousmembersbs/" + userId,
        dataType: "json",
    }).done(function (response) {

        JsonData.previousCompany = response;
        if (JsonData.previousCompany == "" || JsonData.previousCompany == null || JsonData.previousCompany.length == 0) {
            $('.dynamicResultsContainer.previousCompany').html("<em>" + formBcompNoData + "</em>")
        } else {
            fillJsTemplate('previousCompany', 'dynamicResultTemplatePreviousCompany', JsonData.previousCompany);
        }

        setTimeout(stickyFooter, 200);
    })
        .fail(function (response) {
            console.log("FAILOAL SEM PRI PREJŠNJIH ČLANSTVIH");
        });

    if (userRoleUrlPath === 'user') {
        initDateRangePickers('previousPositionStartDate', 'previousPositionEndDate');
    }


    // show "drugo" field on education field
    $('#educationField').change(function () {
        if ($(this).val() == 'Z - Drugo') {
            $('.otherEducationFieldContainer').removeClass('d-none');
        } else {
            $('.otherEducationFieldContainer').not('.hidden').addClass('d-none');
        }
    });

    // show "drugo" field on field of work
    $('#fieldOfWork').change(function () {
        var thisVal = $(this).val();

        if ($.inArray('Drugo', thisVal) > -1) {
            $('.otherFieldOfWorkContainer').removeClass('d-none');
        } else {
            $('.otherFieldOfWorkContainer').not('.hidden').addClass('d-none');
        }
    });
}

function loadManagementExperience() {
    $.ajax({
        url: "/" + userRoleUrlPath + "/form-b/managementExperience/" + userId,
        dataType: "json",
    }).done(function (response) {

        JsonData.managementExperience = response;
        if (JsonData.managementExperience == "" || JsonData.managementExperience == null || JsonData.managementExperience.length == 0) {
            $('.dynamicResultsContainer.managementExperience').html("<em>" + formBmanagementExperienceNoData + "</em>")
        } else {
            fillJsTemplate('managementExperience', 'dynamicResultTemplateManagementExperience', JsonData.managementExperience);
        }

        setTimeout(stickyFooter, 200);
    })
        .fail(function (response) {
        });

    if (userRoleUrlPath === 'user') {
        initDateRangePickers('functionFrom', 'functionTo');
    }

}

function loadPreviousEmployments() {
    // formB() teče tudi na predogledih in posnetkih, kjer je seznam izrisan na strežniku
    if ($('.dynamicResultsContainer.previousEmployment').length === 0) {
        return;
    }

    $.ajax({
        url: "/" + userRoleUrlPath + "/form-b/previousemployments/" + userId,
        dataType: "json",
    }).done(function (response) {

        JsonData.previousEmployment = response;
        if (JsonData.previousEmployment == "" || JsonData.previousEmployment == null || JsonData.previousEmployment.length == 0) {
            $('.dynamicResultsContainer.previousEmployment').html("<em>" + formBpreviousEmploymentNoData + "</em>")
        } else {
            fillJsTemplate('previousEmployment', 'dynamicResultTemplatePreviousEmployment', JsonData.previousEmployment);
        }

        checkHowManyPreviousEmployments();
        setTimeout(stickyFooter, 200);
    });

    if (userRoleUrlPath === 'user') {
        initDateRangePickers('previousEmployedFrom', 'previousEmployedTo');
    }
}

function loadCompaniesNew(id) {
    $.ajax({
        url: "/admin/user/form-b/membersbs/" + id,
        dataType: "json",
    }).done(function (response) {

        JsonData.companyCurrent = response;

        if (JsonData.companyCurrent == "" || JsonData.companyCurrent == null || JsonData.companyCurrent.length == 0) {
            $('.dynamicResultsContainer.companyCurrent').html("<em>" + formBcompNoData + "</em>")
        } else {
            fillJsTemplate('companyCurrent', 'dynamicResultTemplateCompanyCurrent', JsonData.companyCurrent);
        }

        setTimeout(stickyFooter, 200);
        checkHowManyCompanies();
    });

}

function loadPreviousCompaniesNew(id) {
    $.ajax({
        url: "/admin/user/form-b/previousmembersbs/" + id,
        dataType: "json",
    }).done(function (response) {
        JsonData.previousCompanyCurrent = response;
        if (JsonData.previousCompanyCurrent == "" || JsonData.previousCompanyCurrent == null || JsonData.previousCompanyCurrent.length == 0) {
            $('.dynamicResultsContainer.previousCompanyCurrent').html("<em>" + formBcompNoData + "</em>")
        } else {
            fillJsTemplate('previousCompanyCurrent', 'dynamicResultTemplatePreviousCompanyCurrent', JsonData.previousCompanyCurrent);
        }
        setTimeout(stickyFooter, 200);
    });
}

function loadManagementExperienceNew(id) {
    $.ajax({
        url: "/admin/user/form-b/managementExperience/" + id,
        dataType: "json",
    }).done(function (response) {
        JsonData.managementExperienceCurrent = response;
        if (JsonData.managementExperienceCurrent == "" || JsonData.managementExperienceCurrent == null || JsonData.managementExperienceCurrent.length == 0) {
            $('.dynamicResultsContainer.previousCompanyCurrent').html("<em>" + formBmanagementExperienceNoData + "</em>")
        } else {
            fillJsTemplate('managementExperienceCurrent', 'dynamicResultTemplateManagementExperienceCurrent', JsonData.managementExperienceCurrent);
        }
        setTimeout(stickyFooter, 200);
    });
}


function fillJsTemplate(dynamicSectionClass, templateId, response) {

    $('.dynamicResultsContainer.' + dynamicSectionClass).loadTemplate($('#' + templateId), response);

    var dynamicContainer = $('.dynamicResultsContainer.' + dynamicSectionClass);
    if (dynamicContainer.length > 0) {
        migrateLegacyBootstrapAttributes(dynamicContainer[0]);
        refreshBootstrapInteractions(dynamicContainer[0]);
        a11yLabelItemActions(dynamicContainer[0]);
    }

}


function saveDynamicItem(containerClass, dynamicResultTemplate, action) {
    var postUrl = $('#osnovniPodatki').attr('action');
    var dynamicEditItem = $("." + containerClass).closest('.dynamicEditItem');
    var dynamicFormFields = {};

    $.each($("." + containerClass + " .addOrEditItem :input").serializeArray(), function (i, field) {
        dynamicFormFields[field.name] = field.value;
    });

    dynamicFormFields["action"] = action;

    $.ajax({
        method: "POST",
        url: postUrl,
        data: dynamicFormFields
    })
        .fail(function (response) {
            $("." + containerClass + " .addOrEditItem").find('.help-block.has-error').remove();
            $("." + containerClass + " .addOrEditItem").find('.has-error').removeClass('has-error');

            $.each(JSON.parse(response.responseText), function (i, errorItem) {
                var field = $('[name="' + errorItem.field + '"]');
                var wrapper = field.closest('.mb-3');

                // Clear previous error on this field
                wrapper.find('.help-block.has-error').remove();
                wrapper.removeClass('has-error');

                // Add error
                wrapper.addClass('has-error');
                wrapper.find('div[class^="col"]').append(
                    '<span class="help-block has-error">' + errorItem.message + '</span>'
                );
            });
        })
        .done(function (response) {
            JsonData[containerClass] = response;
            for (var i = 0; i < JsonData.company.length; i++) {
                if (JsonData.company[i].previous == true) {
                    JsonData.company[i].previous = membershipSBIsPrevious
                    JsonData.company[i].companyType = 'previous'
                } else {
                    JsonData.company[i].companyType = 'current'
                }
            }

            fillJsTemplate(containerClass, dynamicResultTemplate, response);
            closeDynamicItem($("." + containerClass + " .dynamicEditItem"));

            checkHowManyCompanies();
            checkHowManyPreviousEmployments();
        });

}

// največ 3 predhodne zaposlitve (PreviousEmployment.MAX_COUNT) – pri polnem seznamu skrijemo gumb za dodajanje
var MAX_PREVIOUS_EMPLOYMENTS = 3;

function checkHowManyPreviousEmployments() {
    var full = $('.dynamicResultsContainer.previousEmployment .resultItem').length >= MAX_PREVIOUS_EMPLOYMENTS;
    $('.dynamicSection.previousEmployment .addItem').toggle(!full);
    $('.dynamicSection.previousEmployment .previousEmploymentMaxNote').toggleClass('d-none', !full);
}

function checkHowManyCompanies() {
    // max 3 companies
    if ($('.dynamicResultsContainer.company .resultItem').length >= 3) {
        $('.dynamicSection.company .addItem').hide();
    } else {
        $('.dynamicSection.company .addItem').show();
    }
}

function deleteDynamicItem(thisDeleteButton, action) {

    var postUrl = $('#osnovniPodatki').attr('action');
    var thisResultItem = $(thisDeleteButton).closest('.resultItem');
    var thisSection = $(thisDeleteButton).closest('.dynamicResultsContainer');
    var thisDynamicSection = $(thisDeleteButton).closest('.dynamicSection');
    var itemId = $(thisResultItem).find('input:hidden').val();

    Swal.fire({
        title: formBcancelInsert,
        text: formBchangeNotSaved,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#DD6B55",
        confirmButtonText: formBdelete2,
        cancelButtonText: formBcancel,
    }).then((result) => {
            if (result.isConfirmed) {

        var dynamicFormFields = {};
        dynamicFormFields["action"] = action;
        dynamicFormFields["id"] = itemId;

        $.ajax({
            method: "POST",
            url: postUrl,
            data: dynamicFormFields
        })
            .fail(function (response) {
                console.log(response);
                alert(formBdeleteError);
            })
            .done(function (response) {
                // $(thisResultItem).fadeOut('fast', function () {
                //     $(thisResultItem).remove();
                // });
                $(thisResultItem).fadeOut('fast', function () {
                    $(thisSection).html("");

                    // Dostopnost (ID 16): izbrisani gumb izgine – fokus premaknemo na smiselno mesto
                    // (gumb "Dodaj" v istem razdelku ali razdelek sam) in napovemo izbris.
                    var $focusTarget = $(thisDynamicSection).find('.addItem:visible').first();
                    if ($focusTarget.length === 0) {
                        $focusTarget = $(thisDynamicSection).attr('tabindex', '-1');
                    }
                    $focusTarget.trigger('focus');
                    a11yAnnounce('deleted');

                    switch (action) {
                        case 'deleteEducation':
                            closeDynamicItem($('.education'));
                            loadEducations();
                            break;
                        case 'deleteLanguage':
                            closeDynamicItem($('.language'));
                            loadLanguages();
                            break;
                        case 'deleteCompany':
                            closeDynamicItem($('.company'));
                            loadCompanies();
                            break;
                        case 'deletePreviousCompany':
                            closeDynamicItem($('.previousCompany'));
                            loadPreviousCompanies();
                            break;
                        case 'deleteMembership':
                            closeDynamicItem($('.membership'));
                            loadMembership();
                            break;
                        case 'deleteCompetence':
                            closeDynamicItem($('.competence'));
                            loadCompetences();
                            loadCompetencesAdmin();
                            break;
                        case 'deleteManagementExperience':
                            closeDynamicItem($('.managementExperience'));
                            loadManagementExperience();
                            break;
                        case 'deletePreviousEmployment':
                            closeDynamicItem($('.previousEmployment'));
                            loadPreviousEmployments();
                            break;
                    }
                });
            });


            }
    });

}

function editNews() {

    initDateTimePicker('newsDate');

    var fontsizevariable = "19";
    var fontfamilyvariable = "Arial, Verdana, sans-serif";

    tinymce.init({
        selector: '#newsContent',
        license_key: 'gpl',
        language: 'sl-SI',
        height: 800,
        menubar: false,
        skin: false,
        content_css: false,
        contextmenu: 'paste pastetext | cut copy | link unlink | removeformat',
        plugins: ['link', 'lists', 'code', 'fullscreen'],
        toolbar: 'bold italic underline strikethrough | forecolor backcolor | subscript superscript | bullist numlist | link unlink | removeformat | cut copy paste pastetext | undo redo | code fullscreen',
        content_style: 'body { font-family: Arial; font-size: 14px; }',
        paste_as_text: false,
        paste_remove_styles_if_webkit: true,
        paste_strip_class_attributes: 'all',
        paste_retain_style_properties: 'none',
        paste_postprocess: function(plugin, args) {
            const ALLOWED_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'A', 'P', 'BR', 'UL', 'OL', 'LI']);
            const clean = function(node) {
                const children = Array.from(node.childNodes);
                children.forEach(function(child) {
                    if (child.nodeType === Node.TEXT_NODE) {
                        return;
                    }
                    if (child.nodeType === Node.ELEMENT_NODE) {
                        const tag = child.tagName.toUpperCase();

                        clean(child);

                        if (ALLOWED_TAGS.has(tag)) {
                            Array.from(child.attributes).forEach(function(attr) {
                                if (tag === 'A' && (attr.name === 'href' || attr.name === 'target')) {
                                    return;
                                }
                                child.removeAttribute(attr.name);
                            });
                        } else {
                            const parent = child.parentNode;
                            while (child.firstChild) {
                                parent.insertBefore(child.firstChild, child);
                            }
                            parent.removeChild(child);
                        }
                    }
                });
            };
            clean(args.node);
        },
        setup: function(editor) {
            editor.on('init', function() {
                stickyFooter();
            });
        }
    });


}

function newsList() {
    $.fn.dataTableExt.oStdClasses.sPageButton = "btn btn-sm btn-secondary";
    $.fn.dataTableExt.oStdClasses.sPageButtonActive = "active";

    $('#newsList').DataTable({
        searching: false,
        ordering: false,
        paging: true,
        info: false,
        destroy: true,
        "lengthChange": false,
        "pageLength": 15,
        "language": {
            "emptyTable": formBnoData,
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        }
    });

    $('body').on('click', '#newsList .btnDelete', function (e) {
        e.preventDefault();
        var thisRow = $(this).parents('tr');
        var newsId = $(thisRow).data('id');
        var newsTitle = $(thisRow).find('td').eq(1).text();

        Swal.fire({
            title: "Ste prepričani, da želite izbrisati novico " + newsTitle + "?",
            text: "Izbire ne bo mogoče preklicati.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#DD6B55",
            confirmButtonText: "Izbriši",
            cancelButtonText: "Prekliči",
        }).then((result) => {
            if (result.isConfirmed) {

            var postUrl = 'news-item/' + newsId;
            var action = "delete";

            var dynamicFormFields = {};
            dynamicFormFields["action"] = action;
            dynamicFormFields["id"] = newsId;

            $.ajax({
                method: "POST",
                url: postUrl,
                data: dynamicFormFields
            })
                .fail(function (response) {
                    alert("Pri brisanju je prišlo do težave. Poskusite ponovno.");
                })
                .done(function (response) {


                    $(thisRow).fadeOut('fast', function () {
                        $(thisRow).remove();
                    });

                });


            }
        });


    });

    $('body').on('click', '#newsList .btnEdit', function (e) {
        e.preventDefault();
        var newsId = $(this).parents('tr').data('id');

    });

}

function userSnapshotList() {
    $.fn.dataTableExt.oStdClasses.sPageButton = "btn btn-sm btn-secondary";
    $.fn.dataTableExt.oStdClasses.sPageButtonActive = "active";

    var userSnapshotTable = $('#userSnapshotList').DataTable({
        searching: false,
        ordering: false,
        paging: true,
        info: false,
        destroy: true,
        "lengthChange": false,
        "pageLength": 15,
        "language": {
            "emptyTable": "Ni posnetkov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        }
    });

    // recalculate footer position on table change (pagination clicked)
    userSnapshotTable.on('page.dt', function () {
        setTimeout(stickyFooter, 10);
    });
}

function profileInAdmin() {

    if ($('#usernameBeforeConfirmed').val() != '') {
        Swal.fire({
            title: "Uporabnik mora spremembo e-poštnega računa potrditi!",
            text: "Za spremembo e-poštnega računa (oz. uporabniškega imena) je potrebna potrditev. Na naveden e-poštni naslov smo poslali sporočilo s povezavo do potrditvene strani. Do potrditve zamenjave, nov e-poštni račun še ne bo upoštevan!",
            icon: "info",
            showCancelButton: false,
            confirmButtonColor: "#5bc0de",
            confirmButtonText: "Potrdi",
        }).then((result) => {
            if (result.isConfirmed) {



            }
        });
    }

    stickyFooter();
}

function profileOTPLogic() {
    // 2fa logic
    $('#regen2fa').on('click', function (e) {
        e.preventDefault()

        bootstrap.Modal.getOrCreateInstance(document.getElementById('otp-modal')).show();
    })

    $('#regen2faConfirm').on('click', function (e) {
        // $ajax and fill modal
        $.ajax({url: "/otp/regenerate", method: "POST"})
            .fail(function () {
                alert("Prišlo je do napake. Prosimo, poskusite ponovno.");
            })
            .done(function (response) {
                (function(){ var bsModal = bootstrap.Modal.getInstance(document.getElementById('otp-modal')); if (bsModal) bsModal.hide(); })()
                $('#qrCodeImage').attr('src', response)
                bootstrap.Modal.getOrCreateInstance(document.getElementById('otp-2nd-modal')).show()
            });
    })

    stickyFooter();
}

function profile() {

    var initEmail = $('#email').val();
    var initMsisdnCC = $('#msisdnCC').val();
    var initMsisdn = $('#msisdn').val();

    if ($('#usernameBeforeConfirmed').val() != '') {
        Swal.fire({
            title: emailTitle,
            text: emailText,
            icon: "info",
            showCancelButton: false,
            confirmButtonColor: "#5bc0de",
            confirmButtonText: profileConfirm,
        }).then((result) => {
            if (result.isConfirmed) {



            }
        });
    }

    $('body').on('click', '.btnEdit', function (e) {
        e.preventDefault();
        $(this).addClass('d-none');
        $('input:visible[disabled="disabled"]').removeAttr('disabled');
        $('.saveBtn').removeClass('d-none');
        $('.cancelBtn').removeClass('d-none');
    });

    $('body').on('click', '.cancelBtn', function (e) {
        e.preventDefault();

        $('#email').val(initEmail);
        $('#msisdnCC').val(initMsisdnCC);
        $('#msisdn').val(initMsisdn);

        $(this).addClass('d-none');
        $('input:visible[type="text"]').attr('disabled', 'disabled');
        $('.saveBtn').addClass('d-none');
        $('.btnEdit').removeClass('d-none');
    });

    $('body').on('click', '.btnDeactivate', function (e) {
        e.preventDefault();

        Swal.fire({
            title: deactivationTitle,
            text: deactivationText,
            icon: "info",
            showCancelButton: true,
            confirmButtonColor: "#5bc0de",
            confirmButtonText: profileConfirm,
            cancelButtonText: profileCancel,
        }).then((result) => {
            if (result.isConfirmed) {

            var postUrl = 'profile';
            var action = "deactivate";

            var dynamicFormFields = {};
            dynamicFormFields["action"] = action;

            $.ajax({
                method: "POST",
                url: postUrl,
                data: dynamicFormFields
            })
                .fail(function (response) {
                    console.log(response);
                    alert(deactivationError);
                })
                .done(function (response) {
                    window.location.href = "/user/profile";
                });
                }
        });
    });

    $('body').on('click', '.btnDelete', function (e) {
        e.preventDefault();

        Swal.fire({
            title: deleteTitle,
            text: deleteText,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#DD6B55",
            confirmButtonText: profileConfirm,
            cancelButtonText: profileCancel,
        }).then((result) => {
            if (result.isConfirmed) {

            var postUrl = 'profile';
            var action = "delete";

            var dynamicFormFields = {};
            dynamicFormFields["action"] = action;

            $.ajax({
                method: "POST",
                url: postUrl,
                data: dynamicFormFields
            })
                .fail(function (response) {
                    console.log(response);
                    alert(deleteError);
                })
                .done(function (response) {
                    window.location.href = "/user/profile";
                });
                }
        });
    });

    $('body').on('click', '.btnInform', function (e) {
        e.preventDefault();

        Swal.fire({
            title: accquaintanceTitle,
            text: accquaintanceText,
            icon: "info",
            showCancelButton: true,
            confirmButtonColor: "#5bc0de",
            confirmButtonText: profileConfirm,
            cancelButtonText: profileCancel,
        }).then((result) => {
            if (result.isConfirmed) {

            var postUrl = 'profile';
            var action = "inform";

            var dynamicFormFields = {};
            dynamicFormFields["action"] = action;

            $.ajax({
                method: "POST",
                url: postUrl,
                data: dynamicFormFields
            })
                .fail(function (response) {
                    console.log(response);
                    alert(accquaintanceError);
                })
                .done(function (response) {
                    window.location.href = "/user/profile";
                });
                }
        });
    });
    setTimeout(stickyFooter, 200);


    if ($('#vopCheckbox').val() == "true") {
        $('#vop').prop('checked', true);
        $('#vop').prop('disabled', true);
    }


    $('#vop').on('change',  function () {
        var checked = $('#vop').is(':checked');
        if (checked===true) {
            updateUserVop("updateVop", checked);
            $('#vop').prop('disabled', true);
        }

    stickyFooter();
    });
}


function getGeocompleteData(result, searchItem) {
    var geocompleteData = "";


    if (typeof (_.find(result.address_components, function (ac) {
        return ac.types[0] == searchItem
    })) != 'undefined') {
        geocompleteData = (_.find(result.address_components, function (ac) {
            return ac.types[0] == searchItem
        }).long_name);
    }


    return geocompleteData;
}

function closeDynamicItem(dynamicEditItem) {
    $(dynamicEditItem).find('.addItemActionContainer').removeClass('d-none');
    $(dynamicEditItem).find('.saveItemActionContainer').addClass('d-none');
    $(dynamicEditItem).find('.addOrEditItem').addClass('d-none');
    $(dynamicEditItem).find(':input').val('');
    $(dynamicEditItem).find('input:hidden').val('');
    $(dynamicEditItem).find('select').trigger('change').trigger("chosen:updated");
    setTimeout(stickyFooter, 200);
    a11yRestoreFocusToAddItem($(dynamicEditItem));
}

// Dostopnost (WCAG 2.4.3): ko se obrazec za vnos zapre (preklic, shranjevanje), gumb "Prekliči"/"Shrani"
// izgine – fokus premaknemo na gumb "Dodaj" istega razdelka, a le, če je bil fokus v tem razdelku in se je
// izgubil (na <body> ali skrit element). Pogovorna okna SweetAlert2 fokus vračajo na zdaj skrit sprožilec,
// zato poskus ponovimo po zaprtju okna; ponovitev prekliče vsak klik ali pritisk tipke uporabnika.
var a11yLastFocused = null;
document.addEventListener('focusin', function (e) {
    if (!$(e.target).closest('.swal2-container').length) {
        a11yLastFocused = e.target;
    }
});

function a11yRestoreFocusToAddItem($dynamicEditItem) {
    var cancelled = false;
    function cancel() {
        cancelled = true;
    }
    function shouldRestore() {
        var a = document.activeElement;
        if (a && a !== document.body && $(a).is(':visible')) {
            return false; // fokus je na vidnem elementu – uporabnik je že drugje
        }
        return !!a11yLastFocused && $.contains($dynamicEditItem[0], a11yLastFocused) && !$(a11yLastFocused).is(':visible');
    }
    function restore() {
        if (!cancelled && shouldRestore()) {
            $dynamicEditItem.find('.addItem:visible').first().trigger('focus');
        }
    }
    restore();
    setTimeout(function () {
        document.addEventListener('mousedown', cancel, {once: true, capture: true});
        document.addEventListener('keydown', cancel, {once: true, capture: true});
    }, 0);
    setTimeout(function () {
        restore();
        document.removeEventListener('mousedown', cancel, {capture: true});
        document.removeEventListener('keydown', cancel, {capture: true});
    }, 400);
}

// Dostopnost (WCAG 2.4.3): ob odprtju obrazca za nov vnos ali urejanje gre fokus na prvo polje obrazca
// (pri Select2 na njegov kontrolnik). Registrirano na document, da se izvede za obdelovalci na body,
// ki obrazec prikažejo in napolnijo.
$(document).on('click', '.addItem', function () {
    var $form = $(this).closest('.dynamicEditItem').find('.addOrEditItem');
    setTimeout(function () {
        var $first = $form.find(':input, .select2-selection').filter(function () {
            if ($(this).hasClass('select2-selection')) {
                return $(this).is(':visible');
            }
            return this.type !== 'hidden' && !this.disabled && $(this).is(':visible') && !$(this).hasClass('select2-hidden-accessible');
        }).first();
        if ($first.length) {
            $first.trigger('focus');
        }
    }, 50);
});

function interviewInvitation() {

    $(".select2-select").each(function () {
        if ($(this).data("max")) {
            $(this).select2({
                max_selected_options: $(this).data("max"),
                disable_search: true,
                width: "100%",
                allowClear: true,
                placeholder: $(this).attr("placeholder") || ""
            });
        } else {
            $(this).select2({
                disable_search: false,
                width: "100%",
                search_contains: true,
                placeholder_text_single: $(this).attr("placeholder"),
                no_results_text: "Ni zadetkov",
                allowClear: true,
                placeholder: $(this).attr("placeholder") || ""
            });
        }

    });

    initDatePicker('interviewDate');

    $('body').on('change', '.checkboxLabel input[type="checkbox"]', function () {
        if (syncToggleLabel(this, 'checked', null)) {
            $('#interviewDate').val('').attr('disabled', 'disabled');
        } else {
            $('#interviewDate').removeAttr('disabled');
        }
    });

    var fontsizevariable = "19";
    var fontfamilyvariable = "Arial, Verdana, sans-serif";

    tinymce.init({
        selector: '#interviewInvitationMail',
        license_key: 'gpl',
        language: 'sl-SI',
        height: 800,
        menubar: false,
        skin: false,
        content_css: false,
        contextmenu: 'paste pastetext | cut copy | link unlink | removeformat',
        plugins: ['link', 'lists', 'code', 'fullscreen'],
        toolbar: 'bold italic underline strikethrough | forecolor backcolor | subscript superscript | bullist numlist | link unlink | removeformat | cut copy paste pastetext | undo redo | code fullscreen',
        content_style: 'body { font-family: Arial; font-size: 14px; }',
        paste_as_text: false,
        paste_remove_styles_if_webkit: true,
        paste_strip_class_attributes: 'all',
        paste_retain_style_properties: 'none',
        paste_postprocess: function(plugin, args) {
            const ALLOWED_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'A', 'P', 'BR', 'UL', 'OL', 'LI']);
            const clean = function(node) {
                const children = Array.from(node.childNodes);
                children.forEach(function(child) {
                    if (child.nodeType === Node.TEXT_NODE) {
                        return;
                    }
                    if (child.nodeType === Node.ELEMENT_NODE) {
                        const tag = child.tagName.toUpperCase();

                        clean(child);

                        if (ALLOWED_TAGS.has(tag)) {
                            Array.from(child.attributes).forEach(function(attr) {
                                if (tag === 'A' && (attr.name === 'href' || attr.name === 'target')) {
                                    return;
                                }
                                child.removeAttribute(attr.name);
                            });
                        } else {
                            const parent = child.parentNode;
                            while (child.firstChild) {
                                parent.insertBefore(child.firstChild, child);
                            }
                            parent.removeChild(child);
                        }
                    }
                });
            };
            clean(args.node);
        },
        setup: function(editor) {
            editor.on('init', function() {
                stickyFooter();
            });
            }
    });

}


window.EMSOvalidator = {
    FORMAT: 'format',
    INVALID: 'invalid',
    pattern: /^\d{13}$/,
    validate: function (element, value) {

        if (!value || value.toString().length == 0) {
            return;
        }

        //var vc = PrimeFaces.util.ValidationContext;

        if (!this.pattern.test(value)) {
            //console.log(this, $(element));
            return false;
        }

        var dan = value.substring(0, 2);
        var mesec = value.substring(2, 4);
        var leto = value.substring(4, 7);
        leto = (parseInt(leto) > 850 ? "1" : "2") + leto;

        var datum = new Date(leto, --mesec, dan);

        if (datum.getFullYear() != leto || datum.getMonth() != mesec ||
            datum.getDate() != dan) {
            return false;
        }

        var kraj = parseInt(value.substring(7, 9));

        if (kraj > 50 && kraj < 70) {
            return false;
        }

        var control = 0;

        for (var i = 0; i < value.length - 1; i++) {
            control += parseInt(value.charAt(i)) * (7 - (i % 6));
        }

        if (((11 - (control % 11)) % 11) !=
            parseInt(value.charAt(value.length - 1))) {
            return false;
        }

        return true;
    }
};


$.fn.getType = function () {
    return this[0].tagName == "INPUT" ? this[0].type.toLowerCase() : this[0].tagName.toLowerCase();
}

var datepickerIcons = {
    time: 'fas fa-clock',
    date: 'fas fa-calendar',
    up: 'fas fa-arrow-up',
    down: 'fas fa-arrow-down',
    previous: 'fas fa-arrow-left',
    next: 'fas fa-arrow-right',
    today: 'fas fa-crosshairs',
    clear: 'fas fa-times',
    close: 'fas fa-close'
};


function accreditationPreview() {

    $('body').on('click', '.printBtn', function (e) {

        e.preventDefault();

        // Wait for jQuery plugins to load, then use printThis plugin
        if (window.jQueryPluginsReady) {
            window.jQueryPluginsReady.then(() => {
                $(".printContent").printThis({
                    importCSS: true,
                    importStyle: true,
                    printContainer: true,
                    pageTitle: "Predogled vloge za akreditacijo",
                    removeInline: false,
                    printDelay: 333,
                    header: null,
                    footer: null,
                    formValues: true
                });
            });
        } else {
            // Fallback to native print if plugin not available
            window.print();
        }

    });

    // if statement is init checked
    $('.statement').each(function () {
        var thisStatement = $(this);
        if ($(thisStatement).find('input[type="checkbox"][id^="statement"]').is(':checked')) {
            $(thisStatement).find('.btn input[type="checkbox"]').trigger('change');
        }
    });

    // open panel if additional info textarea is filled
    $('textarea').each(function () {
        var thisTextarea = $(this);
        if (String($(thisTextarea).val() ?? '').trim()) {
            $(thisTextarea).closest('.collapse').addClass('show');
        }
    });

    $('body').on('change', '.vop .btn input[type="checkbox"]', function () {
        var checked = syncToggleLabel(this);
        var $target = $('#canEditBeforeNomination').val() == 'true' ? $('#saveBeforeNomination') : $('#success');
        setButtonLinkDisabled($target, !checked);
    });
    $('.vop .btn input[type="checkbox"]').trigger('change');

    // save competence
    $('body').on('click', '.competenceAdmin .btn.saveItem', function (e) {
        e.preventDefault();
        saveDynamicItem('competenceAdmin', 'dynamicResultTemplateCompetenceAdmin', 'saveCompetence');
    });

    // edit item
    $('body').on('click', '.resultItem .editItem', function (e) {

        var itemId = $(this).closest('.resultItem').find('input:hidden').val();
        var itemName = $(this).closest('.resultItem').find('input:hidden').attr('name');
        var dynamicSection = $(this).closest('.dynamicSection');

        $(dynamicSection).find('.addItem').trigger('click');
        $(dynamicSection).find('.actionTitle').text($(dynamicSection).find('.actionTitle').data('edit'));

        // populate edit data from json
        var dataSection = eval("JsonData." + $(dynamicSection).data('section'));
        var thisItemData = _.find(dataSection, function (item) {
            return item[itemName] == itemId;
        });

        console.log(thisItemData);

        $(dynamicSection).find('.dynamicEditItem :input').each(function () {
            var thisInput = $(this);
            var fieldType = $(thisInput).getType();

            switch (fieldType) {
                case 'hidden':
                    $(thisInput).val(thisItemData[$(this).attr('name')]);
                    break;
                case 'text':
                    $(thisInput).val(thisItemData[$(this).attr('name')]);
                    break;
                case 'select':
                case 'select-one':
                    var idFieldName = $(this).data('idField');
                    var thisFieldValue = idFieldName ? thisItemData[idFieldName] : getOptionValueByText($(thisInput), thisItemData[$(this).attr('name')]);
                    $(thisInput).val(thisFieldValue).trigger('change').trigger("chosen:updated");
                    break
            }
        });
        setTimeout(stickyFooter, 200);
    });

    // add item
    $('body').on('click', '.addItem', function (e) {
        var dynamicEditItem = $(this).closest('.dynamicEditItem');

        $(dynamicEditItem).find('.actionTitle').text((dynamicEditItem).find('.actionTitle').data('new'));

        $(dynamicEditItem).find('.mb-3.has-error, .form-group.has-error').removeClass('has-error');
        $(dynamicEditItem).find('.mb-3 .help-block.has-error, .form-group .help-block.has-error').remove();

        $(dynamicEditItem).find('.addItemActionContainer').addClass('d-none');
        $(dynamicEditItem).find('.saveItemActionContainer').removeClass('d-none');
        $(dynamicEditItem).find('.addOrEditItem').removeClass('d-none');

        if ($(dynamicEditItem).find('input:hidden').val() == "") {
            $(dynamicEditItem).find(':input').val('');
        }
        setTimeout(stickyFooter, 200);
    });

    // edit item
    $('body').on('click', '.resultItem .editItem', function (e) {

        var itemId = $(this).closest('.resultItem').find('input:hidden').val();
        console.log(itemId);
        var itemName = $(this).closest('.resultItem').find('input:hidden').attr('name');
        var dynamicSection = $(this).closest('.dynamicSection');

        $(dynamicSection).find('.addItem').trigger('click');
        $(dynamicSection).find('.actionTitle').text($(dynamicSection).find('.actionTitle').data('edit'));

        // populate edit data from json
        var dataSection = eval("JsonData." + $(dynamicSection).data('section'));
        var thisItemData = _.find(dataSection, function (item) {
            return item[itemName] == itemId;
        });

        console.log(thisItemData);

        $(dynamicSection).find('.dynamicEditItem :input').each(function () {
            var thisInput = $(this);
            var fieldType = $(thisInput).getType();

            switch (fieldType) {
                case 'hidden':
                    $(thisInput).val(thisItemData[$(this).attr('name')]);
                    break;
                case 'text':
                    $(thisInput).val(thisItemData[$(this).attr('name')]);
                    break;
                case 'select':
                case 'select-one':
                    var idFieldName = $(this).data('idField');
                    var thisFieldValue = idFieldName ? thisItemData[idFieldName] : getOptionValueByText($(thisInput), thisItemData[$(this).attr('name')]);
                    $(thisInput).val(thisFieldValue).trigger('change').trigger("chosen:updated");
                    break
            }
        });

        setTimeout(stickyFooter, 200);
    });

    // cancel
    $('body').on('click', '.cancelItem', function (e) {
        var dynamicEditItem = $(this).closest('.dynamicEditItem');

        Swal.fire({
            title: formBcancelInsert,
            text: formBchangeNotSaved,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#DD6B55",
            confirmButtonText: formBconfirm,
            cancelButtonText: formBcancel,
        }).then((result) => {
            if (result.isConfirmed) {

            closeDynamicItem($(dynamicEditItem));


            }
        });
    });

    // delete competence
    $('body').on('click', '.competenceAdmin .resultItem .deleteItem', function (e) {
        deleteDynamicItem($(this), 'deleteCompetence');
    });


}

function changesBeforeNomination() {

    userRoleUrlPath = "admin";
    userId = $('#userRevisionId').val();
    var id = $('#userNewId').val();

    $('#emso').on('blur', function (e) {
        var emsoField = $(this);

        if (EMSOvalidator.validate($('input#emso'), $('input#emso').val())) {
            // set gender
            if (parseInt($('input#emso').val().substring(9, 12)) <= 499) {
                $('#spol').val('MALE');
                $('#spol').val('MALE');
            } else {
                $('#spol').val('FEMALE');
            }

            // set date of birth
            var dayOfBirth = $('input#emso').val().substring(0, 2);
            var monthOfBirth = $('input#emso').val().substring(2, 4);
            var yearOfBirth = $('input#emso').val().substring(4, 7);

            if (yearOfBirth < 800) {
                yearOfBirth = '2' + yearOfBirth;
            } else {
                yearOfBirth = '1' + yearOfBirth;
            }

            $('#danRojstva').val(dayOfBirth).trigger('change');
            $('#mesecRojstva').val(monthOfBirth).trigger('change');
            $('#letoRojstva').val(yearOfBirth).trigger('change');


            $(emsoField).closest('.mb-3, .form-group').removeClass('has-error');
            $(emsoField).next('.help-block.has-error').remove();

        } else {
            $(emsoField).next('.help-block.has-error').remove();
            $(emsoField).closest('.mb-3, .form-group').addClass('has-error');
            $(emsoField).after($('<span class="help-block has-error"></span>').text(emsoError));

            $('#danRojstva').val("").trigger('change');
            $('#mesecRojstva').val("").trigger('change');
            $('#letoRojstva').val("").trigger('change');
        }
    });

    $('#emsoOld').on('blur', function (e) {
        var emsoField = $(this);

        if (EMSOvalidator.validate($('input#emsoOld'), $('input#emsoOld').val())) {
            // set gender
            if (parseInt($('input#emsoOld').val().substring(9, 12)) <= 499) {
                $('#spolOld').val('MALE');
                $('#spolOld').val('MALE');
            } else {
                $('#spolOld').val('FEMALE');
            }

            // set date of birth
            var dayOfBirth = $('input#emsoOld').val().substring(0, 2);
            var monthOfBirth = $('input#emsoOld').val().substring(2, 4);
            var yearOfBirth = $('input#emsoOld').val().substring(4, 7);

            if (yearOfBirth < 800) {
                yearOfBirth = '2' + yearOfBirth;
            } else {
                yearOfBirth = '1' + yearOfBirth;
            }

            $('#danRojstvaOld').val(dayOfBirth).trigger('change');
            $('#mesecRojstvaOld').val(monthOfBirth).trigger('change');
            $('#letoRojstvaOld').val(yearOfBirth).trigger('change');


            $(emsoField).closest('.mb-3, .form-group').removeClass('has-error');
            $(emsoField).next('.help-block.has-error').remove();

        } else {
            $(emsoField).next('.help-block.has-error').remove();
            $(emsoField).closest('.mb-3, .form-group').addClass('has-error');
            $(emsoField).after($('<span class="help-block has-error"></span>').text(emsoError));

            $('#danRojstvaOld').val("").trigger('change');
            $('#mesecRojstvaOld').val("").trigger('change');
            $('#letoRojstvaOld').val("").trigger('change');
        }
    });

    // if differentContactAddress is init checked
    if ($('.differentContactAddressOld').is(':checked')) {
        $('#obvescanje_panelOld').addClass('show');
    }

    $("#danRojstvaOld").ForceNumericOnly();
    $("#mesecRojstvaOld").ForceNumericOnly();
    $("#letoRojstvaOld").ForceNumericOnly();


    ////////////////////////
    // dynamic add/edit form
    ////////////////////////
    loadEducations();
    loadLanguages();
    loadCompanies();
    loadPreviousCompanies();
    loadAttachments();
    loadCompetences();
    loadManagementExperience()
    loadCompetencesAdmin();
    loadEducationsNew(id);
    loadLanguagesNew(id);
    loadCompaniesNew(id);
    loadPreviousCompaniesNew(id);
    loadAttachmentsNew(id);
    loadCompetencesNew(id);
    loadCompetencesAdminNew(id);
    loadManagementExperienceNew(id);
}

function getOptionValueByText(select, text) {
    var value = $(select).find("option").filter(function () {
        return $(this).text() === text;
    }).first().attr("value");
    return value;
}


function dataAccessPurpose() {

    $.ajax({
        url: "/audit/causes",
    })
        .fail(function () {
            alert("Pri pridobivanju podatkov je prišlo do napake. Prosimo, poskusite ponovno.");
        })
        .done(function (response) {
            $.each(response, function () {
                $('#selectedPurpose').append('<option value="' + this.id + '">' + this.description + '</option>');
            });
        });


    $('body').on('click', '.dataPurposeBtn', function (e) {
        var dataPurposeModal = $('#dataPurposeModal');
        var modalTitle = 'Navedba razloga vpogleda v osebne podatke';

        $(dataPurposeModal).find('.modal-title').text(modalTitle);
        var modal = new bootstrap.Modal(dataPurposeModal[0], {backdrop: 'static', keyboard: false});
        modal.show();
    });

    $('body').on('click', '.dataPurposeSubmit', function (e) {
        var dataPurposeModal = $('#dataPurposeModal');
        var selectedPurpose = $('#selectedPurpose option:selected').val();

        var formData = {
            'causeId': selectedPurpose
        };

        $.ajax({
            url: '/audit/cause',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            data: JSON.stringify(formData),
            type: 'POST',
            dataType: 'json',
            contentType: false,
            processData: false
        })
            .fail(function (response) {
                alert("Pri pošiljanju podatkov je prišlo do napake.");
            })
            .done(function (response) {
                sessionStorage.setItem('dataPurpose', selectedPurpose);
                (function(){ var bsModal = bootstrap.Modal.getInstance(dataPurposeModal[0]); if (bsModal) bsModal.hide(); })();
            });

    });

    if (sessionStorage.getItem('dataPurpose') != null) {

        // nothing
    } else {
        if ($('.dataPurposeBtn').length) {
            $('.dataPurposeBtn').trigger('click');
        } else {
            window.location.href = $('.nav.navbar-nav.navbar-right li').first().find('a').attr('href');
        }

    }
}

function userRequests(userRole) {

    $.fn.dataTableExt.oStdClasses.sPageButton = "btn btn-sm btn-secondary";
    $.fn.dataTableExt.oStdClasses.sPageButtonActive = "active";

    var t1 = $('#userRequestsInform').DataTable({
        searching: false,
        ordering: true,
        paging: true,
        info: false,
        destroy: true,
        "lengthChange": false,
        "pageLength": 10,
        "language": {
            "emptyTable": "Ni podatkov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        }
    });

    var t2 = $('#userRequestsDeactivate').DataTable({
        searching: false,
        ordering: true,
        paging: true,
        info: false,
        destroy: true,
        "lengthChange": false,
        "pageLength": 10,
        "language": {
            "emptyTable": "Ni podatkov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        }
    });

    var t3 = $('#userRequestsDelete').DataTable({
        searching: false,
        ordering: true,
        paging: true,
        info: false,
        destroy: true,
        "lengthChange": false,
        "pageLength": 10,
        "language": {
            "emptyTable": "Ni podatkov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        }
    });

    $('#userRequestsInform tbody, #userRequestsDeactivate tbody, #userRequestsDelete tbody').on('click', 'tr', function () {
        if ($(this).hasClass('selected')) {
            //$(this).removeClass('selected');
            var userId = $(this).data('user-id');
            //alert("V novem zavihku odpri informacije o uporabniku z ID " + userId);
            window.location.href = "/" + userRole + "/user/" + userId;
        } else {
            t1.$('tr.selected').removeClass('selected');
            t2.$('tr.selected').removeClass('selected');
            t3.$('tr.selected').removeClass('selected');
            $(this).addClass('selected');
        }
    });

    $(document).ready(function() {
        setTimeout(stickyFooter, 200);
    });

}

function nominations(role) {
    $.fn.dataTableExt.oStdClasses.sPageButton = "btn btn-sm btn-secondary";
    $.fn.dataTableExt.oStdClasses.sPageButtonActive = "active";

    const { fromPicker, toPicker } = initDateRangePickers('dateFrom', 'dateTo');

    $(".select2-select").each(function () {
        if ($(this).data("max")) {
            $(this).select2({
                max_selected_options: $(this).data("max"),
                disable_search: true,
                width: "100%",
                allowClear: true,
                placeholder: $(this).attr("placeholder") || ""
            });
        } else {
            $(this).select2({
                disable_search: false,
                width: "100%",
                search_contains: true,
                placeholder_text_single: $(this).attr("placeholder"),
                no_results_text: "Ni zadetkov",
                allowClear: true,
                placeholder: $(this).attr("placeholder") || ""
            });
        }

    });

    var columns = [
        // Kandidat / Nadzornik
        {
            "data": { candidate: "candidate" },
            "render": function (data) {
                return '<td>' + data.candidate + '</td>';
            }
        },

        // Družba
        {
            "data": { companyName: "companyName" },
            "render": function (data) {
                return '<td>' + data.companyName + '</td>';
            },
            "orderable": false
        },

        // Datum nominacije
        {
            "data": { nominatedAt: "nominatedAt" },
            "render": function (data) {
                if (data.nominatedAt) {
                    return '<td data-sort="' + data.nominatedAt + '">' +
                        '<i class="fas fa-calendar"></i>' +
                        '<span> ' + data.nominatedAt + '</span>' +
                        '</td>';
                } else {
                    return '<td></td>';
                }
            }
        },

        // Namen
        {
            "data": { purpose: "purpose" },
            "render": function (data) {
                return '<td>' + data.purpose + '</td>';
            },
            "orderable": false
        },

        // Status
        {
            "data": { status: "status" },
            "render": function (data) {
                return '<td><span>' + data.status + '</span></td>';
            }
        },

        // Izbran (with icons)
        {
            "data": { status: "status", selected: "selected" },
            "render": function (data) {
                var html = '<td style="text-align:center">';
                switch (data.status) {
                    case 'Povabljen k nominaciji':
                    case 'Posodobitev podatkov končana':
                    case 'Izjave poslane v pregled':
                    case 'Ocenjen s strani KK':
                    case 'Nominacija vrnjena v dopolnitev':
                        html += '<i class="fas fa-2x fa-question-circle" style="color:#6c757d" aria-hidden="true"></i><span class="visually-hidden">Odločitev še ni sprejeta</span>';
                        break;
                    case 'Nominacija umaknjena zaradi neopravljenega testa s področja korporativnega upravljanja':
                        html += '<i class="fas fa-2x fa-exclamation-circle" style="color:#b35c00" aria-hidden="true"></i><span class="visually-hidden">Nominacija umaknjena (test ni opravljen)</span>';
                        break;
                    case 'Nominacija izbrisana':
                        html += '<i class="fas fa-2x fa-minus-circle" style="color:#d9534f" aria-hidden="true"></i><span class="visually-hidden">Nominacija prekinjena</span>';
                        break;
                    case 'Povabilo k nominaciji zavrnjeno':
                        html += '<i class="fas fa-2x fa-minus-circle" style="color:#b35c00" aria-hidden="true"></i><span class="visually-hidden">Nominacija prekinjena</span>';
                        break;
                    case 'Nominacija zaključena':
                        html += data.selected
                            ? '<i class="fas fa-2x fa-check-circle" style="color:#198754" aria-hidden="true"></i><span class="visually-hidden">Izbran</span>'
                            : '<i class="fas fa-2x fa-circle-xmark" style="color:#d9534f" aria-hidden="true"></i><span class="visually-hidden">Ni izbran</span>';
                        break;
                    default:
                        html += '<i class="fas fa-2x fa-question-circle" style="color:#6c757d" aria-hidden="true"></i><span class="visually-hidden">Odločitev še ni sprejeta</span>';
                }
                return html + '</td>';
            },
            "orderable": true
        },

        // Posnetek stanja akreditacije na dan
        {
            "data": { interviewDate: "interviewDate", candidateRevisionId: "candidateRevisionId" },
            "render": function (data) {
                if (data.interviewDate && data.candidateRevisionId) {
                    return '<td style="text-align:center">' +
                        '<a class="btn btn-sm btn-primary" href="/' + role + '/user-snapshot/' + data.candidateRevisionId + '?from=nominations">' +
                        '<i class="fas fa-table"></i> ' + data.interviewDate +
                        '</a>' +
                        '</td>';
                } else {
                    return '<td style="text-align:center"></td>';
                }
            },
            "orderable": false
        }
    ];

    // Arhiv
    if (role === 'admin') {
        columns.push({
            "data": { id: "id", arhiv: "arhiv" },
            "render": function (data) {
                var selectedNo = !data.arhiv ? 'selected' : '';
                var selectedYes = data.arhiv ? 'selected' : '';
                return '<td style="text-align:center">' +
                    '<select class="arhiv-select" aria-label="Arhivirano" data-id="' + data.id + '">' +
                    '<option value="false" ' + selectedNo + '>-</option>' +
                    '<option value="true" ' + selectedYes + '>DA</option>' +
                    '</select>' +
                    '</td>';
            },
            "orderable": false
        });
    }

    // Arhiv
    if (role === 'management') {
        columns.push({
            "data": { id: "id", arhiv: "arhiv" },
            "render": function (data) {
                var badgeClass = data.arhiv ? 'bg-success' : 'bg-danger';
                var badgeText = data.arhiv ? 'DA' : '-';
                return '<td style="text-align:center">' +
                    '<span class="badge ' + badgeClass + '">' + badgeText + '</span>' +
                    '</td>';
            },
            "orderable": false
        });
    }


    var url = window.location.pathname;
    var pageLength = 10;

    var nominationsTable = $('#nominationList').DataTable({
        searching: false,
        ordering: true,
        "order": [[ 2, "desc" ]],
        info: false,
        "lengthChange": false,
        "pageLength": pageLength,
        "language": {
            "emptyTable": "Ni podatkov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        },
        "processing": true,
        "serverSide": true,
        "ajax": {
            "url": url + "/fillNominations",
            "data": function (reqParam) {

                reqParam.candidate = $('#candidate').val();

                const selectedCompanies = $('#company').val();
                if (selectedCompanies && selectedCompanies.includes('')) {
                    reqParam.company = null;
                } else {
                    reqParam.company = selectedCompanies || [];
                }

                reqParam.dateFrom = $('#dateFrom').val();
                reqParam.dateTo = $('#dateTo').val();

                const selectedStatuses = $('#status').val();
                if (selectedStatuses && selectedStatuses.includes('')) {
                    // If "Vsi statusi" is selected, send all status values or don't send the parameter
                    reqParam.status = null;
                } else {
                    reqParam.status = selectedStatuses || [];
                }

                reqParam.purpose = $('#purpose').val();
                $('.showLoadingOverlay').css('height', '100%').fadeIn();
            },
            "dataSrc": function (json) {
                setTimeout(stickyFooter, 500);
                $('.showLoadingOverlay').css('height', '0px').fadeOut();
                return json.data;
            },
            "dataType": 'json'
        },
        dom: 'rtip',
        "columns": columns
    });

    $('#nominationList').on('change', '.arhiv-select', function () {
        var $select = $(this);
        var id = $select.data('id');
        var arhiv = $select.val() === 'true';
        var previousValue = arhiv ? 'false' : 'true'; // for revert on error

        $select.prop('disabled', true);

        $.ajax({
            url: url + '/updateArhiv',
            method: 'POST',
            data: { id: id, arhiv: arhiv },
            success: function () {
                $select.prop('disabled', false);
            },
            error: function () {
                $select.val(previousValue).prop('disabled', false);
                alert('Napaka pri shranjevanju arhiva.');
            }
        });
    });

    // nominacija se odpre z enim klikom na vrstico; klik na spustni seznam (Arhiv) ali drug kontrolnik v vrstici je ne odpre
    nominationsTable.on('click', 'tbody tr', function (e) {
        if ($(e.target).closest('select, option, input, textarea, button, a, label, .dropdown, .select2-container').length) {
            return;
        }
        var rowData = nominationsTable.row(this).data();
        if (!rowData) {
            return; // prazna tabela
        }
        var nominationUrl = "/" + role + "/user/nomination/" + rowData.id;
        if (e.ctrlKey || e.metaKey) {
            window.open(nominationUrl, '_blank'); // Ctrl/Cmd + klik: v novem zavihku
        } else {
            window.location.href = nominationUrl;
        }
    });

    // Handle the search button click specifically
    $('#search').on('click', function(e) {
        e.preventDefault();
        e.stopPropagation();

        // Reload the DataTable with current form values
        if (nominationsTable) {
            nominationsTable.ajax.reload();
        }

        return false;
    });

    setTimeout(stickyFooter, 500);

}
function archiveNotices(role) {
    $.fn.dataTableExt.oStdClasses.sPageButton = "btn btn-sm btn-secondary";
    $.fn.dataTableExt.oStdClasses.sPageButtonActive = "active";

    var url = window.location.pathname;
    var pageLength = 10;

    var archiveNoticesTable = $('#archiveNoticesList').DataTable({
        searching: false,
        ordering: true,
        "order": [[ 1, "desc" ]],
        info: false,
        "lengthChange": false,
        "pageLength": pageLength,
        "language": {
            "emptyTable": "Ni podatkov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        },
        "processing": true,
        "serverSide": true,
        "ajax": {
            "url": url + "/fillArchiveNotices",
            "data": function (reqParam) {
                var documentTypeVal = $('#documentType').val();
                if (documentTypeVal && documentTypeVal !== '') {
                    reqParam.documentType = documentTypeVal;
                }
                $('.showLoadingOverlay').css('height', '100%').fadeIn();
            },
            "dataSrc": function (json) {
                $('.showLoadingOverlay').css('height', '0px').fadeOut();
                return json.data;
            },
            "dataType": 'json'
        },
        dom: 'rtip',
        "columns": [
            // Kandidat
            {
                "data": {
                    userId: "userId",
                    firsName: "firsName",
                    lastName: "lastName"
                },
                "render": function (data) {
                    return '<td><a href="/' + role + '/user/' + data.userId + '">' +
                        data.firstName + ' ' + data.lastName + '</a></td>';
                }
            },
            // Datum dokumenta
            {
                "data": {
                    dateTime: "dateTime"
                },
                "render": function (data) {
                    return '<td>' + data.dateTime + '</td>';
                }
            },


            // Dokument
            {
                "data": {
                    archiveNoticeId: "archiveNoticeId",
                    archiveNoticeName: "archiveNoticeName",
                    fileId: "fileId"
                },
                "render": function (data) {
                    return '<td><a href="/' + role + '/file/' + data.fileId + '" target="_blank">' +
                        data.archiveNoticeName + '</a></td>';
                }
            }
        ]
    });

    // Handle the search button click
    $('#search').on('click', function(e) {
        e.preventDefault();
        e.stopPropagation();

        // Reload the DataTable
        if (archiveNoticesTable) {
            archiveNoticesTable.ajax.reload(null, false); // false = stay on current page
        }

        return false;
    });



    $(document).on('click', '#exportExcel', function(e) {
        e.preventDefault();

        var button = $(this);
        var documentTypeVal = $('#documentType').val();
        var exportUrl = url + '/exportExcel';
        var params = [];

        if (documentTypeVal && documentTypeVal !== '') {
            params.push('documentType=' + encodeURIComponent(documentTypeVal));
        }

        if (params.length > 0) {
            exportUrl += '?' + params.join('&');
        }

        var originalHtml = button.html();
        button.prop('disabled', true);
        button.html('<i class="fas fa-spinner fa-spin"></i> Izvažam...');

        window.location.href = exportUrl;

        setTimeout(function() {
            button.html(originalHtml);
            button.prop('disabled', false);
        }, 2000);
    });
}

function conditionVerification(role) {

    $.fn.dataTableExt.oStdClasses.sPageButton = "btn btn-sm btn-secondary";
    $.fn.dataTableExt.oStdClasses.sPageButtonActive = "active";

    if (role === 'admin') {
        var confirmModal = new bootstrap.Modal(document.getElementById('confirmSendModal'))
        var confirmSendForEmptyStatementModal = new bootstrap.Modal(document.getElementById('confirmSendForEmptyStatementModal'))

    }

    var savedDocumentType = sessionStorage.getItem('conditionVerification_documentType');
    if (savedDocumentType) {
        $('#documentType').val(savedDocumentType);
        if ($.fn.select2) {
            $('#documentType').trigger('change.select2');
        }
    }

    var url = window.location.pathname;
    var pageLength = 10;

    var conditionVerificationTable = $('#conditionVerificationList').DataTable({
        searching: false,
        ordering: true,
        "order": [[ 1, "desc" ]],
        info: false,
        "lengthChange": false,
        "pageLength": pageLength,
        "language": {
            "emptyTable": "Ni podatkov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        },
        "processing": true,
        "serverSide": true,
        "ajax": {
            "url": "/" + role + "/condition-verification/fillArchiveNotices",
            "data": function (reqParam) {
                reqParam.kandidat = $('#search-kandidat').val();
                var documentTypeVal = $('#documentType').val();
                if (documentTypeVal && documentTypeVal !== '') {
                    reqParam.documentType = documentTypeVal;
                }
                $('.showLoadingOverlay').css('height', '100%').fadeIn();
            },
            "dataSrc": function (json) {
                $('.showLoadingOverlay').css('height', '0px').fadeOut();
                return json.data;
            },
            "dataType": 'json'
        },
        dom: 'rtip',
        "columns": [
            // Kandidat
            {
                "data": {
                    userId: "userId",
                    firsName: "firsName",
                    lastName: "lastName"
                },
                "render": function (data) {
                    return '<td><a href="/' + role + '/user/' + data.userId + '">' +
                        data.firstName + ' ' + data.lastName + '</a></td>';
                }
            },

            // Družba
            {
                "data": {
                    company: "company"
                },
                "render": function (data) {
                    return '<td>' + data.company + '</td>';
                }
            },
            // Funkcija
            {
                "data": {
                    title: "title"
                },
                "render": function (data) {
                    return '<td>' + data.title + '</td>';
                }
            },
            // Datum
            {
                "data": {
                    date: "date"
                },
                "render": function (data) {
                    return '<td>' + data.date + '</td>';
                }
            },
            // Izjava izpolnjena
            {
                "data": {
                    submitted: "submitted"
                },
                "render": function (data) {
                    return '<td>' + data.submitted + '</td>';
                }
            },
            // Status
            {
                "data": {
                    status: "status"
                },
                "render": function (data) {
                    return '<td>' + data.status + '</td>';
                }
            },

            // Dokument
            {
                "data": {
                    conditionRecheckId: "conditionRecheckId",
                    conditionRecheckFinalPdfName: "conditionRecheckFinalPdfName",
                    fileId: "fileId"
                },
                "render": function (data) {
                    if (data.conditionRecheckFinalPdfName == null) {
                        return '<td><td/>';
                    } else {
                        return '<td><a href="/' + role + '/file/' + data.fileId + '" target="_blank">' +
                            data.conditionRecheckFinalPdfName + '</a></td>';
                    }
                }
            },

            // Pogoji izpolnjeni
           {
                "data": {
                    conditionsFullfilled: "conditionsFullfilled",
                    conditionRecheckId: "conditionRecheckId"
                },
                "render": function (data, type, row) {
                    // Determine the selected value based on conditionsFullfilled
                    var selectedValue = '';
                    if (data.conditionsFullfilled === true) {
                        selectedValue = 'true';
                    } else if (data.conditionsFullfilled === false) {
                        selectedValue = 'false';
                    }

                    var html = '<div style="display: flex; align-items: center; gap: 10px;">';

                    // Editable dropdown for admin and commission
                    if (role === "admin" || role === "commission") {
                        html += '<select class="form-control form-control-sm conditions-dropdown" aria-label="Pogoji izpolnjeni" data-condition-recheck-id="' + data.conditionRecheckId + '">';
                        html += '<option value=""' + (selectedValue === '' ? ' selected' : '') + '>-</option>';
                        html += '<option value="true"' + (selectedValue === 'true' ? ' selected' : '') + '>DA</option>';
                        html += '<option value="false"' + (selectedValue === 'false' ? ' selected' : '') + '>NE</option>';
                        html += '</select>';
                    }
                    // Read-only display for other users
                    else {
                        if (data.conditionsFullfilled === true) {
                            html += '<span class="badge bg-success">DA</span>';
                        } else if (data.conditionsFullfilled === false) {
                            html += '<span class="badge bg-danger">NE</span>';
                        } else {
                            html += '<span class="badge bg-secondary">-</span>';
                        }
                    }

                    html += '</div>';

                    return html;
                },
                "orderable": true
            },
            // urejanje opombe na dokumentu
            {
                "data": {
                    text: "text",
                    conditionRecheckId: "conditionRecheckId"
                },
                "render": function (data, type, row) {
                    const isTextEmpty = !data.text || data.text === '';
                    return '<button class="btn btn-sm edit-text-btn ' + (isTextEmpty ? 'btn-secondary' : 'btn-warning') + '" ' +
                        'data-condition-recheck-id="' + data.conditionRecheckId + '" ' +
                        'data-text="' + (data.text ? data.text.replace(/"/g, '&quot;') : '') + '" ' +
                        'title="Opomba">' +
                        '<i class="fas fa-pen"></i>' +
                        '</button>';
                },
                "orderable": false
            }
        ]
    });

    $('#search').on('click', function(e) {
        e.preventDefault();
        e.stopPropagation();

        var docTypeVal = $('#documentType').val();

        if (docTypeVal && docTypeVal !== '') {
            sessionStorage.setItem('conditionVerification_documentType', docTypeVal);
        } else {
            sessionStorage.removeItem('conditionVerification_documentType');
        }

        if (conditionVerificationTable) {
            conditionVerificationTable.ajax.reload(null, false);
        }
        return false;
    });

    $('#search-kandidat').on('keypress', function(e) {
        if (e.which === 13 || e.keyCode === 13) {
            e.preventDefault();
            e.stopPropagation();

            var docTypeVal = $('#documentType').val();

            if (docTypeVal && docTypeVal !== '') {
                sessionStorage.setItem('conditionVerification_documentType', docTypeVal);
            } else {
                sessionStorage.removeItem('conditionVerification_documentType');
            }

            if (conditionVerificationTable) {
                conditionVerificationTable.ajax.reload(null, false);
            }
            return false;
        }
    });

    $('#documentType').on('change', function() {
        var docTypeVal = $(this).val();
        if (docTypeVal && docTypeVal !== '') {
            sessionStorage.setItem('conditionVerification_documentType', docTypeVal);
        } else {
            sessionStorage.removeItem('conditionVerification_documentType');
        }
    });

    $(document).on('change', '.conditions-dropdown', function(e) {
        var dropdown = $(this);
        var conditionRecheckId = dropdown.data('condition-recheck-id');
        var selectedValue = dropdown.val();

        dropdown.prop('disabled', true);
        dropdown.css('background-color', '#fff3cd');

        var requestUrl = '/' + role + '/condition-verification/updateConditions?conditionRecheckId=' + conditionRecheckId;
        if (selectedValue !== '') {
            requestUrl += '&conditionsFullfilled=' + selectedValue;
        }

        $.ajax({
            url: requestUrl,
            method: 'POST',
            success: function(response) {
                dropdown.css('background-color', '#d4edda');
                dropdown.prop('disabled', false);

                setTimeout(function() {
                    dropdown.css('background-color', '');
                }, 1500);
            },
            error: function(xhr, status, error) {
                dropdown.css('background-color', '#f8d7da');
                dropdown.prop('disabled', false);

                setTimeout(function() {
                    dropdown.css('background-color', '');
                }, 2000);

                alert('Napaka pri shranjevanju: ' + error);
            }
        });
    });

    $(document).on('click', '.edit-text-btn', function(e) {
        e.preventDefault();
        e.stopPropagation();

        var button = $(this);
        var conditionRecheckId = button.data('condition-recheck-id');
        var currentText = button.data('text') || '';

        // Set values in modal
        $('#textConditionRecheckId').val(conditionRecheckId);
        $('#textAreaField').val(currentText);

        // Show modal - Bootstrap 5 way
        var myModal = new bootstrap.Modal(document.getElementById('editTextModal'));
        myModal.show();
    });

    $(document).on('click', '#saveTextBtn', function(e) {
        e.preventDefault();

        var conditionRecheckId = $('#textConditionRecheckId').val();
        var text = $('#textAreaField').val();

        var saveBtn = $(this);
        saveBtn.prop('disabled', true);
        saveBtn.html('<i class="fas fa-spinner fa-spin"></i> Shranjujem...');

        $.ajax({
            url: '/' + role + '/condition-verification/updateText',
            method: 'POST',
            contentType: 'application/x-www-form-urlencoded',
            data: {
                conditionRecheckId: conditionRecheckId,
                text: text
            },
            success: function(response) {
                // Show success
                saveBtn.html('<i class="fas fa-check"></i> Shranjeno');
                saveBtn.removeClass('btn-primary').addClass('btn-success');

                // Close modal after 1 second - Bootstrap 5 way
                setTimeout(function() {
                    var myModal = bootstrap.Modal.getInstance(document.getElementById('editTextModal'));
                    myModal.hide();

                    // Reset button
                    saveBtn.html('<i class="fas fa-save"></i> Shrani');
                    saveBtn.removeClass('btn-success').addClass('btn-primary');
                    saveBtn.prop('disabled', false);

                    // Reload table to show updated data
                    conditionVerificationTable.ajax.reload(null, false);
                }, 1000);
            },
            error: function(xhr, status, error) {
                saveBtn.html('<i class="fas fa-times"></i> Napaka');
                saveBtn.removeClass('btn-primary').addClass('btn-danger');

                setTimeout(function() {
                    saveBtn.html('<i class="fas fa-save"></i> Shrani');
                    saveBtn.removeClass('btn-danger').addClass('btn-primary');
                    saveBtn.prop('disabled', false);
                }, 2000);

                alert('Napaka pri shranjevanju besedila: ' + error);
            }
        });
    });

    $(document).on('click', '#exportExcel', function(e) {
        e.preventDefault();

        var button = $(this);
        var documentTypeVal = $('#documentType').val();
        var candidateVal = $('#search-kandidat').val();

        var exportUrl = '/'+ role + '/condition-verification/exportExcel';
        var params = [];

        if (documentTypeVal && documentTypeVal !== '') {
            params.push('documentType=' + encodeURIComponent(documentTypeVal));
        }
        if (candidateVal && candidateVal !== '') {
            params.push('candidateSearch=' + encodeURIComponent(candidateVal));
        }

        if (params.length > 0) {
            exportUrl += '?' + params.join('&');
        }

        var originalHtml = button.html();
        button.prop('disabled', true);
        button.html('<i class="fas fa-spinner fa-spin"></i> Izvažam...');

        window.location.href = exportUrl;

        setTimeout(function() {
            button.html(originalHtml);
            button.prop('disabled', false);
        }, 2000);
    });


    $('#sendClaimsBtn').on('click', function(e) {
        e.preventDefault();

        // Check if there are users to send to
        if ($('#countUsers').val() === 0) {
            alert('Ni uporabnikov za pošiljanje pozivov.');
            return;
        }

        const modalEl = document.getElementById('confirmSendModal');
        var confirmModal = bootstrap.Modal.getOrCreateInstance(modalEl);

        confirmModal.show();

    });

    $('#confirmSendBtn').on('click', function(e) {
        e.preventDefault();
        var confirmBtn = $(this);
        var originalHtml = confirmBtn.html();
        confirmBtn.prop('disabled', true);
        confirmBtn.html('<i class="fas fa-spinner fa-spin"></i> Pošiljam...');

        var docTypeId = $('#documentType').val();
        if (!docTypeId) {
            alert('Prosimo, izberite tip dokumenta.');
            confirmBtn.html(originalHtml);
            confirmBtn.prop('disabled', false);
            return;
        }

        $.ajax({
            url: '/admin/condition-verification/sendClaims?docTypeId=' + docTypeId,
            method: 'POST',
            dataType: 'json',
            success: function(response) {

                if (response.failedCount > 0) {
                    // Has errors - reset and close modal
                    confirmBtn.html(originalHtml);
                    confirmBtn.removeClass('btn-success').addClass('btn-primary');
                    confirmBtn.prop('disabled', false);

                    confirmModal.hide();

                    var message = 'Poslano: ' + response.sentCount + ' od ' + response.totalClaims + ' pozivov.';
                    message += '\n\nNapake (' + response.failedCount + '):';

                    response.failedClaims.forEach(function(failed, index) {
                        if (index < 10) {
                            message += '\n- User ID: ' + failed.userId +
                                ' (' + failed.company + '): ' + failed.reason;
                        }
                    });

                    if (response.failedCount > 10) {
                        message += '\n... in še ' + (response.failedCount - 10) + ' napak.';
                    }

                    alert(message);

                } else {
                    // All successful - show success in modal
                    confirmBtn.removeClass('btn-primary');
                    confirmBtn.addClass('btn-success');
                    confirmBtn.html('<i class="fas fa-check-circle"></i> Uspešno poslano');
                    confirmBtn.prop('disabled', true);

                    // Update modal body to show success message
                    $('#confirmSendModal .modal-body').html(
                        '<div class="alert alert-success mb-0">' +
                        '<i class="fas fa-check-circle"></i> ' +
                        'Vsi pozivi so bili uspešno poslani.<br>' +
                        'Poslano: <strong>' + response.sentCount + '</strong> sporočil.' +
                        '</div>'
                    );

                }
                $('#conditionVerificationList').ajax.reload(null, false);
            },
            error: function(xhr, status, error) {
                console.error('Failed to send claims:', error);

                // Reset button
                confirmBtn.html(originalHtml);
                confirmBtn.removeClass('btn-success').addClass('btn-primary');
                confirmBtn.prop('disabled', false);

                confirmModal.hide();

                var errorMessage = 'Napaka pri pošiljanju pozivov.';
                if (xhr.responseJSON && xhr.responseJSON.message) {
                    errorMessage = xhr.responseJSON.message;
                }
                alert(errorMessage);
            }
        });
    });

    $('#confirmSendModal').on('hidden.bs.modal', function () {

        var confirmBtn = $('#confirmSendBtn');

        // Reset button
        confirmBtn.html('<i class="fas fa-check"></i> Potrdi pošiljanje');
        confirmBtn.removeClass('btn-success').addClass('btn-primary');
        confirmBtn.prop('disabled', false);

        // Re-enable close buttons (in case they were disabled)
        $('#confirmSendModal [data-bs-dismiss="modal"]').prop('disabled', false);

        // Reset modal body
        $('#confirmSendModal .modal-body').html(
            '<p class="mb-3">Ali ste prepričani, da želite poslati pozive k izpolnitvi naknadnih izjav?</p>' +
            '<p>Poslano bo <strong>' + $('#countUsers').val() + '</strong> e-poštnih sporočil.</p>'
        );
    });

    $('#sendClaimsWithEmptyStatementBtn').on('click', function(e) {
        e.preventDefault();

        // Check if there are users to send to
        if ($('#countUsersWithEmptyStatement').val() === 0) {
            alert('Ni uporabnikov za pošiljanje pozivov.');
            return;
        }

        const modalEl = document.getElementById('confirmSendForEmptyStatementModal');
        var confirmModal = bootstrap.Modal.getOrCreateInstance(modalEl);

        confirmModal.show();

    });

    $('#confirmSendForEmptyStatementModalBtn').on('click', function(e) {
        e.preventDefault();
        var confirmBtn = $(this);
        var originalHtml = confirmBtn.html();
        confirmBtn.prop('disabled', true);
        confirmBtn.html('<i class="fas fa-spinner fa-spin"></i> Pošiljam...');

        var docTypeId = $('#documentType').val();
        if (!docTypeId) {
            alert('Prosimo, izberite tip dokumenta.');
            confirmBtn.html(originalHtml);
            confirmBtn.prop('disabled', false);
            return;
        }

        $.ajax({
            url: '/admin/condition-verification/sendClaimsAgain?docTypeId=' + docTypeId,
            method: 'POST',
            data: {
                email: $('#textForAdditionalClaim').val(),
                emailEn: $('#textForAdditionalClaimEn').val()
            },
            dataType: 'json',
            success: function(response) {

                confirmBtn.removeClass('btn-primary');
                confirmBtn.addClass('btn-success');
                confirmBtn.html('<i class="fas fa-check-circle"></i> Uspešno poslano');
                confirmBtn.prop('disabled', true);

                $('#confirmSendForEmptyStatementModal .modal-body').html(
                    '<div class="alert alert-success mb-0">' +
                    '<i class="fas fa-check-circle"></i> ' +
                    'Vsi pozivi so bili uspešno poslani.<br>' +
                    'Poslano: <strong>' + response.sentCount + '</strong> sporočil.' +
                    '</div>'
                );


                $('#conditionVerificationList').ajax.reload(null, false);
            },
            error: function(xhr, status, error) {
                console.error('Failed to send claims:', error);

                // Reset button
                confirmBtn.html(originalHtml);
                confirmBtn.removeClass('btn-success').addClass('btn-primary');
                confirmBtn.prop('disabled', false);

                confirmModal.hide();

                var errorMessage = 'Napaka pri pošiljanju pozivov.';
                if (xhr.responseJSON && xhr.responseJSON.message) {
                    errorMessage = xhr.responseJSON.message;
                }
                alert(errorMessage);
            }
        });
    });

    $('#confirmSendForEmptyStatementModal').on('hidden.bs.modal', function () {

        var confirmBtn = $('#confirmSendForEmptyStatementModal');

        // Reset button
        confirmBtn.html('<i class="fas fa-check"></i> Potrdi pošiljanje');
        confirmBtn.removeClass('btn-success').addClass('btn-primary');
        confirmBtn.prop('disabled', false);

        $('#confirmSendForEmptyStatementModal [data-bs-dismiss="modal"]').prop('disabled', false);

        $('#confirmSendForEmptyStatementModal .modal-body').html(
            '<p class="mb-3">Ali ste prepričani, da želite poslati ponovne pozive k izpolnitvi naknadnih izjav?</p>' +
            '<p>Poslano bo <strong>' + $('#countUsersWithEmptyStatement').val() + '</strong> e-poštnih sporočil.</p>'
        );
    });

    setTimeout(stickyFooter, 500);

}

var userRoleUrlPath = "";
var nominationId = "";
function UserNominationView(userRoleUrlPath) {
    $.fn.dataTableExt.oStdClasses.sPageButton = "btn btn-sm btn-secondary";
    $.fn.dataTableExt.oStdClasses.sPageButtonActive = "active";
    nominationId = $('#nominationId').val();

    var reportReadOnly = $('#nominacijskoPorociloBody').hasClass('read-only');
    // .permanent-readonly fields are always locked - never toggle their disabled state here.
    var $lockableFields = $('#nominacijskoPorociloBody').find('input, textarea, select').not('.permanent-readonly');
    var $selectionFields = $lockableFields.filter('select, input[type="checkbox"], input[type="radio"]');
    var $textFields = $lockableFields.not($selectionFields);
    $selectionFields.prop('disabled', reportReadOnly);
    $textFields.prop('readonly', reportReadOnly);

    // Clicking enter when focus is inside text input triggers form submission - prevent that and allow "Enter" click only on inputs of types specified bellow
    $('#osnovniPodatki').on('keydown', 'input:not([type=file], [type=button], [type=submit], [type=reset])', function (e) {
        let key = (e.keyCode || e.which);
        if (key === 13) {
            e.preventDefault();
        }
    })

    initDatePicker('bookmarkDate');
    if (userRoleUrlPath !== 'commission') {
        initDatePicker('meetingDate');
        initDatePicker('interviewDate');
    }
    if (userRoleUrlPath === 'admin') {
        initDatePicker('addendumDate');
    }
    initDatePicker('oldNominationDate');

    setTimeout(stickyFooter, 200);

    $('body').on('change', '.rejected .rejectButton input[type="checkbox"]', function () {
        syncToggleLabel(this, 'btn-warning');

        // hide openEmailModal button if no chechbox is selected
        if ($('#rejectedStatement1').is(':checked') || $('#rejectedStatement2').is(':checked') || $('#rejectedStatement3').is(':checked')) {
            $('.openEmailModal').removeClass('d-none');
        } else {
            $('.openEmailModal').addClass('d-none');
        }
    });

    // if statement is init checked
    $('.rejected').each(function () {
        var thisStatement = $(this);
        if ($(thisStatement).find('input[type="checkbox"][id^="rejectedStatement"]').is(':checked')) {
            $(thisStatement).find('.rejectButton input[type="checkbox"]').trigger('change');
        }
    });


    $(".select2-select").each(function () {
        if ($(this).data("max")) {
            $(this).select2({
                max_selected_options: $(this).data("max"),
                disable_search: true,
                width: "100%",
                allowClear: true,
                placeholder: $(this).attr("placeholder") || ""
            });
        } else {
            $(this).select2({
                disable_search: false,
                width: "100%",
                search_contains: true,
                placeholder_text_single: $(this).attr("placeholder"),
                no_results_text: "Ni zadetkov",
                allowClear: true,
                placeholder: $(this).attr("placeholder") || ""
            });
        }

    });

    $('#evaluationList').DataTable({
        searching: false,
        ordering: false,
        paging: true,
        info: false,
        destroy: true,
        "lengthChange": false,
        "pageLength": 10,
        "language": {
            "emptyTable": "Ni podatkov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        }
    });


    var nDoc = $('#nominacijskiDokumenti').DataTable({
        searching: false,
        ordering: true,
        "order": [[ 0, "asc" ]],
        info: false,
        "lengthChange": false,
        "pageLength": 10,
        "language": {
            "emptyTable": "Ni podatkov",
            "paginate": {
                "previous": "<",
                "next": ">"
            }
        },
        "processing": true,
        "serverSide": true,
        "ajax": {
            "url": "/" + userRoleUrlPath + "/user/nomination/" + nominationId + "/fillNominationDocuments",
            "data": function (reqParam) {
                reqParam.search = $('#searchNominationDocument').val();
                return reqParam;
            }
        },

        "columns": [
            {
                "data": "name",
                "render": function (data, type, row, meta) {
                    if (userRoleUrlPath === 'admin') {
                        return '<td class="nameCell">' +
                            '<div class="viewMode"><span class="filename">' + data + '</span><button type="button" class="a11y-icon-btn renameFile" style="padding: 0px 10px;"><i class="fas fa-pencil" aria-hidden="true"></i><span class="visually-hidden">Preimenuj datoteko</span></button></div>' +
                            '<div class="editMode d-none"><input type="text" class="form-control form-control-sm" value="' + data + '" style="width: 100%;"/><button type="button" class="a11y-icon-btn saveRenamedFile" style="padding: 0px 10px;"><i class="fas fa-save" aria-hidden="true"></i><span class="visually-hidden">Shrani novo ime datoteke</span></button><button type="button" class="a11y-icon-btn cancelRenamingFile" style="padding: 0px 10px;"><i class="fas fa-times" aria-hidden="true"></i><span class="visually-hidden">Prekliči preimenovanje</span></button></div>' +
                            '</td>';
                    } else {
                        return '<td class="nameCell">' +
                            '<div class="viewMode"><span class="filename">' + data + '</span></div>' +
                            '</td>';
                    }
                }
            },
            {
                "data": "date",
                "render": function (data, type, row, meta) {
                    return (type === 'sort' || type === 'order') ? data : '<td data-order="' + data + '"  data-sort="' + data + '">' +
                        '<div>' + moment.unix(data/1000).format('DD. MM. YYYY') + '</div>' +
                        '</td>';
                }
            },
            {
                "data": "plain",
                "render": function (data, type, row, meta) {
                    if (userRoleUrlPath === 'admin') {
                        return '<td><button type="button" class="a11y-icon-btn deleteItem" data-toggle="tooltip" title="" data-original-title="Odstrani datoteko" style="margin-left : 25%"><i class="fas fa-trash" aria-hidden="true"></i><span class="visually-hidden">Odstrani datoteko</span></button></td>';
                    }
                    else {
                        return '<td/>';
                    }
                }
            }
        ]
    });

    nDoc.on('keydown', '#searchNominationDocument', function (e) {
        let key = (e.keyCode || e.which);
        if (key === 13) {
            nDoc.ajax.reload();
        }
    });

    nDoc.on('click', '.deleteItem', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        $(this).parents('tr').removeClass('selected');
        deleteFiles($(this), 'deleteNominationDocuments', nDoc);
    });
    nDoc.on('click', '.renameFile', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        $(this).parents('tr').find('.viewMode').addClass('d-none');
        $(this).parents('tr').find('.editMode').removeClass('d-none');
    });
    nDoc.on('click', '.cancelRenamingFile', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        $(this).parents('tr').find('.viewMode').removeClass('d-none');
        $(this).parents('tr').find('.editMode').addClass('d-none');
        $(this).parents('tr').find('editMode input').val($(this).parents('tr').find('.viewMode .filename').text());
    });
    nDoc.on('click', '.editMode input', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
    });
    nDoc.on('click', '.saveRenamedFile', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        var newName = $(this).parents('tr').find('.editMode input').val();
        var itemId = nDoc.row($(this).parents('tr')).data().id;

        $(this).parents('.nameCell').find('.viewMode').removeClass('d-none');
        $(this).parents('.nameCell').find('.editMode').addClass('d-none');

        renameFile(itemId, newName, nDoc, "/admin/user/nomination/" + nominationId);
    });
    nDoc.on('click', 'tbody tr', function () {
        if ($(this).hasClass('selected')) {
            var thisRow = $(this);
            var fileId = nDoc.row(thisRow).data().file;
            var win = window.open("/" + userRoleUrlPath + "/file/" + fileId, '_blank');
            win.focus();
        } else {
            nDoc.$('tr.selected').removeClass('selected');
            $(this).addClass('selected');
        }
    });

    $('body').on('click', '.openUpload1Modal', function (e) {
        e.preventDefault();
        var modal = $('#uploadModal');
        $(modal).find('.modal-title').text("").text("Naloži izjavo");
        $('#statementNumber').val("1")
        bootstrap.Modal.getOrCreateInstance(modal[0]).show();
    });


    $('body').on('click', '.openUpload2Modal', function (e) {
        e.preventDefault();
        var modal = $('#uploadModal');
        $(modal).find('.modal-title').text("").text("Naloži izjavo");
        $('#statementNumber').val("2")
        bootstrap.Modal.getOrCreateInstance(modal[0]).show();
    });

    $('body').on('click', '.openUpload3Modal', function (e) {
        e.preventDefault();
        var modal = $('#uploadModal');
        $(modal).find('.modal-title').text("").text("Naloži izjavo");
        $('#statementNumber').val("3")
        bootstrap.Modal.getOrCreateInstance(modal[0]).show();
    });


    // add attachment
    $('body').on('change', 'input[type="file"]', function () {
        var thisInput = $(this);
        var thisDroppableArea = $(thisInput).closest('.droppableArea');
        $(thisDroppableArea).removeClass('droppable');

        if (this.files.length == 1) {

            var correctFileType = false;
            if (this.files[0].type == 'application/pdf' || this.files[0].type == 'application/x-pdf') {
                correctFileType = true;
            }

            if (!correctFileType) {
                $(thisDroppableArea).find('p').html("<em class='text-danger' style='font-size: 18px;'><i class='fas fa-exclamation-triangle'></i>" + formBwrongFormat + "</em>");
                setTimeout(function () {
                    $(thisInput).val('');
                    $(thisDroppableArea).find('p').html("<em>" + formBattachmentHelp + "<em>");
                }, 3000);
                $(thisDroppableArea).find('button').hide();
                return;
            }

            if (this.files[0].size <= (20 * 1024 * 1024) && correctFileType) {
                $(thisDroppableArea).find('p').html("<em>" + userDashboardChosenFile + ":</em> <b>" + this.files[0].name + "</b>.");
                $(thisDroppableArea).find('button').fadeIn();
            } else {
                $(thisDroppableArea).find('p').html("<em class='text-danger' style='font-size: 18px;'><i class='fas fa-exclamation-triangle'></i>" + formBsizeError + "</em>");
                setTimeout(function () {
                    $(thisInput).val('');
                    $(thisDroppableArea).find('p').html("<em>" + formBattachmentHelp + "<em>");
                }, 3000);
                $(thisDroppableArea).find('button').hide();
            }
        } else {
            $(thisDroppableArea).find('p').html("<em>" + formBattachmentHelp + "<em>");
            $(thisDroppableArea).find('button').hide();
        }

    });

    $(".droppableArea").on("dragover", function (event) {
        event.preventDefault();
        event.stopPropagation();
        $(this).addClass('droppable');
    });

    $(".droppableArea").on("dragleave", function (event) {
        event.preventDefault();
        event.stopPropagation();
        $(this).removeClass('droppable');
    });

    $('body').on('click', '.upload', function (e) {
        e.preventDefault();

        var thisButton = $(this);
        var thisSection = $(thisButton).closest('.uploadSectionItem');
        var thisDataSection = $(thisSection).data('section');
        var thisInput = $(thisButton).closest('.input-group').find('input[type="file"]');

        var postUrl;
        var formData = new FormData();

        // Determine URL and form data based on section
        if (thisDataSection === 'nominationDocument') {
            var nominationId = $('#nominationId').val();
            postUrl = '/' + userRoleUrlPath + '/user/nomination/' + nominationId;
            formData.append('action', 'uploadNominationDocuments');
            formData.append('file', $(thisInput)[0].files[0]);
        } else {
            postUrl = $('form').attr('action');
            formData.append('action', 'uploadStatement');
            formData.append('file', $(thisInput)[0].files[0]);
            formData.append('statementNumber', $('#statementNumber').val());
        }

        $(thisButton).hide();
        $(thisInput).parents('.droppableArea').find('p').prepend(' <i class="fas fa-circle-notch fa-spin"></i> ');

        $.ajax({
            url: postUrl,
            data: formData,
            type: 'POST',
            contentType: false,
            processData: false,
        })
            .fail(function (response) {
                console.log(response);
                alert(formBerrorFile);
                $(thisButton).show();
                $('div.loader').remove();

            })
            .done(function (response) {
                if (thisDataSection === 'nominationDocument') {
                    $(thisInput).val('').trigger('change');
                    $('div.loader').remove();
                    $('#nominacijskiDokumenti').val('').trigger('change');
                    nDoc.row.add($('<tr data-file-id="' + response.file.id + '" data-id="' + response.id + '"><td>' + response.name + '</td><td>' + response.date + '</td><td class="text-center"><button type="button" class="a11y-icon-btn deleteItem" data-toggle="tooltip" title="" data-placement="right" data-original-title="Odstrani datoteko"><i class="fas fa-trash" aria-hidden="true"></i><span class="visually-hidden">Odstrani datoteko</span></button></td></tr>')[0]).draw();
                }
                else {
                    window.location.href = postUrl;
                }
            });
    });

    $('body').on('click', '.openEmailModal', function (e) {
        e.preventDefault();
        var thisBtn = $(this);
        var company = $('#company').val();
        var nominationId = $('#nominationId').val();

        showEmailModal(nominationId, company, nominationId + "/", null);
    });


    $('body').on('click', '.openEmailFinishModal', function (e) {
        e.preventDefault();
        var thisBtn = $(this);
        var company = $('#company').val();
        var nominationId = $('#nominationId').val();

        showEmailFinishModal(nominationId, company, nominationId + "/");
    });

    $('body').on('click', '.openEmailDeleteModal', function (e) {
        e.preventDefault();
        var thisBtn = $(this);
        var company = $('#company').val();
        var nominationId = $('#nominationId').val();

        showDeleteEmailModal(nominationId, company, nominationId + "/");
    });

    $('body').on('click', '.openEmailFinishUpdatingDataModal', function (e) {
        e.preventDefault();
        var thisBtn = $(this);
        var company = $('#company').val();
        var nominationId = $('#nominationId').val();

        showFinishUpdatingDataEmailModal(nominationId, company, nominationId + "/");
    });


    // open panel if addidtional info textarea is filled
    $('textarea').each(function () {
        var thisTextarea = $(this);
        if (String($(thisTextarea).val() ?? '').trim()) {
            $(thisTextarea).closest('.collapse').addClass('show');
        }

    });

    var fontsizevariable = "19";
    var fontfamilyvariable = "Arial, Verdana, sans-serif";

    var elementExists = document.getElementById("writtenRemark");
    if (elementExists) {
        // Wait for CKEditor to be ready before initializing
        if (typeof window.CKEditorReady !== 'undefined') {
            window.CKEditorReady.then(function() {
                tinymce.init({
                    selector: '#writtenRemark',
                    readonly: reportReadOnly,
                    license_key: 'gpl',
                    language: 'sl-SI',
                    height: 800,
                    menubar: false,
                    skin: false,
                    content_css: false,
                    contextmenu: 'paste pastetext | cut copy | link unlink | removeformat',
                    plugins: ['link', 'lists', 'code', 'fullscreen'],
                    toolbar: 'bold italic underline strikethrough | forecolor backcolor | subscript superscript | bullist numlist | link unlink | removeformat | cut copy paste pastetext | undo redo | code fullscreen',
                    content_style: 'body { font-family: Arial; font-size: 14px; }',
                    paste_as_text: false,
                    paste_remove_styles_if_webkit: true,
                    paste_strip_class_attributes: 'all',
                    paste_retain_style_properties: 'none',
                    paste_postprocess: function(plugin, args) {
                        const ALLOWED_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'A', 'P', 'BR', 'UL', 'OL', 'LI']);
                        const clean = function(node) {
                            const children = Array.from(node.childNodes);
                            children.forEach(function(child) {
                                if (child.nodeType === Node.TEXT_NODE) {
                                    return;
                                }
                                if (child.nodeType === Node.ELEMENT_NODE) {
                                    const tag = child.tagName.toUpperCase();

                                    clean(child);

                                    if (ALLOWED_TAGS.has(tag)) {
                                        Array.from(child.attributes).forEach(function(attr) {
                                            if (tag === 'A' && (attr.name === 'href' || attr.name === 'target')) {
                                                return;
                                            }
                                            child.removeAttribute(attr.name);
                                        });
                                    } else {
                                        const parent = child.parentNode;
                                        while (child.firstChild) {
                                            parent.insertBefore(child.firstChild, child);
                                        }
                                        parent.removeChild(child);
                                    }
                                }
                            });
                        };
                        clean(args.node);
                    },
        setup: function(editor) {
            editor.on('init', function() {
                stickyFooter();
            });
        }
    });
            });
        } else {
            console.error('CKEditorReady promise not found!');
            stickyFooter();
        }
    } else {
        stickyFooter();
    }

    var bookmarkTextElement = document.getElementById("bookmarkText");
    if (bookmarkTextElement && typeof window.CKEditorReady !== 'undefined') {
        var bookmarkTextReadOnly = bookmarkTextElement.disabled;
        window.CKEditorReady.then(function () {
            tinymce.init({
                selector: '#bookmarkText',
                readonly: bookmarkTextReadOnly,
                license_key: 'gpl',
                language: 'sl-SI',
                height: 500,
                menubar: false,
                skin: false,
                content_css: false,
                contextmenu: 'paste pastetext | cut copy | link unlink | removeformat',
                plugins: ['link', 'lists', 'code', 'fullscreen'],
                toolbar: 'bold italic underline strikethrough | forecolor backcolor | subscript superscript | bullist numlist | link unlink | removeformat | cut copy paste pastetext | undo redo | code fullscreen',
                content_style: 'body { font-family: Arial; font-size: 14px; }',
                paste_as_text: false,
                paste_remove_styles_if_webkit: true,
                paste_strip_class_attributes: 'all',
                paste_retain_style_properties: 'none',
                paste_postprocess: function (plugin, args) {
                    const ALLOWED_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'A', 'P', 'BR', 'UL', 'OL', 'LI']);
                    const clean = function (node) {
                        const children = Array.from(node.childNodes);
                        children.forEach(function (child) {
                            if (child.nodeType === Node.TEXT_NODE) {
                                return;
                            }
                            if (child.nodeType === Node.ELEMENT_NODE) {
                                const tag = child.tagName.toUpperCase();

                                clean(child);

                                if (ALLOWED_TAGS.has(tag)) {
                                    Array.from(child.attributes).forEach(function (attr) {
                                        if (tag === 'A' && (attr.name === 'href' || attr.name === 'target')) {
                                            return;
                                        }
                                        child.removeAttribute(attr.name);
                                    });
                                } else {
                                    const parent = child.parentNode;
                                    while (child.firstChild) {
                                        parent.insertBefore(child.firstChild, child);
                                    }
                                    parent.removeChild(child);
                                }
                            }
                        });
                    };
                    clean(args.node);
                }
            });
        });
    }

    $('body').on('click', '.start-editing-btn', function (e) {
        e.preventDefault();

        var thisButton = $(this);
        var postUrl = '/' + userRoleUrlPath + '/user/nomination/' + nominationId;

        var formData = new FormData();
        formData.append('action', 'startEditingNomination');

        $(thisButton).prop('disabled', true);
        $(thisButton).append(' <i class="fas fa-circle-notch fa-spin"></i>');

        $.ajax({
            url: postUrl,
            data: formData,
            type: 'POST',
            contentType: false,
            processData: false,
        })
            .fail(function (response) {
                if (response.status === 409 && response.responseJSON) {
                    alert('Poročilo trenutno ureja: ' + response.responseJSON.editorName);
                } else {
                    alert('Pri urejanju je prišlo do napake');
                }
                window.location.reload(); // refresh to show the current real state
            })
            .done(function (response) {
                window.location.reload(); // server now renders editingByMe=true, opens the body
            });
    });

    $('body').on('click', '.end-editing-btn', function (e) {
        e.preventDefault();

        var thisButton = $(this);
        var thisTarget = $($(thisButton).data('target'));

        var postUrl = '/' + userRoleUrlPath + '/user/nomination/' + nominationId;

        var formData = new FormData();
        formData.append('action', 'endEditingNomination');

        $(thisButton).prop('disabled', true);
        $(thisButton).append(' <i class="fas fa-circle-notch fa-spin"></i>');

        $.ajax({
            url: postUrl,
            data: formData,
            type: 'POST',
            contentType: false,
            processData: false,
        })
            .fail(function (response) {
                console.log(response);
                alert("Pri zaključevanju urejanja je prišlo do napake");
                $(thisButton).prop('disabled', false);
                $(thisButton).find('.fa-circle-notch').remove();
            })
            .done(function (response) {
                window.location.reload();
            });
    });

    $('body').on('click', '.force-end-editing-btn', function (e) {
        e.preventDefault();

        // if (!confirm('Ali res želite prekiniti urejanje drugemu uporabniku? Njegove neshranjene spremembe bodo izgubljene.')) {
        //     return;
        // }

        var thisButton = $(this);
        var postUrl = '/' + userRoleUrlPath + '/user/nomination/' + nominationId;

        var formData = new FormData();
        formData.append('action', 'forceEndEditingNomination');

        $(thisButton).prop('disabled', true);
        $(thisButton).append(' <i class="fas fa-circle-notch fa-spin"></i>');

        $.ajax({
            url: postUrl,
            data: formData,
            type: 'POST',
            contentType: false,
            processData: false,
        })
            .fail(function (response) {
                console.log(response);
                alert("Pri prekinitvi tujega urejanja je prišlo do napake");
                $(thisButton).prop('disabled', false);
                $(thisButton).find('.fa-circle-notch').remove();
            })
            .done(function (response) {
                window.location.reload();
            });
    });

    $(document).ready(function() {
        // Force close all accordions on page load
        $('#collapseDocuments').removeClass('show');
        // $('.accordion-button').addClass('collapsed').attr('aria-expanded', 'false');

        if ($('#openAddendum').val() === 'true') {
            $('#collapseAddendum').addClass('show');
            $('[data-bs-target="#collapseAddendum"]').removeClass('collapsed').attr('aria-expanded', 'true');
        } else {
            $('#collapseAddendum').removeClass('show');
        }

        if ($('#openBookmark').val() === 'true') {
            $('#collapseBookmark').addClass('show');
            $('[data-bs-target="#collapseBookmark"]').removeClass('collapsed').attr('aria-expanded', 'true');
        } else {
            $('#collapseBookmark').removeClass('show');
        }
    });
}


function nominationAdd() {

    initDatePicker('date');

    $(".select2-select").each(function () {
        if ($(this).data("max")) {
            $(this).select2({
                max_selected_options: $(this).data("max"),
                disable_search: true,
                width: "100%",
                allowClear: true,
                placeholder: $(this).attr("placeholder") || ""
            });
        } else {
            $(this).select2({
                disable_search: false,
                width: "100%",
                search_contains: true,
                placeholder_text_single: $(this).attr("placeholder"),
                no_results_text: "Ni zadetkov",
                allowClear: true,
                placeholder: $(this).attr("placeholder") || ""
            });
        }

    });

    $('body').on('click', '.openEmailModal', function (e) {
        e.preventDefault();
        var thisBtn = $(this);
        var company = $('#company').val();
        var candidate = $('#candidate').val();
        var poslovodstvo = $('#nominationPurpose').val();

        checkNominationRequirements({
            candidate: candidate,
            onContinue: function () {
                showEmailModal(candidate, company, "", poslovodstvo === 'OV');
            }
        })
    });
}

// Options
// candidate -> candidate id
// onContinue -> function to fire if continuing
function checkNominationRequirements(options) {
    $.ajax({
        type: "GET",
        url: "/admin/nomination-add/" + options.candidate + "/nomination-date",
        success: function (response) {
            if (!response) {
                options.onContinue()
            } else {
                Swal.fire({
                    title: formA2warning,
                    text: shouldNotNominateText.replace("{datum}", response),
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#DD6B55",
                    confirmButtonText: formA2confirm,
                    cancelButtonText: formA2cancel,
                }).then((result) => {
                    if (result.isConfirmed) {
                    options.onContinue()
                    }
                })
            }
                    }
    });
}

function showEmailModal(candidate, company, url, poslovodstvo) {
    var modal = $('#emailModal');
    var modalTitle = "Urejanje elektronskega sporočila";


    var editorContent = '';

    $.ajax({
        type: "GET",
        url: url + "getEmailContent",
        data: {
            candidate: candidate,
            company: company,
            poslovodstvo: poslovodstvo,
            doNotNeedATest: $('#doNotNeedATest').prop('checked') === true
        },
        success: function (response) {
            if (tinymce.get('email') != null) {
                tinymce.get('email').setContent(response)
            }

            $('#emailModal #email').val(response);
            $(modal).find('.modal-title').text("").text(modalTitle);
            bootstrap.Modal.getOrCreateInstance(modal[0]).show();
        }
    });

    $(modal).on('shown.bs.modal', function () {

        if (tinymce.get('email') != null) {
            // TinyMCE is already initialized
            return;
        }

        var fontsizevariable = "19";
        var fontfamilyvariable = "Arial, Verdana, sans-serif";

        tinymce.init({
        selector: '#email',
            license_key: 'gpl',
            language: 'sl-SI',
            height: 800,
            menubar: false,
            skin: false,
            content_css: false,
            contextmenu: 'paste pastetext | cut copy | link unlink | removeformat',
            plugins: ['link', 'lists', 'code', 'fullscreen'],
            toolbar: 'bold italic underline strikethrough | forecolor backcolor | subscript superscript | bullist numlist | link unlink | removeformat | cut copy paste pastetext | undo redo | code fullscreen',
            content_style: 'body { font-family: Arial; font-size: 14px; }',
            paste_as_text: false,
            paste_remove_styles_if_webkit: true,
            paste_strip_class_attributes: 'all',
            paste_retain_style_properties: 'none',
            paste_postprocess: function(plugin, args) {
                const ALLOWED_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'A', 'P', 'BR', 'UL', 'OL', 'LI']);
                const clean = function(node) {
                    const children = Array.from(node.childNodes);
                    children.forEach(function(child) {
                        if (child.nodeType === Node.TEXT_NODE) {
                            return;
                        }
                        if (child.nodeType === Node.ELEMENT_NODE) {
                            const tag = child.tagName.toUpperCase();

                            clean(child);

                            if (ALLOWED_TAGS.has(tag)) {
                                Array.from(child.attributes).forEach(function(attr) {
                                    if (tag === 'A' && (attr.name === 'href' || attr.name === 'target')) {
                                        return;
                                    }
                                    child.removeAttribute(attr.name);
                                });
                            } else {
                                const parent = child.parentNode;
                                while (child.firstChild) {
                                    parent.insertBefore(child.firstChild, child);
                                }
                                parent.removeChild(child);
                            }
                        }
                    });
                };
                clean(args.node);
            },
        setup: function(editor) {
            editor.on('init', function() {
                stickyFooter();
            });
        }
    });

    });
}


function showEmailFinishModal(candidate, company, url) {
    var modal = $('#emailFinishModal');
    var modalTitle = "Urejanje elektronskega sporočila";


    var editorContent = '';

    $.ajax({
        type: "GET",
        url: url + "finish/getEmailContent",
        data: {
            candidate: candidate,
            company: company
        },
        success: function (response) {
            $('#emailFinishModal #emailFinish').val(response);
            $(modal).find('.modal-title').text("").text(modalTitle);
            bootstrap.Modal.getOrCreateInstance(modal[0]).show();
        }
    });

    $(modal).on('shown.bs.modal', function () {

        var fontsizevariable = "19";
        var fontfamilyvariable = "Arial, Verdana, sans-serif";

        tinymce.init({
        selector: '#emailFinish',
            license_key: 'gpl',
            language: 'sl-SI',
            height: 800,
            menubar: false,
            skin: false,
            content_css: false,
            contextmenu: 'paste pastetext | cut copy | link unlink | removeformat',
            plugins: ['link', 'lists', 'code', 'fullscreen'],
            toolbar: 'bold italic underline strikethrough | forecolor backcolor | subscript superscript | bullist numlist | link unlink | removeformat | cut copy paste pastetext | undo redo | code fullscreen',
            content_style: 'body { font-family: Arial; font-size: 14px; }',
            paste_as_text: false,
            paste_remove_styles_if_webkit: true,
            paste_strip_class_attributes: 'all',
            paste_retain_style_properties: 'none',
            paste_postprocess: function(plugin, args) {
                const ALLOWED_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'A', 'P', 'BR', 'UL', 'OL', 'LI']);
                const clean = function(node) {
                    const children = Array.from(node.childNodes);
                    children.forEach(function(child) {
                        if (child.nodeType === Node.TEXT_NODE) {
                            return;
                        }
                        if (child.nodeType === Node.ELEMENT_NODE) {
                            const tag = child.tagName.toUpperCase();

                            clean(child);

                            if (ALLOWED_TAGS.has(tag)) {
                                Array.from(child.attributes).forEach(function(attr) {
                                    if (tag === 'A' && (attr.name === 'href' || attr.name === 'target')) {
                                        return;
                                    }
                                    child.removeAttribute(attr.name);
                                });
                            } else {
                                const parent = child.parentNode;
                                while (child.firstChild) {
                                    parent.insertBefore(child.firstChild, child);
                                }
                                parent.removeChild(child);
                            }
                        }
                    });
                };
                clean(args.node);
            },
        setup: function(editor) {
            editor.on('init', function() {
                stickyFooter();
            });
        }
    });

    });
}

function showDeleteEmailModal(candidate, company, url) {
    var modal = $('#emailDeleteModal');
    var modalTitle = "Urejanje elektronskega sporočila";

    var editorContent = '';

    $.ajax({
        type: "GET",
        url: url + "getEmailDeleteContent",
        data: {
            candidate: candidate,
            company: company
        },
        success: function (response) {

            $('#emailDeleteModal #emailDelete').val(response);
            $(modal).find('.modal-title').text("").text(modalTitle);
            bootstrap.Modal.getOrCreateInstance(modal[0]).show();
        }
    });

    $(modal).on('shown.bs.modal', function () {

        var fontsizevariable = "19";
        var fontfamilyvariable = "Arial, Verdana, sans-serif";

        tinymce.init({
        selector: '#emailDelete',
            license_key: 'gpl',
            language: 'sl-SI',
            height: 800,
            menubar: false,
            skin: false,
            content_css: false,
            contextmenu: 'paste pastetext | cut copy | link unlink | removeformat',
            plugins: ['link', 'lists', 'code', 'fullscreen'],
            toolbar: 'bold italic underline strikethrough | forecolor backcolor | subscript superscript | bullist numlist | link unlink | removeformat | cut copy paste pastetext | undo redo | code fullscreen',
            content_style: 'body { font-family: Arial; font-size: 14px; }',
            paste_as_text: false,
            paste_remove_styles_if_webkit: true,
            paste_strip_class_attributes: 'all',
            paste_retain_style_properties: 'none',
            paste_postprocess: function(plugin, args) {
                const ALLOWED_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'A', 'P', 'BR', 'UL', 'OL', 'LI']);
                const clean = function(node) {
                    const children = Array.from(node.childNodes);
                    children.forEach(function(child) {
                        if (child.nodeType === Node.TEXT_NODE) {
                            return;
                        }
                        if (child.nodeType === Node.ELEMENT_NODE) {
                            const tag = child.tagName.toUpperCase();

                            clean(child);

                            if (ALLOWED_TAGS.has(tag)) {
                                Array.from(child.attributes).forEach(function(attr) {
                                    if (tag === 'A' && (attr.name === 'href' || attr.name === 'target')) {
                                        return;
                                    }
                                    child.removeAttribute(attr.name);
                                });
                            } else {
                                const parent = child.parentNode;
                                while (child.firstChild) {
                                    parent.insertBefore(child.firstChild, child);
                                }
                                parent.removeChild(child);
                            }
                        }
                    });
                };
                clean(args.node);
            },
        setup: function(editor) {
            editor.on('init', function() {
                stickyFooter();
            });
        }
    });

    });
}

function showFinishUpdatingDataEmailModal(candidate, company, url) {
    var modal = $('#emailFinishUpdatingDataModal');
    var modalTitle = "Urejanje elektronskega sporočila";

    var editorContent = '';

    $.ajax({
        type: "GET",
        url: url + "getEmailFinishUpdatingDataContent",
        data: {
            candidate: candidate,
            company: company
        },
        success: function (response) {

            $('#emailFinishUpdatingDataModal #emailFinishUpdatingData').val(response);
            $(modal).find('.modal-title').text("").text(modalTitle);
            bootstrap.Modal.getOrCreateInstance(modal[0]).show();
        }
    });

    $(modal).on('shown.bs.modal', function () {

        var fontsizevariable = "19";
        var fontfamilyvariable = "Arial, Verdana, sans-serif";

        tinymce.init({
        selector: '#emailFinishUpdatingData',
            license_key: 'gpl',
            language: 'sl-SI',
            height: 800,
            menubar: false,
            skin: false,
            content_css: false,
            contextmenu: 'paste pastetext | cut copy | link unlink | removeformat',
            plugins: ['link', 'lists', 'code', 'fullscreen'],
            toolbar: 'bold italic underline strikethrough | forecolor backcolor | subscript superscript | bullist numlist | link unlink | removeformat | cut copy paste pastetext | undo redo | code fullscreen',
            content_style: 'body { font-family: Arial; font-size: 14px; }',
            paste_as_text: false,
            paste_remove_styles_if_webkit: true,
            paste_strip_class_attributes: 'all',
            paste_retain_style_properties: 'none',
            paste_postprocess: function(plugin, args) {
                const ALLOWED_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'A', 'P', 'BR', 'UL', 'OL', 'LI']);
                const clean = function(node) {
                    const children = Array.from(node.childNodes);
                    children.forEach(function(child) {
                        if (child.nodeType === Node.TEXT_NODE) {
                            return;
                        }
                        if (child.nodeType === Node.ELEMENT_NODE) {
                            const tag = child.tagName.toUpperCase();

                            clean(child);

                            if (ALLOWED_TAGS.has(tag)) {
                                Array.from(child.attributes).forEach(function(attr) {
                                    if (tag === 'A' && (attr.name === 'href' || attr.name === 'target')) {
                                        return;
                                    }
                                    child.removeAttribute(attr.name);
                                });
                            } else {
                                const parent = child.parentNode;
                                while (child.firstChild) {
                                    parent.insertBefore(child.firstChild, child);
                                }
                                parent.removeChild(child);
                            }
                        }
                    });
                };
                clean(args.node);
            },
        setup: function(editor) {
            editor.on('init', function() {
                stickyFooter();
            });
        }
    });

    });
}

function showModalToSendEmail(modalName, userId) {
    var modal = $('#emailModal');
    var modalTitle = "Urejanje elektronskega sporočila";

    var editorContent = '';

    $.ajax({
        type: "GET",
        url: '/admin/user/' + userId + '/getEmail',
        success: function (response) {

            $('#emailModal #emailData').val(response);
            $(modal).find('.modal-title').text("").text(modalTitle);

            // Bootstrap 5: Get or create Modal instance
            var bsModal = bootstrap.Modal.getOrCreateInstance(modal[0]);
            bsModal.show();
        }
    });

    // Bootstrap 5: Use native event listener
    modal[0].addEventListener('shown.bs.modal', function () {

        var fontsizevariable = "19";
        var fontfamilyvariable = "Arial, Verdana, sans-serif";

        tinymce.init({
        selector: '#emailData',
            license_key: 'gpl',
            language: 'sl-SI',
            height: 800,
            menubar: false,
            skin: false,
            content_css: false,
            contextmenu: 'paste pastetext | cut copy | link unlink | removeformat',
            plugins: ['link', 'lists', 'code', 'fullscreen'],
            toolbar: 'bold italic underline strikethrough | forecolor backcolor | subscript superscript | bullist numlist | link unlink | removeformat | cut copy paste pastetext | undo redo | code fullscreen',
            content_style: 'body { font-family: Arial; font-size: 14px; }',
            paste_as_text: false,
            paste_remove_styles_if_webkit: true,
            paste_strip_class_attributes: 'all',
            paste_retain_style_properties: 'none',
            paste_postprocess: function(plugin, args) {
                const ALLOWED_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'A', 'P', 'BR', 'UL', 'OL', 'LI']);
                const clean = function(node) {
                    const children = Array.from(node.childNodes);
                    children.forEach(function(child) {
                        if (child.nodeType === Node.TEXT_NODE) {
                            return;
                        }
                        if (child.nodeType === Node.ELEMENT_NODE) {
                            const tag = child.tagName.toUpperCase();

                            clean(child);

                            if (ALLOWED_TAGS.has(tag)) {
                                Array.from(child.attributes).forEach(function(attr) {
                                    if (tag === 'A' && (attr.name === 'href' || attr.name === 'target')) {
                                        return;
                                    }
                                    child.removeAttribute(attr.name);
                                });
                            } else {
                                const parent = child.parentNode;
                                while (child.firstChild) {
                                    parent.insertBefore(child.firstChild, child);
                                }
                                parent.removeChild(child);
                            }
                        }
                    });
                };
                clean(args.node);
            },
        setup: function(editor) {
            editor.on('init', function() {
                stickyFooter();
            });
        }
    });
    });
}

function comparison() {
    $('.ba-slider').each(function () {
        var cur = $(this);
        // Adjust the slider
        var width = cur.width() + 'px';
        var height = cur.height() + 'px';
        cur.find('.resize .accreditationPreviewOld').css('width', width).css('height', height);
        // cur.find('.resize div iframe').css('width', width).css('height', height);
        // Bind dragging events
        drags(cur.find('.handle'), cur.find('.resize'), cur);
    });

    // Update sliders on resize.
    // We all do it: i.imgur.com/YkbaV.gif
    $(window).on('resize', function () {
        $('.ba-slider').each(function () {
            var cur = $(this);
            var width = cur.width() + 'px';
            var height = cur.height() + 'px';
            cur.find('.resize .accreditationPreviewOld').css('width', width).css('height', height);
            // cur.find('.resize div iframe').css('width', width).css('height', height);
        });
    });

    $(".select2-select").each(function () {
        if ($(this).data("max")) {
            $(this).select2({
                max_selected_options: $(this).data("max"),
                disable_search: true,
                width: "100%",
                allowClear: true,
                placeholder: $(this).attr("placeholder") || ""
            });
        } else {
            $(this).select2({
                disable_search: false,
                width: "100%",

                search_contains: true,
                placeholder_text_single: $(this).attr("placeholder"),
                no_results_text: "Ni zadetkov",
                allowClear: true,
                placeholder: $(this).attr("placeholder") || ""
            });
        }

    });
}

function drags(dragElement, resizeElement, container) {

    // Initialize the dragging event on mousedown.
    dragElement.on('mousedown touchstart', function (e) {

        dragElement.addClass('draggable');
        resizeElement.addClass('resizable');

        // Check if it's a mouse or touch event and pass along the correct value
        var startX = (e.pageX) ? e.pageX : e.originalEvent.touches[0].pageX;

        // Get the initial position
        var dragWidth = dragElement.outerWidth(),
            posX = dragElement.offset().left + dragWidth - startX,
            containerOffset = container.offset().left,
            containerWidth = container.outerWidth();

        // Set limits
        var minLeft = containerOffset + 10;
        var maxLeft = containerOffset + containerWidth - dragWidth - 10;

        // Calculate the dragging distance on mousemove.
        dragElement.parents().on("mousemove touchmove", function (e) {

            // Check if it's a mouse or touch event and pass along the correct value
            var moveX = (e.pageX) ? e.pageX : e.originalEvent.touches[0].pageX;

            var leftValue = moveX + posX - dragWidth;

            // Prevent going off limits
            if (leftValue < minLeft) {
                leftValue = minLeft;
            } else if (leftValue > maxLeft) {
                leftValue = maxLeft;
            }

            // Translate the handle's left value to masked divs width.
            var widthValue = (leftValue + dragWidth / 2 - containerOffset) * 100 / containerWidth + '%';

            // Set the new values for the slider and the handle.
            // Bind mouseup events to stop dragging.
            $('.draggable').css('left', widthValue).on('mouseup touchend touchcancel', function () {
                $(this).removeClass('draggable');
                resizeElement.removeClass('resizable');
            });
            $('.resizable').css('width', widthValue);
        }).on('mouseup touchend touchcancel', function () {
            dragElement.removeClass('draggable');
            resizeElement.removeClass('resizable');
        });
        e.preventDefault();
    }).on('mouseup touchend touchcancel', function (e) {
        dragElement.removeClass('draggable');
        resizeElement.removeClass('resizable');
    });
}

function otp() {
    $(document).ready(function () {
        var $tabTriggers = $('button[data-bs-toggle="tab"]');
        var urlParams = new URLSearchParams(window.location.search);
        var requestedTab = urlParams.get('tab');
        var activeTabId = null;

        function focusFirstInput(tabId) {
            var input = document.querySelector('#' + tabId + ' .securityCode2fa input');
            if (input) {
                input.focus();
            }
        }

        function requestSmsCode() {
            $.ajax({
                url: '/verification/send',
                type: 'POST'
            });
        }

        $tabTriggers.on('shown.bs.tab', function (event) {
            var targetSelector = event.target.getAttribute('data-bs-target');
            if (!targetSelector) {
                return;
            }

            activeTabId = targetSelector.replace('#', '');

            var url = new URL(window.location.href);
            if (activeTabId) {
                url.searchParams.set('tab', activeTabId);
            } else {
                url.searchParams.delete('tab');
            }
            window.history.replaceState({}, '', url);

            if (activeTabId === 'sms_login') {
                requestSmsCode();
            }

            focusFirstInput(activeTabId);
        });

        if (requestedTab) {
            var requestedTrigger = document.querySelector('[data-bs-target="#' + requestedTab + '"]');
            if (requestedTrigger) {
                window.bootstrap.Tab.getOrCreateInstance(requestedTrigger).show();
            }
        } else {
            var defaultTrigger = document.querySelector('[data-bs-target="#otp_login"]');
            if (defaultTrigger) {
                window.bootstrap.Tab.getOrCreateInstance(defaultTrigger).show();
            }
        }

        if (requestedTab === 'sms_login') {
            requestSmsCode();
            focusFirstInput('sms_login');
        } else {
            focusFirstInput('otp_login');
        }
    });
}

window.otp = otp;

function eassessment() {
    $(document).ready(function () {
        // Dostopnost (ID 26): po neuspešni oddaji fokus na povzetek neodgovorjenih vprašanj (s povezavami)
        var errorSummary = document.getElementById('eassessment-errors');
        if (errorSummary != null) {
            errorSummary.focus();
        }

        var modalElement = $('#eassessment-modal');
        if (modalElement.length > 0) {
            // Dostopnost (ID 24): če je bil test oddan zaradi izteka časa, to besedilno pojasnimo v oknu z rezultatom
            try {
                var expiredNote = sessionStorage.getItem('eassessmentExpired');
                if (expiredNote) {
                    sessionStorage.removeItem('eassessmentExpired');
                    $('<p class="alert alert-warning text-start"></p>').text(expiredNote).prependTo(modalElement.find('.modal-body'));
                }
            } catch (e) {
                // sessionStorage ni na voljo – pojasnilo je bilo vsaj napovedano pred oddajo
            }
            var modal = new bootstrap.Modal(modalElement[0]);
            modal.show();
            modalElement.on('hidden.bs.modal', function (e) {
                // redirect to the redirect page which redirects to the correct target page
                window.location.replace(window.location.pathname + '/redirect')
            })
        }

        var isSubmitting = false;

        function submitForm() {
            if ($('#eassessment-modal').length > 0 || isSubmitting) {
                return;
            }
            isSubmitting = true;

            var $form = $('#eassessment-form');
            var postUrl = $form.attr('action');
            var formData = $form.serializeArray();

            $.ajax({
                url: postUrl,
                type: 'POST',
                data: formData
            })
                .fail(function (response) {
                    isSubmitting = false;
                    alert('Pri oddaji obrazca je prišlo do napake. Prosimo, poskusite ponovno.');
                })
                .done(function (response) {
                    // reload or redirect as needed
                    window.location.reload();
                });
        }

        // Dostopnost (ID 24, 25): časovnik ima role="timer" (brez sprotnih napovedi); ob 30, 10, 5, 2 in 1 minuti
        // se preostali čas napove v statusnem območju, pri 5 in 1 minuti se prikaže dostopno opozorilo z možnostjo
        // takojšnje oddaje. Ob izteku se besedilno pojasni, da se test oddaja z dotlej izbranimi odgovori.
        var timerBox = document.getElementById('eassessment-timer');
        var timerAnnounce = document.getElementById('eassessment-timer-announce');
        var announcedMinutes = {};
        var warnedMinutes = {};

        function timerMsg(key) {
            return timerBox ? (timerBox.getAttribute('data-msg-' + key) || '') : '';
        }

        function announceTime(text) {
            if (timerAnnounce) {
                timerAnnounce.textContent = text;
            }
        }

        function announceMinutesLeft(min) {
            var text = min === 1 ? timerMsg('one') : (min === 2 ? timerMsg('two') : timerMsg('many').replace('{0}', min));
            announceTime(text);
        }

        function showTimeWarning(min) {
            if (typeof Swal === 'undefined') {
                return;
            }
            Swal.fire({
                title: timerMsg('warning-title'),
                text: timerMsg('warning-text').replace('{0}', min),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: timerMsg('continue'),
                cancelButtonText: timerMsg('submit-now'),
                focusConfirm: true,
                returnFocus: true
            }).then(function (result) {
                if (result.dismiss === Swal.DismissReason.cancel && !isSubmitting) {
                    // navadna oddaja obrazca: strežnik vrne stran z morebitnimi neodgovorjenimi vprašanji (povzetek napak)
                    isSubmitting = true;
                    if (timerUpdater != null) {
                        clearInterval(timerUpdater);
                    }
                    var form = document.getElementById('eassessment-form');
                    if (form.requestSubmit) {
                        form.requestSubmit();
                    } else {
                        form.submit();
                    }
                }
            });
        }

        var timerUpdater;
        function updateTimer() {
            var timerEl = document.querySelector('p[datetime]');
            if (timerEl === null) {
                return
            }

            var timeoutDate = Number(timerEl.getAttribute('datetime'))
            var timeUntil = timeoutDate - Date.now()

            if (timeUntil < 0) {
                timerEl.innerText = '0 min 0 s'
                if (timerUpdater != null) {
                    clearInterval(timerUpdater)
                }
                if (typeof Swal !== 'undefined' && Swal.isVisible()) {
                    Swal.close();
                }
                announceTime(timerMsg('expired'));
                try {
                    sessionStorage.setItem('eassessmentExpired', timerMsg('expired'));
                } catch (e) {
                    // brez sessionStorage – pojasnilo ostane samo v statusnem območju
                }
                submitForm()
                return
            }

            var minutes = Math.floor(timeUntil / (1000 * 60));
            var seconds = Math.floor((timeUntil % (1000 * 60)) / 1000);

            timerEl.innerText = minutes + ' min ' + seconds + ' s'

            // napoved/opozorilo, ko preostanek prvič pade pod mejo (minute zaokrožene navzgor)
            var minutesLeft = Math.ceil(timeUntil / (1000 * 60));
            if ([30, 10, 5, 2, 1].indexOf(minutesLeft) > -1 && !announcedMinutes[minutesLeft]) {
                announcedMinutes[minutesLeft] = true;
                announceMinutesLeft(minutesLeft);
                if ((minutesLeft === 5 || minutesLeft === 1) && !warnedMinutes[minutesLeft]) {
                    warnedMinutes[minutesLeft] = true;
                    showTimeWarning(minutesLeft);
                }
            }
            if (minutesLeft <= 5 && timerBox) {
                timerBox.classList.add('eassessment-timer--warning');
                var inlineWarning = timerBox.querySelector('.eassessment-timer-warning');
                if (inlineWarning) {
                    inlineWarning.classList.remove('d-none');
                }
            }
        }

        updateTimer()
        timerUpdater = setInterval(updateTimer, 1000)

        $('input.eassessment-radio').on('change', function (event) {
            var eassessmentGroup = event.target.parentNode.parentNode.parentNode
            var answerId = event.target.value;
            var userAnswerId = event.target.getAttribute('name').replace('question-', '')
            var eassessmentId = window.location.pathname.split("/")[3]
            var apiUrl = '/user/eassessment/' + eassessmentId + "/" + userAnswerId + "/" + answerId

            // set loading state
            $(eassessmentGroup).addClass('eassessment-loading')
            $(eassessmentGroup).find('input[type=radio]').prop('disabled', true)

            $.ajax({
                url: apiUrl,
                type: 'PUT'
            }).fail(function (response) {
            }).always(function () {
                // remove loaders
                $(eassessmentGroup).removeClass('eassessment-loading')
                $(eassessmentGroup).find('input[type=radio]').removeAttr('disabled')
                $(eassessmentGroup).remove('p.alert-danger')
            });
        })
    })
}

(function () {
    var originalAddClassMethod = jQuery.fn.addClass;

    jQuery.fn.addClass = function () {
        var result = originalAddClassMethod.apply(this, arguments);
        jQuery(this).trigger('cssClassChanged');
        return result;
    }
})();


// Numeric only control handler
jQuery.fn.ForceNumericOnly = function () {
    return this.each(function () {
        $(this).keydown(function (e) {
            var key = e.charCode || e.keyCode || 0;
            // allow backspace, tab, delete, enter, arrows, numbers and keypad numbers ONLY
            // home, end, period, and numpad decimal
            return (
                key == 8 ||
                key == 9 ||
                key == 13 ||
                key == 46 ||
                key == 110 ||
                key == 190 ||
                (key >= 35 && key <= 40) ||
                (key >= 48 && key <= 57) ||
                (key >= 96 && key <= 105));
        });
    });
};

/**
 * Initialize Select2 on elements with specific class
 * Replaces old Chosen library functionality
 * @param {string} selector - CSS selector for elements (default: '.select2-select')
 * @param {object} options - Select2 options
 */
function initSelect2(selector, options) {
    selector = selector || '.select2-select';
    var defaultOptions = {
        theme: 'bootstrap-5',
        width: '100%',
        allowClear: true,
        placeholder: function() {
            return $(this).data('placeholder') || 'Izberite...';
        }
    };

    var finalOptions = $.extend({}, defaultOptions, options || {});

    $(selector).each(function() {
        // Get placeholder from data attribute if exists
        if ($(this).data('placeholder')) {
            finalOptions.placeholder = $(this).data('placeholder');
        }

        // Initialize Select2
        if (!$(this).hasClass('select2-hidden-accessible')) {
            $(this).select2(finalOptions);
        }
    });
}

$(document).on('click', '.lang-switch', function(e) {
    e.preventDefault();
    var lang = $(this).data('lang');

    // Take current URL with all existing params and add/replace lang
    var url = new URL(window.location.href);
    url.searchParams.set('lang', lang);  // set replaces if exists, adds if not

    window.location.href = url.toString();
});

// Dostopnost (ID 19): napake in obvezna polja – enoten vzorec za strežniške in sprotne (JS) napake.
// - polje z napako dobi aria-invalid="true" in aria-describedby na besedilo napake; besedilo dobi ikono (ne le barva)
// - po oddaji obrazca s strežniškimi napakami: povzetek napak s povezavami na polja na vrhu obrazca, fokus nanj
// - napake, ki jih doda JS, se napovejo v statusnem območju
// - polja v vrsticah .required dobijo aria-required; obrazec opombo "Polja, označena z *, so obvezna."
var A11Y_ERROR_SELECTOR = '.form-text.has-error, .help-block.has-error, span.has-error';
// napake na ravni razdelka (npr. "Vnesite vsaj eno izobrazbo") – brez posameznega polja
var A11Y_SECTION_ERROR_SELECTOR = 'form .a11y-section-error';
var a11yErrorSeq = 0;

function a11yMsg(key, arg) {
    var region = document.getElementById('a11yStatus');
    var msg = (region && region.getAttribute('data-msg-' + key)) || '';
    return arg !== undefined ? msg.replace('{0}', arg) : msg;
}

function a11yIsField(el) {
    return /^(INPUT|SELECT|TEXTAREA)$/.test(el.tagName) && ['hidden', 'submit', 'button'].indexOf(el.type) < 0;
}

// Polje, ki mu napaka pripada: najprej polje (ali skupina polj) tik pred napako v istem staršu,
// sicer polja v stolpcu znotraj iste vrstice oz. v vrstici sami – nikoli višje od vrstice.
function a11yFieldsNear(errorEl) {
    for (var sib = errorEl.previousElementSibling; sib; sib = sib.previousElementSibling) {
        if (sib.classList.contains('select2-container')) {
            continue; // kontrolnik Select2 – pripadajoči <select> je pred njim
        }
        if (a11yIsField(sib)) {
            return [sib];
        }
        var inner = Array.prototype.filter.call(sib.querySelectorAll('input, select, textarea'), a11yIsField);
        if (inner.length) {
            return inner;
        }
    }
    var row = errorEl.closest('.mb-3, .form-group, .row');
    if (!row) {
        return [];
    }
    var col = errorEl.closest('[class*="col-"], .col');
    var scope = col && row.contains(col) && col !== row ? col : row;
    return Array.prototype.filter.call(scope.querySelectorAll('input, select, textarea'), a11yIsField);
}

function a11yAddDescribedBy(el, id) {
    var ids = (el.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
    if (ids.indexOf(id) < 0) {
        ids.push(id);
        el.setAttribute('aria-describedby', ids.join(' '));
    }
}

function a11yEnhanceError(errorEl) {
    if (errorEl.getAttribute('data-a11y-error') === '1' || !String(errorEl.textContent ?? '').trim()) {
        return null;
    }
    errorEl.setAttribute('data-a11y-error', '1');
    if (!errorEl.id) {
        errorEl.id = 'a11y-error-' + (++a11yErrorSeq);
    }
    if (!errorEl.querySelector('.fa-exclamation-circle')) {
        $(errorEl).prepend('<i class="fas fa-exclamation-circle me-1" aria-hidden="true"></i>');
    }
    var fields = a11yFieldsNear(errorEl);
    fields.forEach(function (f) {
        f.setAttribute('aria-invalid', 'true');
        a11yAddDescribedBy(f, errorEl.id);
        var sel2 = $(f).next('.select2-container').find('.select2-selection')[0];
        if (sel2) {
            sel2.setAttribute('aria-invalid', 'true');
            a11yAddDescribedBy(sel2, errorEl.id);
        }
    });
    return fields[0] || null;
}

// polja, katerih napake ni več (JS jo je odstranil), niso več označena kot napačna
function a11yCleanupErrors() {
    document.querySelectorAll('[aria-invalid="true"]').forEach(function (f) {
        var ids = (f.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
        var kept = ids.filter(function (id) {
            return id.indexOf('a11y-error-') !== 0 || document.getElementById(id);
        });
        if (kept.length !== ids.length) {
            if (kept.length) {
                f.setAttribute('aria-describedby', kept.join(' '));
            } else {
                f.removeAttribute('aria-describedby');
            }
            var stillError = kept.some(function (id) { return id.indexOf('a11y-error-') === 0; });
            if (!stillError) {
                f.removeAttribute('aria-invalid');
            }
        }
    });
}

function a11yFieldLabel(field) {
    var label = field.id ? document.querySelector('label[for="' + CSS.escape(field.id) + '"]') : null;
    var text = label ? label.textContent : '';
    var group = field.closest('[role="group"], [role="radiogroup"]');
    if (group && group.getAttribute('aria-labelledby')) {
        var g = document.getElementById(group.getAttribute('aria-labelledby').split(' ')[0]);
        if (g) {
            text = g.textContent + (text && text !== g.textContent ? ' – ' + text : '');
        }
    }
    return text.replace(/\s+/g, ' ').replace(/\*/g, '').trim();
}

// naslov razdelka, v katerem je napaka brez polja (npr. manjkajoče članstvo) – povzetek nanj pokaže
function a11yErrorSectionHeading(errorEl) {
    var c = errorEl.parentElement;
    while (c && c.tagName !== 'FORM') {
        var headings = c.querySelectorAll('h2, h3, h4');
        for (var i = headings.length - 1; i >= 0; i--) {
            if (headings[i].compareDocumentPosition(errorEl) & Node.DOCUMENT_POSITION_FOLLOWING) {
                return headings[i];
            }
        }
        c = c.parentElement;
    }
    return null;
}

function a11yBuildErrorSummary() {
    var entries = [];
    document.querySelectorAll(A11Y_ERROR_SELECTOR + ', ' + A11Y_SECTION_ERROR_SELECTOR).forEach(function (el) {
        if (!el.textContent.trim() || el.getClientRects().length === 0) {
            return; // skrita napaka (neaktiven razdelek) – ni v povzetku
        }
        var form = el.closest('form');
        if (!form || form.querySelector('#eassessment-errors, .a11y-error-summary')) {
            return;
        }
        var isSection = el.matches(A11Y_SECTION_ERROR_SELECTOR);
        var field = isSection ? null : (a11yEnhanceError(el) || a11yFieldsNear(el)[0]);
        if (field && field.getClientRects().length === 0 && !$(field).next('.select2-container').length) {
            field = null; // polje ni vidno – napaka v povzetku kot besedilo
        }
        var entry = entries.filter(function (e) { return e.form === form; })[0];
        if (!entry) {
            entry = {form: form, items: []};
            entries.push(entry);
        }
        // več sporočil istega polja je ločenih z <br> – v povzetku jih ločimo s presledkom
        var copy = el.cloneNode(true);
        $(copy).find('br').replaceWith(' ');
        entry.items.push({field: field, section: field ? null : a11yErrorSectionHeading(el), text: copy.textContent.replace(/\s+/g, ' ').trim()});
    });
    var first = null;
    entries.forEach(function (entry) {
        // brez role="alert": fokus na povzetek zadošča, sicer ga nekateri bralniki preberejo dvakrat
        var $summary = $('<div class="alert alert-danger a11y-error-summary" tabindex="-1"></div>');
        $summary.append($('<h2 class="h6"></h2>').text(a11yMsg('errors-title', entry.items.length)));
        var $ul = $('<ul class="mb-0"></ul>');
        entry.items.forEach(function (it) {
            var label = it.field ? a11yFieldLabel(it.field) : '';
            var $li = $('<li></li>');
            if (it.field && it.field.id) {
                $('<a></a>').attr('href', '#' + it.field.id).text((label ? label + ': ' : '') + it.text)
                    .on('click', function (e) {
                        e.preventDefault();
                        var target = it.field;
                        var sel2 = $(target).next('.select2-container').find('.select2-selection')[0];
                        (sel2 || target).focus();
                    }).appendTo($li);
            } else if (it.section) {
                var section = it.section;
                if (!section.id) {
                    section.id = 'a11y-section-' + (++a11yErrorSeq);
                }
                section.setAttribute('tabindex', '-1');
                var sectionLabel = section.textContent.replace(/\s+/g, ' ').replace(/\*/g, '').trim();
                $('<a></a>').attr('href', '#' + section.id).text((sectionLabel ? sectionLabel + ': ' : '') + it.text)
                    .on('click', function (e) {
                        e.preventDefault();
                        section.focus();
                        section.scrollIntoView({block: 'start'});
                    }).appendTo($li);
            } else {
                $li.text((label ? label + ': ' : '') + it.text);
            }
            $ul.append($li);
        });
        $summary.append($ul);
        var $existing = $(entry.form).children('.alert-danger').first();
        if ($existing.length) {
            $existing.after($summary);
        } else {
            $(entry.form).prepend($summary);
        }
        first = first || $summary[0];
    });
    if (first) {
        first.focus();
    }
}

function a11yMarkRequired(root) {
    $(root || document).find('form').each(function () {
        var marked = 0;
        $(this).find('.mb-3.required, .form-group.required, .row.required').each(function () {
            var hasAsterisk = $(this).children('label').length > 0;
            $(this).find('input, select, textarea').each(function () {
                if (this.type !== 'hidden' && this.type !== 'radio' && this.type !== 'checkbox') {
                    this.setAttribute('aria-required', 'true');
                    if (this.tagName === 'SELECT' && $(this).next('.select2-container').length) {
                        a11ySelect2(this);
                    }
                    if (hasAsterisk && !$(this).is(':disabled') && !this.readOnly) {
                        marked++;
                    }
                }
            });
            $(this).find('[role="radiogroup"]').attr('aria-required', 'true');
        });
        // opomba le, kjer je vsaj eno urejljivo obvezno polje z vidno zvezdico
        if (marked && !$(this).find('.a11y-required-note').length) {
            var note = a11yMsg('required-note');
            if (note) {
                $(this).prepend($('<p class="a11y-required-note small"></p>').text(note));
            }
        }
    });
}

$(function () {
    a11yMarkRequired(document);
    a11yBuildErrorSummary();

    // sprotne napake (JS): označi polje in napovej besedilo
    if (window.MutationObserver) {
        var errorObserver = new MutationObserver(function (mutations) {
            var removed = false;
            var texts = [];
            mutations.forEach(function (m) {
                Array.prototype.forEach.call(m.removedNodes, function (node) {
                    if (node.nodeType === 1 && (node.hasAttribute('data-a11y-error') || node.querySelector('[data-a11y-error]'))) {
                        removed = true;
                    }
                });
                Array.prototype.forEach.call(m.addedNodes, function (node) {
                    if (node.nodeType !== 1) {
                        return;
                    }
                    var errors = node.matches(A11Y_ERROR_SELECTOR) ? [node] : Array.prototype.slice.call(node.querySelectorAll(A11Y_ERROR_SELECTOR));
                    errors.forEach(function (el) {
                        var isNew = el.getAttribute('data-a11y-error') !== '1';
                        a11yEnhanceError(el);
                        if (isNew && el.textContent.trim()) {
                            texts.push(el.textContent.replace(/\s+/g, ' ').trim());
                        }
                    });
                });
            });
            if (removed) {
                a11yCleanupErrors();
            }
            if (texts.length) {
                a11yAnnounceText(texts.join(' '));
            }
        });
        errorObserver.observe(document.body, {childList: true, subtree: true});
    }
});

// Dostopnost (ID 29, G-12): ikone Font Awesome dobijo aria-hidden; povezave, ki se odprejo v novem oknu (target=_blank), dobijo skrito besedilo
// "(odpre se v novem oknu)" (WCAG 3.2.5 priporočilo, 2.4.4) in rel="noopener"; velja tudi za pozneje dodane povezave
// (tabele, predloge). Povezave, ki opozorilo že imajo (npr. noga), se ne spremenijo.
$(function a11yNewWindowLinks() {
    var status = document.getElementById('a11yStatus');
    var hint = status ? status.getAttribute('data-msg-new-window') : '';
    if (!hint) {
        return;
    }

    function mark(root) {
        // G-12: ikone Font Awesome so okras – pomen nosi besedilo (prej jih je bralnik lahko bral kot neznan znak)
        $(root).find('i[class*="fa-"]').addBack('i[class*="fa-"]').filter(function () {
            // ne skrijemo ikon, ki nosijo podatek v title ali so same kontrolnik (npr. klikljiv <i class="btn">)
            return !this.hasAttribute('aria-hidden') && !this.hasAttribute('aria-label') && !this.hasAttribute('role') &&
                !this.hasAttribute('title') && !$(this).is('.btn, [data-toggle], [data-bs-toggle], [tabindex]');
        }).attr('aria-hidden', 'true');
        $(root).find('a[target="_blank"]').addBack('a[target="_blank"]').each(function () {
            if (this.hasAttribute('data-a11y-nw')) {
                return;
            }
            this.setAttribute('data-a11y-nw', '');
            var rel = (this.getAttribute('rel') || '').split(/\s+/);
            if (rel.indexOf('noopener') === -1) {
                this.setAttribute('rel', String(rel.concat('noopener').join(' ') ?? '').trim());
            }
            if (this.hasAttribute('aria-labelledby')) {
                return;
            }
            var label = this.getAttribute('aria-label');
            if (label !== null) {
                if (label.indexOf(hint) === -1) {
                    this.setAttribute('aria-label', label + ' ' + hint);
                }
            } else if ($(this).text().indexOf(hint) === -1) {
                $('<span class="visually-hidden"></span>').text(' ' + hint).appendTo(this);
            }
        });
    }

    mark(document.body);
    if (window.MutationObserver) {
        new MutationObserver(function (mutations) {
            mutations.forEach(function (mutation) {
                Array.prototype.forEach.call(mutation.addedNodes, function (node) {
                    if (node.nodeType === 1) {
                        mark(node);
                    }
                });
            });
        }).observe(document.body, {childList: true, subtree: true});
    }
});

// Dostopnost (ID 20): nalaganje datotek (velja za vsa območja .droppableArea)
// - izbrana datoteka oz. napaka oblike/velikosti (besedilo, ki ga obstoječa koda zapiše v <p>) se napove;
//   programska ponastavitev polja (brez datotek) se ne napove
// - po uspešnem nalaganju (ajaxComplete zahteve, ki je poslala prav to datoteko, brez response.error) se napove
//   "Datoteka je naložena: <ime>" in fokus se s skritega gumba vrne na polje oz. na nov vnos;
//   pri tokovih, ki po nalaganju osvežijo stran, se napoved shrani v sessionStorage in izvede po nalaganju strani
var A11Y_UPLOAD_KEY = 'a11yUploadAnnouncement';

$(document).on('change', '.droppableArea input[type="file"]', function () {
    if (this.files && this.files.length) {
        var text = String($(this).closest('.droppableArea').find('p').first().text() ?? '').trim();
        if (text) {
            a11yAnnounceText(text);
        }
    }
});

$(document).on('click', '.droppableArea .upload', function () {
    var input = $(this).closest('.droppableArea').find('input[type="file"]')[0];
    var file = input && input.files && input.files[0];
    var status = document.getElementById('a11yStatus');
    if (!file || !status) {
        return;
    }
    var message = (status.getAttribute('data-msg-uploaded') || '{0}').replace('{0}', file.name);

    function sentThisFile(data) {
        if (!(data instanceof FormData)) {
            return false;
        }
        var found = false;
        data.forEach(function (value) {
            if (value instanceof File && value.name === file.name && value.size === file.size) {
                found = true;
            }
        });
        return found;
    }

    function onComplete(event, xhr, settings) {
        if (!sentThisFile(settings.data)) {
            return;
        }
        $(document).off('ajaxComplete', onComplete);
        var json = xhr.responseJSON;
        // tudi odgovor 200 z neveljavnim JSON jQuery zavrne (state 'rejected') – takrat ni uspeha
        if ((xhr.state && xhr.state() !== 'resolved') || xhr.status < 200 || xhr.status >= 300 || (json && json.error)) {
            return; // napako že sporoči obstoječa koda (alert)
        }
        var unloading = false;
        $(window).one('pagehide beforeunload', function () {
            unloading = true;
        });
        try {
            sessionStorage.setItem(A11Y_UPLOAD_KEY, JSON.stringify({text: message, at: Date.now(), name: file.name}));
        } catch (e) {
            // brez sessionStorage – napoved le na tej strani
        }
        setTimeout(function () {
            if (unloading) {
                return; // napove se po ponovnem nalaganju strani
            }
            try {
                sessionStorage.removeItem(A11Y_UPLOAD_KEY);
            } catch (e) {
                // ignoriraj
            }
            a11yAnnounceText(message);
            var active = document.activeElement;
            if (!active || active === document.body || !$(active).is(':visible')) {
                var target = $(input).is(':visible') ? input : null;
                if (!target) { // vrstica za nalaganje skrita – na nov vnos (po imenu datoteke, sicer zadnji)
                    var items = $('.resultItem:visible').filter(function () {
                        return $(this).text().indexOf(file.name) !== -1;
                    });
                    items = items.length ? items : $(input).closest('.uploadSectionItem').find('.resultItem:visible');
                    target = items.last().find('a, button').filter(':visible')[0];
                }
                if (target) {
                    target.focus();
                }
            }
        }, 500);
    }

    $(document).on('ajaxComplete', onComplete);
    setTimeout(function () {
        $(document).off('ajaxComplete', onComplete);
    }, 120000);
});

$(function a11yAnnounceUploadAfterReload() {
    try {
        var saved = JSON.parse(sessionStorage.getItem(A11Y_UPLOAD_KEY) || 'null');
        sessionStorage.removeItem(A11Y_UPLOAD_KEY);
        if (saved && saved.text && Date.now() - saved.at < 30000) {
            // uporabnik se je med čakanjem sam premaknil (tipka, klik, fokus – tudi »Skoči na vsebino«) – fokusa ne prevzamemo
            // poslušalci na window v fazi zajema (capture) – pred poslušalci na document (npr. CKEditor focusin),
            // zato jih zaustavljeno širjenje dogodka ne obide; tudi »click« (bralniki zaslona)
            var moved = false;
            var moveEvents = ['keydown', 'mousedown', 'pointerdown', 'touchstart', 'click', 'focusin'];
            var stopWatching = function () {
                moveEvents.forEach(function (type) {
                    window.removeEventListener(type, markMoved, true);
                });
            };
            var markMoved = function () {
                moved = true;
                stopWatching();
            };
            moveEvents.forEach(function (type) {
                window.addEventListener(type, markMoved, true);
            });
            setTimeout(function () {
                stopWatching();
                a11yAnnounceText(saved.text);
                // fokus na vnos z naloženo datoteko (vrstica tabele ali povezava) – le, če se uporabnik ni premaknil
                // in je na strani natanko en vnos s tem imenom (sicer bi lahko fokusirali napačen dokument)
                var active = document.activeElement;
                if (moved || !saved.name || (active && active !== document.body)) {
                    return;
                }
                var isName = function (html) {
                    return String($('<div>').html(html == null ? '' : String(html)).text() ?? '').trim() === saved.name;
                };
                // enoličnost preverimo prek vseh dokumentov, ne le vidnih: pri DataTables prek vseh podatkov tabele
                // (vse strani, ne glede na filter); pri strežniškem straničenju enoličnosti ni mogoče potrditi
                var total = 0;
                var unknown = false;
                $('main table').each(function () {
                    if (!$.fn.dataTable || !$.fn.dataTable.isDataTable(this)) {
                        return;
                    }
                    var api = $(this).DataTable();
                    if (api.settings()[0].features.serverSide) {
                        unknown = true;
                        return;
                    }
                    api.rows().every(function () {
                        var data = this.data();
                        var values = Array.isArray(data) ? data : $.map(data || {}, function (v) {
                            return [v];
                        });
                        if (values.some(isName)) {
                            total++;
                        }
                    });
                });
                var dtTables = $('main table').filter(function () {
                    return $.fn.dataTable && $.fn.dataTable.isDataTable(this);
                });
                // ujemanja (vrstica po celicah, povezava/gumb po besedilu) preslikamo na dokument: vrstico z
                // tabindex, sicer element sam – vrstica in povezava v njej sta en dokument
                var docMatches = function ($els) {
                    var docs = [];
                    $els.each(function () {
                        var parts = $(this).is('tr') ? $(this).children('td') : $(this);
                        var hit = parts.filter(function () {
                            return isName($(this).html());
                        }).length > 0;
                        var doc = $(this).closest('tr[tabindex]')[0] || this;
                        if (hit && docs.indexOf(doc) === -1) {
                            docs.push(doc);
                        }
                    });
                    return docs;
                };
                var candidates = $('main').find('tr[tabindex], a[href], button');
                // zunaj DataTables štejemo vse vnose (tudi skrite)
                total += docMatches(candidates.filter(function () {
                    return !dtTables.has(this).length;
                })).length;
                var exact = docMatches(candidates.filter(':visible'));
                if (!unknown && total === 1 && exact.length === 1) {
                    exact[0].focus();
                }
            }, 1500);
        }
    } catch (e) {
        // brez sessionStorage
    }
});

// Dostopnost (ID 13): Friendly Captcha – stanje preverjanja (reševanje, uspeh, napaka, potek) se napove
// v #a11yStatus; obrazca ni mogoče oddati pred zaključenim preverjanjem, razlog je izpisan kot besedilo.
// Stanje beremo iz skritega polja z rešitvijo (".UNSTARTED", ".UNFINISHED", ".EXPIRED", ".ERROR" ... ali rešitev).
$(function a11yFriendlyCaptcha() {
    var widget = document.querySelector('.frc-captcha');
    if (!widget) {
        return;
    }
    var lastState = '';

    function solutionValue() {
        var field = widget.querySelector('.frc-captcha-solution');
        return field ? field.value : '';
    }

    function isSolved(value) {
        return !!value && value.charAt(0) !== '.';
    }

    new MutationObserver(function () {
        var value = solutionValue();
        var state = isSolved(value) ? 'done' : value;
        if (!state || state === lastState) {
            return;
        }
        lastState = state;
        var message = '';
        if (state === 'done') {
            var title = widget.querySelector('svg title');
            message = title ? title.textContent : '';
            $('#frcPendingMsg').remove();
        } else if (state !== '.UNSTARTED') {
            var text = widget.querySelector('.frc-text');
            message = text ? (text.innerText || text.textContent) : ''; // innerText: <br> → presledek med deli
            message = message.replace(/\s+/g, ' ');
        }
        if (String(message ?? '').trim()) {
            a11yAnnounceText(String(message ?? '').trim());
        }
    }).observe(widget, {childList: true, subtree: true});

    var form = widget.closest('form');
    if (form) {
        form.addEventListener('submit', function (e) {
            if (isSolved(solutionValue())) {
                return;
            }
            e.preventDefault();
            var value = solutionValue();
            var failed = !widget.querySelector('.frc-captcha-solution') || /^\.(ERROR|HEADLESS_ERROR|EXPIRED)$/.test(value);
            var message = widget.getAttribute(failed ? 'data-msg-failed' : 'data-msg-pending');
            if (value === '.UNSTARTED' && widget.friendlyChallengeWidget) {
                widget.friendlyChallengeWidget.start(); // začetek ob fokusu se morda ni sprožil (skripta naložena pozneje)
            }
            var note = document.getElementById('frcPendingMsg');
            if (!note) {
                note = $('<p id="frcPendingMsg" class="text-danger mt-2"></p>').insertAfter(widget)[0];
            }
            $(note).text(message);
            a11yAnnounceText(message);
            var button = widget.querySelector('.frc-button'); // začni / poskusi znova
            if (button) {
                button.focus();
            }
        });
    }
});

// Dostopnost (ID 28, 8): SweetAlert2 – privzeta besedila gumbov in zapiralnega gumba v jeziku strani
// (prej angleški "OK", "Cancel", "No", "Close this dialog", kjer jih klic ni nastavil).
(function patchSwal() {
    if (!window.Swal || window.Swal.__a11y) {
        return;
    }
    var isEn = (document.documentElement.getAttribute('lang') || 'sl').substring(0, 2) === 'en';
    var defaults = isEn
        ? {confirmButtonText: 'OK', cancelButtonText: 'Cancel', denyButtonText: 'No', closeButtonAriaLabel: 'Close this dialog'}
        : {confirmButtonText: 'V redu', cancelButtonText: 'Prekličite', denyButtonText: 'Ne', closeButtonAriaLabel: 'Zaprite to okno'};
    var base = window.Swal;
    var originalFire = window.Swal.fire;
    window.Swal.fire = function () {
        var args = Array.prototype.slice.call(arguments);
        // Swal.mixin() in predloge (template) imajo svoje privzete vrednosti – teh ne povozimo
        if (this !== base || (args[0] && typeof args[0] === 'object' && args[0].template)) {
            return originalFire.apply(this, args);
        }
        if (args.length && $.isPlainObject(args[0])) {
            args[0] = $.extend({}, defaults, args[0]);
        } else if (args.length && typeof args[0] === 'string') {
            args = [$.extend({}, defaults, {title: args[0], html: args[1], icon: args[2]})];
        }
        return originalFire.apply(this, args);
    };
    window.Swal.__a11y = true;
})();

// Dostopnost (ID 28): Bootstrap pogovorna okna – dostopno ime iz naslova okna (.modal-title) ob vsakem odprtju
$(document).on('show.bs.modal', '.modal', function (e) {
    var title = this.querySelector('.modal-title');
    if (title && !this.getAttribute('aria-labelledby')) {
        // ID naslova mora biti na strani enoličen (več oken ima npr. "usersModalTitle")
        if (!title.id || document.querySelectorAll('[id="' + title.id + '"]').length > 1) {
            var base = (this.id || 'modal') + '-title', id = base, i = 2;
            while (document.getElementById(id) && document.getElementById(id) !== title) {
                id = base + '-' + i++;
            }
            title.id = id;
        }
        this.setAttribute('aria-labelledby', title.id);
    }
    // okna, odprta programsko (ne prek data-bs-toggle), Bootstrap ne vrne fokusa na sprožilni element –
    // zapomnimo si ga ob odprtju in ga vrnemo ob zaprtju, če je fokus ostal na <body>
    var active = document.activeElement;
    if (e.target === this && active && active !== document.body && !this.contains(active)) {
        this.__a11yTrigger = active;
    }
});

// Fokus vrnemo že ob začetku zapiranja (po izklopu pasti fokusa, ki se zgodi takoj za dogodkom hide), ne šele po
// animaciji: sicer je fokus ~150 ms na <body> in NVDA začne brati celotno stran (preizkus z NVDA, V-1).
$(document).on('hide.bs.modal', '.modal', function (e) {
    if (e.target !== this || e.isDefaultPrevented()) {
        return;
    }
    var modal = this;
    var trigger = this.__a11yTrigger;
    queueMicrotask(function () {
        // zapiranje je lahko preklicano (preventDefault v drugem poslušalcu) – tedaj okno ostane odprto (.show)
        if (modal.classList.contains('show')) {
            return;
        }
        var active = document.activeElement;
        if (trigger && document.contains(trigger) && (!active || active === document.body || modal.contains(active))) {
            trigger.focus();
        }
    });
});

$(document).on('hidden.bs.modal', '.modal', function (e) {
    if (e.target !== this) {
        return;
    }
    var trigger = this.__a11yTrigger;
    this.__a11yTrigger = null;
    var active = document.activeElement;
    if (trigger && document.contains(trigger) && (!active || active === document.body || this.contains(active))) {
        trigger.focus();
    }
});

// Dostopnost (ID 8, 27): DataTables – imena za podporne tehnologije (paginacija, razvrščanje) in nizi v jeziku strani.
// Posamezne inicializacije lahko nize še vedno prepišejo (npr. "Ni vnosov", vidni "<" ">"); privzete vrednosti
// dopolnijo, kar prej ni bilo prevedeno (angleški "First/Previous/Next/Last", "pagination", "Search:" …).
(function dataTablesA11yDefaults() {
    if (!$.fn.dataTable || $.fn.dataTable.__a11y) {
        return;
    }
    $.fn.dataTable.__a11y = true;
    var isEn = (document.documentElement.getAttribute('lang') || 'sl').substring(0, 2) === 'en';
    var language = isEn ? {
        aria: {
            paginate: {first: 'First page', previous: 'Previous page', next: 'Next page', last: 'Last page'}
        }
    } : {
        emptyTable: 'Ni podatkov.',
        info: 'Prikazani zapisi od _START_ do _END_ (skupaj _TOTAL_)',
        infoEmpty: 'Ni zapisov',
        infoFiltered: '(filtrirano izmed _MAX_ zapisov)',
        lengthMenu: 'Prikaži _MENU_ zapisov',
        loadingRecords: 'Nalaganje …',
        processing: 'Obdelava …',
        search: 'Iskanje:',
        zeroRecords: 'Ni zadetkov.',
        aria: {
            // DataTables niz doda neposredno za ime stolpca – zato začetno ločilo (kot v privzetem ": Activate to sort")
            orderable: ': razvrsti po tem stolpcu',
            orderableReverse: ': obrni vrstni red',
            orderableRemove: ': odstrani razvrščanje',
            paginate: {first: 'Prva stran', previous: 'Prejšnja stran', next: 'Naslednja stran', last: 'Zadnja stran'}
        }
    };
    $.extend(true, $.fn.dataTable.defaults, {language: language});

    // Gumbi za prvo/prejšnjo/naslednjo/zadnjo stran: ikone namesto znakov « < > » (inicializacije jih nastavljajo
    // različno, npr. "<" ">"), za vse tabele. Ikona je aria-hidden; ime gumba je iz language.aria.paginate
    // (»Prva stran« …), ki ga DataTables nastavi kot aria-label.
    var pagingIcons = {first: 'fa-angles-left', previous: 'fa-angle-left', next: 'fa-angle-right', last: 'fa-angles-right'};
    var pagingRenderers = $.fn.dataTable.ext.renderer.pagingButton;
    Object.keys(pagingRenderers).forEach(function (key) {
        var base = pagingRenderers[key];
        pagingRenderers[key] = function (settings, buttonType, content, active, disabled) {
            if (pagingIcons[buttonType]) {
                content = '<i class="fa-solid ' + pagingIcons[buttonType] + '" aria-hidden="true"></i>';
            }
            return base.call(this, settings, buttonType, content, active, disabled);
        };
    });

    // Navigacija po straneh: DataTables 2.3 ne podpira language.aria.paging – ime nastavimo sami,
    // z imenom tabele, da so navigacije na isti strani razločljive (ID 27, landmark-unique).
    // najbližji predhodni viden naslov razdelka (tudi če je v prejšnji vrstici .row), največ 6 ravni navzgor;
    // preskoči pogovorna okna, skrite zavihke in zaprte razdelke
    function precedingHeading(el) {
        for (var node = el, depth = 0; node && node !== document.body && depth < 6; node = node.parentNode, depth++) {
            var siblings = $(node).prevAll().not('.modal, .tab-pane:not(.active), .collapse:not(.show), [hidden]');
            for (var i = 0; i < siblings.length; i++) {
                var sibling = $(siblings[i]);
                var heading = sibling.is('h1, h2, h3, h4') ? sibling : sibling.find('h2, h3, h4').filter(':visible').last();
                if (heading.length && heading.is(':visible') && String(heading.text() ?? '').trim()) {
                    return heading;
                }
            }
        }
        return $();
    }

    // N-28 (WCAG 2.1.1): v več tabelah (dokumenti, seznam kandidatov ...) prvi klik na vrstico izbere vrstico,
    // drugi jo odpre – samo z miško. Vrstice tabel z obravnavo klika na vrstico postanejo fokusabilne,
    // Enter/presledek izvede isto dejanje (izbira + odpiranje). Obravnava se preveri po trenutnem klicu,
    // ker jo koda pogosto doda šele po inicializaciji tabele.
    // vrne 'click' ali 'dblclick', sicer null
    function rowActionEvent(node) {
        var events = node && $._data(node, 'events');
        var match = function (type) {
            return !!(events && events[type] && events[type].some(function (h) {
                return h.selector && /(^|\s)tr$/.test(h.selector.trim());
            }));
        };
        return match('click') ? 'click' : (match('dblclick') ? 'dblclick' : null);
    }

    $(document).on('keydown', 'tr[data-a11y-row-action]', function (e) {
        if (e.target !== this || (e.key !== 'Enter' && e.key !== ' ') ||
            (e.originalEvent && e.originalEvent.repeat) || e.ctrlKey || e.altKey || e.metaKey) {
            return;
        }
        e.preventDefault();
        var $row = $(this);
        if ($row.attr('data-a11y-row-action') === 'dblclick') {
            $row.trigger('dblclick');
            return;
        }
        var wasSelected = $row.hasClass('selected');
        $row.trigger('click'); // neposredno dejanje ali izbira vrstice
        if (!wasSelected && $row.hasClass('selected')) {
            $row.trigger('click'); // tabele z dvostopenjskim klikom: odpiranje
        }
    });

    // skupno navodilo za bralnike zaslona (aria-describedby na vrsticah): »Pritisnite Enter za odprtje.«
    function rowHintId() {
        var hint = document.getElementById('a11yRowHint');
        if (!hint) {
            var status = document.getElementById('a11yStatus');
            hint = document.createElement('span');
            hint.id = 'a11yRowHint';
            // hidden: besedilo je le opis vrstic (aria-describedby), bralnik ga ne sme brati kot vsebino strani
            hint.hidden = true;
            hint.textContent = (status && status.getAttribute('data-msg-row-hint')) || 'Enter';
            document.body.appendChild(hint);
        }
        return hint.id;
    }

    $(document).on('init.dt draw.dt', function (e, settings) {
        var rowTable = new $.fn.dataTable.Api(settings).table().node();
        setTimeout(function () {
            var tbody = $(rowTable).children('tbody')[0];
            var action = rowActionEvent(rowTable) || rowActionEvent(tbody);
            if (action) {
                $(tbody).children('tr').not('[data-a11y-row-action]').filter(function () {
                    // prazna tabela ali vrstica, ki že ima gumb za odpiranje (novice, obvestila – N-18)
                    return !$(this).find('td.dt-empty, td.dataTables_empty, .a11y-link-btn').length;
                }).attr({'tabindex': '0', 'data-a11y-row-action': action, 'aria-describedby': rowHintId()});
            }
        }, 0);
    });

    $(document).on('init.dt draw.dt', function (e, settings) {
        var api = new $.fn.dataTable.Api(settings);
        var table = api.table().node();
        var container = api.table().container();
        var labelledBy = table.getAttribute('aria-labelledby');
        var name = table.getAttribute('aria-label') || $(table).find('caption').first().text().trim() ||
            (labelledBy ? String($('#' + labelledBy.split(/\s+/).join(', #')).text() ?? '').trim() : '');
        if (!name) {
            // ID 27: tabela dobi ime po vidnem naslovu razdelka (aria-labelledby – brez podvajanja besedila)
            var heading = precedingHeading(container);
            if (heading.length) {
                if (!heading.attr('id')) {
                    heading.attr('id', 'a11yTableHeading' + Math.random().toString(36).slice(2, 8));
                }
                table.setAttribute('aria-labelledby', heading.attr('id'));
                name = String(heading.text() ?? '').trim();
            }
        }
        name = name.replace(/\s+/g, ' ');
        // sklop 11 (ID 14, notranje vloge): filtri v glavi tabele (vrstica s polji) dobijo k imenu še ime stolpca
        // iz vrstice z naslovi stolpcev, npr. "Išči: ime datoteke" (prej 26× samo "Išči")
        if (e.type === 'init') {
            var headerRow = $(table).children('thead').children('tr').filter(function () {
                return !$(this).find('input, select').length;
            }).last();
            $(table).children('thead, tfoot').children('tr').each(function () {
                var row = $(this);
                var fields = row.find('input[aria-label], select[aria-label]');
                if (!fields.length) {
                    return;
                }
                fields.each(function () {
                    var base = this.getAttribute('data-a11y-base-label') || this.getAttribute('aria-label');
                    var column = String(headerRow.children().eq($(this).closest('th, td').index()).text() ?? '').trim().replace(/\s+/g, ' ');
                    this.setAttribute('data-a11y-base-label', base);
                    if (column && column.toLowerCase() !== base.toLowerCase()) {
                        this.setAttribute('aria-label', base + ': ' + column);
                        if ($(this).next('.select2-container').length) {
                            a11ySelect2(this);
                        }
                    }
                });
            });
        }
        var label = (isEn ? 'Pages' : 'Strani') + (name ? ' – ' + name.replace(/\s+/g, ' ') : '');
        $(container).find('.dt-paging nav').filter(function () {
            return $(this).closest('.dt-container')[0] === container; // ne gnezdene tabele
        }).first().attr('aria-label', label);
    });
})();

// Dostopnost (ID 22, 8): TinyMCE – jezik vmesnika sledi jeziku strani (prej povsod sl-SI, tudi na angleških
// straneh), okvir urejevalnika dobi ime iz oznake izvirnega <textarea> (label for) oz. njegovega aria-label.
(function patchTinymce() {
    if (!window.tinymce || window.tinymce.__a11y) {
        return;
    }
    var original = window.tinymce.init.bind(window.tinymce);
    var pageLang = (document.documentElement.getAttribute('lang') || 'sl').substring(0, 2) === 'en' ? 'en' : 'sl-SI';
    window.tinymce.init = function (options) {
        var o = $.extend({}, options);
        o.language = pageLang;
        var target = o.target || (o.selector ? document.querySelector(o.selector) : null);
        if (target && !o.iframe_aria_text) {
            var label = target.id ? document.querySelector('label[for="' + CSS.escape(target.id) + '"]') : null;
            var name = label ? label.textContent.replace(/\s+/g, ' ').trim() : target.getAttribute('aria-label');
            if (name) {
                o.iframe_aria_text = name;
            }
        }
        // iframe_aria_text v Chromu poimenuje le urejevalno telo – okvir potrebuje še title (axe frame-title)
        var userCallback = o.init_instance_callback;
        o.init_instance_callback = function (editor) {
            var frame = editor.getContentAreaContainer && editor.getContentAreaContainer().querySelector('iframe');
            if (frame && !frame.getAttribute('title') && o.iframe_aria_text) {
                frame.setAttribute('title', o.iframe_aria_text);
            }
            if (typeof userCallback === 'function') {
                return userCallback.apply(this, arguments);
            }
        };
        return original(o);
    };
    window.tinymce.__a11y = true;
})();

// Dostopnost (ID 17, 8): Select2 – dostopno ime in prevodi.
// Vsak kontrolnik (tudi tisti, ki se inicializirajo z neposrednim klicem .select2(...)) dobi ime iz oznake
// izvirnega <select> (label for), iskalno polje dobi aria-label, nizi knjižnice so v jeziku strani.
// slovenske oblike: 1 znak, 2 znaka, 3–4 znake, 5+ znakov (ter 101, 102 …)
function slChars(n) {
    var m = n % 100;
    return n + ' ' + (m === 1 ? 'znak' : m === 2 ? 'znaka' : (m === 3 || m === 4) ? 'znake' : 'znakov');
}

var select2Language = {
    sl: {
        errorLoading: function () { return 'Zadetkov ni bilo mogoče naložiti.'; },
        inputTooLong: function (a) { return 'Izbrišite ' + slChars(a.input.length - a.maximum) + '.'; },
        inputTooShort: function (a) { return 'Vnesite še ' + slChars(a.minimum - a.input.length) + '.'; },
        loadingMore: function () { return 'Nalaganje več zadetkov …'; },
        maximumSelected: function (a) { return 'Izberete lahko največ ' + a.maximum + ' možnosti.'; },
        noResults: function () { return 'Ni zadetkov.'; },
        searching: function () { return 'Iskanje …'; },
        removeAllItems: function () { return 'Odstrani vse'; },
        removeItem: function () { return 'Odstrani'; },
        search: function () { return 'Iskanje'; }
    },
    en: {
        errorLoading: function () { return 'The results could not be loaded.'; },
        inputTooLong: function (a) { return 'Please delete ' + (a.input.length - a.maximum) + ' characters.'; },
        inputTooShort: function (a) { return 'Please enter ' + (a.minimum - a.input.length) + ' more characters.'; },
        loadingMore: function () { return 'Loading more results…'; },
        maximumSelected: function (a) { return 'You can only select ' + a.maximum + ' items.'; },
        noResults: function () { return 'No results found.'; },
        searching: function () { return 'Searching…'; },
        removeAllItems: function () { return 'Remove all items'; },
        removeItem: function () { return 'Remove item'; },
        search: function () { return 'Search'; }
    }
};

function a11ySelect2Label(select) {
    var label = select.id ? document.querySelector('label[for="' + CSS.escape(select.id) + '"]') : null;
    if (!label) {
        label = select.closest('label'); // oznaka, ki ovija <select>
    }
    if (label && !label.id) {
        var base = (select.id || select.name || 'select2') + '-label', id = base, i = 2;
        while (document.getElementById(id)) {
            id = base + '-' + i++;
        }
        label.id = id;
    }
    return label;
}

function a11ySelect2(select) {
    var $container = $(select).next('.select2-container');
    if (!$container.length) {
        return;
    }
    var label = a11ySelect2Label(select);
    var labelText = label ? label.textContent.replace(/\s+/g, ' ').trim() : (select.getAttribute('aria-label') || '');
    var $selection = $container.find('.select2-selection');
    // Select2 v kontrolnik vstavi <span role="textbox">, ki brez izbrane vrednosti nima imena (axe aria-input-field-name)
    var $rendered = $selection.find('.select2-selection__rendered[role="textbox"]');
    if (label) {
        var rendered = $selection.find('.select2-selection__rendered').attr('id');
        $selection.attr('aria-labelledby', rendered && !select.multiple ? label.id + ' ' + rendered : label.id);
        $rendered.attr('aria-labelledby', label.id);
    } else if (labelText) {
        // Select2 kontrolnik poimenuje z aria-labelledby na izbrano vrednost (aria-label bi bil prezrt) –
        // zato skrita oznaka z imenom in vrednost skupaj (npr. »Status: Akreditiran«)
        var hiddenId = select.getAttribute('data-a11y-name-id');
        var hidden = hiddenId ? document.getElementById(hiddenId) : null;
        if (!hidden) {
            var base = (select.id || select.name || 'select2') + '-a11y-name', i = 2;
            hiddenId = base;
            while (document.getElementById(hiddenId)) {
                hiddenId = base + '-' + i++;
            }
            hidden = document.createElement('span');
            hidden.id = hiddenId;
            hidden.className = 'visually-hidden';
            select.parentNode.appendChild(hidden); // na konec – ne spremeni :first-child / + pravil
            select.setAttribute('data-a11y-name-id', hiddenId);
        }
        hidden.textContent = labelText;
        var renderedId = $selection.find('.select2-selection__rendered').attr('id');
        $selection.removeAttr('aria-label').attr('aria-labelledby',
            renderedId && !select.multiple ? hiddenId + ' ' + renderedId : hiddenId);
        // vgnezden aria-labelledby se pri izračunu imena prezre → prebere se vsebina (izbrana vrednost)
        $rendered.removeAttr('aria-label').attr('aria-labelledby', hiddenId);
    }
    if (labelText) {
        $container.find('.select2-search__field').attr('aria-label', labelText);
    }
    // stanje napake/obveznosti (ID 19) se prenese z izvirnega <select> na kontrolnik
    ['aria-invalid', 'aria-describedby', 'aria-required'].forEach(function (attr) {
        var v = select.getAttribute(attr);
        if (v) {
            $selection.attr(attr, v);
        } else {
            $selection.removeAttr(attr);
        }
    });
    // pri večizbirnem Select2 je fokus na iskalnem polju – tudi to dobi stanje napake; opis napake se doda
    // k lastnim opisom Select2 (izbrane vrednosti), stari ID-ji napak se odstranijo
    var errorIds = (select.getAttribute('aria-describedby') || '').split(/\s+/).filter(function (id) {
        return id.indexOf('a11y-error-') === 0;
    });
    $container.find('.select2-search--inline .select2-search__field').each(function () {
        var own = (this.getAttribute('aria-describedby') || '').split(/\s+/).filter(function (id) {
            return id && id.indexOf('a11y-error-') !== 0;
        });
        var all = own.concat(errorIds);
        if (all.length) {
            this.setAttribute('aria-describedby', all.join(' '));
        } else {
            this.removeAttribute('aria-describedby');
        }
        if (select.getAttribute('aria-invalid') === 'true') {
            this.setAttribute('aria-invalid', 'true');
        } else {
            this.removeAttribute('aria-invalid');
        }
        if (select.getAttribute('aria-required') === 'true') {
            this.setAttribute('aria-required', 'true');
        }
    });
}

(function patchSelect2() {
    if (!$.fn.select2 || $.fn.select2.__a11y) {
        return;
    }
    var original = $.fn.select2;
    var lang = (document.documentElement.getAttribute('lang') || 'sl').substring(0, 2) === 'en' ? 'en' : 'sl';
    if (original.defaults && original.defaults.set) {
        original.defaults.set('language', select2Language[lang]);
    }
    $.fn.select2 = function (options) {
        if (options && typeof options === 'object' && typeof options.language === 'string') {
            options.language = select2Language[lang]; // npr. 'sl-SI' ni naložen – uporabimo lastne prevode
        }
        var result = original.apply(this, arguments);
        if (typeof options !== 'string') {
            this.each(function () {
                a11ySelect2(this);
            });
        }
        return result;
    };
    $.extend($.fn.select2, original);
    $.fn.select2.__a11y = true;

    // Select2 ob spremembah znova izriše del kontrolnika – ime ohranimo
    $(document).on('select2:select select2:unselect select2:clear change.select2', 'select.select2-hidden-accessible', function () {
        a11ySelect2(this);
    });
    $(document).on('select2:open', function (e) {
        var label = a11ySelect2Label(e.target);
        if (label) {
            $('.select2-container--open .select2-search__field').attr('aria-label', label.textContent.replace(/\s+/g, ' ').trim());
        }
    });
})();

// Auto-initialize Select2 on page load and after dynamic content
$(document).ready(function() {
    initSelect2('.select2-select');
});

// Expose Select2 initialization for dynamic content
window.initSelect2 = initSelect2;

$(document).ready(function() {
    var csrf = $('meta[name="_csrf"]').attr('content');
    var csrfHeader = $('meta[name="_csrf_header"]').attr('content');

    $.ajaxSetup({
        beforeSend: function(xhr) {
            xhr.setRequestHeader(csrfHeader, csrf);
        }
    });
});

// All window exports are now at the end of the file (after line 9850)

// Expose all page-specific functions to window for template compatibility
window.stickyFooter = stickyFooter;
window.otp = otp;
window.eassessment = eassessment;
window.profile = profile;
window.profileOTPLogic = profileOTPLogic;
window.profileInAdmin = profileInAdmin;
window.accreditationPreview = accreditationPreview;
window.formA1 = formA1;
window.formA2 = formA2;
window.formB = formB;
window.formC = formC;
window.userNewsList = userNewsList;
window.nominationView = nominationView;
window.UserNominationView = UserNominationView;
window.nominationUploadStatements = nominationUploadStatements;
window.uploadConditionRecheck = uploadConditionRecheck;
window.nominationAdd = nominationAdd;
window.NominationForm1 = NominationForm1;
window.ConditionRecheck = ConditionRecheck;
window.nominations = nominations;
window.conditionVerification = conditionVerification;
window.archiveNotices = archiveNotices;
// All window exports are now at the end of the file (after line 9850)
window.registration = registration;
window.changesBeforeNomination = changesBeforeNomination;
window.comparison = comparison;
window.userOverview = userOverview;
window.userOverviewAdmin = userOverviewAdmin;
window.userSnapshotList = userSnapshotList;
window.userStatus = userStatus;
window.userRequests = userRequests;
window.vsiUporabniki = vsiUporabniki;
window.vsiUporabnikiAdmin = vsiUporabnikiAdmin;
window.newsList = newsList;
window.editNews = editNews;
window.interviewInvitation = interviewInvitation;
window.declineNomination = declineNomination;

// Utility functions
window.fillJsTemplate = fillJsTemplate;
window.saveDynamicItem = saveDynamicItem;
window.deleteDynamicItem = deleteDynamicItem;
window.closeDynamicItem = closeDynamicItem;
window.truncate = truncate;
window.drags = drags;
window.migrateLegacyBootstrapAttributes = migrateLegacyBootstrapAttributes;
window.startBootstrapAttributeObserver = startBootstrapAttributeObserver;
window.refreshBootstrapInteractions = refreshBootstrapInteractions;

// Load functions
window.loadCompanies = loadCompanies;
window.loadCompaniesNew = loadCompaniesNew;
window.loadPreviousCompanies = loadPreviousCompanies;
window.loadPreviousCompaniesNew = loadPreviousCompaniesNew;
window.loadPreviousEmployments = loadPreviousEmployments;
window.loadEducations = loadEducations;
window.loadEducationsNew = loadEducationsNew;
window.loadLanguages = loadLanguages;
window.loadLanguagesNew = loadLanguagesNew;
window.loadCompetences = loadCompetences;
window.loadCompetencesNew = loadCompetencesNew;
window.loadCompetencesAdmin = loadCompetencesAdmin;
window.loadCompetencesAdminNew = loadCompetencesAdminNew;
window.loadManagementExperience = loadManagementExperience;
window.loadManagementExperienceNew = loadManagementExperienceNew;
window.loadMembership = loadMembership;
window.loadAttachments = loadAttachments;
window.loadAttachmentsNew = loadAttachmentsNew;
window.loadAttachmentsForNomination = loadAttachmentsForNomination;

// File functions
window.deleteAttachment = deleteAttachment;
window.deleteAttachmentForNomination = deleteAttachmentForNomination;
window.makeLeadingAttachment = makeLeadingAttachment;
window.deleteFiles = deleteFiles;
window.moveFiles = moveFiles;
window.signFile = signFile;
window.renameFile = renameFile;

// Modal functions
window.showEmailModal = showEmailModal;
window.showEmailFinishModal = showEmailFinishModal;
window.showFinishUpdatingDataEmailModal = showFinishUpdatingDataEmailModal;
window.showDeleteEmailModal = showDeleteEmailModal;
window.showMessageModal = showMessageModal;
window.showMessageModalUserNotice = showMessageModalUserNotice;
window.showMotivationalLetterModal = showMotivationalLetterModal;
window.showMotivationalLetterModalWithVersion = showMotivationalLetterModalWithVersion;
window.showUsersModal = showUsersModal;
window.showModalToSendEmail = showModalToSendEmail;

// Other utility functions
window.checkNominationRequirements = checkNominationRequirements;
window.checkIfAllThreeStatementsAreUploaded = checkIfAllThreeStatementsAreUploaded;
window.checkHowManyCompanies = checkHowManyCompanies;
window.getFavourites = getFavourites;
window.updateFavourites = updateFavourites;
window.updateUserVop = updateUserVop;
window.updateUserDashboardToggles = updateUserDashboardToggles;
window.getGeocompleteData = getGeocompleteData;
window.getOptionValueByText = getOptionValueByText;
window.finishUpdatingData = finishUpdatingData;
window.fillFiltersWithCookie = fillFiltersWithCookie;
window.setFiltersCookie = setFiltersCookie;
window.selectBtnFilterFromCookie = selectBtnFilterFromCookie;
window.setBtnFilterCookie = setBtnFilterCookie;
window.newexportaction = newexportaction;
window.EMSOvalidator = EMSOvalidator;

// Dashboard functions
window.dashboardUser = dashboardUser;
window.dashboardAdmin = dashboardAdmin;
window.dashboard = dashboard;
window.dataAccessPurpose = dataAccessPurpose;

// Wait for jQuery plugins (geocomplete, loadtemplate) to be ready before signaling app-ready
// This ensures that functions like formA1() which use geocomplete will work correctly
if (window.jQueryPluginsReady) {
  window.jQueryPluginsReady.then(() => {
    console.log('jQuery plugins ready - dispatching app-ready event');
    window._appReady = true;
    window.dispatchEvent(new Event('app-ready'));
  });
} else {
  // Fallback: if jQueryPluginsReady is not defined, dispatch immediately
  console.warn('jQueryPluginsReady not found - dispatching app-ready immediately');
  window._appReady = true;
  window.dispatchEvent(new Event('app-ready'));
}
